// =====================================================================
//  The table's "referee". It holds where every card and token is, applies
//  each action (draw, play, tap…), writes the game log, and decides what
//  each player is allowed to SEE. It never enforces game rules — only the
//  table basics (hand limit, who owns which card, hidden cards).
//
//  The same engine runs in the browser for the solo table and will run on
//  the server for online games, so hidden cards are never sent to the
//  other player's browser.
//
//  Positions are "world" coordinates: seat 0 sits at +z, seat 1 at -z.
// =====================================================================
import { CONFIG } from '../config.ts';
import { useLayout } from './layouts.ts';
import { type CardDb, type CardDef, type CardEffect, type EmperorAbility, MAX_TIERS, arrivalCounters, formOf, isEmperor, roman, spanMode, tierRefund, tieredDamage, tieredRemoval, typeWithArticle } from './cards.ts';
import { type CostFacts, priceOf } from './costs.ts';
import type { TokenDb } from './tokens.ts';

/** Statuses that only add to attacks: never given to a unit that can never attack. */
const NEEDS_ATTACK = ['damage-boost', 'overclock'];
const numbersIn = (cell: string) => (cell.match(/\d+(\.\d+)?/g) ?? []).map(Number);

export type Seat = 0 | 1 | 2 | 3;
/**
 * Seats: 1v1 uses 0 and 1. A 2v2 game (teams) uses all four: seats 0 and 2 are one team, 1 and 3 the other.
 * Seats 0 and 1 sit on the left of their team's side of the mat, 2 and 3 on the right.
 */
/** Which side of the mat a seat plays on: 1 = the near side in the settings' coordinates, -1 = the far side. */
export function sideOf(seat: Seat | number): 1 | -1 {
  return seat % 2 === 0 ? 1 : -1;
}
/** Which half of their side a seat has in a 2v2 game: 1 = the left (as they see it), -1 = the right. Always 1 in 1v1. */
export function handOf(seat: Seat | number): 1 | -1 {
  return seat < 2 ? 1 : -1;
}
/** A seat's team: 0 or 1 (in 1v1, each player is their own team). */
export function teamOf(seat: Seat | number): 0 | 1 {
  return (seat % 2) as 0 | 1;
}
export type PileName = 'deck' | 'discard';
/** Where a card goes into a pile: on top, on the bottom, about the middle, or (a number) with that many cards above it. */
export type Where = 'top' | 'bottom' | 'middle' | number;
/** "on top of", "8th from the top of"… for the log. */
export function whereWords(where: Where): string {
  if (typeof where === 'number') {
    const n = Math.max(0, Math.floor(where)) + 1;
    if (n === 1) return 'on top of';
    const th = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
    return `${n}${th} from the top of`;
  }
  return where === 'top' ? 'on top of' : where === 'bottom' ? 'on the bottom of' : 'into the middle of';
}
export const PHASES = ['Overdraw Phase', 'Summon Phase', 'Attack Phase', 'End Phase'] as const;
const ROTATION_NAMES = ['active', 'inactive', 'entrenched', 'inactive'];

export interface HeldCard {
  id: string;
  num: number;
  spawned: boolean;
}
export interface FieldCard extends HeldCard {
  owner: Seat;
  x: number;
  z: number;
  rot: number; // quarter turns clockwise, seen from the owner's seat (0 = forward)
  faceUp: boolean;
  layer: number; // higher = on top
  merged: HeldCard[]; // copies merged underneath; tier = 1 + merged.length
  tierToken: string | null; // id of the Tier II / Tier III token sitting on this card
  form?: number; // a card with two costs (3/7): which one it was summoned at (0 = the first; left out for ordinary cards)
  restarts?: number; // a Tome: how many times its owner has paid to restart it (each restart lasts one turn less)
  playedTurn?: number; // which of its owner's turns it was played in (a Spell that does more on later turns)
  lanes?: { side: 1 | -1; names: string[] }; // the lanes chosen for it as it was played (Toxic Cloud, Totem of Regeneration)
  host?: string; // a Spell attached to a Unit: that Unit's id (Balloons, Cursed Hand, Invisibility Cloak)
  cloakBy?: string; // a Unit hidden by an Invisibility Cloak (that Spell's id): it can't be attacked and doesn't fill its lane
  tauntAt?: number; // the turn in which its opponents may attack nothing else (Taunt)
  usedAt?: number; // the turn (turnStamp) its once-a-turn "click" effect was last used in (Moldering Tome)
  usedBy?: Record<number, number>; // …a "click" effect either player may use (Coral Reef): the turn each one last used it in
  retapAt?: string; // the turn (turnKey) it last used a Tap ability while already tapped (Wandering Spirit)
  paidHit?: number; // an attack paid for as it was declared (Dark Gerzite Titan): the damage of each hit of that attack
  boostHit?: number; // damage added to this one attack (Volcanic Disciple)
  shiftTo?: { slot: string; side: 1 | -1 } | null; // an attack Gravitation shifted: the lane it goes into instead (null = not shifted)
  sideAs?: 1 | -1; // a Spell turned back by Reflection: the side of the field it now works for
  reflectedFrom?: Seat; // …and whose Spell it was (it goes back to their discard pile)
  trueNum?: number; // turned into something else for a while (Sheepify): the card it really is
  walkStage?: number; // Void Walker: how many times it has come back
  hp?: number; // its full health set as it came back (Dark Sovereign: 1; Void Walker: 10, then 4)
  dmgNow?: number; // …and its damage (Void Walker: 2, then 1)
  rise?: boolean; // Dark Sovereign: destroyed, it is about to come back as an Undead Unit with 1 health
  crossing?: boolean; // Eldritch Realm Crosser: destroyed, it is about to become the Biome
  asBiome?: boolean; // …and now it is the Biome
  illusion?: string; // Illusionist: the other one of the pair (the first to be damaged goes; attacking first sends the other away)
  soul?: { num: number; until: number }; // Valley of Waiting: the Troop this Soul was, and the turn of its owner's it lasts until the end of
  bonusHit?: number; // damage its first hit of each attack has added for good (Cordyceps Hatchlings)
  hurtFirst?: boolean; // it has taken damage before (Cavity Clam)
  berry?: number; // Berry Reserve: the damage it took during the opponent's last turn
  berryAt?: number; // …and the turn that was (turnStamp): damage from an older turn is never added in
  intercede?: string; // Grave Intercessor: the Troop it is destroyed in place of
  wrath?: number; // Wrathful Ghost: its attacks since its last Special Hit
  rewind?: { x: number; z: number; from: Token[]; to?: Token[]; ready?: number }; // Time Bender: its tokens as its opponent's last turn began (from) and as it ended (to); ready = its owner's turn it may be undone in
  rewoundAt?: number; // Time Bender: its owner's turn (players[].turns) it last undid a turn in
  sun?: { base: number; attacked?: string; reset?: boolean }; // Sun Magnifier: its base damage now, and what happened this turn
  copied?: { num: number; kind: 'enter' | 'passive' | 'tap'; at: number }; // Woods Wailer: the effect it copied, and the turn it copied it in
  revealedBy?: string; // a face-down Trigger shown to its opponent by a card (Glacial Wyrm, Molten Stallion): that card's id
  borrowed?: number[]; // Soul Collector: the Troops attached to it, whose mechanics it uses
  heldBy?: string; // a Troop Soul Collector holds (its id): attached under it to show what it has taken; it can't be hit and does nothing itself
  tribunalOf?: string; // a Unit laid face-down by Tribunal Trigger (its id): it can't be targeted and doesn't block its lane
  paidFor?: number; // what was paid to play this card (Tribunal Trigger gives half of it back)
  mustAttack?: string; // Jouster: the card this one has to attack, and nothing else
  pair?: string[]; // a Spell joining two Units (Branch Out, Shadow of Death, Symbiotic Vines): their ids
  mergedInto?: string; // Branch Out: the second Unit's damage goes onto the first, whose health holds both
  wardAt?: number; // the turn (turnStamp) in which nothing can damage it (Impervious Smite)
  transmitting?: boolean; // Transmissive Death: it has put its token on a Unit (it goes once none is left)
  troopForm?: boolean; // a card that is a Building or a Troop: it has become the Troop (Melstone Terror, once its SP ran out)
  spellWardAt?: number; // the turn in which Spells can't damage it (Impervious Shield)
  tierBoost?: number; tierBoostAt?: number; // tiers lent by an effect (and the only turn they count in, if they are for a turn)
  veilAt?: number; // the turn (turnStamp) in which it can't be attacked
  marks?: Record<string, number>; // "mark_look coat on it": what an ability left on it (and the seat whose ability it was), for the table to show and for what comes of it (Molten Coat; Burning Necrosis: Burn spreads when it is destroyed)
  veilLanes?: boolean; // a Spell guarding the lanes marked on it: while it is active, nothing of its owner's opponents can target or damage what stands there (Protective Petals)
  negating?: number; // an Emperor: no damage that isn't Pierce lands on it while this ability (its slot) is on cooldown
  mode?: number; // the choice made as it was played (Forest Paths)
  gave?: Record<string, number>; // what it has given of a status, by "token:card" (for "no more than 3 from this card", and to take it back)
  holdTapped?: number; // it stays tapped through this many of its owner's untaps
  fightsFor?: 1 | -1; // solo table: a unit summoned into the far side's lane by a card on the near side (or the other way): the side it fights for
  summonedBy?: string; // a unit an effect summoned: the id of the card that summoned it (for "at most 3 of its own")
  hurtAt?: number; // the turn (turnStamp) it last took damage in
  restedAt?: number; // which of its owner's turns it last ended untapped
  cappedAt?: number; // "take: cap 3 once": the turn that was last used in
  acted?: string; // the turn (turnKey) it attacked or used its Tap ability in: it can't be untapped again that turn
  swings?: { key: string; left: number }; // the hits it still has this turn, once it has started attacking
  // Cragged Skulker: the attacking actions it has gained, one each turn it begins in play (it keeps them). Each is an
  // attack of its own (a target each), and the card is only turned once the last one is used
  actions?: number;
  chainAt?: { x: number; z: number }; // a Spell that jumps from Unit to Unit (CONFIG.looks.chain): where its last jump landed
  swarm?: string; // a swarm (a card with a Count) is several cards on the table, one for each unit: the id of the card played (CONFIG.swarms)
  swarmHit?: string; // the turn (turnKey) its swarm attacked: the rest of the swarm must attack too, or is tapped as the Attack Phase ends
  swarmMerged?: number; // a swarm that has been tiered up: how many copies were merged into it (every unit of it has that tier)
  swarmAim?: { key: string; target: string }; // what it attacked this turn (a swarm all at one target: Fleet of Archers' bonus)
  restHits?: number; // Marlin Knight: hits added to its next attack, one for each of its owner's turns it went without attacking
  harvested?: string; // a tap_harvest card (Gerzite Farmer): the turn it last gained Harvest in, so a second tap that turn gains nothing
  atZero?: boolean; // its health (or SP) is at 0 and the table has already marked it destroyed: it isn't marked twice
  // tokens this card arrived with and has to keep (its sheet row says ends = token_gone): when one of them is
  // no longer on the table, the card ends. The name is kept so the table can still say which one went.
  brought?: { id: string; name: string }[];
  roll?: Seat; // that player owes a die roll for this card (ends = opponent_rolls_even): they can't move on before it
  rollFor?: 'coin'; // …and what it is for: a coin flip for Gerzite (on_play = coin_gerzite) rather than ending the card
  spanOf?: string; // one of a Lanespanner's extra units: the id of the card played from the hand (they are spawned copies)
  legion?: string; // one of Undead Legion's soldiers (an Undead or a Spirit Soldier): the id of its General, the card played
  // how big it lies on the table (1 or left out = full size): cards sharing a lane shrink to fit, and a summoned
  // unit attached to a card is small. The tokens on it shrink with it (Token.scale).
  scale?: number;
  attachedTo?: string; // a summoned unit snapped onto the bottom edge of this card (its id): it follows that card
  attachOrder?: number; // …in this order along the edge
  swapped?: boolean; // on_untap swap_tokens: its tokens are off for now, and come back the next time it is untapped
  clone?: { by: string; health?: number; damage?: number }; // a clone of a card (From the Mirror Realm…): what made it, and its full health (and damage) if that was changed
  wakes?: number; // how many of its owner's turns have begun since it appeared (Pile of Ashes)
  bornIn?: Seat; // whose turn it appeared in
  secret?: boolean; // in play and at work, but its opponents see only a face-down card (Timed Blast, until it goes off)
}
/** Something that just happened that the table should show an effect for. */
/** What an effect knows as it is carried out: what was attacked, who attacked, the card in question, an amount, an answer given. */
interface FxCtx { shifts?: Record<string, { slot: string; side: 1 | -1 } | null>; target?: FieldCard; attacker?: FieldCard; it?: FieldCard; amount?: number; fresh?: boolean; chose?: number; lane?: string; laneFoe?: boolean; actor?: Seat; count?: number; hurt?: FieldCard[]; tossed?: boolean; picked?: string[]; moved?: boolean }
/** A choice a card effect is waiting for. */
/** Cards turned up from a deck by a card effect, for both players to see (Occult Sorcerer's Entrance). */
export interface Showing {
  id: number;
  by: Seat; // whose deck, and who says Continue
  name: string; // the card that did it
  cards: { id: string; num: number; to: 'hand' | 'discard' | 'stays' }[];
  gain?: number; // Gerzite gained for each card that goes to the hand
  waiting: boolean; // nothing has happened to them yet: their player clicks Continue
}
export interface PendingPick {
  id: number;
  seat: Seat; // who chooses
  name: string; // the card asking
  text: string; // what is being chosen
  options: string[]; // the cards that may be chosen (ids on the table); empty for a yes / no question
  yesNo?: boolean;
  optional?: boolean; // it may be passed
  showing?: number; // waits for its player to have seen the cards turned up (state.showing, by its id): Continue
  skip?: string; // what the button that passes it says ("Discard nothing")
  src?: FieldCard; // the card whose effect this is, as it was (it may have left the table since)
  rest?: CardEffect[]; // the effect that needs the choice, and those after it
  ctx?: { target?: string; attacker?: string; it?: string; amount?: number; chose?: number; lane?: string; laneFoe?: boolean; actor?: Seat; count?: number; tossed?: boolean; picked?: string[]; shifts?: Record<string, { slot: string; side: 1 | -1 } | null> };
  gravLanes?: { target: string; lanes: { slot: string; side: 1 | -1 }[] }; // Gravitation: the lanes a Spell's damage on `target` may be shifted into
  entomb?: string; // Cursed Sarcophagus (its id): a Spell from the hand to lay face-down under it, sprung when it is destroyed
  toss?: boolean; // a card to discard from the hand, whose cost is added to what the effect deals (Sacrifice)
  lava?: 'ask' | 'hand'; // Molten Lava River: a card to discard, from the hand or the top of the deck
  choices?: string[]; // a choice between things to do (or lanes), by name: answered with its place in the list, from 1
  laneChoices?: { name: string; side: 1 | -1 }[]; // …a lane to move a Unit into: the lane behind each choice
  laneHints?: { x: number; z: number }[][]; // a choice of lanes: the middle of each lane behind each choice (they light up as the choice is pointed at)
  swap?: { first?: string; by: string }; // Coral Reef: two of your Units to swap (the first one, once chosen; the Biome)
  // damage dealt 1 point per click, as the player likes (Dividend): how much is left, how much more may go on the
  // opposing Emperor or Minion, what has gone on the Emperor so far (halved at the end), and the kind of points
  split?: { left: number; cap: number | null; emp: number; kind: 'damage' | 'wound'; down: boolean; any: boolean; most?: number; hit: string[]; total: number };
  numbers?: { label: string; min: number; max: number; value: number }[]; // numbers to set (steppers), answered all at once
  confirm?: string; // the button that answers a numbers choice
  hold?: { seat: Seat; from: string; to: string; stage: number; kind: 'titan' | 'disciple' | 'gravity'; by?: string; lanes?: { slot: string; side: 1 | -1 }[] };
  blind?: { foe: Seat; left: number; gone?: number }; // Banishment: cards of the opponent's hand to discard, chosen by place without seeing them
  arrange?: { foe: Seat; undead: string[] }; // Ephemeral Gaze: the top cards of the opponent's deck to put in order (and the Undead ones that may be discarded)
  repeat?: string; // a choice asked again and again until its player is done (Forbidden Resurgence: the verb done to each card)
  celestial?: string; // Celestial: a Troop to bring back from the discard pile in its place (its id) // an attack waiting for numbers (Dark Gerzite Titan's payment, Volcanic Disciple's boost)
  cards?: { id: string; num: number }[]; // a search: the cards in the pile that may be taken (only its player sees them)
  search?: { w: string[]; left: number }; // …and the search itself (its words; how many may still be taken)
  strike?: { seat: Seat; from: string; to: string; cost: number }; // an attack on hold while its target's owner decides (Orc Patroller)
}
export interface FxEvent {
  id: number;
  kind: 'play' | 'emerge' | 'spell' | 'look' | 'reveal' | 'ambush' | 'tier' | 'damage' | 'roll' | 'slot' | 'link' | 'attack' | 'attackEnd' | 'ping' | 'sacrifice' | 'status' | 'pileMove' | 'peek' | 'ability' | 'decay' | 'abilityAsk' | 'toHand' | 'barFull' | 'freePlay' | 'cure' | 'revealCard' | 'spent';
  card: string; // id of the card on the table ('' for a dice roll)
  tier?: number;
  token?: string; // roll: the die that was rolled · status: the token whose effect just happened
  from?: { x: number; z: number }; // roll: where the die was before it was thrown
  effect?: string; // slot: which effect to play on the card that was placed in a slot · status: 'damage', 'heal' or 'notice' · pileMove: the pile the card left ('deck' or 'discard')
  text?: string; // status, ping: a line to show ("Burn on Card 9: roll to cure it…"), to `seat` only, or to everyone if there is no seat
  target?: string; // link, attack, spell: the card at the other end (spell: what a Spell is aimed at; from = where the Spell is, effect = 'strike' if it deals damage, tint = its affiliation)
  at?: { x: number; z: number }; // attack, ping: a spot on the table
  seat?: Seat; // attack, ping, sacrifice: who did it
  num?: number; // revealCard: the card that was shown (its number)
  tint?: string; // toHand: 'remaining' = The Remaining is the card flying to the hand (its glow is deep red, not the usual color)
  ref?: number; // attackEnd: the id of the attack whose arrow is taken away
  hop?: number; // a Spell jumping from one Unit to the next (Chain Lightning): which jump of this update it is (0 = the first), each one setting off as the last lands
  count?: number; // peek: how many cards are being looked at (effect = which pile, seat = whose) · toHand: how many cards went from that pile into `seat`'s hand
  // abilityAsk: a Lock or a timer was put on an Emperor (`card`) by `seat`, who is asked which ability the
  // `token` is for. effect = 'lock' or 'cooldown'.
  // cure: a roll to cure a status. value = the face the die showed, effect = 'cured' or 'stays', seat = who
  // rolled, text = what happened, token = the status token, target = its copy across the Emperor–Minion link.
  // (The die itself is a real die on the table: its throw is the 'roll' event just before this one.)
  value?: number;
  // look: a card's own effect (effect = its name in CONFIG.looks / the effect recipes): on `card`, travelling from
  // `card` (or `from`) to `target`, on a `token`, at a spot (`at`), or on `seat`'s `pile`
  pile?: 'deck' | 'discard';
}
export interface Token {
  id: string;
  tokenId: string;
  x: number;
  z: number;
  value: number;
  layer: number;
  by: Seat | null; // who put it on the table
  since?: number; // which of that player's turns it was placed in (so a fresh Entrenched isn't ticked down at once)
  // a token that belongs to one of an Emperor's abilities: a turn timer = that ability's cooldown; a Locked
  // token = that ability is locked. Which Emperor, which ability.
  ability?: { card: string; slot: number };
  twin?: string; // the copy of this status token on the linked card (Emperor ↔ Minion); their numbers stay together
  // a counter that holds an amount (Weakness 2, Shield 3) can also carry a turn clock: how many more turns it
  // lasts. It goes down as its card's owner enters their End Phase; at 0 the counter is taken off.
  turns?: number;
  turnsSince?: number; // which of its card owner's turns the clock was set in (only for CONFIG.turn.durations.startsNextTurn)
  // …or a wear-off timer: this much of its number is temporary and wears off, 1 at a time, as its card owner's
  // turns begin (CONFIG.turn.durations.drainWhen) (Thorns 1 + 2 from Cagurn's ability: 3, then 2, then its own 1)
  drain?: number;
  drainSince?: number; // which of its card owner's turns the wear-off timer was set in (for startsNextTurn)
  // a Link counter: the card under it takes only half of the damage copied onto it across the link (rounded up,
  // over all the hits together: two hits of 1 make 1). linkSum = all the damage that has come across to it.
  half?: boolean;
  linkSum?: number;
  span?: string; // a Lanespanner token: which Lanespanner it belongs to (the id of the card played from the hand)
  // a turn timer that is waiting for its number (a card whose turns cell says "?"): it shows "?", does not
  // count down and never runs out until a player gives it a number. Its value is 0 until then.
  unset?: boolean;
  from?: string; // the card that brought this token with it (the `brings` cell of its sheet row)
  // an affiliation token that adds its affiliation to the card's own instead of replacing them, for as long as the card
  // keeps a status of this kind (Givellt's Arcane Ascension: Magical, until its Overclock is gone)
  alsoWhile?: string;
  native?: number; // a counter the card came with from its own sheet row (its own Buffer): this much of it doesn't count as a status put on it
  lent?: { spell: string; n: number }[]; // how much of its number Spells in play lend it ("status buffer 1 with_spell": Buffer): it goes when that Spell leaves play
  until?: { seat: Seat; turn: number }; // it comes off as that player ends that turn of theirs ("until the end of your next turn")
  payBy?: Seat; // a Lock: the only player who may pay it off (Persistent Shield's Lock is its opponent's to pay)
  max?: number; // a Count-Up Timer: the number it stops at
  cure?: Seat; // this status has just dealt its damage and that player now owes a die roll to try and cure it (Burn)
  cureDie?: Seat; // a die: the one that player's cure rolls are made with (it is picked up again for the next one)
  all?: boolean; // a damage counter on a card of several units (a Count) that every unit has taken (splash): it stays as units fall
  grounds?: boolean; // a Lock that keeps its Unit from Flying while it lasts (Grasping Twine)
  onPaid?: { by: Seat; w: string[] }; // a Lock: what happens when it is paid off (gain 2 for the player who placed it, or statuses on its card)
  tally?: string; // a Health counter the table keeps for a card (Forest Hivemind): that card's id
  price?: number; // a Locked token: what it costs to take it off (0 or missing = no cost set)
  priceDark?: boolean; // …paid in Dark Gerzite rather than Gerzite
  scale?: number; // it sits on a card that is smaller than full size, and is shrunk to match (1 or left out = full size)
  // A Gerzite or Dark Gerzite token lying on the table rather than on a bar is a counter: its number is an amount,
  // and it can be set to add that much to (or take it off) its owner's bar as each of their turns begins or ends,
  // for as many turns as its turn clock says (Tome of Gerzite: 1 at the start of each turn while it lasts).
  asCounter?: boolean;
  flow?: { mode: 'add' | 'remove'; when: 'start' | 'end' };
  actAt?: 'start' | 'end'; // a status whose moment its player picks (Regeneration): when it acts and its clock goes down
}
export interface PlayerState {
  name: string;
  discounts?: { amount: number; w: string[]; stamp?: number; by?: string }[]; // costs taken off the next card of a kind they play (stamp: only in that turn)
  darkSpent?: { stamp: number; n: number }; // Dark Gerzite spent in this turn (stamp: the turn), for Gemstone Hoarder's cap
  playedNow?: { stamp: number; types: string[] }; // the types of the cards they have played this turn (for "the first Spell each turn")
  out?: boolean; // 2v2: this player's Emperor has fallen. Their turns are skipped and their teammate plays on alone
  deck: HeldCard[]; // index 0 = top
  hand: HeldCard[];
  discard: HeldCard[]; // index 0 = top
  limbo?: HeldCard[]; // discarded while Heart of Limbo is active: set aside, out of reach of anything (index 0 = top)
  searching: PileName | null;
  shuffles: { deck: number; discard: number };
  jumps?: { deck: number; discard: number }; // how many times The Remaining has moved within each pile (the table plays its effect when one goes up)
  turns: number;
  overflow: HeldCard | null; // a card drawn by an effect with a full hand, waiting for its owner to choose where it goes
  span?: { card: string; starts: number[] } | null; // a Lanespanner just played: its owner chooses where the rest go (`starts` = the first lane of each choice, 0 = Lane 1)
  noSpellsThrough?: number; // they can't cast Spells until this turn of theirs is over (Split Chasm)
  noEndGerzite: string | null; // a card played this turn that takes away the end-of-turn Gerzite (its name, for the log)
  // the Unit in this player's hand that a "unit + 1" card paid for (Tribunal Trigger): it will be played
  // face-down for free. `by` is the name of the card that chose it.
  freeUnit: { id: string; by: string } | null;
  played: number[]; // every card this player has played or placed from their hand (and their Emperor), once each: for the card stats
}
export interface LogEntry {
  id: number;
  text: string;
  seat: Seat | null;
}
/** "Ana wants to see the top 3 cards of your deck." */
export interface PeekRequest {
  from: Seat;
  owner?: Seat; // whose pile (2v2, where there is more than one other player)
  pile: PileName;
  count: number;
}
/** How a game can be brought to an end: one player gives up, claims the win, or proposes to call it where it stands. */
export type EndKind = 'forfeit' | 'win' | 'call';
export interface EndRequest {
  kind: EndKind;
  from: Seat;
}
/**
 * The Battle Timer (1v1 and 2v2): while it is on, each player has CONFIG.battleTimer.seconds for each of their
 * turns, and one who runs out loses by forfeit. Turning it on or off is asked for and needs everyone on the other
 * side to agree (bots always do). The game server keeps the time and sends `timeUp` when it runs out.
 */
export interface BattleTimer {
  on: boolean;
  ask: { from: Seat; on: boolean; yes: Seat[] } | null; // a request waiting: who asked, to turn it on or off, who has agreed so far
}
export interface GameResult {
  winner: Seat | null; // null = a draw, or a solo game (nobody to beat). 2v2: a seat of the winning team
  team?: 0 | 1; // 2v2: the winning team
  kind: EndKind | 'end'; // 'end' = a solo game simply stopped
  timeUp?: { seat: Seat; why: 'clock' | 'gone' | 'everyone' }; // a forfeit the game server called: this player's Battle Timer ran out, or they left and didn't come back
  damage: number[]; // the damage on each player's Emperor when the game ended
}
export interface GameState {
  mode: 'solo' | 'versus';
  players: PlayerState[];
  field: FieldCard[];
  tokens: Token[];
  turn: Seat;
  first: Seat; // who was picked to go first (blue); the other player is red
  fractalsTaken: boolean; // has the player whose turn it is collected this turn's Fractals yet?
  // the last card each player played face-up, and what they paid for it (for costs that copy it: "last_card + 1")
  lastPlayed: ({ num: number; cost: number; dark: boolean; at?: number } | null)[];
  teams?: boolean; // a 2v2 game: four seats, two teams (see sideOf / teamOf)
  handsOpen: string | null; // the card in play that keeps both hands in plain view (its name), or null
  limboCards?: string[]; // the Heart of Limbo cards on the table (when one leaves, the Limbo piles come back)
  phase: number;
  log: LogEntry[];
  fx: FxEvent[];
  reveals: ({ from: Seat; cards: HeldCard[] } | null)[]; // indexed by the viewer
  peekRequest: PeekRequest | null; // one player has asked to see the top cards of the other's pile
  // cards a card effect has turned up from its player's deck (Occult Sorcerer), shown to both players before
  // anything happens to them: each with what will happen to it. `waiting` = its player hasn't said Continue yet
  showing?: Showing | null;
  peeks: ({ owner: Seat; pile: PileName; cards: HeldCard[] } | null)[]; // what each viewer is being shown
  endRequest: EndRequest | null;
  battleTimer?: BattleTimer;
  emperorHits?: number[]; // the damage each player has dealt to opposing Emperors with attacks and Spells (deciding a call or an Overtime finish that is otherwise even)
  bots?: Seat[]; // seats played by the computer (online "play against a bot"): the other player may handle their cards too
  botLevels?: Partial<Record<number, 'easy' | 'skilled' | 'expert'>>; // how well each bot chooses what its cards' effects aim at
  // a bot wants to move on ("Martin wants to move to the Attack Phase") and waits for a Go ahead. end = it wants to
  // end its turn: that waits until everything on its To settle list has been settled
  botAsk?: { from: Seat; text: string; end?: boolean } | null;
  // choices a card effect is waiting for ("Burn any one Unit your opponent controls"): the first one for a seat is
  // put to that player; what the effect still has to do (`rest`) carries on once they have answered
  picks?: PendingPick[];
  biomeOff?: { side: 1 | -1; at: number }; // Traveler's Map: the Biome in play does nothing to that side during that turn
  lastFallen?: { num: number; owner: Seat }; // the Troop most recently destroyed, on either side (Dead Rising)
  returns?: { num: number; owner: Seat; lane: string; side: 1 | -1; stage: number; turn: number }[];
  bargain?: { by: string; seat: Seat; value: number; max: number; agreed: Seat[] }; // Dark Bargain: the amount both players lose, waiting for both to agree
  graves?: { num: number; owner: Seat; at: number }[]; // Troops destroyed lately, and when (Celestial)
  echoes?: { by: string; seat: Seat; lane: string; side: 1 | -1; amount: number; pierce: boolean; foe: Seat; turn: number }[]; // Executioner's follow-up attacks, due as that opponent's turn ends // Void Walkers coming back at the end of their owner's turn
  calling?: Seat[]; // players whose Calling Pillars have just filled their lanes: the eruption is playing, and the rest follows (resolveCalling)
  // Cagurn's Thorny Snare: laid in an opposing lane, it waits there; the first Unit summoned into that lane takes `hits`
  // hits of `amount` damage each (a Shield breaks on the first, and so on), and the snare is gone
  snares?: { lane: string; side: 1 | -1; by: Seat; from: string; hits: number; amount: number }[];
  botNotes?: string[]; // what a bot has announced that its opponent still has to settle (each is ticked off on its own)
  tutorial?: boolean; // a game against Martin with the tutorial's hints on (it never counts toward the card stats)
  // miss chances left on Units for one turn (Drifts Tarragon, Ash Beetle): that card, how likely, and the turn of
  // its owner's in which it counts (their turns count then)
  missMarks?: { card: string; chance: number; by: string; seat: Seat; turns: number }[];
  // the last roll to miss: which attack, and whether it missed (an arrow drawn again this turn keeps its roll)
  lastMiss?: { from: string; to: string; missed: boolean; key: string } | null;
  missRolls?: Record<string, boolean>; // this turn's rolls, by attacker:target
  result: GameResult | null;
  nextId: number;
  nextLayer: number;
  version: number;
}

export type Action =
  | { type: 'draw'; pay?: boolean; overflow?: boolean } // pay = an Overdraw: costs Gerzite, only in your Overdraw Phase · overflow = an Effect Draw with a full hand
  | { type: 'overflowChoice'; to: 'discard' | 'bottom' } // where the card from an Effect Draw with a full hand goes
  // free = don't pay its cost · dark = a Darkgale summon, paid in Dark Gerzite
  // form = which of a two-cost card's costs (0 or 1) · choice = the number a cost formula asks the player for
  // unit = the hand card whose cost a "unit + 1" formula reads
  // replace = the Biome already in the slot, which is removed first (its removal cost is paid along with this card's cost)
  | { type: 'play'; id: string; x: number; z: number; faceDown: boolean; free?: boolean; dark?: boolean; form?: number; choice?: number; unit?: string; replace?: string; spread?: boolean } // spread = a card that may be a Lanespanner is spread across lanes
  | { type: 'removeCard'; id: string } // pay a Biome's removal cost: it goes to its owner's discard pile (either player may)
  | { type: 'restart'; id: string } // pay a Tome's replay cost once its turns are over: it runs again, one turn shorter
  | { type: 'move'; id: string; x: number; z: number; carry?: { id: string; x: number; z: number }[] }
  | { type: 'moveMany'; items: { id: string; x: number; z: number }[] } // a selected group moved together
  | { type: 'deleteMany'; ids: string[] } // tokens (and spawned cards) thrown off the table together
  | { type: 'tap'; id: string; dir: 1 | -1; as?: 'attack' | 'ability' } // as: it attacked, or its Tap ability is being used (a plain tap only turns the card)
  | { type: 'strike'; from: string; to: string }
  | { type: 'activate'; id: string } // a card of yours with a once-a-turn effect you set off by clicking it (Moldering Tome)
  | { type: 'pick'; pick: number; card?: string; yes?: boolean; choice?: number; numbers?: number[]; order?: string[]; drop?: string[]; amount?: number }
  | { type: 'bargain'; delta?: number; deal?: boolean } // Dark Bargain: change the amount, or agree to it // answers a choice a card effect is waiting for (card = the one chosen; neither = pass) // an attack the table deals itself: your card hits that card
  | { type: 'flip'; id: string }
  | { type: 'toHand'; id: string }
  | { type: 'merge'; id: string; into: string; free?: boolean } // id = a copy in your hand or on the table · free = a card effect lets it happen outside its phase
  | { type: 'split'; id: string } // take one merged copy back off a tiered card
  | { type: 'toPile'; id: string; pile: PileName; where: Where }
  | { type: 'shuffle'; pile: PileName }
  | { type: 'searchStart'; pile: PileName }
  | { type: 'searchTake'; id: string }
  | { type: 'takeDiscard' } // take the top card of your discard pile back into your hand
  | { type: 'pileMove'; from: PileName; where: Where } // the top card of one of your piles goes straight to the other, never through your hand
  | { type: 'searchEnd' }
  | { type: 'spawnCard'; num: number; at?: { x: number; z: number } } // at = where the player is looking: it appears near there
  | { type: 'attach'; id: string; to: string } // snap a summoned unit onto the bottom edge of a card
  | { type: 'clone'; id: string; by: string } // a clone of a Unit, made by a card in CONFIG.cloneSources (by = its name)
  // count: how many to spawn at once (they are laid out in rows); value: the number each counter starts at
  | { type: 'handMove'; id: string; to: number } // rearrange your hand: the card moves to this place (0 = the left end)
  | { type: 'cloneToken'; id: string } // an exact copy of a token or counter (hold C and click it), set down just beside it
  | { type: 'spawnToken'; tokenId: string; count?: number; value?: number; turns?: number; at?: { x: number; z: number }; toBar?: boolean } // toBar: Gerzite straight onto your bar (otherwise it lands on the table as a counter)
  | { type: 'tokenFlow'; id: string; mode: 'add' | 'remove' | null; when?: 'start' | 'end' } // a Gerzite counter: what it does to its owner's bar each turn (null = nothing)
  | { type: 'callingResolve' } // the Calling Pillars' eruption has played: now the hand is discarded, The Remaining comes, and the Pillars go (sent by the table itself)
  | { type: 'harvestSpend'; id: string; gives: string } // a Harvest counter: spend some of it for Gerzite or Dark Gerzite (CONFIG.harvest.spend)
  | { type: 'tokenToBar'; id: string } // a Gerzite counter on the table goes onto your bar: as many as its number // turns = each arrives with a turn clock set to this · at = where the player is looking
  | { type: 'tokenValue'; id: string; delta: number }
  | { type: 'tokenTurns'; id: string; turns: number } // set the turn clock on a counter that holds an amount (0 = take the clock off)
  | { type: 'tokenActAt'; id: string; at: 'start' | 'end' } // when a status like Regeneration acts: as its card owner's turn begins or ends
  | { type: 'tokenDrain'; id: string; drain: number } // set how much of a counter's number wears off, 1 a turn (0 = none)
  | { type: 'spanPlace'; start: number } // a Lanespanner just played: the rest of its units go in the lanes from this one (0 = Lane 1)
  | { type: 'linkHalf'; id: string; half: boolean } // a Link counter: the card under it takes half (or all) of the damage copied to it
  | { type: 'cureRoll'; id: string } // roll the die a status token is waiting for (Burn: an even number cures it)
  | { type: 'lockPrice'; id: string; cost: number; dark: boolean } // set what a Locked token costs to remove, and in which resource
  | { type: 'payLock'; id: string } // pay that cost: the Locked token comes off
  | { type: 'cardRoll'; id: string } // roll the die a card is waiting for (Pestilence Approaching: an even roll ends it)
  | { type: 'recast'; id: string } // a card that has ended and may be recast: its owner pays its cost again
  | { type: 'roll'; id: string; x?: number; z?: number } // roll a die where it is, or throw it to x, z
  | { type: 'collectFractals' } // End Phase: take the Fractals your idle units earned
  | { type: 'sacrifice'; id: string } // discard one of your units for Dark Gerzite
  | { type: 'payout'; id: string } // a sprung Trigger: gain its cost + 2 in Gerzite and discard it
  | { type: 'deckOut' } // you couldn't draw: 1 damage to your Emperor
  | { type: 'attack'; from: string; to?: string; x?: number; z?: number; effect?: boolean } // point an arrow from your card at a target (effect = an ability or Spell aiming, which can't miss)
  | { type: 'endAttack'; arrow: number } // take away an arrow you drew (the id of its 'attack' event)
  | { type: 'ping'; x: number; z: number } // flash a marker on the table
  | { type: 'delete'; id: string }
  | { type: 'nextPhase' }
  | { type: 'revealCard'; id: string } // show one card from your hand, deck or discard pile to your opponent (hold R and click it)
  | { type: 'revealHand' }
  | { type: 'closeReveal' }
  | { type: 'peekAsk'; seat: Seat; pile: PileName; count: number } // look at the top cards of a pile: your own at once, your opponent's if they agree
  | { type: 'peekAnswer'; accept: boolean } // the pile's owner answers (or the asker takes the request back)
  | { type: 'emperorAbility'; id: string; slot: number } // use one of your Emperor's abilities: pay its cost, start its cooldown
  | { type: 'linkAbility'; id: string; slot: number | null } // a timer or Lock lying on an Emperor: say which ability it is for (null = none)
  | { type: 'peekClose' }
  | { type: 'requestEnd'; kind: EndKind | 'end' } // 'end' only on the solo table; 'call' = decide it by Emperor damage
  | { type: 'answerEnd'; accept: boolean }
  | { type: 'timerAsk'; on: boolean } // ask to turn the Battle Timer on (or off): the other side has to agree
  | { type: 'timerAnswer'; accept: boolean } // the other side answers (or the asker takes the request back)
  // the game server only, never a player: this seat's turn time ran out with the Battle Timer on ('clock'), or they
  // left the game and didn't come back in time ('gone'). They lose by forfeit (2v2: their team does).
  // 'everyone': every player has left and none came back: the game ends in a draw
  | { type: 'timeUp'; why: 'clock' | 'gone' | 'everyone' }
  // a bot only: say something to the other player (what one of its cards does, for them to settle), optionally about a card
  | { type: 'announce'; text: string; card?: string }
  // a bot only: ask the other player before moving on, so they can settle what just happened · the other player: go ahead
  | { type: 'botAsk'; text: string; end?: boolean } // end = it wants to end its turn (answered only once its To settle list is empty)
  | { type: 'botAnswer' }
  | { type: 'botSettle'; text?: string; all?: boolean }; // one thing on a bot's To settle list (or all of them) has been settled: it comes off the list

// ---------- What one player is allowed to see ----------
export interface CardView {
  id: string;
  num: number | null; // null = face-down card you don't own
  owner: Seat;
  x: number;
  z: number;
  rot: number;
  faceUp: boolean;
  spawned: boolean;
  layer: number;
  tier: number; // 1 = a single card, 2 = Tier II (two merged copies), 3 = Tier III
  armed?: boolean; // a face-down card of a type that is always set face-down (a Trigger): both players see that much
  form?: number; // a two-cost card that was summoned at its second cost (face-up cards only)
  asBiome?: boolean; // a Troop standing in the Biome slot as the Biome (Eldritch Realm Crosser)
  laneSpots?: { x: number; z: number }[];
  guarded?: boolean; // a Grave Intercessor has chosen this Troop: it is destroyed in its place (a ward lingers over it until then)
  frenzy?: boolean; // it has Frenzy by its own text, for good (Great Hay Golem): two hits each attack
  restHits?: number; // Marlin Knight: extra hits its next attack has (it went without attacking)
  swarm?: string; // one unit of a swarm: the id of its swarm (see FieldCard)
  // marks a player can't otherwise see, each shown by a look of its own on the card while it lasts:
  warded?: true; // Ailleiah's Impervious Shield: Spells can't damage it (from the moment it is given until the opponent's next turn is over)
  taunting?: true; // Taunt's chosen Unit: during the opponent's next turn it is the only one they may target
  ascended?: true; // empowered by Givellt's Arcane Ascension (Magical as well, while its Overclock lasts)
  blinded?: true; // Drifts Tarragon's sand: it may miss its attacks during its player's next turn
  jousted?: true; // the opposing Unit Jouster chose: it must attack the Jouster, and nothing else
  veiled?: true; // J'miele's Hidden Veil: during the opponent's next turn only Spells can target it
  exiting?: true; // Scackle's Emergency Exit: no damage but Pierce lands on this Emperor while the ability is on cooldown
  mapped?: true; // a Biome Bastian's Traveler's Map has switched off for one side this turn
  coated?: true; // Flamburst's Molten Coat: it lasts while the Shield it gave holds
  necrotic?: true; // Barragand's Burning Necrosis: its Bleed and Weakness are on it (destroyed meanwhile, Burn spreads in its lane)
  vined?: 'tangle' | 'wing'; // wrapped in vines: Locked by a card whose Lock does that (CONFIG.looks.lockVines: Tangleweed, dark seaweed), or held to the ground by Wingwrap (bright green)
  grounded?: boolean; // a Flying Troop Wingwrap holds to the ground: it sits on the table while that lasts
  legionWorth?: number; // Undead Legion's General: the Dark Gerzite sacrificing its whole Legion would give right now
  wyrm?: true; // Sand Wyrm at work: this Support card is lending its boosts to Duned cards (or this is the Wyrm doing it)
  bufferAura?: number; // Buffer it has from another card's aura right now (Scorching Drifts), shown as a Buffer dome
  rangeAura?: number; // range it gains from another card's aura right now (Watch Tower: +2 to your Units in its range), for the table's reach glow
  actions?: { total: number; left: number }; // Cragged Skulker: its attacking actions a turn, and how many are left this turn // the middles of the lanes chosen for it as it was played (Spatial Rift, Tectonic Shift)
  restarts?: number; // a Tome that has been restarted: how many times so far
  roll?: Seat; // that player owes a die roll for this card
  rollFor?: 'coin'; // see FieldCard
  spanOf?: string; // one of a Lanespanner's extra units (see FieldCard)
  scale?: number; // see FieldCard
  attachedTo?: string;
  clone?: { by: string; health?: number; damage?: number };
}
export interface PlayerView {
  name: string;
  out?: boolean; // 2v2: their Emperor has fallen, and they are watching their teammate
  deckCount: number;
  handCount: number;
  discard: HeldCard[];
  limboCount?: number; // cards set aside in their Limbo pile (Heart of Limbo)
  searching: PileName | null;
  shuffles: { deck: number; discard: number };
  jumps: { deck: number; discard: number }; // how many times The Remaining has moved within each pile
  turns: number;
  gerzite: number; // Gerzite on their bar
  darkGerzite: number; // Dark Gerzite on their bar
  openHand?: HeldCard[]; // their hand, when a card in play keeps both hands in plain view (Lexicon of Truth)
}
export interface GameView {
  mode: 'solo' | 'versus';
  teams?: boolean; // a 2v2 game (four players: seats 0 and 2 against 1 and 3)
  spectator?: boolean; // a view for someone watching: no hand and no hidden cards (`you` is only the side they look from)
  you: Seat;
  players: PlayerView[];
  field: CardView[];
  // what each of your cards can do right now (by card id): the cards it may attack (or why it can't), and whether
  // its Tap ability can be used. Only cards you control are listed.
  pick: { id: number; name: string; text: string; options: string[]; yesNo?: boolean; optional?: boolean; skip?: string; arrange?: boolean; undead?: string[]; numbers?: { label: string; min: number; max: number; value: number }[]; confirm?: string; done?: string; left?: number; cards?: { id: string; num: number }[]; choices?: string[]; laneHints?: { x: number; z: number }[][]; showing?: number } | null; // a choice a card effect is waiting for you to make (showing: the Continue on cards turned up, see GameView.showing)
  combat: Record<string, { targets: string[]; why?: string; left?: number; tap?: boolean; tapWhy?: string; used?: boolean; never?: boolean }>; // never = it can never attack (no Attack in its menu)
  tokens: Token[];
  turn: Seat;
  first: Seat; // who went first: that player is blue, the other is red
  overtime: boolean; // both players have finished 20 turns: the endgame
  doubleOvertime?: boolean; // Double Overtime (from global turn CONFIG.overtime.doubleFrom): more damage, more Gerzite
  wideLanes?: boolean; // Vast Expanse is active: each lane holds two cards
  fractalOffer: number | null; // in your End Phase: how many Fractals you can collect (null = not now, or already taken)
  fallen: Seat[]; // players whose Emperor has taken damage equal to its health or more
  phase: number;
  globalTurn: number;
  log: LogEntry[];
  fx: FxEvent[];
  hand: HeldCard[];
  hornCards?: string[]; // cards in your hand a held free summon can bring in this turn (Okrum's Bellowing Horn): they glow
  search: { pile: PileName; cards: HeldCard[] } | null;
  reveal: { fromName: string; cards: HeldCard[] } | null;
  peekRequest: PeekRequest | null; // both players see that it was asked
  showing: Showing | null; // cards a card effect has turned up, shown to everyone (see GameState)
  peek: { owner: Seat; pile: PileName; cards: HeldCard[] } | null; // the cards you are being shown, top card first
  overflow: HeldCard | null; // the card you drew by an effect with a full hand: choose where it goes
  span: { card: string; num: number; choices: string[][] } | null; // your Lanespanner just played: choose which lanes the rest go in (each choice lists its lanes)
  costChange: Record<string, number>; // for the cards in your hand whose cost is changed right now (by card id): by how much
  costFacts: CostFacts; // what a cost formula would read right now, if you played a card (see shared/costs.ts)
  handsOpen: string | null; // the card in play that keeps both hands in plain view (its name), or null
  freeUnit: string | null; // the card in your hand that is waiting to be played face-down for free (Tribunal Trigger)
  handBlocked?: Record<string, string>;
  bargain?: { by: string; value: number; max: number; agreed: Seat[] } | null; // Dark Bargain: the amount both players would lose, and who has agreed
  supportInSpell?: boolean; // Mystwing: your Support cards may go in your Spell slots // cards in your hand a card effect stops you playing right now, and why (they show greyed out)
  endRequest: EndRequest | null;
  battleTimer?: BattleTimer; // (1v1 and 2v2 only, once someone has asked for it)
  bots?: Seat[]; // seats played by the computer
  calling?: Seat[]; // players whose Calling Pillars are erupting: the table sends callingResolve once the effect has played
  snares?: { x: number; z: number; by: Seat }[]; // Thorny Snares lying in wait: where (the middle of the lane)
  botAsk?: { from: Seat; text: string; end?: boolean } | null; // a bot waiting for a Go ahead before it moves on
  botNotes?: string[]; // what it has announced that is still to be settled
  tutorial?: boolean; // the tutorial's hints are on
  // Executioner's axe, on its way back: the lane it comes back to (and whose side of it), when (the end of `foe`'s
  // turn), and what it deals. Everyone sees these: the table marks each lane. (It comes back whether or not the
  // Executioner is still in play.)
  echoes?: { lane: string; side: 1 | -1; by: string; amount: number; foe: Seat; dueNow: boolean }[];
  result: GameResult | null;
  version: number;
}

export interface GameOptions {
  mode: 'solo' | 'versus';
  teams?: boolean; // 2v2: four names and four decks (seats 0 and 2 against 1 and 3)
  names: string[];
  decks: number[][]; // card numbers, one entry per copy, Emperor included
  rng?: () => number;
  bots?: Seat[]; // seats the computer plays
  botLevels?: Partial<Record<number, 'easy' | 'skilled' | 'expert'>>; // how well each bot chooses (Martin, Practice Buddy, Creator)
  tutorial?: boolean; // a tutorial game (hints on the player's screen; no card stats)
}

const LOG_LIMIT = 400;

/**
 * Your zone positions are set for seat 0; seat 1 is the mirror image. In a 2v2 game they are set for the left
 * player of a side: the right player's are mirrored left to right.
 */
export function seatPoint(seat: Seat, p: { x: number; z: number }) {
  const side = sideOf(seat);
  return { x: p.x * side * handOf(seat), z: p.z * side };
}

/** The lanes, left to right as their owner sees them. */
export function laneOrder() {
  return CONFIG.slots.filter((sl) => slotKind(sl.name) === 'Lane').sort((a, b) => a.x - b.x);
}
/** The middle lane of a side (Lane 3 of five), or the middle two on a table with an even number of lanes (2v2). */
export function middleLanes() {
  const row = laneOrder();
  return row.length % 2 ? [row[(row.length - 1) / 2]] : [row[row.length / 2 - 1], row[row.length / 2]];
}
/**
 * Is this card, summoned this way, a Lanespanner (one unit per lane)? It needs a Count of 2 or more, and the
 * Lanespanner descriptor: on its own it always spreads; as one of two choices ("Ground/Lanespanner") only when
 * its player chose to spread it (`spread`).
 */
export function isLanespanner(def: Parameters<typeof formOf>[0] & { traits: string[] } | undefined, form = 0, spread = false) {
  if (!def || (formOf(def, form).count ?? 0) < 2) return false;
  const mode = spanMode(def, CONFIG.tokenRules.lanespanTrait);
  return mode === 'always' || (mode === 'optional' && spread);
}
/**
 * Where a Lanespanner of `count` units played into lane `middle` (0 = Lane 1) may spread: the first lane of each
 * run of `count` lanes in a row that holds it and stays on the mat. With 3 or more units the lane it is played in
 * has to be a middle one, never an end of the run (so 3 units are centred on it, and 4 can't start in Lane 1).
 */
export function spanStarts(count: number, middle: number): number[] {
  const lanes = laneOrder().length;
  const out: number[] = [];
  for (let st = 0; st + count <= lanes; st++) {
    if (middle < st || middle > st + count - 1) continue;
    if (count >= 3 && (middle === st || middle === st + count - 1)) continue;
    out.push(st);
  }
  return out;
}

export function barSlots(seat: Seat, bar: 'gerzite' | 'dark') {
  const b = bar === 'gerzite' ? CONFIG.zones.gerziteBar : CONFIG.zones.darkGerziteBar;
  const rows = Math.ceil(b.slots / b.columns);
  const sx = b.width / b.columns;
  const sz = b.depth / rows;
  const slots: { x: number; z: number }[] = [];
  for (let i = 0; i < b.slots; i++) {
    const col = i % b.columns;
    const row = Math.floor(i / b.columns);
    slots.push(
      seatPoint(teamOf(seat), { x: b.x - b.width / 2 + sx * (col + 0.5), z: b.z - b.depth / 2 + sz * (row + 0.5) }),
    );
  }
  return { slots, spacing: Math.min(sx, sz) };
}

export type Slot = (typeof CONFIG.slots)[number];
/** The slot on the mat that a spot falls in (on either player's side), and which side: 1 = seat 0's, -1 = seat 1's. */
export function slotAt(pt: { x: number; z: number }): { slot: Slot; side: 1 | -1 } | null {
  for (const slot of CONFIG.slots) {
    for (const side of [1, -1] as const) {
      if (Math.abs(pt.x - slot.x * side) <= slot.width / 2 && Math.abs(pt.z - slot.z * side) <= slot.depth / 2) return { slot, side };
    }
  }
  return null;
}
const typeName = (t: string) => t[0].toUpperCase() + t.slice(1);
/** A card's affiliations for what card effects pick out: its own, plus any it counts as ("always: counts_as neutral": the Icewings). */
function defAffiliations(def: CardDef): string[] {
  const extra = def.effects.filter((e) => e.when === 'always' && e.words[0] === 'counts_as').flatMap((e) => e.words.slice(1));
  return extra.length ? [...new Set([...def.affiliations, ...extra])] : def.affiliations;
}
/**
 * The play-phase rule: may a card of this kind be played from its owner's hand right now? Every card can be
 * played in its owner's Summon Phase; after that only Spells, in the Attack Phase (playPhases in the settings).
 * Returns null if it may, or a sentence saying when it can be.
 */
export function playWindowRefusal(def: { types: string[] }, turn: Seat, phase: number, seat: Seat): string | null {
  const R = CONFIG.turn.playPhases;
  if (!R.enabled) return null;
  if (turn !== seat) return 'Cards are played on your own turn.';
  const phases = [...new Set(def.types.flatMap((t) => R.byType[t] ?? R.phases))];
  if (phases.includes(phase)) return null;
  const names = phases.map((i) => PHASES[i]).filter(Boolean);
  const when = names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0];
  return `${typeName(def.types[0])} cards are played in your ${when}: this is your ${PHASES[phase]}.`;
}
/** A slot's kind: its name without the number ("Lane 3" → "Lane", "Spell 1" → "Spell", "Minion" → "Minion"). */
export const slotKind = (name: string) => name.replace(/\s*\d+$/, '');
/** Every kind of slot on the mat, in the order they are listed in the settings. */
export const slotKinds = () => [...new Set(CONFIG.slots.map((sl) => slotKind(sl.name)))];
/** "a Lane", "a Spell slot", "the Minion slot": how a kind of slot is named in a sentence. */
function slotPhrase(kind: string) {
  const many = CONFIG.slots.filter((sl) => slotKind(sl.name) === kind).length > 1;
  return many ? (kind === 'Lane' ? 'a Lane' : `a ${kind} slot`) : `the ${kind} slot`;
}
/**
 * The kinds of slot a card may be placed in: the ones its types allow (slotsFor in the settings) plus
 * any it is let into by extra_slots in cards.csv. null = the card isn't tied to any slot.
 */
export function slotsOpenTo(def: NonNullable<SlotCard>): string[] | null {
  if (!def.types.some((t) => CONFIG.slotsFor[t])) return null;
  const extra = (def.extraSlots ?? []).map((e) => slotKinds().find((k) => k.toLowerCase() === e)).filter(Boolean) as string[];
  return [...new Set([...def.types.flatMap((t) => CONFIG.slotsFor[t] ?? []), ...extra])];
}
/** A card already on the table, as far as slot limits care: where it is and what it is (null = a face-down card you can't see). */
export interface Placed {
  x: number;
  z: number;
  types: string[] | null;
  shares?: boolean; // a summoned unit or a clone: it may share a lane, and doesn't take up the lane's room
}
/** What waits for a search to be over: the rest of the card's effects (a Spell's "done"). */
type SearchThen = { src: FieldCard; rest: CardEffect[]; ctx: NonNullable<PendingPick['ctx']> };
type SlotCard = { types: string[]; sharesLane?: boolean; extraSlots?: string[] } | undefined;

/** How many of the cards on the table take up room in a slot. */
function occupants(slot: Slot, side: 1 | -1, others: Placed[]) {
  return others.filter((o) => {
    const at = slotAt(o);
    return !!at && !o.shares && at.slot === slot && at.side === side && (!slot.counts || !o.types || o.types.some((t) => slot.counts!.includes(t)));
  }).length;
}

/**
 * Where a card dropped at a spot ends up, or why it isn't allowed there:
 *   - every card type has its own slots (Troops in lanes or the Minion slot, Spells in Spell slots…),
 *     and a card can't be placed anywhere else; extra_slots in cards.csv lets one card into more
 *   - a slot holds only so many cards (one unit per lane, one Support card)
 *   - a card dropped on a row of slots (the Spell slots) goes to the first open one
 *   - a card in a slot settles onto its middle
 * `others` are the cards already on the table, without the one being placed.
 */
export function landing(def: SlotCard, pt: { x: number; z: number }, others: Placed[] = [], wide = false): { refusal: string | null; at: { x: number; z: number } } {
  const stay = { refusal: null, at: { x: pt.x, z: pt.z } };
  if (!def) return stay;
  const no = (refusal: string) => ({ refusal, at: stay.at });
  const hit = slotAt(pt);
  let slot = hit?.slot;
  // every card belongs in the slots of its type (plus any its extra_slots tag opens), and nowhere else
  const open = slotsOpenTo(def);
  if (open && !(slot && open.includes(slotKind(slot.name)))) {
    const places = open.map(slotPhrase);
    const list = places.length > 1 ? `${places.slice(0, -1).join(', ')} or ${places.at(-1)}` : places[0];
    // a card that extra_slots lets into more slots than its type is told about itself, not about its type
    const who = open.length > new Set(def.types.flatMap((t) => CONFIG.slotsFor[t] ?? [])).size ? 'This card' : `${typeName(def.types.find((t) => CONFIG.slotsFor[t])!)} cards`;
    return no(`${who} can only be placed in ${list}.`);
  }
  if (!hit || !slot) return stay;
  const limited = slot.holds !== undefined && (!slot.counts || def.types.some((t) => slot!.counts!.includes(t))) && !(slot.counts && def.sharesLane);
  if (limited && slot.row) {
    // a row fills from its first slot: the card goes to the first one with room
    const row = CONFIG.slots.filter((sl) => sl.row === slot!.row);
    const open = row.find((sl) => occupants(sl, hit.side, others) < (sl.holds ?? Infinity));
    if (!open) return no(`All ${row.length} ${slot.name.replace(/\s*\d+$/, '')} slots are full.`);
    slot = open;
    return { refusal: null, at: { x: slot.x * hit.side, z: slot.z * hit.side } };
  }
  // (Vast Expanse: while it is active, each lane holds one more)
  if (limited && occupants(slot, hit.side, others) >= slot.holds! + (wide && slot.counts ? 1 : 0)) {
    return no(slot.counts ? `${slot.name} already holds a unit.` : `The ${slot.name} slot already holds a card.`);
  }
  return CONFIG.snapToSlots ? { refusal: null, at: { x: slot.x * hit.side, z: slot.z * hit.side } } : stay;
}
/** Why a card isn't allowed at a spot, or null (see `landing`). */
export function slotRefusal(def: SlotCard, pt: { x: number; z: number }, others: Placed[] = [], wide = false): string | null {
  return landing(def, pt, others, wide).refusal;
}
/** Which of a list of tokens are resting on a card (the same test the table uses everywhere). */
export function tokensOnCard<T extends { x: number; z: number }>(card: { x: number; z: number; rot: number; scale?: number }, tokens: T[]): T[] {
  return tokens.filter((t) => onCard(t, card));
}
/** Half the width and depth a card covers on the table: turned sideways or not, shrunk or not. */
export function cardHalf(card: { rot: number; scale?: number }) {
  const turned = card.rot % 2 === 1;
  const sc = card.scale ?? 1;
  return { halfW: ((turned ? CONFIG.card.height : CONFIG.card.width) / 2) * sc, halfD: ((turned ? CONFIG.card.width : CONFIG.card.height) / 2) * sc };
}
/** Whether a spot is on a card (with a small margin round the edge, smaller on a shrunk card). */
export function onCard(pt: { x: number; z: number }, card: { x: number; z: number; rot: number; scale?: number }) {
  const { halfW, halfD } = cardHalf(card);
  const margin = 0.3 * (card.scale ?? 1);
  return Math.abs(pt.x - card.x) <= halfW + margin && Math.abs(pt.z - card.z) <= halfD + margin;
}
/**
 * Dark Gerzite from sacrificing a unit: current health / full health x Gerzite cost, rounded.
 * Damage counters lower its current health; Overheal raises it. Returns null if the card's
 * health isn't set in cards.csv.
 */
/**
 * The same for a card that is several units (a Count, Skell-Minis ×3): it is sacrificed as one, with the health
 * of all its units together. Three alive of three is 3/3 of its cost; two alive is 2/3 (.66, which rounds to 1);
 * one alive is 1/3 (.33, which rounds to 0). `alive` = the number on its Count counter (null = no counter: one
 * unit). Health it has gained (`gained`) counts like health it never lost.
 */
export function countedSacrificeWorth(def: { health: number | null; cost: number | null } | undefined, count: number | null, alive: number | null, damage: number, overheal: number, gained = 0): number | null {
  if (!def || !def.health) return null;
  const units = count && count > 1 && alive !== null ? count : 1;
  const left = units > 1 ? Math.max(0, Math.min(alive!, units)) : 1;
  return sacrificeWorth({ health: def.health * units, cost: def.cost }, damage - gained + (units - left) * def.health, overheal);
}
export function sacrificeWorth(def: { health: number | null; cost: number | null } | undefined, damage: number, overheal: number): number | null {
  if (!def || !def.health) return null;
  const current = Math.max(0, def.health - damage) + overheal;
  // rounded to the nearest whole, halves going up (.5 → 1, .49 → 0); multiplied first so the halves come out exact
  return Math.max(0, Math.round((current * (def.cost ?? 0)) / def.health));
}

/**
 * Emperor abilities, the timing rule: may this ability be used right now by `seat`? By default an ability is
 * used in its owner's Attack Phase; its `when` cell in the sheet can name other phases, or the other player's
 * turn. Returns null if it may, or a sentence saying when it can be.
 */
export function abilityWindowRefusal(ab: Pick<EmperorAbility, 'name' | 'phases' | 'opponentTurn'>, turn: Seat, phase: number, seat: Seat, solo = false): string | null {
  const E = CONFIG.emperorAbilities;
  const list = (phases: number[]) => {
    const names = phases.map((i) => PHASES[i]).filter(Boolean);
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0];
  };
  if (turn !== seat && !solo) {
    if (ab.opponentTurn || !E.ownTurnOnly) return null;
    return `${ab.name} is used on your own turn.`;
  }
  const phases = ab.phases ?? E.phases;
  if (ab.phases === null && !phases.length) return null; // the settings say: any phase
  if (phases.includes(phase)) return null;
  if (!phases.length) return `${ab.name} is used on your opponent’s turn.`;
  return `${ab.name} is used in your ${list(phases)}${ab.opponentTurn ? ', or on your opponent’s turn' : ''}: this is your ${PHASES[phase]}.`;
}

/**
 * Sacrifice, the timing rule: a unit is sacrificed for Dark Gerzite on its owner's own turn, in their Summon
 * Phase or Attack Phase (turn.sacrifice in the settings). Returns null if it may be done now, or why not.
 */
export function sacrificeWindowRefusal(turn: Seat, phase: number, seat: Seat): string | null {
  const R = CONFIG.turn.sacrifice;
  const names = R.phases.map((i) => PHASES[i]).filter(Boolean);
  const when = !names.length ? '' : `, in your ${names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names.at(-1)}` : names[0]}`;
  if (R.ownTurnOnly && turn !== seat) return `Units are sacrificed on your own turn${when}.`;
  if (turn === seat && R.phases.length && !R.phases.includes(phase)) return `Units are sacrificed${when.replace(/^,/, '')}: this is your ${PHASES[phase]}.`;
  return null;
}

/** Does a token with this effect count against the health (or SP) of the card it is on? Damage, and SP decay. */
export function countsAgainstHealth(effect: string): boolean {
  return CONFIG.tokenRules.damageTokens.includes(effect) || effect === CONFIG.tokenRules.spDecayToken;
}

/** The Gerzite a sprung Trigger pays its owner: its own cost plus 2. */
export function triggerPayout(def: { cost: number | null } | undefined): number {
  return Math.max(0, (def?.cost ?? 0) + CONFIG.trigger.payoutBonus);
}

/** How many times its cost a card pays as a Darkgale summon (Spells: double). */
export function darkgaleMultiplier(def: { types: string[] } | undefined): number {
  const m = CONFIG.turn.darkgale.multiplier;
  return Math.max(1, ...(def?.types ?? []).map((t) => m[t] ?? 1));
}

/** True for card types that lie sideways while in play (Biomes, by default). */
/** "undead" → "Undead" */
function titleCaseWord(w: string) {
  return w.charAt(0).toUpperCase() + w.slice(1);
}

export function sitsSideways(def: { types: string[] } | undefined): boolean {
  return !!def && def.types.some((t) => CONFIG.arrival.sidewaysTypes.includes(t));
}

/** True for card types that are always played face-down (Triggers, by default). */
export function arrivesFaceDown(def: { types: string[] } | undefined): boolean {
  return !!def && def.types.some((t) => CONFIG.arrival.faceDownTypes.includes(t));
}

/** The faces of a die that cure a status, in words: [2, 4, 6] → "an even number". */
export function cureWords(faces: number[]): string {
  const set = [...new Set(faces)].sort((a, b) => a - b);
  const all = [1, 2, 3, 4, 5, 6];
  if (set.join() === all.filter((n) => n % 2 === 0).join()) return 'an even number';
  if (set.join() === all.filter((n) => n % 2 === 1).join()) return 'an odd number';
  if (set.length === 1) return `a ${set[0]}`;
  const top = all.filter((n) => n >= set[0]);
  if (set.join() === top.join()) return `${set[0]} or higher`;
  const low = all.filter((n) => n <= set[set.length - 1]);
  if (set.join() === low.join()) return `${set[set.length - 1]} or lower`;
  return `${set.slice(0, -1).join(', ')} or ${set[set.length - 1]}`;
}

export class Game {
  state: GameState;
  private rng: () => number;
  private cards: CardDb;
  private tokenDb: TokenDb;

  /** The Emperors among these card numbers (a deck's Emperor is on the table from the start: the card stats count it as played). */
  emperorsIn(nums: number[]): number[] {
    return nums.filter((n) => { const d = this.cards.byNumber.get(n); return !!d && isEmperor(d); });
  }
  constructor(cards: CardDb, tokenDb: TokenDb, options: GameOptions) {
    this.cards = cards;
    this.tokenDb = tokenDb;
    this.rng = options.rng ?? Math.random;
    const teams = options.mode === 'versus' && !!options.teams;
    useLayout(teams); // the 2v2 table has its own mat, slots and zones
    const seats = options.mode === 'solo' ? 1 : teams ? 4 : 2;
    this.state = {
      mode: options.mode,
      ...(teams ? { teams: true } : {}),
      ...(options.bots?.length ? { bots: [...options.bots], botAsk: null, ...(options.botLevels ? { botLevels: { ...options.botLevels } } : {}) } : {}),
      ...(options.tutorial ? { tutorial: true } : {}),
      players: Array.from({ length: seats }, (_, i) => ({
        name: options.names[i] || `Player ${i + 1}`,
        deck: [],
        hand: [],
        discard: [],
        searching: null,
        shuffles: { deck: 0, discard: 0 },
        turns: 0,
        overflow: null,
        noEndGerzite: null,
        freeUnit: null,
        played: [],
      })),
      field: [],
      tokens: [],
      turn: 0,
      first: 0,
      fractalsTaken: false,
      lastPlayed: Array.from({ length: seats }, () => null),
      handsOpen: null,
      phase: 0,
      log: [],
      fx: [],
      reveals: Array.from({ length: seats }, () => null),
      peekRequest: null,
      peeks: Array.from({ length: seats }, () => null),
      endRequest: null,
      result: null,
      nextId: 1,
      nextLayer: 1,
      version: 0,
    };
    this.setUp(options.decks);
  }

  // ---------------- setup ----------------

  private setUp(decks: number[][]) {
    const s = this.state;
    this.log('The game has started.', null);
    s.players.forEach((p, i) => {
      const seat = i as Seat;
      const list = [...(decks[i] ?? [])];
      const emperorIndex = list.findIndex((n) => {
        const card = this.cards.byNumber.get(n);
        return !!card && isEmperor(card);
      });
      if (emperorIndex >= 0) {
        const [num] = list.splice(emperorIndex, 1);
        const pos = seatPoint(seat, CONFIG.zones.emperor);
        const dug = CONFIG.turn.emperorEntrenched;
        const marker = this.tokenDb.list.find((t) => t.effect === CONFIG.tokenRules.entrenchedToken);
        s.field.push({ ...this.held(num), owner: seat, ...pos, rot: dug > 0 && marker ? CONFIG.arrival.entrenchedRotation : 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null });
        p.played.push(num);
        // Emperors can't attack or use abilities until their owner's third turn: 2 turns of Entrenched
        if (dug > 0 && marker) {
          const side = sideOf(seat);
          s.tokens.push({ id: this.newId('t'), tokenId: marker.id, x: pos.x - 1.7 * side, z: pos.z - 2.6 * side, value: dug, layer: s.nextLayer++, by: seat, since: 0 });
        }
        this.addStartCounters(s.field.at(-1)!); // an Emperor's own Shield, Buffer or Thorns
        this.log(`${p.name}'s Emperor, ${this.cardName(num)}, takes the field${dug > 0 && marker ? `, Entrenched for ${dug} turns` : ''}.`, seat);
      }
      p.deck = list.map((num) => this.held(num));
      this.shuffle(p.deck);
      // Starting hand: the first 7 cards of the shuffle that are allowed in a starting hand: at most
      // CONFIG.startingSpells Spells or Supports (the rest Units), no Win Condition card and nothing costing more than
      // CONFIG.startingMaxCost. Cards marked starting_hand = no in cards.csv are always passed over and stay in the deck.
      // A deck that can't fill the hand that way loosens the rules in turn: the Spell/Unit split first, then the rest.
      let rest: HeldCard[] = [...p.deck];
      const defOf = (num: number) => this.cards.byNumber.get(num);
      const isUnitCard = (num: number) => !!defOf(num)?.types.some((t) => t === 'troop' || t === 'building');
      const tagged = (num: number) => !(defOf(num)?.startingHand ?? true);
      const limited = (num: number) => { const d = defOf(num); return !!d && ((d.cost ?? 0) > CONFIG.startingMaxCost || d.roles.some((r) => CONFIG.startingNoRoles.includes(r))); };
      const nonUnitCap = Math.max(0, Math.min(CONFIG.startingSpells, CONFIG.startingHand));
      const deal = (ok: (num: number) => boolean, split: boolean) => {
        const left: HeldCard[] = [];
        for (const card of rest) {
          const roomForKind = !split || (isUnitCard(card.num) ? p.hand.filter((c) => isUnitCard(c.num)).length < CONFIG.startingHand - nonUnitCap : p.hand.filter((c) => !isUnitCard(c.num)).length < nonUnitCap);
          if (p.hand.length < CONFIG.startingHand && !tagged(card.num) && ok(card.num) && roomForKind) p.hand.push(this.rehide(card));
          else left.push(card);
        }
        rest = left;
      };
      deal((n) => !limited(n), true); // the rules as they are
      deal((n) => !limited(n), false); // short of one kind: the other fills the hand
      deal(() => true, false); // still short: a Win Condition or a costly card may come too
      p.deck = rest;
      this.shuffle(p.deck); // so the passed-over cards aren't bunched at the top
      this.log(`${p.name} shuffled their deck and drew ${p.hand.length} cards.`, seat);

      // Starting gerzite, laid out on the player's gerzite bar
      const gerzite = this.tokenDb.list.find((t) => t.bar === 'gerzite');
      if (s.teams) {
        // 2v2: one bar for the team, filled once (by its left player's setup)
        if (gerzite && handOf(seat) === 1 && CONFIG.teams.startingGerzite > 0) {
          const n = this.gain(seat, gerzite.id, CONFIG.teams.startingGerzite);
          this.log(`${this.teamName(seat)} start with ${n} ${gerzite.name} on their shared bar.`, seat);
        }
      } else if (gerzite && CONFIG.startingGerzite > 0) {
        const n = this.gain(seat, gerzite.id, CONFIG.startingGerzite);
        this.log(`${p.name} starts with ${n} ${gerzite.name}.`, seat);
      }
    });
    if (s.teams) {
      // a team is picked to go first and plays blue: its left player starts, then its right player, then the other team
      s.turn = this.rng() < 0.5 ? 0 : 1;
      s.first = s.turn;
      this.log(`${this.teamName(s.turn)} were randomly picked to go first and play blue.`, s.turn);
    } else if (s.mode === 'versus') {
      s.turn = this.rng() < 0.5 ? 0 : 1;
      s.first = s.turn;
      this.log(`${s.players[s.turn].name} was randomly picked to go first and plays blue.`, s.turn);
    }
    this.startTurn(s.turn);
  }

  // ---------------- helpers ----------------

  private newId(prefix: string) {
    return `${prefix}${this.state.nextId++}`;
  }
  private held(num: number, spawned = false): HeldCard {
    return { id: this.newId('c'), num, spawned };
  }
  /** Gives a card a fresh id when it goes somewhere hidden, so nobody can track it. */
  private rehide(card: HeldCard): HeldCard {
    return { id: this.newId('c'), num: card.num, spawned: card.spawned };
  }
  private shuffle<T>(list: T[]) {
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
  }
  private cardName(num: number) {
    return this.cards.byNumber.get(num)?.name ?? `Card #${num}`;
  }
  private log(text: string, seat: Seat | null) {
    const s = this.state;
    const id = (s.log.at(-1)?.id ?? 0) + 1;
    s.log.push({ id, text, seat });
    if (s.log.length > LOG_LIMIT) s.log.splice(0, s.log.length - LOG_LIMIT);
  }
  private clampToTable(x: number, z: number) {
    const hx = CONFIG.table.width / 2 - 2;
    const hz = CONFIG.table.depth / 2 - 2;
    return { x: Math.max(-hx, Math.min(hx, x)), z: Math.max(-hz, Math.min(hz, z)) };
  }
  private other(seat: Seat): Seat {
    return seat === 0 ? 1 : 0;
  }
  /** The last card the opponent played face-up (2v2: whichever opponent played last). On the solo table: your own. */
  private opponentsLast(seat: Seat) {
    const s = this.state;
    if (s.mode === 'solo') return s.lastPlayed[seat] ?? null;
    let best: GameState['lastPlayed'][number] = null;
    s.lastPlayed.forEach((l, i) => {
      if (l && this.opposed(seat, i as Seat) && (!best || (l.at ?? 0) >= (best.at ?? 0))) best = l;
    });
    return best;
  }
  /** "Ana & Cam" (2v2): the players of a seat's team. */
  private teamName(seat: Seat) {
    return this.state.players.filter((_, i) => teamOf(i) === teamOf(seat)).map((p) => p.name).join(' & ');
  }
  /** Are these two seats on the same team? (In 1v1 only a seat with itself.) */
  private allies(a: Seat, b: Seat) {
    return this.state.teams ? teamOf(a) === teamOf(b) : a === b;
  }
  /** Are these two seats against each other? */
  private opposed(a: Seat, b: Seat) {
    return this.state.mode !== 'solo' && !this.allies(a, b);
  }
  /** A seat's teammate (2v2), or null. */
  private mateOf(seat: Seat): Seat | null {
    return this.state.teams ? (((seat + 2) % 4) as Seat) : null;
  }
  /** The order seats take their turns in: 1v1 0 ↔ 1; 2v2 the blue team (left, then right), then the red team. */
  private nextSeat(seat: Seat): Seat {
    const s = this.state;
    if (s.mode === 'solo') return seat;
    if (!s.teams) return this.other(seat);
    const other = 1 - teamOf(s.first); // the other team's left seat
    const order = [s.first, s.first + 2, other, other + 2] as Seat[];
    let i = order.indexOf(seat);
    for (let k = 0; k < 4; k++) {
      i = (i + 1) % 4;
      if (!s.players[order[i]].out) return order[i];
    }
    return seat;
  }
  /** 2v2: is this the first turn of its team's turn (its teammate goes after it, or is out)? */
  private teamTurnStarts(seat: Seat) {
    const mate = this.mateOf(seat);
    return mate === null || handOf(seat) === 1 || !!this.state.players[mate].out;
  }
  /** 2v2: does its team's turn end with this seat's turn? */
  private teamTurnEnds(seat: Seat) {
    const mate = this.mateOf(seat);
    return mate === null || handOf(seat) === -1 || !!this.state.players[mate].out;
  }
  /** Who controls a Possessed card: the player who possessed it (an opponent of its owner), or null if it isn't. */
  private possessor(card: FieldCard): Seat | null {
    const t = this.tokensOn(card).find((o) => this.effectOf(o) === 'possession');
    if (!t || this.state.players.length < 2) return null;
    if (t.by !== null && !this.allies(card.owner, t.by)) return t.by;
    return (this.state.players.findIndex((_, i) => !this.allies(card.owner, i as Seat)) as Seat) ?? null;
  }
  /** While a card is Possessed its owner (and their teammate) can't attack or tap with it: it is the other side's to use. */
  private possessedAway(seat: Seat, card: FieldCard) {
    const by = this.possessor(card);
    if (by === null || !this.allies(card.owner, seat)) return null;
    return `${this.cardName(card.num)} is Possessed: ${this.state.players[by].name} controls it until the Possession ends.`;
  }
  /**
   * 2v2: your teammate's Emperor, Units and Spells are theirs to use: you can't attack, tap, use, move or sacrifice
   * with them, until their Emperor has fallen (then everything they left on the table is yours to use). A card
   * Possessed by you is yours whoever owns it. Returns why not, or null.
   */
  private mateHeld(seat: Seat, card: FieldCard): string | null {
    const s = this.state;
    if (!s.teams || card.owner === seat || !this.allies(card.owner, seat)) return null;
    if (s.players[card.owner]?.out) return null;
    if (this.possessor(card) === seat) return null;
    return `${this.cardName(card.num)} is ${s.players[card.owner].name}’s: only they can use it (until their Emperor falls).`;
  }
  /** 2v2: the half of the mat a card may be put in: your own slots, and your teammate's lanes once they are out. */
  private halfRefusal(seat: Seat, at: { x: number; z: number }, def?: CardDef): string | null {
    const s = this.state;
    const into = slotAt(at);
    const half = into?.slot.hand;
    if (!s.teams || !into || half === undefined || half === handOf(seat) || CONFIG.sharedSlots.includes(into.slot.name)) return null;
    const mate = this.mateOf(seat)!;
    const lane = slotKind(into.slot.name) === 'Lane';
    if (lane && s.players[mate].out) return null;
    if (lane && def?.whileInPlay.includes('calling')) return null; // (a Calling Pillar may go in a teammate's lanes: the call takes all four)
    return lane ? `That lane is ${s.players[mate].name}’s: you play into your own two lanes (both of theirs are yours too once their Emperor has fallen).` : `That slot is ${s.players[mate].name}’s: play into your own slots.`;
  }
  private canControl(seat: Seat, card: FieldCard) {
    // a Possession token hands a card to the other player for as long as it sits on it. In 2v2 teammates may
    // handle each other's cards (counters, summons, a sacrifice) to keep the game moving.
    // Against a bot, its opponent may handle the bot's cards too, to put right anything the bot got wrong.
    const fixesBot = !!this.state.bots?.includes(card.owner) && !this.state.bots.includes(seat);
    return (card.spawned && !card.spanOf && !card.swarm) || fixesBot || this.allies(card.owner, seat) || this.tokensOn(card).some((t) => this.effectOf(t) === 'possession');
  }

  // ---------------- tokens on cards ----------------

  private effectOf(t: Token) {
    return this.tokenDb.byId.get(t.tokenId)?.effect ?? '';
  }
  /** A spot on a card, `dx`/`dz` from its middle as on a full-size card: closer in on a shrunk one. */
  private near(card: FieldCard, dx: number, dz: number) {
    const sc = card.scale ?? 1;
    return { x: card.x + dx * sc, z: card.z + dz * sc };
  }
  /** A card's stats as it is in play: its form and tier, and a clone's own health (From the Mirror Realm: 1). */
  private fullForm(card: FieldCard, def: CardDef) {
    const f = formOf(def, card.form, this.tierOf(card));
    if (card.hp !== undefined) return { ...f, health: card.hp }; // (it came back with this much: Dark Sovereign, Void Walker)
    if (card.troopForm) { const m = /\bhealth (\d+)/i.exec(def.statsOverride); if (m) return { ...f, health: Number(m[1]) }; } // Melstone Terror as a Troop
    return card.clone?.health !== undefined ? { ...f, health: card.clone.health } : f;
  }
  /** A card's row as slot limits see it: a clone may share a lane like a summoned unit. */
  private slotDef(card: FieldCard) {
    const def = this.cards.byNumber.get(card.num);
    return def && card.clone ? { ...def, sharesLane: true } : def;
  }
  /** A summoned unit or a clone may share a lane with another unit (and doesn't take up its room). */
  private shares(card: FieldCard) {
    return !!card.clone || !!card.attachedTo || !!card.tribunalOf || !!card.soul || !!this.cards.byNumber.get(card.num)?.sharesLane;
  }
  private isOn(pt: { x: number; z: number }, card: FieldCard) {
    return onCard(pt, card);
  }
  private isDie(t: Token) {
    return this.tokenDb.byId.get(t.tokenId)?.category === 'dice';
  }
  private rollDie() {
    return 1 + Math.floor(this.rng() * 6);
  }
  /**
   * A damage counter just landed on a card, or went up while on one: show the hit.
   * `amount` is how much damage that was (the counter's number when it lands, or how much it went up).
   */
  private hit(t: Token, wasOn: FieldCard | undefined, amount: number) {
    if (!CONFIG.tokenRules.damageTokens.includes(this.effectOf(t))) return;
    const card = this.cardUnder(t);
    if (!card || card.id === wasOn?.id) return;
    this.fx('damage', card.id);
    if (amount > 0) this.wake(card);
    this.mirrorDamage(card, t.tokenId, amount);
  }
  /**
   * A Troop, Building or Emperor whose sheet row gives it Shield, Buffer or Thorns comes into play with those
   * counters on it, at those numbers (CardDef shield / buffer / thorns: see arrivalCounters). They sit in a row
   * across the card, a little above its middle, clear of the corners the other automatic tokens use.
   */
  private addStartCounters(card: FieldCard) {
    const s = this.state;
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    if (!def || !CONFIG.tokenRules.startCounters) return;
    // (a card summoned one of several ways has one number per way: Clay Soldiers' Buffer 1/0 is the big Troop's alone)
    const perForm = (eff: 'shield' | 'buffer' | 'thorns') => !!def.forms && def[eff].split('/').length === def.forms.length;
    const list = [
      ...arrivalCounters(def).filter((c) => !perForm(c.effect)),
      ...(['shield', 'buffer', 'thorns'] as const).filter(perForm).map((eff) => ({ effect: eff, value: Number.parseInt(def[eff].split('/')[Math.min(def.forms!.length - 1, card.form ?? 0)], 10) || 0 })).filter((c) => c.value > 0),
    ]
      .map((c) => ({ ...c, token: this.tokenDb.list.find((d) => d.effect === c.effect && d.counter) }))
      .filter((c) => !!c.token);
    if (!list.length) return;
    const side = sideOf(card.owner);
    const step = list.length > 1 ? Math.min(2, 3.4 / (list.length - 1)) : 0;
    list.forEach((c, i) => {
      s.tokens.push({ id: this.newId('t'), tokenId: c.token!.id, ...this.near(card, (i - (list.length - 1) / 2) * step * side, -0.9 * side), value: c.value, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner]?.turns ?? 0, native: c.value });
    });
  }
  /** A card with a number in its `count` cell (several small units in one card) gets a Count counter as it arrives. */
  /**
   * Swarms (CONFIG.swarms.split): a card with a Count (Skell-Minis ×3) is laid out as that many cards in its lane,
   * one for each unit, all copies of the card played. Each is a Unit of its own (its own health, counters and
   * statuses, and it can be aimed at on its own), but they stay one swarm: attacking with one sends the rest at the
   * same target, sacrificing one sacrifices them all, and the card goes to its owner's discard pile with the last.
   * A card comes onto the table with its Count counter as before; at the end of the move it is split up.
   */
  private splitSwarms(before: Map<string, string>) {
    if (!CONFIG.swarms.split) return;
    const s = this.state;
    for (const c of [...s.field]) {
      if (!c.faceUp || c.swarm || c.attachedTo || c.heldBy || c.soul || c.spanOf) continue;
      const def = this.cards.byNumber.get(c.num);
      if (!def || !this.isUnit(def)) continue;
      // (only the tokens that are this card's: a card dropped into a lane covers the units already there until the
      // lane is laid out, and their tokens stay theirs. A Royal Guard under a swarm of Wraiths isn't split into five)
      const on = this.tokensOwned(c, before);
      const count = on.find((t) => this.effectOf(t) === CONFIG.tokenRules.countToken);
      if (!count) continue;
      const n = Math.max(1, count.value);
      this.removeTokens([count]);
      c.swarm = c.id;
      // what every unit had (splash every unit took, statuses, Entrenched…) goes on each copy too; damage aimed at one unit stays on this one
      const each = on.filter((t) => t !== count && t.id !== c.tierToken && !t.ability && !t.span && !t.tally && !(CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)) && !t.all));
      for (const t of on) before.set(t.id, c.id);
      for (const t of each) if (t.all) delete t.all;
      for (let k = 1; k < n; k++) {
        const u: FieldCard = { ...(JSON.parse(JSON.stringify(c)) as FieldCard), id: this.newId('c'), spawned: true, merged: [], tierToken: null, layer: s.nextLayer++, swarm: c.id };
        delete u.swings; delete u.acted;
        s.field.push(u);
        for (const t of each) { const copy: Token = { ...(JSON.parse(JSON.stringify(t)) as Token), id: this.newId('t'), layer: s.nextLayer++ }; delete copy.twin; s.tokens.push(copy); before.set(copy.id, u.id); }
      }
      // merge damage counters that are now the same kind on one card
      for (const card of this.swarmOf(c)) {
        const dmg = this.tokensOn(card).filter((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)));
        if (dmg.length > 1) { dmg[0].value += dmg.slice(1).reduce((a, t) => a + t.value, 0); this.removeTokens(dmg.slice(1)); }
      }
      if (n > 1) this.log(`${def.name} takes the field as ${n} units.`, c.owner);
    }
  }
  /** The cards of a swarm still on the table (just the card itself if it isn't one). */
  private swarmOf(card: FieldCard): FieldCard[] {
    return card.swarm ? this.state.field.filter((m) => m.swarm === card.swarm) : [card];
  }
  /**
   * The units that declare their attacks together: a swarm, or a Lanespanner's units (the card played from the hand
   * and the extra units it spread, all carrying the same Lanespanner tokens). Once one of them attacks, the rest must
   * attack that turn too, or are tapped as the Attack Phase ends. Units that never attack (a Body Segment) are left out.
   */
  private attackGroup(card: FieldCard): FieldCard[] {
    if (card.swarm) return this.swarmOf(card);
    // Undead Legion: the General and every soldier it raised (Undead or Spirit) attack together, like a swarm
    const legion = this.legionHead(card);
    if (legion) return this.state.field.filter((c) => c.id === legion || c.legion === legion);
    const head = card.spanOf ?? (this.state.field.some((c) => c.spanOf === card.id) ? card.id : null);
    if (!head) return [card];
    return this.state.field.filter((c) => (c.id === head || c.spanOf === head) && !this.cards.byNumber.get(c.num)?.traits.some((t) => /body segment/i.test(t)));
  }
  /**
   * Deleting tokens or summoned cards by hand is only for the solo table (a sandbox) now that the table plays the
   * rules itself: in a game against anyone it would be cheating. Bots still clear away what they have finished with,
   * and a destroyed Lanespanner unit can still be cleared away.
   */
  private noDeleting(seat: Seat, id: string): string | null {
    const s = this.state;
    if (s.mode === 'solo' || CONFIG.deleteInGames || s.bots?.includes(seat)) return null;
    const card = this.findField(id);
    if (card?.spanOf && this.hasOn(card, CONFIG.turn.lifetime.expiredToken)) return null;
    return card ? 'Summoned cards can’t be deleted in a game: the table clears them away itself.' : 'Tokens can’t be deleted in a game: the table keeps them itself.';
  }
  /** Why a card on the table can't be tiered up right now (null = it can): for the bots, and the checks in 'merge'. */
  tierRefusal(card: FieldCard): string | null {
    const def = this.cards.byNumber.get(card.num);
    if (card.swarm && def?.count && this.unitsLeft(card) < def.count) return 'a unit of the swarm is gone';
    for (const c of this.swarmOf(card)) { const why = this.tierBlock(c); if (why) return why; }
    return null;
  }
  /** Why a card can't be tiered up right now (null = it can): damage on it, or a status other than Entrenched or Ambush. */
  private tierBlock(card: FieldCard): string | null {
    const on = this.tokensOn(card);
    if (on.some((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)) && t.value > 0)) return 'is damaged';
    const st = on.find((t) => this.tokenDb.byId.get(t.tokenId)?.category === 'status' && ![CONFIG.tokenRules.entrenchedToken, CONFIG.tokenRules.ambushToken].includes(this.effectOf(t)) && !(t.native !== undefined && t.value <= t.native)); // (its own Buffer from its sheet row doesn't count)
    return st ? `has ${this.tokenDb.byId.get(st.tokenId)?.name ?? 'a status'} on it` : null;
  }
  /** How many of a card's units are still standing: the cards of its swarm still alive, or its Count. */
  private unitsLeft(card: FieldCard): number {
    if (card.swarm) return this.swarmOf(card).filter((m) => !m.atZero && !this.hasOn(m, CONFIG.turn.lifetime.expiredToken)).length;
    return this.valueOn(card, CONFIG.tokenRules.countToken);
  }
  /**
   * A card with a Count laid face-down got no Count counter (nobody could see it yet): turned face-up, it gets one,
   * and is split into its units at the end of the move (Skell-Mateys ×2, Skell-Minis ×3).
   */
  private countOnReveal(card: FieldCard) {
    if (!card.faceUp || card.swarm || card.spanOf || this.hasOn(card, CONFIG.tokenRules.countToken)) return;
    if (!this.isUnit(this.cards.byNumber.get(card.num))) return;
    this.addCount(card); // (a Lanespanner that always spreads is left alone)
  }
  private addCount(card: FieldCard, spreading?: boolean) {
    const s = this.state;
    const cardDef = this.cards.byNumber.get(card.num);
    const n = card.faceUp && cardDef ? formOf(cardDef, card.form).count : null;
    const def = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.countToken);
    // a Lanespanner's units are spread out instead (one that always spreads, or one its player chose to spread)
    if (!n || n < 2 || !def || (spreading ?? isLanespanner(cardDef, card.form))) return;
    const side = sideOf(card.owner);
    // on the card's left edge, half-way up (the corners are for Entrenched, tiers, Darkgale and damage)
    s.tokens.push({ id: this.newId('t'), tokenId: def.id, ...this.near(card, -1.7 * side, 0), value: n, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns });
  }
  /**
   * Some cards keep both players' hands in plain view for as long as they are face-up in play
   * (while_in_play = reveal_hands: Lexicon of Truth). One whose turns are over (a Destroyed token is on it)
   * no longer counts. The log says when the hands open and when they close again.
   */
  private syncOpenHands() {
    const s = this.state;
    const dead = CONFIG.turn.lifetime.expiredToken;
    const by = s.field.find((c) => c.faceUp && this.cards.byNumber.get(c.num)?.whileInPlay.includes('reveal_hands') && !this.tokensOn(c).some((t) => this.effectOf(t) === dead));
    const name = by ? this.cardName(by.num) : null;
    if (name === s.handsOpen) return;
    const both = s.players.length > 1 ? 'both players’ hands are' : 'your hand is';
    if (name && !s.handsOpen) this.log(`${name} is in play: ${both} in plain view.`, null);
    else if (!name) this.log(`${s.handsOpen} is no longer in play: ${s.players.length > 1 ? 'hands are' : 'your hand is'} hidden again.`, null);
    s.handsOpen = name;
  }
  /**
   * What a cost formula reads when this player plays a card: their Gerzite, their discard pile, and the last
   * card their opponent played face-up. (On the solo table there is no opponent, so it is your own last card.)
   */
  private costFacts(seat: Seat): CostFacts {
    const s = this.state; const p = s.players[seat];
    const last = this.opponentsLast(seat);
    return {
      gerzite: this.barTokens(seat, 'gerzite').length,
      discard: p.discard.length,
      discardTroops: p.discard.filter((c) => this.cards.byNumber.get(c.num)?.types.includes('troop')).length,
      lastCard: last ? last.cost : null,
      lastDark: !!last?.dark,
      lastName: last ? this.cardName(last.num) : '',
    };
  }
  /** Is this a card that lasts a set number of turns (a Biome or a Tome)? */
  private hasLifetime(card: FieldCard) {
    return !!this.cards.byNumber.get(card.num)?.types.some((t) => CONFIG.turn.lifetime.types.includes(t));
  }
  /**
   * A Biome or Tome with a number in its turns cell arrives with a turn timer set to that number, on the
   * card's right edge. It counts down as its owner's turns end, the turn it was played being the first.
   */
  private addTimer(card: FieldCard, turns?: number) {
    const s = this.state;
    const cardDef = this.cards.byNumber.get(card.num);
    const lives = card.faceUp && this.hasLifetime(card);
    if (cardDef?.turnsTimer === 'up' && turns === undefined) {
      // "8 up": a Count-Up Timer at 0, which gains 1 as each of its owner's turns begins and stops at 8
      const up = this.tokenDb.list.find((d) => d.effect === CONFIG.turn.countUp.token);
      if (!lives || !up || !cardDef.turns) return;
      const side = sideOf(card.owner);
      s.tokens.push({ id: this.newId('t'), tokenId: up.id, ...this.near(card, 1.7 * side, 0), value: 0, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns, max: cardDef.turns });
      return;
    }
    let n = turns ?? (lives ? cardDef?.turns : null);
    if (n && cardDef?.types.includes('tome') && this.libraryActive()) n += 1; // (Library of Infinite Knowledge: a turn longer)
    // a "?" in the turns cell: the timer still arrives, but blank, for its owner to set by hand
    const unset = !n && turns === undefined && lives && !!cardDef?.turnsUnset && CONFIG.turn.lifetime.unsetTimers;
    // the card's turns cell can ask for a Start of Turn or an End of Turn Timer; otherwise it gets the usual one
    const L = CONFIG.turn.lifetime;
    const wanted = (cardDef?.turnsTimer && L.timers[cardDef.turnsTimer]) || L.timer;
    const def = this.tokenDb.list.find((d) => d.effect === wanted);
    if ((!n && !unset) || !def) return;
    const side = sideOf(card.owner);
    const out = card.rot % 2 === 1 ? 2.9 : 1.7; // a card lying sideways (a Biome) is wider: the timer sits nearer its end
    const timer: Token = { id: this.newId('t'), tokenId: def.id, x: card.x + out * side, z: card.z, value: n ?? 0, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns };
    s.tokens.push(timer);
    if (!unset) return;
    timer.unset = true;
    const name = this.cardName(card.num);
    this.fx('status', card.id, undefined, { token: timer.id, effect: 'notice', seat: card.owner, text: `${name}: set its turn timer. Click the “?” timer beside it.` });
    this.log(`${name} arrived with a turn timer that isn’t set yet. ${s.players[card.owner].name} sets its number by hand.`, card.owner);
  }
  /** The face-up cards of this player that still have a turn timer waiting for its number. */
  private unsetTimers(seat: Seat) {
    const out: { card: FieldCard; token: Token }[] = [];
    for (const t of this.state.tokens) {
      if (!t.unset) continue;
      const card = this.cardUnder(t);
      if (card && this.turnOf(card.owner) === seat) out.push({ card, token: t });
    }
    return out;
  }
  /**
   * The tokens a card arrives with (the `brings` cell of its sheet row): a Lock with its cost, a Burn… Each is
   * tied to the card. It appears just off the card, for its owner to drag onto the target, or on the card
   * itself ("here"). A card that ends = token_gone ends when one of them is no longer on the table.
   */
  private bring(card: FieldCard, quiet = false): number {
    const s = this.state;
    const def = this.cards.byNumber.get(card.num);
    if (!def?.brings.length || !card.faceUp || !CONFIG.turn.endings.enabled) return 0;
    const made: { token: Token; name: string; on: FieldCard | null; price: string }[] = [];
    // a loose one lies just off the card on the side of the field, where its target will be; if a card is
    // in the way there, wherever beside the card is free
    const away = -sideOf(card.owner);
    const turned = card.rot % 2 === 1;
    const far = this.clampToTable(card.x, card.z + away * ((turned ? CONFIG.card.width : CONFIG.card.height) / 2 + CONFIG.dice.size / 2 + 0.6));
    const loose = this.cardUnder(far) || slotAt(far) ? this.besideCard(card) : far;
    // "summoner" tokens go on the card that summoned this one: the nearest of its owner's cards named in summoned_by
    const callers = new Set(def.summonedBy);
    const summoner = def.brings.some((b) => b.summoner)
      ? s.field.filter((c) => c.id !== card.id && c.owner === card.owner && c.faceUp && callers.has(this.cardName(c.num).toLowerCase())).sort((a, b) => Math.hypot(a.x - card.x, a.z - card.z) - Math.hypot(b.x - card.x, b.z - card.z))[0]
      : undefined;
    // every Link it brings shares one new number, so the cards they go on are linked
    const linkNo = 1 + Math.max(0, ...s.tokens.filter((o) => this.effectOf(o) === CONFIG.tokenRules.linkToken).map((o) => o.value));
    const onCard = new Map<string, number>(); // how many tokens have gone on each card so far (they sit in a row)
    const marked: string[] = []; // "each <affiliation>" tokens: one per matching Unit, told in one line
    for (const b of def.brings) {
      const tdef = this.tokenDb.list.find((d) => d.id === b.token) ?? this.tokenDb.list.find((d) => d.effect === b.token);
      if (!tdef) continue;
      if (b.each) {
        // one on each face-up Unit its owner controls of that affiliation (not on itself)
        const units = s.field.filter((c) => {
          if (c.id === card.id || c.owner !== card.owner || !c.faceUp) return false;
          const d = this.cards.byNumber.get(c.num);
          return !!d && (d.types.includes('troop') || d.types.includes('building')) && d.affiliations.includes(b.each!);
        });
        for (const u of units) {
          const k = onCard.get(u.id) ?? 0;
          onCard.set(u.id, k + 1);
          const sc = u.scale ?? 1;
          const at = { x: u.x + ((k % 3) - 1) * 2 * sc, z: u.z - sideOf(u.owner) * (CONFIG.card.height / 2 - 1.6) * sc + Math.floor(k / 3) * 2 * sc * sideOf(u.owner) };
          const value = tdef.counter ? (b.value ?? tdef.start) : tdef.start;
          const t: Token = { id: this.newId('t'), tokenId: tdef.id, ...at, value, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns, from: card.id };
          s.tokens.push(t);
          this.fx('status', u.id, undefined, { token: t.id, effect: 'notice' });
        }
        if (units.length) marked.push(`${this.cardName(card.num)} put a ${tdef.name} token on ${units.length === 1 ? this.cardName(units[0].num) : `each of ${units.length} ${titleCaseWord(b.each)} Units: ${units.map((u) => this.cardName(u.num)).join(', ')}`}.`);
        continue;
      }
      const lock = tdef.effect === CONFIG.tokenRules.lockToken;
      const link = tdef.effect === CONFIG.tokenRules.linkToken;
      const target = b.summoner && summoner ? summoner : b.here || b.summoner ? card : null;
      const n = made.filter((m) => !m.on).length; // several loose ones lie in a row
      let at: { x: number; z: number };
      if (target) {
        const k = onCard.get(target.id) ?? 0;
        onCard.set(target.id, k + 1);
        const sc = target.scale ?? 1;
        at = { x: target.x + ((k % 3) - 1) * 2 * sc, z: target.z + Math.floor(k / 3) * 2 * sc * sideOf(target.owner) };
      } else at = this.clampToTable(loose.x + n * (CONFIG.dice.size + 0.6) * sideOf(card.owner), loose.z);
      const value = link ? linkNo : tdef.counter && !lock ? (b.value ?? tdef.start) : tdef.start;
      const t: Token = { id: this.newId('t'), tokenId: tdef.id, ...at, value, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns, from: card.id };
      if (lock && b.value) {
        t.price = Math.min(b.dark ? CONFIG.zones.darkGerziteBar.slots : CONFIG.tokenRules.lockMaxCost, b.value);
        if (b.dark) t.priceDark = true;
      }
      if (lock && b.opponent && s.mode !== 'solo') t.payBy = this.other(card.owner);
      s.tokens.push(t);
      made.push({ token: t, name: tdef.name, on: target, price: t.price ? ` (${t.price} ${t.priceDark ? 'Dark Gerzite' : 'Gerzite'} to take off)` : '' });
    }
    for (const line of marked) this.log(line, card.owner);
    if (!made.length) return 0;
    if (def.ends.includes('token_gone')) card.brought = made.map((m) => ({ id: m.token.id, name: m.name }));
    const name = this.cardName(card.num);
    const ends = def.ends.includes('token_gone');
    for (const m of made) {
      const text = m.on && m.on !== card
        ? `${name} put a ${m.name} token on ${this.cardName(m.on.num)}${m.price}.`
        : m.on
        ? `${name} has a ${m.name} token on it${m.price}.${ends ? ` ${name} ends when that token is gone.` : ''}`
        : `${name} came with a ${m.name} token${m.price}: drag it onto its target.${ends ? ` ${name} ends when that token is gone.` : ''}`;
      // (quiet: the tokens still pulse as they arrive, but whatever summoned the card says what happened, once)
      this.fx('status', card.id, undefined, { token: m.token.id, effect: 'notice', seat: card.owner, ...(quiet ? {} : { text }) });
    }
    const list = (ms: typeof made) => ms.map((m) => `a ${m.name} token${m.price}`).reduce((a, b, i, all) => (i === 0 ? b : i === all.length - 1 ? `${a} and ${b}` : `${a}, ${b}`), '');
    const own = made.filter((m) => !m.on || m.on === card);
    const given = made.filter((m) => m.on && m.on !== card);
    const parts = [own.length ? `${name} came with ${list(own)}` : '', given.length ? `${own.length ? 'it put' : `${name} put`} ${list(given)} on ${this.cardName(given[0].on!.num)}` : ''].filter(Boolean);
    this.log(`${parts.join('; ')}.`, card.owner);
    return made.length;
  }
  /**
   * A summoned unit was just attached to a card: some pairs give it tokens (CONFIG.attachGains: a Skell-Mini on a
   * Skeleton Dropship gets Ambush). It doesn't get a second one of a token it already has.
   */
  private attachGains(unit: FieldCard, parent: FieldCard, seat: Seat) {
    const s = this.state;
    const unitName = this.cardName(unit.num).toLowerCase();
    const parentName = this.cardName(parent.num).toLowerCase();
    const rule = CONFIG.attachGains.find((g) => g.parent.toLowerCase() === parentName && (!g.units || g.units.some((u) => u.toLowerCase() === unitName)));
    if (!rule) return;
    const got: string[] = [];
    rule.tokens.forEach((id, k) => {
      const tdef = this.tokenDb.byId.get(id) ?? this.tokenDb.list.find((d) => d.effect === id);
      if (!tdef || this.tokensOn(unit).some((t) => t.tokenId === tdef.id)) return;
      s.tokens.push({ id: this.newId('t'), tokenId: tdef.id, x: unit.x + (k % 2) * 0.6, z: unit.z, value: tdef.start, layer: s.nextLayer++, by: seat, since: s.players[seat].turns, from: parent.id });
      got.push(tdef.name);
    });
    if (got.length) this.log(`${this.cardName(unit.num)} on ${this.cardName(parent.num)} gets ${got.join(' and ')}.`, seat);
  }
  /**
   * A card was just untapped. Its `on_untap` cell (cards.csv) can say what happens to the tokens it arrived with
   * (its `brings ... here`): lose_tokens = they come off the first time (Firelance's Special Hit); swap_tokens =
   * they come off, and the next untap puts them back, and so on (Sunken Soldier's Range and Special Hit).
   */
  private untapped(card: FieldCard) {
    const s = this.state;
    const def = this.cards.byNumber.get(card.num);
    if (!def?.onUntap || !card.faceUp) return;
    const ids = new Set(def.brings.filter((b) => b.here).map((b) => (this.tokenDb.byId.get(b.token) ?? this.tokenDb.list.find((d) => d.effect === b.token))?.id));
    const mine = s.tokens.filter((t) => t.from === card.id && ids.has(t.tokenId) && this.cardUnder(t)?.id === card.id);
    const name = this.cardName(card.num);
    const names = (list: string[]) => (list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list.at(-1)}` : list[0]);
    if (mine.length) {
      this.removeTokens(mine);
      if (def.onUntap === 'swap_tokens') card.swapped = true;
      else card.swapped = undefined;
      this.log(`${name} was untapped and lost its ${names(mine.map((t) => this.tokenDb.byId.get(t.tokenId)?.name ?? t.tokenId))}.`, card.owner);
      return;
    }
    if (def.onUntap !== 'swap_tokens' || !card.swapped) return;
    delete card.swapped;
    const back: string[] = [];
    def.brings.filter((b) => b.here).forEach((b, k) => {
      const tdef = this.tokenDb.byId.get(b.token) ?? this.tokenDb.list.find((d) => d.effect === b.token);
      if (!tdef) return;
      const sc = card.scale ?? 1;
      s.tokens.push({ id: this.newId('t'), tokenId: tdef.id, x: card.x + ((k % 3) - 1) * 2 * sc, z: card.z + Math.floor(k / 3) * 2 * sc * sideOf(card.owner), value: tdef.counter ? (b.value ?? tdef.start) : tdef.start, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns, from: card.id });
      back.push(tdef.name);
    });
    if (back.length) this.log(`${name} was untapped and gets its ${names(back)} back.`, card.owner);
  }
  /**
   * The summoned units a card brings by itself (cards.csv): `arrive` = the ones it arrives with, attached to its
   * bottom edge (arrives_with: Zombie Knight's Zombie Horse); `destroyed` = the ones that appear in its lane when
   * it is destroyed (destroyed_summons: Battering Ram's two Skell-Minis, Blockhead Zombie's Chicken).
   */
  private summonUnits(card: FieldCard, when: 'arrive' | 'destroyed') {
    const s = this.state;
    const def = this.cards.byNumber.get(card.num);
    const list = when === 'arrive' ? def?.arrivesWith : def?.destroyedSummons;
    if (!def || !list?.length) return;
    const owned = this.owners(); // (the card keeps its own tokens when the lane is laid out again)
    const made: string[] = [];
    for (const { name, count } of list) {
      const unit = this.cards.spawnOnly.find((d) => d.name.toLowerCase() === name);
      if (!unit) continue;
      for (let k = 0; k < count; k++) {
        const u: FieldCard = { ...this.held(unit.number, true), owner: card.owner, x: card.x, z: card.z, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null };
        if (when === 'arrive') {
          u.attachedTo = card.id;
          u.attachOrder = s.nextLayer;
        }
        s.field.push(u);
        this.addCount(u);
        this.addStartCounters(u);
        this.bring(u);
        this.fx('play', u.id);
      }
      made.push(count > 1 ? `${count} ${unit.name}s` : `a ${unit.name}`);
    }
    if (!made.length) return;
    this.layout(owned); // (so a Zombie Knight's Shield stays the Knight's, and isn't taken for its Horse's)
    const what = made.length > 1 ? `${made.slice(0, -1).join(', ')} and ${made.at(-1)}` : made[0];
    this.log(when === 'arrive' ? `${def.name} arrives with ${what}.` : `${def.name} is destroyed and leaves ${what} in its lane.`, card.owner);
  }
  /**
   * A card ends by something other than a turn count: exactly what a turn timer running out does. A Destroyed
   * token marks it as no longer active and both players are told. The tokens it brought are ordinary tokens
   * from then on.
   */
  private endCard(card: FieldCard, why: string, tell = true) {
    const s = this.state;
    delete card.brought;
    delete card.roll;
    for (const t of s.tokens) if (t.from === card.id) { delete t.from; delete t.payBy; }
    if (!this.expire(card)) return;
    const text = `${this.cardName(card.num)} has ended: ${why}. It is no longer active.`;
    if (tell) this.fx('status', card.id, undefined, { effect: 'notice', text });
    this.log(text, card.owner);
  }
  /**
   * Run after every action: a card that had to keep a token it brought (ends = token_gone) and no longer has
   * it ends now, whichever way the token went (its Lock was paid, its Burn was cured, it was deleted). And a
   * token whose card has left play is an ordinary token again.
   */
  private settleBrought() {
    const s = this.state;
    const here = new Set(s.tokens.map((t) => t.id));
    for (const card of s.field) {
      const lost = card.brought?.find((b) => !here.has(b.id));
      if (lost) this.endCard(card, `the ${lost.name} token it came with is gone`);
    }
    for (const t of s.tokens) {
      if (t.from && !s.field.some((c) => c.id === t.from)) { delete t.from; delete t.payBy; }
    }
  }
  /** As a turn begins: every card that asks its opponent for a die roll each turn (ends = opponent_rolls_even) is owed one by this player. */
  private askRolls(seat: Seat) {
    const s = this.state;
    if (!CONFIG.turn.endings.enabled) return;
    for (const card of s.field) {
      const def = this.cards.byNumber.get(card.num);
      if (!card.faceUp || !def?.ends.includes('opponent_rolls_even') || this.isExpired(card)) continue;
      if (s.mode === 'solo' ? card.owner !== seat : !this.opposed(card.owner, seat)) continue;
      card.roll = seat;
      const text = `${def.name}: ${s.players[seat].name} rolls a die as their turn begins. ${cureWords(CONFIG.turn.endings.rollEnds).replace(/^./, (c) => c.toUpperCase())} ends it.`;
      this.fx('status', card.id, undefined, { effect: 'notice', seat, text });
      this.log(text, seat);
    }
  }
  /** Count-Up Timers on this player's cards (or put down by them) gain 1 as their turn begins, up to their stop. */
  private tickUp(seat: Seat) {
    const s = this.state; const p = s.players[seat];
    const rules = CONFIG.turn.countUp;
    if (!rules.enabled) return;
    const notes: string[] = [];
    for (const t of s.tokens) {
      if (this.effectOf(t) !== rules.token) continue;
      const card = this.cardUnder(t);
      if (this.turnOf(card ? card.owner : t.by) !== seat) continue;
      if (t.max !== undefined && t.value >= t.max) continue;
      t.value += 1;
      const where = !card ? '' : card.faceUp ? this.cardName(card.num) : 'a face-down card';
      const on = where ? ` on ${where}` : '';
      const full = t.max !== undefined && t.value >= t.max;
      notes.push(`Count-Up Timer ${t.value - 1} → ${t.value}${on}${full ? ' (its highest)' : ''}`);
      if (full && card) this.fx('status', card.id, undefined, { token: t.id, effect: 'notice', seat, text: `${where}’s Count-Up Timer has reached ${t.value}, its highest.` });
    }
    if (notes.length) this.log(`${p.name}’s counters: ${notes.join('; ')}.`, seat);
  }
  /**
   * Throws this player's cure die down at `land` showing `rolled`: the die from their last roll is picked up
   * again, otherwise one comes in from their edge of the table.
   */
  private throwDie(seat: Seat, land: { x: number; z: number }, rolled: number) {
    const s = this.state;
    const rules = CONFIG.turn.cureDie;
    const dieDef = this.tokenDb.byId.get(seat === s.first ? rules.first : rules.second);
    let die = s.tokens.find((k) => k.cureDie === seat && this.isDie(k));
    if (!die && dieDef?.category !== 'dice') return;
    const side = sideOf(seat);
    const from = die ? { x: die.x, z: die.z } : this.clampToTable(land.x + side * 6, land.z + side * 22); // a new die comes in from its roller's edge of the table
    if (die) Object.assign(die, land, { layer: s.nextLayer++ });
    else {
      die = { id: this.newId('t'), tokenId: dieDef!.id, ...land, value: rolled, layer: s.nextLayer++, by: seat, since: s.players[seat].turns, cureDie: seat };
      s.tokens.push(die);
    }
    die.value = rolled;
    this.fx('roll', '', undefined, { token: die.id, from });
  }
  /** Has this card's time in play run out? (A Destroyed token is on it.) */
  /** A card's affiliations right now: its own, and any it has been given by an affiliation token (Dark Sovereign). */
  private affiliationsOf(card: FieldCard): string[] {
    const def = this.cards.byNumber.get(card.num);
    const given = this.tokensOn(card).map((t) => this.effectOf(t)).filter((e) => e.startsWith('aff-')).map((e) => e.slice(4));
    return [...new Set([...(def?.affiliations ?? []), ...given])];
  }
  /** Is a face-up card of this name in play and still active (its turns not over)? Optionally only one owned by `owner`. */
  private activeNamed(name: string, test: (c: FieldCard) => boolean = () => true) {
    const want = name.toLowerCase();
    return this.state.field.filter((c) => c.faceUp && !this.isExpired(c) && test(c) && this.cardName(c.num).toLowerCase() === want);
  }
  /**
   * The chance this attack misses, and why: every card in play that gives one (CONFIG.misses). 0 when the
   * attacker can't miss. Several chances together: it misses if any one of them does.
   */
  missChance(from: FieldCard, to: FieldCard): { chance: number; why: string[] } {
    const M = CONFIG.misses;
    const none = { chance: 0, why: [] as string[] };
    if (!M.enabled || !from.faceUp) return none;
    const def = this.cards.byNumber.get(from.num);
    if (!def) return none;
    const emperor = isEmperor(def);
    const unit = def.types.includes('troop') || def.types.includes('building');
    if (!emperor && !unit) return none; // Spells and the rest don't miss
    const affs = this.affiliationsOf(from);
    const attacker = from.owner;
    for (const n of M.neverMiss) {
      if (n.card && this.cardName(from.num).toLowerCase() === n.card.toLowerCase()) return none;
      if (n.affiliation && affs.includes(n.affiliation) && (!n.while || this.activeNamed(n.while).length)) return none;
      if (n.given && n.while && this.activeNamed(n.while).some((c) => Object.keys(c.gave ?? {}).some((k) => k.endsWith(`:${from.id}`)))) return none;
    }
    const chances: number[] = [];
    const why: string[] = [];
    const typeWords = def.traits.flatMap((t) => t.split(/[|/]/)).map((w) => w.trim().toLowerCase());
    const tokenTypes = this.tokensOn(from).map((t) => this.effectOf(t)).filter((e) => e === 'target' || e === 'pierce' || e === 'direct');
    const attackTypes = tokenTypes.length ? tokenTypes : emperor ? ['target'] : typeWords.filter((w) => w === 'target' || w === 'pierce' || w === 'direct');
    for (const src of M.sources) {
      const p = emperor ? src.emperors : src.units;
      if (!p) continue;
      if (src.affiliations && !src.affiliations.some((x) => affs.includes(x))) continue;
      if (src.notAffiliations && src.notAffiliations.some((x) => affs.includes(x))) continue;
      if (src.target) {
        // it makes attacks AT itself miss (Ash Druid): only attacks of those target types, from the other side
        if (this.cardName(to.num).toLowerCase() !== src.card.toLowerCase() || !to.faceUp || !this.opposed(to.owner, attacker)) continue;
        if (!attackTypes.some((x) => src.target!.includes(x))) continue;
        chances.push(p);
        why.push(src.card);
        continue;
      }
      const cards = this.activeNamed(src.card, (c) => src.by !== 'opponents' || this.opposed(c.owner, attacker));
      if (!cards.length) continue;
      chances.push(p);
      why.push(src.card);
    }
    // a miss chance left on this very attacker for this turn (Drifts Tarragon, Ash Beetle)
    const s = this.state;
    for (const m of s.missMarks ?? []) {
      if (m.card === from.id && s.turn === m.seat && s.players[m.seat].turns === m.turns) {
        chances.push(m.chance);
        why.push(m.by);
      }
    }
    const hit = chances.reduce((h, c) => h * (1 - Math.max(0, Math.min(1, c))), 1);
    return { chance: 1 - hit, why };
  }
  /** "1 in 3", "2 in 3", "1 in 4", or "47%" */
  private oddsWords(p: number) {
    for (const d of [2, 3, 4, 6, 8, 9, 12, 36]) {
      const n = Math.round(p * d);
      if (n > 0 && Math.abs(n / d - p) < 1e-6) return `${n} in ${d}`;
    }
    return `${Math.round(p * 100)}%`;
  }
  /**
   * An attack arrow was drawn at a card: if anything gives the attacker a chance to miss, it is rolled for now.
   * In sixths it is a real die thrown beside the target (missing on the low faces); otherwise a plain roll. An
   * arrow drawn again at the same card this turn keeps the roll it had.
   */
  private rollMiss(seat: Seat, from: FieldCard, to: FieldCard) {
    const s = this.state;
    s.lastMiss = null;
    const { chance, why } = this.missChance(from, to);
    if (chance <= 0) return;
    const key = `${from.id}:${to.id}:${s.turn}:${s.players[s.turn].turns}`;
    s.missRolls ??= {};
    let missed = s.missRolls[key];
    const name = this.cardName(from.num);
    const odds = this.oddsWords(chance);
    let text: string;
    if (missed === undefined) {
      const sixths = Math.round(chance * 6);
      if (Math.abs(sixths / 6 - chance) < 1e-6) {
        const rolled = this.rollDie();
        missed = rolled <= sixths;
        this.throwDie(seat, this.besideCard(to), rolled);
        text = `${name} has a ${odds} chance to miss (${why.join(', ')}): rolled a ${rolled}${sixths === 1 ? '' : ` (1–${sixths} misses)`}. ${missed ? 'It MISSES: no damage.' : 'It hits.'}`;
      } else {
        missed = this.rng() < chance;
        text = `${name} has a ${odds} chance to miss (${why.join(', ')}): ${missed ? 'it MISSES: no damage.' : 'it hits.'}`;
      }
      s.missRolls[key] = missed;
    } else text = `${name} already rolled to miss this turn: it ${missed ? 'misses' : 'hits'}.`;
    s.lastMiss = { from: from.id, to: to.id, missed, key };
    this.fx('status', to.id, undefined, { effect: 'notice', text });
    this.log(text, seat);
  }
  /** A card that leaves a miss chance on the Units in the lane in front of it, for their owner's next turn. */
  private laneMark(card: FieldCard, when: 'arrive' | 'destroyed') {
    const s = this.state;
    const name = this.cardName(card.num).toLowerCase();
    const rule = CONFIG.misses.laneMarks.find((m) => m.when === when && m.card.toLowerCase() === name);
    if (!rule || !CONFIG.misses.enabled) return;
    const here = slotAt(card);
    if (!here || slotKind(here.slot.name) !== 'Lane') return;
    // the Units across from it: in a Lane on the other side, at the same place along the table
    const across = s.field.filter((c) => {
      if (!c.faceUp || !this.opposed(c.owner, card.owner)) return false;
      const at = slotAt(c);
      return !!at && slotKind(at.slot.name) === 'Lane' && at.side !== here.side && Math.abs(c.x - card.x) < here.slot.width / 2 + 0.5;
    });
    if (!across.length) return;
    const foe = across[0].owner;
    const turns = s.turn === foe ? s.players[foe].turns + 1 : s.players[foe].turns; // their next turn
    s.missMarks = [...(s.missMarks ?? []).filter((m) => s.players[m.seat].turns <= m.turns), ...across.map((c) => ({ card: c.id, chance: rule.chance, by: rule.card, seat: foe, turns }))];
    // its own looks (Drifts Tarragon: a gust off its wings, and a cloud of sand over each Unit it blinds)
    if (rule.looks) { this.look(rule.looks.from, card); for (const c of across) this.look(rule.looks.on, c); }
    this.log(`${rule.card}: ${across.map((c) => this.cardName(c.num)).join(', ')} ${across.length === 1 ? 'has' : 'have'} a ${this.oddsWords(rule.chance)} chance to miss during ${s.players[foe].name}’s next turn.`, card.owner);
  }
  private isExpired(card: FieldCard) {
    return this.tokensOn(card).some((t) => this.effectOf(t) === CONFIG.turn.lifetime.expiredToken);
  }
  /**
   * What it costs to take a card out of play by paying for it (a Biome's removal cost), and in what. A card
   * whose turns are over is cleared away for nothing. null = this card can't be removed this way.
   */
  private removalOf(card: FieldCard): { cost: number; dark: boolean; expired: boolean } | null {
    const def = this.cards.byNumber.get(card.num);
    if (!def || !card.faceUp || !def.types.some((t) => CONFIG.turn.removable.includes(t))) return null;
    const expired = this.isExpired(card);
    return { cost: expired ? 0 : Math.max(0, tieredRemoval(def, this.tierOf(card)) ?? 0), dark: def.removalDark, expired };
  }
  /** Takes `cost` off a player's Gerzite or Dark Gerzite bar. Returns false (and takes nothing) if they don't have it. */
  /** This player's (or a teammate's) cards in play that hold for as long as they are there: face-up, not destroyed, with this while_in_play tag. */
  private inPlayWith(seat: Seat, tag: string): FieldCard[] {
    return this.state.field.filter((c) => c.faceUp && this.allies(c.owner, seat) && !!this.cards.byNumber.get(c.num)?.whileInPlay.includes(tag) && !this.tokensOn(c).some((t) => this.effectOf(t) === CONFIG.turn.lifetime.expiredToken));
  }
  // ---------------- the secret rares: Calling Pillar, Realm Beyond Comprehension, The Remaining ----------------

  /** Is this The Remaining (a card tagged jumps)? It is never drawn or taken from a pile, and moves about in it. */
  private jumper(c: { num: number }) {
    return !!this.cards.byNumber.get(c.num)?.whileInPlay.includes('jumps');
  }
  /** The Remaining and Calling Pillars: no card effect can clone or copy them. */
  private uncloneable(num: number) {
    const tags = this.cards.byNumber.get(num)?.whileInPlay ?? [];
    return tags.includes('jumps') || tags.includes('calling');
  }
  /** Can this player draw a card? (The Remaining doesn't count: it can't be drawn.) */
  private canDraw(p: PlayerState) {
    return p.deck.some((c) => !this.jumper(c));
  }
  /** Takes the top card of a deck that can be drawn: The Remaining is passed over and stays where it is. */
  private drawTop(p: PlayerState): HeldCard | undefined {
    const i = p.deck.findIndex((c) => !this.jumper(c));
    return i < 0 ? undefined : p.deck.splice(i, 1)[0];
  }
  /** The Remaining moves to another place in the pile it is in (as its owner's turn begins, or when it is clicked on top of the discard pile). */
  private jump(seat: Seat, pile: PileName) {
    const p = this.state.players[seat];
    const list = p[pile];
    const i = list.findIndex((c) => this.jumper(c));
    if (i < 0) return;
    const [card] = list.splice(i, 1);
    // somewhere else, if the pile has anywhere else
    let to = i;
    for (let tries = 0; tries < 12 && to === i && list.length > 0; tries++) to = Math.floor(this.rng() * (list.length + 1));
    list.splice(Math.min(to, list.length), 0, card);
    p.jumps = { deck: p.jumps?.deck ?? 0, discard: p.jumps?.discard ?? 0 };
    p.jumps[pile]++;
    this.log(`${this.cardName(card.num)} moves within ${p.name}’s ${pile === 'deck' ? 'deck' : 'discard pile'}.`, seat);
  }
  /**
   * A Spirit Soldier (while_in_play intangible): it can never be damaged or aimed at, and doesn't fill its lane, but
   * it still attacks, takes status effects, and is moved with its lane. Only its General's fall takes it away.
   */
  private intangible(card: FieldCard) {
    return card.faceUp && !!this.cards.byNumber.get(card.num)?.whileInPlay.includes('intangible');
  }
  /** Undead Legion: is this card a General (its Entrance raises the Legion)? */
  private isGeneral(card: FieldCard) {
    return card.faceUp && !!this.cards.byNumber.get(card.num)?.effects.some((e) => e.words[0] === 'legion');
  }
  /** A General on the table and still standing (not destroyed): its fallen Undead Soldiers rise as Spirits. */
  private generalStands(id: string | undefined) {
    const g = id ? this.findField(id) : undefined;
    return !!g && !g.atZero && !this.hasOn(g, CONFIG.turn.lifetime.expiredToken) ? g : undefined;
  }
  /** The Legion a card belongs to (the General's id), or null. */
  private legionHead(card: FieldCard): string | null {
    return card.legion ?? (this.isGeneral(card) ? card.id : null);
  }
  /** Can nothing touch this card? (tagged untouchable: no damage, no status effects, no SP decay. Calling Pillar) */
  private untouchable(card: FieldCard) {
    if (card.heldBy) return true; // (a Troop Soul Collector holds is only there to be seen)
    if (card.asBiome) return true; // (Eldritch Realm Crosser in the Biome slot is a Biome now, not a Unit to aim at)
    return card.faceUp && !!this.cards.byNumber.get(card.num)?.whileInPlay.includes('untouchable');
  }
  /** The face-up Troops and Buildings that damage can be put on (not Emperors, not untouchable cards). */
  private hittable(c: FieldCard) {
    const def = this.cards.byNumber.get(c.num);
    return c.faceUp && !!def && !isEmperor(def) && (def.types.includes('troop') || def.types.includes('building')) && !this.untouchable(c) && !this.intangible(c);
  }
  /** Calling Pillar's Entrance: damage to every Unit in its own lane. Returns the line for the log. */
  private laneDamage(card: FieldCard): string {
    const s = this.state; const n = CONFIG.secretRares.laneDamage;
    const at = slotAt(card);
    const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
    const name = this.cardName(card.num);
    if (!at || slotKind(at.slot.name) !== 'Lane' || !damageId || n <= 0) return `${name}: no lane to hit.`;
    // only its own lane: the Units sharing that lane slot with it, not the opposing lane across the mat
    const hit = s.field.filter((c) => {
      const there = slotAt(c);
      return c.id !== card.id && this.hittable(c) && !!there && there.slot === at.slot && there.side === at.side;
    });
    for (const c of hit) this.addDamage(c, damageId, n, null);
    return hit.length ? `${name} deals ${n} damage to every Unit in its lane: ${hit.map((c) => this.cardName(c.num)).join(', ')}.` : `${name}: no Units in its lane to damage.`;
  }
  /** The Remaining's Entrance: damage to every other Troop and Building on both sides. Returns the line for the log. */
  private fieldWipe(card: FieldCard): string {
    const n = CONFIG.secretRares.wipeDamage;
    const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
    const hit = this.state.field.filter((c) => c.id !== card.id && this.hittable(c));
    // (dealt like any other hit: Overshield, warding and the like still count, and — unless it is set not to Pierce —
    // a Shield doesn't stop it, as Pierce never does)
    const notes: string[] = [];
    if (damageId && n > 0) for (const c of hit) this.landHit(card.owner, c, n, { pierce: CONFIG.secretRares.wipePierce, from: card }, notes);
    this.settleFallen();
    // the Biome that let it in answers: a great deep red X across Realm Beyond Comprehension
    for (const gate of this.state.field.filter((c) => c.faceUp && !!this.cards.byNumber.get(c.num)?.whileInPlay.includes('remaining_gate'))) this.fx('slot', gate.id, undefined, { effect: 'remainingArrives' });
    return `${this.cardName(card.num)} deals ${n}${CONFIG.secretRares.wipePierce ? ' pierce' : ''} damage to every Unit on both sides of the field${hit.length ? ` (${hit.length})` : ''}${notes.length ? `. ${notes.join('; ')}` : ''}.`;
  }
  /** The Calling Pillars a player has in their own lanes, face-up and working. */
  private pillarsOf(seat: Seat): FieldCard[] {
    return this.state.field.filter((c) => {
      const at = slotAt(c);
      return c.owner === seat && c.faceUp && !!this.cards.byNumber.get(c.num)?.whileInPlay.includes('calling') && !this.passiveStopper(c) && !!at && slotKind(at.slot.name) === 'Lane' && at.side === sideOf(seat);
    });
  }
  /**
   * Does this player have a Calling Pillar in each lane of their side, and enough in all (CONFIG.secretRares.callingPillars:
   * five)? On the 1v1 table that is one in each of the five lanes; on the 2v2 table, one in each of the four and a
   * second in one of them.
   */
  private pillarsComplete(seat: Seat) {
    const lanes = [...new Set(CONFIG.slots.filter((sl) => slotKind(sl.name) === 'Lane').map((sl) => sl.name))];
    const pillars = this.pillarsOf(seat);
    return lanes.length > 0 && lanes.every((name) => pillars.some((c) => slotAt(c)!.slot.name === name)) && pillars.length >= Math.max(lanes.length, CONFIG.secretRares.callingPillars);
  }
  /**
   * Calling Pillar's Passive, first half: a player now has one in each of their lanes. The call goes out: every
   * Pillar erupts, and so does their Emperor. What follows (resolveCalling) waits until the eruption has played:
   * the table of whoever is watching asks for it a few seconds later, and any real move does it first if it
   * hasn't happened by then.
   */
  private checkCalling() {
    const s = this.state;
    if (s.result) return;
    s.players.forEach((p, i) => {
      const seat = i as Seat;
      if (p.out || s.calling?.includes(seat) || !this.pillarsComplete(seat)) return;
      s.calling = [...(s.calling ?? []), seat];
      const pillars = this.pillarsOf(seat);
      for (const c of pillars) this.fx('slot', c.id, undefined, { effect: 'pillarAnswers' });
      const emperor = this.emperorOf(seat);
      const text = `A ${this.cardName(pillars[0].num)} stands in each of ${p.name}’s lanes. The call goes out…`;
      if (emperor) {
        this.fx('slot', emperor.id, undefined, { effect: 'pillarsCall' });
        this.fx('status', emperor.id, undefined, { effect: 'notice', text });
      }
      this.log(text, seat);
    });
  }
  /**
   * Calling Pillar's Passive, second half, once the eruption has played: the player discards their hand, takes
   * The Remaining from their deck or discard pile into their hand, gains Gerzite, and their Pillars are discarded.
   * (If a Pillar has left a lane in the meantime, nothing happens.)
   */
  private resolveCalling() {
    const s = this.state;
    const waiting = s.calling ?? [];
    delete s.calling;
    for (const seat of waiting) {
      const p = s.players[seat];
      if (!p || p.out || s.result || !this.pillarsComplete(seat)) continue;
      const pillars = this.pillarsOf(seat);
      // the hand is discarded…
      const hand = p.hand.splice(0);
      for (const c of hand) this.insertAt(p.discard, this.rehide(c), 'top');
      // …The Remaining comes from the deck or the discard pile, in a deep red glow…
      let called: HeldCard | undefined;
      let calledFrom: PileName = 'deck';
      for (const pile of ['deck', 'discard'] as PileName[]) {
        const k = p[pile].findIndex((c) => this.jumper(c));
        if (k >= 0 && !called) { called = p[pile].splice(k, 1)[0]; calledFrom = pile; }
      }
      if (called) {
        p.hand.push(this.rehide(called));
        this.fx('toHand', '', undefined, { effect: calledFrom, seat, count: 1, tint: 'remaining' });
      }
      // …Gerzite is gained, and the Pillars are discarded
      const gerzite = CONFIG.tokenRules.fractalGives;
      const got = this.gain(seat, gerzite, CONFIG.secretRares.callGerzite);
      // (each one cracks and bursts apart on the table as it goes, the way a sacrificed unit does)
      for (const c of pillars) {
        this.fx('sacrifice', c.id, undefined, { seat });
        this.discardFromPlay(c);
      }
      const text = `The call is answered. ${p.name} discards their hand${hand.length ? ` (${hand.length} card${hand.length === 1 ? '' : 's'})` : ''}, ${called ? `${this.cardName(called.num)} comes to their hand` : 'finds nothing to call'}, and gains ${this.gainText(gerzite, CONFIG.secretRares.callGerzite, got)}. The Pillars are discarded.`;
      const emperor = this.emperorOf(seat);
      if (emperor) this.fx('status', emperor.id, undefined, { effect: 'notice', text });
      this.log(text, seat);
    }
  }
  // ---------------- what cards do by themselves (the `effects` cell in cards.csv) ----------------

  private fxDepth = 0;

  /** Every turn played so far, by everyone: a stamp for "this turn" and "the turn before". */
  private turnStamp() {
    return this.state.players.reduce((n, p) => n + p.turns, 0);
  }
  /** Which side of the mat a card is on (where it stands; off the mat, its owner's side). */
  private sideOfCard(card: FieldCard): 1 | -1 {
    if (card.sideAs) return card.sideAs;
    return this.standsAt(card)?.side ?? sideOf(card.owner);
  }
  /**
   * The side a card fights for, for what card effects call "opposing" or "yours": the side it stands on, except a unit
   * standing in the other side's lane (an opponent's Royal Guard from Royal Delivery, a Possessed unit), which is the
   * side that controls it. So a Spell can be aimed at the opposing Royal Guard in your own lane, and isn't offered
   * yours in theirs.
   */
  private fightsOn(card: FieldCard): 1 | -1 {
    if (this.state.mode === 'solo') return (card.fightsFor as 1 | -1 | undefined) ?? this.sideOfCard(card);
    return this.intruder(card) ? this.controlSide(card) : this.sideOfCard(card);
  }
  /** A card's affiliations right now: an affiliation token on it says so whatever its sheet row does. */
  private affOf(card: FieldCard): string[] {
    const on = this.tokensOn(card);
    const aff = (list: Token[]) => list.map((t) => this.tokenDb.byId.get(t.tokenId)).filter((d) => d?.category === 'affiliation').map((d) => d!.id.replace(/^aff-/, ''));
    const set = aff(on.filter((t) => !t.alsoWhile));
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    const base = set.length ? set : def ? defAffiliations(def) : [];
    // (one that is "also" an affiliation for now: both its own and that one — Arcane Ascension's Magical)
    const also = aff(on.filter((t) => t.alsoWhile));
    return also.length ? [...new Set([...base, ...also])] : base;
  }
  /** An "also" affiliation token (alsoWhile) whose card no longer has that status comes off (Arcane Ascension's Magical, once its Overclock is gone). */
  private settleAlsoAffs() {
    const s = this.state;
    for (const t of s.tokens.filter((o) => o.alsoWhile)) {
      const card = this.cardUnder(t);
      if (card && this.tokensOn(card).some((o) => this.effectOf(o) === t.alsoWhile && o.value > 0)) continue;
      this.removeTokens([t]);
      if (card) this.log(`${this.cardName(card.num)} is no longer also ${this.tokenDb.byId.get(t.tokenId)?.name ?? 'that affiliation'}: its ${this.tokenDb.list.find((d) => d.effect === t.alsoWhile)?.name ?? t.alsoWhile} is gone.`, card.owner);
    }
  }
  /** The affiliation of the damage a card deals: its own, unless its sheet row says otherwise ("always: damage_aff undead": Wicked Phantom; Icewings stay Frigid). */
  private damageAffOf(card: FieldCard): string[] {
    const set = this.tokensOn(card).map((t) => this.tokenDb.byId.get(t.tokenId)).filter((d) => d?.category === 'affiliation').map((d) => d!.id.replace(/^aff-/, ''));
    if (set.length) return set;
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    const said = def?.effects.find((e) => e.when === 'always' && e.words[0] === 'damage_aff');
    return said ? said.words.slice(1) : (def?.affiliations ?? []);
  }
  private healthLeft(card: FieldCard) {
    const def = this.cards.byNumber.get(card.num);
    const on = this.tokensOn(card);
    const full = def ? (this.fullForm(card, def).health ?? 0) : 0;
    return full + this.gainedHealth(on) + on.filter((t) => this.effectOf(t) === 'overheal').reduce((n, t) => n + t.value, 0) - this.damageOn(card);
  }
  /**
   * The Emperor on a side of the mat. In 2v2 each side has two: `near` says which. On the near card's own side it
   * is its owner's Emperor; on the other side, the one across from it (on the same half of the mat). An Emperor
   * whose player is out is only picked when no other is left.
   */
  private emperorOn(side: 1 | -1, near?: FieldCard | { x: number }) {
    const s = this.state;
    const emperorsHere = (spawnedToo: boolean) => s.field.filter((c) => c.faceUp && (spawnedToo || !c.spawned) && !!this.cards.byNumber.get(c.num)?.types.includes('emperor') && this.sideOfCard(c) === side);
    let all = emperorsHere(false);
    // the solo table has no opponent: an Emperor summoned onto the far side stands in for theirs (so Spells that
    // reach the opposing Emperor, like Primordial Seastorm and Dividend, can be tried out against it)
    if (!all.length && s.mode === 'solo') all = emperorsHere(true);
    if (!s.teams || all.length < 2) return all[0];
    const inGame = all.filter((c) => !s.players[c.owner]?.out);
    const pool = inGame.length ? inGame : all;
    if (!near) return pool[0];
    if ('owner' in near && this.sideOfCard(near) === side) {
      const own = pool.find((c) => c.owner === near.owner);
      if (own) return own;
    }
    return pool.find((c) => Math.sign(c.x) === Math.sign(near.x)) ?? pool[0];
  }
  /** A card's effects for one moment: none while it is face-down, and no "passive" ones while its Passives are stopped. */
  private effectsOf(card: FieldCard, when: string): CardEffect[] {
    if (!CONFIG.combat.enabled || !card.faceUp) return [];
    const def = this.cards.byNumber.get(card.num);
    let list = def?.effects.filter((e) => e.when === when) ?? [];
    if (card.soul) list = [...list, ...(this.cards.byNumber.get(card.soul.num)?.effects.filter((e) => e.when === when && e.passive) ?? [])]; // a Soul can still set off its Troop's passives
    if (card.copied && card.copied.kind !== 'enter') { const cd = this.cards.byNumber.get(card.copied.num); if (cd) list = [...list, ...(card.copied.kind === 'passive' ? cd.effects.filter((e) => e.passive) : this.tapEffects(cd)).filter((e) => e.when === when)]; } // Woods Wailer
    if (card.borrowed?.length && when !== 'enter') for (const n of card.borrowed) list = [...list, ...(this.cards.byNumber.get(n)?.effects.filter((e) => e.when === when && (e.passive || e.when === 'tap' || e.when === 'always')) ?? [])]; // Soul Collector uses the mechanics of the Troops attached to it
    if (!list.length) return list;
    // a Biome, Tome or Spell whose turns are over does nothing more, except what it was waiting to do ("if later", "if age 2": Timed Blast, Royal Delivery)
    if (when !== 'expired' && !this.isUnit(def) && !isEmperor(def!) && this.isExpired(card)) return list.filter((e) => e.conds.some((c) => c[0] === 'later' || c[0] === 'age'));
    return list.some((e) => e.passive) && this.passiveStopper(card) ? list.filter((e) => !e.passive) : list;
  }
  /**
   * A card's Tap, as Woods Wailer copies it: its "tap:" effects, or for a card whose Tap works another way (Charred
   * Clan: tapped as a Volcanic Unit of yours is summoned) the effects it has besides its Entrance and Passives.
   */
  private tapEffects(def: CardDef): CardEffect[] {
    const taps = def.effects.filter((e) => e.when === 'tap');
    if (taps.length || !def.abilities.includes('tap')) return taps;
    return def.effects.filter((e) => !e.passive && !['enter', 'always', 'aura', 'take', 'deal', 'cost', 'play_if'].includes(e.when));
  }
  /** Does one "if …" of an effect hold? */
  private condHolds(card: FieldCard, cond: string[], ctx: FxCtx): boolean {
    const not = cond[0].startsWith('!');
    const [word, ...rest] = [cond[0].replace(/^!/, ''), ...cond.slice(1)];
    const s = this.state;
    const def = this.cards.byNumber.get(card.num);
    let ok = false;
    const cmp = /^health(<=|>=)(\d+)$/.exec(word);
    if (cmp) ok = cmp[1] === '<=' ? this.healthLeft(card) <= Number(cmp[2]) : this.healthLeft(card) >= Number(cmp[2]);
    else if (word === 'biome') ok = s.field.some((c) => c.faceUp && !!this.cards.byNumber.get(c.num)?.types.includes('biome') && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && rest.some((a) => this.affOf(c).includes(a)));
    else if (word === 'untapped') ok = card.rot % 2 === 0;
    else if (word === 'berry') ok = (card.berry ?? 0) > 0 && card.berryAt === this.turnStamp() - 1; // Berry Reserve: it took damage in the opponent's turn just gone
    else if (word === 'alive') ok = s.field.includes(card) && !this.hasOn(card, CONFIG.turn.lifetime.expiredToken) && (!def?.health || this.healthLeft(card) > 0);
    else if (word === 'pool' || word === 'may' || word === 'card' || word === 'else_emperor') ok = true;
    else if (word === 'chose') ok = ctx.chose === Number(rest[0]);
    else if (word === 'mode') ok = card.mode === Number(rest[0]);
    else if (word === 'later') ok = card.playedTurn !== undefined && s.players[card.owner].turns > card.playedTurn;
    else if (word === 'age') ok = card.playedTurn !== undefined && s.players[card.owner].turns - card.playedTurn === Number(rest[0]);
    else if (word === 'foe_emperor_over') { const emp = this.emperorOn(-this.sideOfCard(card) as 1 | -1, card); ok = !!emp && this.healthLeft(emp) > Number(rest[0]); }
    else if (word === 'it_in_lanes') { const t = ctx.it ? this.standsAt(ctx.it) : null; ok = !!t && !!card.lanes && card.lanes.side === t.side && card.lanes.names.includes(t.slot.name); }
    else if (word === 'host_shielded') { const h = card.host ? this.findField(card.host) : undefined; ok = !!h && this.valueOn(h, 'shield') > 0; }
    else if (word === 'few_allies') ok = this.whoOf(card, 'allies', ctx).length < Number(rest[0] ?? 3);
    else if (word === 'it_emperor') ok = !!ctx.it && !!this.cards.byNumber.get(ctx.it.num)?.types.includes('emperor');
    else if (word === 'it_troop') ok = !!ctx.it && !!this.cards.byNumber.get(ctx.it.num)?.types.includes('troop');
    else if (word === 'it_building') ok = !!ctx.it && !!this.cards.byNumber.get(ctx.it.num)?.types.includes('building'); // (Salvager)
    else if (word === 'it_hurt') ok = !!ctx.it && this.damageOn(ctx.it) > 0; // the card chosen has damage on it (Lingering Wound, Recall Rift)
    else if (word === 'last_summoned') ok = !this.state.field.some((c) => c !== card && c.summonedBy === card.summonedBy && c.num === card.num && !c.atZero && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken)); // the last of its kind its card summoned still standing (Undead Guards)
    else if (word === 'slain') { this.settleFallen(); ok = !!ctx.it && (!s.field.includes(ctx.it) || this.hasOn(ctx.it, CONFIG.turn.lifetime.expiredToken)); }
    else if (word === 'foe_lane_empty') { const foe = -this.sideOfCard(card) as 1 | -1; ok = laneOrder().some((l) => !this.unitsAt(l, foe).filter((u) => !this.intruder(u)).length); } // (these narrow a choice, or let it be passed: see runEffects)
    else if (word === 'my_turn') ok = this.allies(card.owner, s.turn);
    else if (word === 'foe_hidden') ok = s.field.some((c) => !c.faceUp && this.opposed(c.owner, card.owner)); // an opponent has a face-down card in play (Questioning reveals it)
    else if (word === 'it_biome') ok = !!ctx.it && s.field.some((c) => c.faceUp && c !== ctx.it && !!this.cards.byNumber.get(c.num)?.types.includes('biome') && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && this.affOf(c).some((a) => this.affOf(ctx.it!).includes(a))); // the card chosen shares an affiliation with the Biome in play (Rekindle)
    else if (word === 'tossed') ok = !!ctx.tossed;
    else if (word === 'moved') ok = !!ctx.moved;
    else if (word === 'form') ok = (card.form ?? 0) === Number(rest[0]);
    else if (word === 'aff') ok = rest.some((a) => this.affOf(card).includes(a));
    else if (word === 'as_biome') ok = !!card.asBiome;
    else if (word === 'gerzite') ok = this.barTokens(card.owner, 'gerzite').length >= Number(rest[0] ?? 1);
    else if (word === 'near_lane') ok = true;
    else if (word === 'target_marked') ok = !!ctx.target && ctx.target.mustAttack === card.id;
    else if (word === 'it_across') { const a = this.standsAt(card); const t = ctx.it ? this.standsAt(ctx.it) : null; ok = !!a && !!t && slotKind(a.slot.name) === 'Lane' && slotKind(t.slot.name) === 'Lane' && a.side !== t.side && this.laneRow(a.side).indexOf(a.slot) === this.laneRow(t.side).indexOf(t.slot); }
    else if (word === 'anyone' || word === 'fresh_lane') ok = true;
    else if (word === 'host_locked') { const h = card.host ? this.findField(card.host) : undefined; ok = !!h && this.tokensOn(h).some((t) => this.effectOf(t) === CONFIG.tokenRules.lockToken && t.from === card.id); }
    else if (word === 'beside_host') ok = this.besideHost(card).length > 0;
    else if (word === 'more_units') { const side = this.sideOfCard(card); const n = (sd: number) => s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.sideOfCard(c) === sd && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken)).length; ok = n(side) > n(-side); }
    else if (word === 'dealt') ok = (ctx.amount ?? 0) > 0;
    else if (word === 'all_in') ok = !!ctx.fresh;
    else if (word === 'status') ok = this.tokensOn(card).some((t) => (rest[0] === 'any' ? this.tokenDb.byId.get(t.tokenId)?.category === 'status' && CONFIG.hostileStatuses.includes(this.effectOf(t)) : this.effectOf(t) === rest[0]));
    else if (word === 'mate') { const at = this.standsAt(card); ok = !!at && this.unitsAt(at.slot, at.side, card).some((u) => rest.some((a) => this.affOf(u).includes(a))); }
    else if (word === 'target') ok = !!ctx.target && (rest[0] === 'emperor' ? !!this.cards.byNumber.get(ctx.target.num)?.types.includes('emperor') : rest[0] === 'unit' ? this.isUnit(this.cards.byNumber.get(ctx.target.num)) : false);
    else if (word === 'it') ok = !!ctx.it && rest.some((a) => this.affOf(ctx.it!).includes(a));
    else if (word === 'full_count') ok = !!def?.count && this.unitsLeft(card) >= def.count;
    else if (word === 'hurt_last_turn') ok = card.hurtAt !== undefined && card.hurtAt === this.turnStamp() - 1;
    else if (word === 'rested') ok = card.restedAt !== undefined && card.restedAt === s.players[card.owner].turns - 1;
    return not ? !ok : ok;
  }
  /** Whom an effect is done to. */
  private whoOf(card: FieldCard, on: string, ctx: FxCtx): FieldCard[] {
    const s = this.state;
    const at = this.standsAt(card);
    const side = this.sideOfCard(card);
    const foe = -side as 1 | -1;
    const row = (sd: number) => laneOrder().map((slot) => ({ slot, x: slot.x * sd })).sort((a, b) => a.x - b.x);
    const i = at && slotKind(at.slot.name) === 'Lane' ? row(side).findIndex((l) => l.slot === at.slot) : -1;
    const lanes = (sd: 1 | -1, idx: number[]) => idx.filter((j) => j >= 0 && j < row(sd).length).flatMap((j) => this.unitsAt(row(sd)[j].slot, sd, card)).filter((u) => !u.faceUp || this.isUnit(this.cards.byNumber.get(u.num)));
    const live = (list: (FieldCard | undefined)[]) => list.filter((c): c is FieldCard => !!c && s.field.includes(c));
    switch (on) {
      case 'self': return live([card]);
      case 'target': return live([ctx.target]);
      case 'attacker': return live([ctx.attacker]);
      case 'attacker_unit': return live([ctx.attacker]).filter((c) => this.isUnit(this.cards.byNumber.get(c.num))); // ("Unit" on a card means a Troop or Building, never an Emperor)
      case 'it': return live([ctx.it]);
      case 'front': return i < 0 ? [] : lanes(foe, [i]);
      case 'front_or_emperor': { const f = i < 0 ? [] : lanes(foe, [i]); return f.length ? f : live([this.emperorOn(foe, card)]); }
      case 'target_lane': { const t = ctx.target ? this.standsAt(ctx.target) : null; return t && slotKind(t.slot.name) === 'Lane' ? this.unitsAt(t.slot, t.side, card, true) : live([ctx.target]); }
      case 'adjacent': {
        // the lanes either side of the one that was attacked (or, with no target, of the one across from this card)
        const t = ctx.target ? this.standsAt(ctx.target) : null;
        const sd = t ? t.side : foe;
        const mid = t && slotKind(t.slot.name) === 'Lane' ? row(sd).findIndex((l) => l.slot === t.slot) : t ? -9 : i;
        return mid < 0 ? [] : lanes(sd, [mid - 1, mid + 1]);
      }
      // (allies: Units on its side of the field that are its player's, or a teammate's: never one an opponent summoned there — Royal Delivery's Royal Guard)
      case 'allies_adjacent': return i < 0 ? [] : lanes(side, [i - 1, i + 1]).filter((u) => !this.opposed(card.owner, u.owner));
      case 'allies_range': { const def = this.cards.byNumber.get(card.num); const r = def ? this.rangeNow(card, def) : 0; return i < 0 ? [] : lanes(side, Array.from({ length: 2 * r + 1 }, (_, k) => i - r + k)).filter((u) => !this.opposed(card.owner, u.owner)); }
      case 'lane': return at ? [...live([card]), ...this.unitsAt(at.slot, at.side, card, true)] : live([card]);
      case 'lane_one': { const all = at ? [...live([card]), ...this.unitsAt(at.slot, at.side, card)] : []; return all.filter((u) => this.damageOn(u) > 0).sort((a, b) => this.damageOn(b) - this.damageOn(a)).slice(0, 1); }
      case 'emperor': return live([this.emperorOn(side, card)]);
      case 'foe_emperor': { const rod = this.magnetFor(card); return rod ? [rod] : live([this.emperorOn(foe, card)]); }
      case 'summoned': return s.field.filter((c) => c.summonedBy === card.id);
      case 'paired': return live((card.pair ?? []).map((id) => this.findField(id)));
      case 'emperors': return live([this.emperorOn(side, card), this.emperorOn(foe, card)]);
      case 'in_lanes': {
        const L = this.lanesFor(card, ctx);
        if (!L) return [];
        const all = laneOrder().filter((l) => L.names.includes(l.name)).flatMap((l) => this.unitsAt(l, L.side, undefined, true));
        // (an opposing lane: what this card's own side summoned there — Royal Delivery's Royal Guard, a Dropship's
        // Skell-Mini — isn't caught in it. A foe's unit it Possessed still is: it is still theirs)
        return s.mode !== 'solo' && L.side !== this.sideOfCard(card) ? all.filter((u) => !this.allies(card.owner, u.owner)) : all;
      }
      case 'damaged': return live(ctx.hurt ?? []);
      // (the Units in that card's lane on its side of the fight: one summoned into the other side's lane — Royal Delivery's
      // Royal Guard — takes a lane-wide effect alone, never the units of the side it stands on, and the other way round)
      case 'it_lane': { const it = ctx.it; const t = it ? this.standsAt(it) : null; return it && t && slotKind(t.slot.name) === 'Lane' ? this.unitsAt(t.slot, t.side, undefined, true).filter((u) => u === it || this.fightsOn(u) === this.fightsOn(it)) : live([ctx.it]); }
      case 'tiered': return s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.tierOf(c) > 1);
      case 'all_cards': return s.field.filter((c) => c.faceUp && (this.isUnit(this.cards.byNumber.get(c.num)) || !!this.cards.byNumber.get(c.num)?.types.includes('emperor')) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
      case 'host': return live([card.host ? this.findField(card.host) : undefined]);
      case 'afflicted': return s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && (this.hasOn(c, 'bleed') || this.hasOn(c, 'decay')));
      case 'across_it': { const t = ctx.it ? this.standsAt(ctx.it) : null; if (!t || slotKind(t.slot.name) !== 'Lane') return []; const j = row(t.side).findIndex((l) => l.slot === t.slot); return lanes(-t.side as 1 | -1, [j]).filter((u) => !!this.cards.byNumber.get(u.num)?.types.includes('troop')); }
      case 'everyone': return s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
      case 'actor_weakest': { const mine = s.field.filter((c) => c.faceUp && c.owner === ctx.actor && this.isUnit(this.cards.byNumber.get(c.num)) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken)); return mine.sort((a, b) => this.healthLeft(a) - this.healthLeft(b)).slice(0, 1); }
      case 'allies': return s.field.filter((c) => c !== card && c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.sideOfCard(c) === side && !this.opposed(card.owner, c.owner) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
      case 'foes': return s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.fightsOn(c) !== this.fightsOn(card) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
    }
    return [];
  }
  /** Runs a card's effects for one moment. */
  /** Traveler's Map: is the Biome in play switched off for this side of the field right now? */
  private biomeOffFor(side: 1 | -1) {
    const b = this.state.biomeOff;
    return !!b && b.side === side && b.at === this.turnStamp();
  }
  private fire(card: FieldCard, when: string, ctx: FxCtx = {}) {
    if (card.heldBy) return; // (a Troop Soul Collector holds does nothing itself: the Collector uses its mechanics)
    // (a Biome's effect on a player whose side it has been switched off for)
    if (ctx.actor !== undefined && this.cards.byNumber.get(card.num)?.types.includes('biome') && this.biomeOffFor(sideOf(ctx.actor))) return;
    const list = this.effectsOf(card, when);
    if (!list.length || this.fxDepth > 3) return;
    // a look of its own each time this moment sets it off (Arcane Doors: its doors swing open for each card it draws)
    const own = CONFIG.looks.enabled ? CONFIG.looks.onTrigger[this.cardName(card.num)] : undefined;
    if (own && own.when === when) this.look(own.look, card);
    this.runEffects(card, list, ctx);
  }
  /**
   * Carries out a list of effects in order. One that needs its player to choose a card ("on pick_foe") stops the
   * list there: the choice is put to them (state.picks), and the rest follows once they have answered. With a
   * single card to choose from it is simply taken; a bot chooses for itself.
   */
  private runEffects(card: FieldCard, list: CardEffect[], ctx: FxCtx) {
    const s = this.state;
    this.fxDepth++;
    try {
      for (let i = 0; i < list.length; i++) {
        const e = list[i];
        if (!e.conds.every((c) => this.condHolds(card, c, ctx))) continue;
        const seat = card.owner;
        if (e.words[0] === 'toss') {
          // "toss": a card from the hand is discarded · "toss spell may": only a Spell, and it may be passed (Bone Box)
          const kinds = e.words.slice(1).filter((x) => x !== 'may');
          const may = e.words.includes('may');
          const fits = s.players[seat].hand.filter((c) => !kinds.length || kinds.some((k) => this.cards.byNumber.get(c.num)?.types.includes(k)));
          if (!fits.length) { ctx.tossed = false; continue; }
          if (!s.bots?.includes(seat) && (fits.length > 1 || may)) {
            const what = kinds.length ? `${kinds.map((k) => typeName(k)).join(' or ')} ` : '';
            (s.picks ??= []).push({ id: s.nextId++, seat, name: this.cardName(card.num), text: `${this.cardName(card.num)}: ${may ? 'you may ' : ''}choose a ${what}card from your hand to discard`, options: [], cards: fits.map((c) => ({ id: c.id, num: c.num })), toss: true, ...(may ? { optional: true, skip: 'Discard nothing' } : {}), src: { ...card, merged: [] }, rest: list.slice(i), ctx: this.packCtx(ctx) });
            return;
          }
        }
        if (e.words[0] === 'reveal' && !e.words.includes('look') && CONFIG.looks.revealPause) {
          // the cards are turned up and shown to both players first; what happens to them waits for Continue
          const plan = this.revealPlan(seat, this.cardName(card.num), e.words.slice(1));
          if (!plan) continue;
          s.showing = plan;
          if (s.bots?.includes(seat)) { this.revealApply(plan); s.showing = { ...plan, waiting: false }; continue; }
          (s.picks ??= []).push({ id: s.nextId++, seat, name: plan.name, text: `${plan.name}: the cards turned up are shown to both players`, options: [], showing: plan.id, src: { ...card, merged: [] }, rest: list.slice(i + 1), ctx: this.packCtx(ctx) });
          return;
        }
        if (e.words[0] === 'divide') {
          // damage point by point: the rest of the list waits until its player is done (see splitAsk)
          const w = e.words.slice(1);
          const kind = w.includes('wound') ? 'wound' : 'damage';
          let n = w[0] === 'discard2' ? 2 * s.players[seat].discard.length : w.includes('chosen') ? (this.lastChoice ?? 0) : Number(w[0]) || 0;
          if (kind === 'damage') n = this.spellScale(card, n);
          const free = kind === 'wound' && w.includes('chosen') && this.lastChoice === null;
          if (free) n = 99;
          const capAt = w.indexOf('cap');
          const most = w.includes('own') ? Number(w[w.indexOf('own') + 1]) || 3 : undefined;
          if (n <= 0) { ctx.amount = 0; continue; }
          this.splitAsk(card, { left: n, cap: capAt >= 0 ? Number(w[capAt + 1]) || 0 : null, emp: 0, kind, down: w.includes('down'), any: w.includes('any'), ...(most !== undefined ? { most } : {}), hit: [], total: 0 }, ctx, free, list.slice(i + 1));
          return;
        }
        if (e.words[0] === 'choose_lane') {
          // a lane to move a Unit into (the one chosen, else the one being held, else the card itself):
          // "adjacent" a lane beside its own · "other" any other lane of its side · "across_aff submerged" a lane across from one of yours of that affiliation
          const unit = ctx.it ?? ctx.target ?? card;
          const opts = this.laneOptions(card, unit, e.words.slice(1));
          if (!opts.length) { delete ctx.lane; continue; }
          const put2 = (o: { name: string; side: 1 | -1 }) => { ctx.lane = o.name; ctx.laneFoe = o.side !== this.sideOfCard(card); };
          if (opts.length === 1 || s.bots?.includes(seat)) { put2(opts[0]); continue; }
          const whose = (sd: 1 | -1) => (sd === sideOf(seat) ? '' : ` (${s.players.find((_, k) => sideOf(k as Seat) === sd)?.name ?? 'their'}’s side)`);
          (s.picks ??= []).push({ id: s.nextId++, seat, name: this.cardName(card.num), text: `${this.cardName(card.num)}: choose the lane ${unit === card ? 'it moves' : `${this.cardName(unit.num)} moves`} into`, options: [], choices: opts.map((o) => `${o.name}${whose(o.side)}`), laneChoices: opts, src: { ...card, merged: [] }, rest: list.slice(i), ctx: this.packCtx(ctx) });
          return;
        }
        if (e.words[0] === 'choose') {
          // "choose Stun_a_Unit/Add_1_cooldown": its player picks one; the rules after it say "if chose 1", "if chose 2"
          const labels = e.words.slice(1).join(' ').split('/').map((x) => x.replace(/_/g, ' ').trim()).filter(Boolean);
          const lanes = e.words[1] === 'lane' || e.words[1] === 'lanes'; // "choose lane foe|own" · "choose lanes foe 3": that many lanes side by side
          let groups = lanes ? this.laneChoiceGroups(card, e.words) : [];
          // (Spirit Rod: an opponent's Spell that picks lanes has to take the Rod's, as the middle where it can)
          const rodAt = lanes && e.words[2] === 'foe' ? this.magnetFor(card) : undefined;
          if (rodAt) { const at = this.standsAt(rodAt); const has = groups.filter((g) => !!at && g.names.includes(at.slot.name)); const row = at ? this.laneRow(at.side).map((l) => l.name) : []; const mid = has.filter((g) => g.names.length >= 3 && g.names[Math.floor(g.names.length / 2)] === at!.slot.name || (row.indexOf(at!.slot.name) === 0 || row.indexOf(at!.slot.name) === row.length - 1)); if (has.length) groups = mid.length ? mid : has; }
          if (lanes && e.conds.some((c) => c[0] === 'fresh_lane') && card.lanes) groups = groups.filter((g) => !g.names.some((n2) => card.lanes!.names.includes(n2)));
          if (lanes && e.conds.some((c) => c[0] === 'near_lane')) { const at = this.standsAt(card); const row = at ? this.laneRow(at.side).map((l) => l.name) : []; const j = at ? row.indexOf(at.slot.name) : -9; groups = groups.filter((g) => g.names.every((n2) => Math.abs(row.indexOf(n2) - j) === 1)); }
          const opts = lanes ? groups.map((g) => g.label) : labels;
          if (lanes && (s.bots?.includes(seat) || opts.length < 2)) {
            // (a bot takes the lanes with the most Units in them; for something it summons, the emptiest)
            // (a bot given "any" lane keeps to its own side)
            if (e.words[2] === 'any') groups = groups.filter((g) => !g.foe);
            const sideFor = (g: { foe: boolean }) => (this.sideOfCard(card) * (g.foe ? -1 : 1)) as 1 | -1;
            const held = (g: { names: string[]; foe: boolean }) => laneOrder().filter((l) => g.names.includes(l.name)).reduce((n, l) => n + this.unitsAt(l, sideFor(g)).length, 0);
            const summons = list.slice(i + 1).some((r) => r.words[0] === 'summon' || r.words[0] === 'resurrect');
            const best = [...groups].sort((x, y) => (summons ? held(x) - held(y) : held(y) - held(x)))[0];
            ctx.lane = best?.names.join(',');
            ctx.laneFoe = best ? best.foe : e.words[2] !== 'own';
            continue;
          }
          if (s.bots?.includes(seat) || opts.length < 2) { ctx.chose = opts.length > 1 && s.botLevels?.[seat] === 'easy' ? 1 + Math.floor(this.rng() * opts.length) : 1; continue; } // (Martin picks any; the others the first, the card's main effect)
          const hintSide = (g: { foe: boolean }) => (this.sideOfCard(card) * (g.foe ? -1 : 1)) as 1 | -1;
          const laneHints = lanes ? groups.map((g) => laneOrder().filter((l) => g.names.includes(l.name)).map((l) => ({ x: l.x * hintSide(g), z: l.z * hintSide(g) }))) : undefined;
          (s.picks ??= []).push({ id: s.nextId++, seat, name: this.cardName(card.num), text: `${this.cardName(card.num)}: ${lanes ? `choose ${e.words[2] === 'own' ? 'your' : e.words[2] === 'any' ? 'any' : 'your opponent’s'} lane${e.words[1] === 'lanes' ? 's' : ''}` : 'choose one'}`, options: [], choices: opts, ...(laneHints ? { laneHints } : {}), src: { ...card, merged: [] }, rest: list.slice(i), ctx: this.packCtx(ctx) });
          return;
        }
        if (!e.on.startsWith('pick_') || e.words[0] === 'repeat') {
          if (this.gravityHold(card, e, ctx, list.slice(i + 1))) return;
          if (e.words[0] === 'search') {
            // a search waits for its player: what comes after it (a Spell's "done") happens once the cards are chosen,
            // so a Spell can't take itself back out of the discard pile (Primordial Seastorm)
            const before = s.picks?.length ?? 0;
            this.searchThen = { src: { ...card, merged: [] }, rest: list.slice(i + 1), ctx: this.packCtx(ctx) };
            try { this.doEffect(card, e, ctx); } finally { this.searchThen = undefined; }
            if ((s.picks?.length ?? 0) > before) return;
            continue;
          }
          this.doEffect(card, e, ctx);
          continue;
        }
        const pool = this.pickPool(card, e, ctx);
        if (!pool.length) {
          // "if else_emperor": with no card left to choose, it goes to the opposing Emperor instead (Cannon Volley)
          if (e.conds.some((c) => c[0] === 'else_emperor')) this.doEffect(card, { ...e, on: 'foe_emperor' }, ctx);
          continue;
        }
        if (s.bots?.includes(seat) && e.words[0] === 'devour') {
          // a bot feeds the Fleshstealer only its cheap Troops (a token, a 1-cost card), never a strong one
          const cheap = pool.filter((u) => this.devourWorth(u) <= 3).sort((a, b) => this.devourWorth(a) - this.devourWorth(b));
          if (!cheap.length) continue;
          ctx.it = cheap[0];
          this.pickedNow.push(ctx.it.id);
          this.doEffect(card, { ...e, on: 'it' }, ctx);
          continue;
        }
        // (one card to choose from is taken at once, unless the effect may be passed: then its player says yes or no)
        if ((pool.length === 1 && !e.conds.some((c) => c[0] === 'may')) || s.bots?.includes(seat)) {
          ctx.it = pool.length === 1 ? pool[0] : this.botTarget(seat, e, pool);
          this.pickedNow.push(ctx.it.id);
          this.doEffect(card, { ...e, on: 'it' }, ctx);
          continue;
        }
        const name = this.cardName(card.num);
        (s.picks ??= []).push({
          id: s.nextId++, seat, name, text: `${name}: ${this.pickWords(e)}`, options: pool.map((c) => c.id),
          ...(e.conds.some((c) => c[0] === 'may') ? { optional: true } : {}),
          src: { ...card, merged: [] }, rest: list.slice(i),
          ctx: this.packCtx(ctx),
        });
        return;
      }
    } finally {
      this.fxDepth--;
    }
  }
  /** One side's lanes in groups of `n` side by side, as its player reads them: "Lane 2" · "Lanes 2 to 4". */
  /**
   * The lanes a "choose lane(s) own|foe|any" rule offers: your side's, your opponent's, or ("any") both, your own
   * first. Each says whether it is on the opponent's side.
   */
  private laneChoiceGroups(card: FieldCard, words: string[]): { label: string; names: string[]; foe: boolean }[] {
    const n = words[1] === 'lanes' ? Number(words[3]) || 2 : 1;
    const mine = this.sideOfCard(card);
    const own = this.laneGroups(mine, n).map((g) => ({ ...g, foe: false }));
    const theirs = this.laneGroups(-mine as 1 | -1, n).map((g) => ({ ...g, foe: true }));
    if (words[2] === 'own') return own;
    if (words[2] === 'any') return [...own.map((g) => ({ ...g, label: `${g.label} (your side)` })), ...theirs.map((g) => ({ ...g, label: `${g.label} (opponent’s side)` }))];
    return theirs;
  }
  private laneGroups(side: 1 | -1, n: number): { label: string; names: string[] }[] {
    const row = laneOrder().map((slot) => ({ slot, x: slot.x * side })).sort((a, b) => a.x - b.x);
    const out: { label: string; names: string[] }[] = [];
    for (let i = 0; i + n <= row.length; i++) {
      const names = row.slice(i, i + n).map((l) => l.slot.name);
      const nums = names.map((x) => x.replace(/\D+/g, '')).sort();
      out.push({ names, label: n === 1 ? names[0] : `Lanes ${nums[0]} to ${nums[nums.length - 1]}` });
    }
    return n === 1 ? out.sort((a, b) => a.label.localeCompare(b.label)) : out.sort((a, b) => a.label.localeCompare(b.label));
  }
  /** The lanes an effect is about: the ones just chosen, or the ones marked on its card as it was played. */
  private lanesFor(card: FieldCard, ctx: FxCtx): { side: 1 | -1; names: string[] } | null {
    if (ctx.lane) return { side: (this.sideOfCard(card) * (ctx.laneFoe === false ? 1 : -1)) as 1 | -1, names: ctx.lane.split(',') };
    return card.lanes ?? null;
  }
  // ---------------- moving Units from lane to lane ----------------

  /**
   * Why this Unit can't be moved by a card effect (null = it can). Entrenched Units and Buildings stay where they
   * are; a Unit riding with another, or one an Invisibility Cloak hides, moves with that card or not at all.
   */
  private unmovable(card: FieldCard): string | null {
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    const name = def?.name ?? 'A face-down card';
    if (!def || !this.isUnit(def)) return `${name} isn’t a Unit.`;
    if (def.types.includes('building') && !def.types.includes('troop')) return `${name} is a Building: Buildings can’t be moved.`;
    if (def.types.includes('building') && this.formIsBuilding(card)) return `${name} is a Building right now: Buildings can’t be moved.`;
    if (this.hasOn(card, CONFIG.tokenRules.entrenchedToken)) return `${name} is Entrenched and can’t be moved.`;
    if (card.attachedTo) return `${name} rides with another card.`;
    if (this.untouchable(card)) return `${name} can’t be affected by card effects.`;
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane') return `${name} isn’t in a lane.`;
    return null;
  }
  /** A two-type card that is a Building right now (Melstone Terror before its SP runs out). */
  private formIsBuilding(card: FieldCard) {
    return !card.troopForm;
  }
  /** One side's lanes, left to right as that side's player sees them. */
  private laneRow(side: 1 | -1) {
    return laneOrder().map((slot) => ({ slot, x: slot.x * side })).sort((a, b) => a.x - b.x).map((l) => l.slot);
  }
  /** The lanes a Unit may be moved into by an effect (see choose_lane in runEffects). */
  private laneOptions(card: FieldCard, unit: FieldCard, words: string[]): { name: string; side: 1 | -1 }[] {
    const at = this.standsAt(unit);
    if (!at || slotKind(at.slot.name) !== 'Lane' || this.unmovable(unit)) return [];
    const row = this.laneRow(at.side);
    const i = row.indexOf(at.slot);
    const mode = words[0] ?? 'adjacent';
    let lanes: Slot[] = [];
    if (mode === 'adjacent') lanes = [row[i - 1], row[i + 1]].filter(Boolean);
    else if (mode === 'other') lanes = row.filter((l) => l !== at.slot);
    else if (mode === 'across_aff') {
      // across from one of the card's owner's Units of that affiliation
      const mySide = this.sideOfCard(card);
      const mine = this.laneRow(mySide);
      const affs = words.slice(1);
      const marks = new Set(this.state.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.sideOfCard(c) === mySide && affs.some((a) => this.affOf(c).includes(a))).map((c) => this.standsAt(c)).filter((t) => t && slotKind(t.slot.name) === 'Lane').map((t) => mine.indexOf(t!.slot)));
      lanes = row.filter((_, j) => marks.has(j) && row[j] !== at.slot);
    }
    return lanes.map((l) => ({ name: l.name, side: at.side }));
  }
  /**
   * Moves a Unit into another lane on the same side or across: it keeps its tokens, whether it is tapped, and
   * anything riding with it. The lane may already hold a Unit: they share it (an effect's move is allowed to).
   */
  private moveUnit(card: FieldCard, slot: Slot, side: 1 | -1) {
    const s = this.state;
    const owned = this.owners();
    const toks = this.tokensOn(card);
    const dx = slot.x * side - card.x;
    const dz = slot.z * side - card.z;
    const before = { x: card.x, z: card.z };
    card.x += dx; card.z += dz;
    card.layer = s.nextLayer++;
    for (const t of toks) { t.x += dx; t.z += dz; t.layer = s.nextLayer++; }
    this.layout(owned);
    this.fx('slot', card.id, undefined, { effect: 'moved' });
    this.movedLane(card, before);
  }
  /** A Unit has gone from one lane to another: the cards of its opponents that watch for that (Mirestalker). */
  private movedLane(card: FieldCard, before: { x: number; z: number }) {
    const was = slotAt(before);
    const now = this.standsAt(card);
    if (!card.faceUp || !this.isUnit(this.cards.byNumber.get(card.num)) || !was || !now) return;
    if (slotKind(was.slot.name) !== 'Lane' || slotKind(now.slot.name) !== 'Lane' || (was.slot === now.slot && was.side === now.side)) return;
    const by = this.possessor(card) ?? card.owner;
    this.fireAll('foe_moved', (c) => c !== card && this.opposed(c.owner, by), { it: card });
  }
  /** Two Units change places (Uproot Rift, Coral Reef). */
  private swapUnits(a: FieldCard, b: FieldCard) {
    const ta = this.standsAt(a); const tb = this.standsAt(b);
    if (!ta || !tb) return false;
    this.moveUnit(a, tb.slot, tb.side);
    this.moveUnit(b, ta.slot, ta.side);
    return true;
  }
  /** Coral Reef: a player starts a swap (once in each of their turns): the first of their two Units. */
  private coralAsk(seat: Seat, biome: FieldCard | undefined, first?: string) {
    const s = this.state;
    if (!biome) return;
    const mine = s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.sideOfCard(c) === sideOf(seat) && !this.unmovable(c) && c.id !== first);
    const name = this.cardName(biome.num);
    if (mine.length < (first ? 1 : 2)) { this.fx('status', biome.id, undefined, { effect: 'notice', seat, text: `${name}: you need two of your Units that can be moved (not Entrenched, not Buildings).` }); return; }
    (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: choose ${first ? 'the second' : 'the first'} of two of your Units to swap`, options: mine.map((c) => c.id), optional: true, skip: 'Cancel', swap: { by: biome.id, ...(first ? { first } : {}) } });
  }
  private coralSwap(seat: Seat, biome: FieldCard, aId: string, bId: string) {
    const s = this.state;
    const a = this.findField(aId); const b = this.findField(bId);
    const name = this.cardName(biome.num);
    if (!a || !b || this.unmovable(a) || this.unmovable(b)) return;
    const free = [a, b].some((u) => this.affOf(u).includes('submerged'));
    if (!free && !this.payBar(seat, 1, false)) { this.fx('status', biome.id, undefined, { effect: 'notice', seat, text: `${name}: the swap costs 1 Gerzite, and you have none.` }); return; }
    (biome.usedBy ??= {})[seat] = this.turnStamp();
    this.swapUnits(a, b);
    this.log(`${name}: ${s.players[seat].name} swaps ${this.cardName(a.num)} and ${this.cardName(b.num)}${free ? ' (a Submerged Unit: free)' : ' for 1 Gerzite'}.`, seat);
  }
  /** A Spell's damage as the table changes it: a Darkgale Spell deals double; Light Screen takes 1 off an opponent's Spells; Frozen Peaks halves some. */
  private halvedFrom: number | null = null; // the damage before a Biome halved it (Frozen Peaks: an Emperor then takes a quarter of this)
  private spellScale(card: FieldCard, n: number) {
    const s = this.state;
    const srcDef = this.cards.byNumber.get(card.num);
    this.halvedFrom = null;
    if (srcDef && isEmperor(srcDef)) {
      // (an Emperor's ability of an affiliation Frozen Peaks halves deals half too)
      for (const c of s.field) for (const e2 of c.faceUp && c !== card && this.cards.byNumber.get(c.num)?.effects.length ? this.effectsOf(c, 'always') : []) if (e2.words[0] === 'spell_half' && e2.words.slice(1).some((a) => this.affOf(card).includes(a))) n = Math.round(n / 2);
      return n;
    }
    if (!srcDef?.types.includes('spell')) return n;
    if (s.field.includes(card) && this.hasOn(card, CONFIG.turn.darkgale.token)) n *= 2;
    for (const c of s.field) for (const e2 of c.faceUp && c !== card && this.cards.byNumber.get(c.num)?.effects.length ? this.effectsOf(c, 'always') : []) {
      if (e2.words[0] === 'spell_less' && this.opposed(c.owner, card.owner)) n = Math.max(0, n - (Number(e2.words[1]) || 1));
      if (e2.words[0] === 'spell_half' && e2.words.slice(1).some((a) => srcDef.affiliations.includes(a))) { this.halvedFrom = n; n = Math.round(n / (2 * this.biomeDoubling(c))); }
    }
    return n;
  }
  /** Murkfin: a Submerged Biome's stated effects are doubled while one is in play on its owner's side (1 = no Murkfin). */
  private biomeDoubling(biome: FieldCard) {
    const def = this.cards.byNumber.get(biome.num);
    if (!def?.types.includes('biome') || !this.affOf(biome).includes('submerged')) return 1;
    return this.state.field.some((c) => c.faceUp && this.cardName(c.num) === 'Murkfin' && !this.passiveStopper(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken)) ? 2 : 1;
  }


  // ---------------- smaller Unit, Building and Biome rules ----------------

  private libraryActive() {
    return this.state.field.some((c) => c.faceUp && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'library'));
  }
  private ashActive() {
    return this.state.field.some((c) => c.faceUp && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'ashy'));
  }
  /** Sand Wyrm: this player has one in play (its Entrance has made their Support cards boost Duned cards too). */
  private sandWyrmFor(seat: Seat) {
    return this.state.field.some((c) => c.faceUp && this.allies(c.owner, seat) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'sand_wyrm'));
  }
  /** Blood Dragon: can this card be affected by an effect of that card? (it shrugs off every Undead card's effects but damage) */
  private shrugsOff(target: FieldCard, source: FieldCard, verb: string) {
    if (['damage', 'wound', 'divide'].includes(verb) || target === source) return false;
    const imm = this.effectsOf(target, 'always').find((e) => e.words[0] === 'immune');
    return !!imm && imm.words.slice(1).some((a) => this.affOf(source).includes(a));
  }

  // ---------------- hands, decks and piles ----------------

  /** Can this player see this face-down card? (a Trigger revealed by Glacial Wyrm or Molten Stallion, or any while Igloo Village is active) */
  private revealedTo(card: FieldCard, seat: Seat) {
    if (card.faceUp || !this.cards.byNumber.get(card.num)?.types.includes('trigger')) return false;
    if (card.revealedBy && this.opposed(card.owner, seat)) return true;
    return this.state.field.some((c) => c.faceUp && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'show_triggers'));
  }
  /** Mystwing: this player may put Support cards in their Spell slots right now. */
  private supportInSpell(seat: Seat) {
    return CONFIG.combat.enabled && this.state.field.some((c) => c.faceUp && this.allies(c.owner, seat) && !this.passiveStopper(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'support_spells'));
  }
  /** Into the Soul Realm: Units of a player that went to their discard pile during this action, while it watches over them. */
  private soulRealm(gone: { num: number; owner: Seat }[]) {
    const s = this.state;
    for (const g of gone) {
      const realm = s.field.find((c) => c.faceUp && c.owner === g.owner && !this.isExpired(c) && this.opposed(g.owner, s.turn) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'soul_realm'));
      if (!realm) continue;
      const p = s.players[g.owner];
      if (!this.canDraw(p) || p.hand.length >= CONFIG.handLimit) continue;
      const got = this.drawTop(p)!;
      p.hand.push(this.rehide(got));
      const realmName = this.cardName(realm.num);
      if (CONFIG.looks.enabled && CONFIG.looks.triggered[realmName]) this.look(CONFIG.looks.triggered[realmName], realm); // (Into the Soul Realm: a spectral swell over it)
      // (the discarded card's spectral light travels from its owner's discard pile to the Realm)
      this.fx('spell', '', undefined, { target: realm.id, from: seatPoint(g.owner, CONFIG.zones.discard), tint: 'spectral', seat: g.owner });
      this.fx('toHand', '', undefined, { effect: 'deck', seat: g.owner, count: 1, ...(CONFIG.looks.handTint[realmName] ? { tint: CONFIG.looks.handTint[realmName] } : {}) });
      const spectral = this.cards.byNumber.get(g.num)?.affiliations.includes('spectral') || s.field.some((b) => b.faceUp && !this.isExpired(b) && !!this.cards.byNumber.get(b.num)?.types.includes('biome') && this.affOf(b).includes('spectral'));
      let healed = '';
      const emp = (s.teams ? this.emperorOf(g.owner) : undefined) ?? this.emperorOn(sideOf(g.owner));
      if (spectral && emp) { const d = this.tokensOn(emp).find((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)) && t.value > 0); if (d) { d.value -= 1; if (d.value <= 0) this.removeTokens([d]); healed = ', and their Emperor heals 1'; this.fx('status', emp.id, undefined, { effect: 'heal' }); } }
      this.log(`${this.cardName(realm.num)}: ${this.cardName(g.num)} was discarded, so ${p.name} draws a card${healed}.`, g.owner);
    }
  }
  private goneToDiscard: { num: number; owner: Seat }[] = [];
  /** Banishment: the next card of the opponent's hand to discard, chosen by its place (the cards stay hidden). */
  private blindAsk(card: FieldCard, foe: Seat, left: number, gone = 0) {
    const s = this.state; const seat = card.owner;
    const hand = s.players[foe].hand;
    const name = this.cardName(card.num);
    if (left <= 0 || !hand.length) {
      // all of them fly from the hand into the discard pile together, and it gives off purple sparks (Banishment)
      if (gone > 0 && CONFIG.looks.handToPile[name]) this.look(CONFIG.looks.handToPile[name], null, { seat: foe, pile: 'discard', count: gone });
      return;
    }
    if (s.bots?.includes(seat) || hand.length === 1) {
      const i = Math.floor(this.rng() * hand.length);
      const [c] = hand.splice(i, 1);
      this.insertAt(s.players[foe].discard, this.rehide(c), 'top');
      this.fx('revealCard', '', undefined, { seat: foe, num: c.num, text: `${name}: ${s.players[foe].name} discards ${this.cardName(c.num)}.` });
      this.log(`${name}: ${s.players[foe].name} discards ${this.cardName(c.num)} from their hand.`, seat);
      this.springTriggers(foe, 'discard');
      this.blindAsk(card, foe, left - 1, gone + 1);
      return;
    }
    (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: choose a card of ${s.players[foe].name}’s hand to discard, unseen (${left} to go)`, options: [], choices: hand.map((_, i) => `Card ${i + 1}`), blind: { foe, left, gone }, src: { ...card, merged: [] }, rest: [], ctx: {} });
  }
  /** A choice of a card, asked again after each answer until its player is done (or `left` runs out). */
  private repeatAsk(card: FieldCard, verb: string, on: string, ctx: FxCtx, left: number) {
    const s = this.state; const seat = card.owner;
    const name = this.cardName(card.num);
    const eff: CardEffect = { when: 'x', passive: false, words: [verb], on, conds: [] };
    const pool = left > 0 ? this.pickPool(card, eff, ctx) : [];
    if (pool.length && verb === 'warlord' && s.bots?.includes(seat)) {
      // a bot spends every hit, each where its own targeting says
      for (let k = 0; k < left; k++) {
        const p2 = this.pickPool(card, eff, ctx);
        if (!p2.length) break;
        ctx.it = this.botTarget(seat, { ...eff, words: ['damage', '2'] }, p2);
        this.doEffect(card, { ...eff, on: 'it' }, ctx);
        this.settleFallen();
      }
      this.repeatEnd(card, verb, { ...ctx, count: 0 });
      return;
    }
    if (!pool.length || s.bots?.includes(seat)) { this.repeatEnd(card, verb, { ...ctx, count: left }); return; }
    const what = verb === 'warlord' ? 'an opposing Unit to deal 2 damage to' : verb === 'resurge' ? 'a Unit of yours to destroy (gain 1 less than its cost, and search your discard pile for a card of that cost)' : verb === 'vanish' ? 'an opposing Biome, Tome or Trigger to discard' : 'a card';
    (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: choose ${what}${left < 50 ? ` (${left} to go)` : ''}`, options: pool.map((c) => c.id), optional: true, skip: 'Done', repeat: verb, src: { ...card, merged: [] }, rest: [eff], ctx: { ...this.packCtx(ctx), count: left } });
  }
  /** A repeated choice is over (Curse of Vanishing gives back what was paid for cards it didn't take; the Spell goes). */
  private repeatEnd(card: FieldCard, verb: string, ctx: FxCtx) {
    const s = this.state;
    if (verb === 'vanish' && (ctx.count ?? 0) > 0 && (ctx.count ?? 0) < 50) {
      const back = ctx.count ?? 0;
      this.log(`${this.cardName(card.num)}: ${back} of the cards paid for weren’t taken, so ${s.players[card.owner].name} gets ${this.gainText(CONFIG.tokenRules.fractalGives, back, this.gain(card.owner, CONFIG.tokenRules.fractalGives, back))} back.`, card.owner);
    }
    if (s.field.includes(card) && this.cards.byNumber.get(card.num)?.types.includes('spell')) this.discardFromPlay(card);
  }
  /** Celestial: as its owner's turn begins, a Troop of theirs destroyed since their last turn may come back in its place. */
  private celestialAsk(seat: Seat) {
    const s = this.state; const p = s.players[seat];
    for (const cel of s.field.filter((c) => c.faceUp && this.turnOf(c.owner) === seat && c.rot % 2 === 0 && !this.passiveStopper(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'celestial'))) {
      const since = (s.graves ?? []).filter((g) => g.owner === seat && g.at >= this.turnStamp() - 2).map((g) => g.num);
      const there = p.discard.filter((c) => since.includes(c.num) && !!this.cards.byNumber.get(c.num)?.types.includes('troop'));
      if (!there.length) continue;
      if (s.bots?.includes(seat)) continue;
      (s.picks ??= []).push({ id: s.nextId++, seat, name: this.cardName(cel.num), optional: true, skip: 'Not now', options: [], cards: there.map((c) => ({ id: c.id, num: c.num })), celestial: cel.id, text: `${this.cardName(cel.num)}: you may bring back a Troop of yours destroyed since your last turn, in its place (it is discarded)` });
    }
  }

  // ---------------- pairs, forced attacks and timed Spells ----------------

  /** A Unit was destroyed: Spells that joined it to another, or watched over it, react. */
  private pairFalls(dead: FieldCard) {
    const s = this.state;
    for (const sp of [...s.field]) {
      if (!sp.faceUp || !s.field.includes(sp)) continue;
      const words = this.cards.byNumber.get(sp.num)?.effects.map((e) => e.words[0]) ?? [];
      if (sp.pair?.includes(dead.id)) {
        const other = this.findField(sp.pair.find((id) => id !== dead.id) ?? '');
        if (words.includes('shadowlink') && !this.isExpired(sp)) {
          // Shadow of Death: the other goes too
          this.log(`${this.cardName(sp.num)}: ${this.cardName(dead.num)} is destroyed, so ${other ? this.cardName(other.num) : 'the other'} is destroyed too.`, sp.owner);
          delete sp.pair;
          this.discardFromPlay(sp);
          if (other) this.destroyNow(other);
          continue;
        }
        if (words.includes('branch')) {
          // Branch Out: the two share one health, so the other goes with it
          delete sp.pair;
          if (other) { delete other.mergedInto; this.removeTokens(s.tokens.filter((t) => t.from === sp.id)); if (other.id !== dead.id && !this.hasOn(other, CONFIG.turn.lifetime.expiredToken)) this.destroyNow(other); }
          this.discardFromPlay(sp);
          continue;
        }
        if (words.includes('vines')) {
          this.log(`${this.cardName(sp.num)}: ${this.cardName(dead.num)} is destroyed, so the Vines are discarded.`, sp.owner);
          this.doEffect(sp, { when: 'x', passive: false, words: ['ungive', 'thorns'], on: '', conds: [] }, {});
          delete sp.pair;
          this.discardFromPlay(sp);
          continue;
        }
      }
      if (sp.host === dead.id && words.includes('smite')) { this.log(`${this.cardName(sp.num)}: the Unit it was for is destroyed, so it is discarded.`, sp.owner); this.discardFromPlay(sp); }
    }
  }
  /** Executioner: the follow-up attacks due as this player's turn ends land on the lanes they were aimed at. */
  private echoesLand(seat: Seat) {
    const s = this.state; const p = s.players[seat];
    for (const e of [...(s.echoes ?? [])]) {
      if (e.foe !== seat || e.turn > p.turns) continue;
      s.echoes = s.echoes!.filter((x) => x !== e);
      const slot = laneOrder().find((l) => l.name === e.lane);
      const hit = slot ? this.unitsAt(slot, e.side).filter((u) => !this.intruder(u) && !u.soul) : [];
      const notes: string[] = [];
      let total = 0;
      for (const u of hit) total += this.landHit(e.seat, u, e.amount, { pierce: e.pierce, splash: true }, notes);
      this.log(`${e.by}’s axe comes back to ${e.lane}${hit.length ? `: ${total} damage` : ', which is empty'}${notes.length ? ` (${notes.join('; ')})` : ''}.`, e.seat);
      if (slot) this.fx('look', '', undefined, { effect: 'axe-return', at: { x: slot.x * e.side, z: slot.z * e.side } }); // the table shows the axe landing
      this.settleFallen();
    }
    if (s.echoes && !s.echoes.length) delete s.echoes;
  }

  // ---------------- cards that become something else ----------------

  /** A Unit's health has run out, but something keeps it in play (true): Melstone Terror becomes a Troop; a Demonic Contract saves it. */
  private cheatDeath(card: FieldCard, def: CardDef): boolean {
    const s = this.state;
    if (def.types.includes('building') && def.types.includes('troop') && !card.troopForm) {
      // Melstone Terror: its SP is gone, so it becomes a Flying Troop that may attack
      this.becomeTroop(card, def);
      return true;
    }
    // Grave Intercessor: it is destroyed instead, and the Troop it chose is left at 1 health
    const sub = this.isUnit(def) ? s.field.find((c) => c.intercede === card.id && c !== card && c.faceUp && !c.atZero && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken)) : undefined;
    if (sub) {
      delete sub.intercede;
      const damageId0 = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
      this.removeTokens(this.tokensOn(card).filter((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)) || this.effectOf(t) === 'overheal'));
      const full0 = (this.fullForm(card, def).health ?? 1) + this.gainedHealth(this.tokensOn(card));
      if (damageId0 && full0 > 1) this.addDamage(card, damageId0, full0 - 1, null, false, true);
      this.fx('link', card.id, undefined, { target: sub.id });
      this.log(`${this.cardName(sub.num)} is destroyed in place of ${def.name}, which is left at 1 health.`, sub.owner);
      // (it falls with the next look at what is destroyed; nothing can save it in turn)
      const subDef = this.cards.byNumber.get(sub.num);
      const damageId1 = damageId0;
      if (subDef && damageId1) { const fullS = (this.fullForm(sub, subDef).health ?? 1) + this.gainedHealth(this.tokensOn(sub)); this.removeTokens(this.tokensOn(sub).filter((t) => this.effectOf(t) === 'overheal')); this.addDamage(sub, damageId1, Math.max(1, fullS - this.damageOn(sub)), null, false, true); }
      this.markAgain = true;
      return true;
    }
    const contract = s.field.find((c) => c.faceUp && c.host === card.id && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'contract'));
    if (contract && this.isUnit(def)) {
      // Demonic Contract: back to 1 health, every status gone, 3 Overheal and 2 Decay, and Undead now
      this.removeTokens(this.tokensOn(card).filter((t) => { const d = this.tokenDb.byId.get(t.tokenId); return (d?.category === 'status' || d?.category === 'affiliation') && !t.ability; }));
      this.removeTokens(this.tokensOn(card).filter((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t))));
      const full = (this.fullForm(card, def).health ?? 1) + this.gainedHealth(this.tokensOn(card));
      const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
      if (damageId && full > 1) this.addDamage(card, damageId, full - 1, null);
      this.giveStatus(contract.owner, card, 'overheal', 3, null);
      this.giveStatus(contract.owner, card, 'decay', 2, null);
      this.giveStatus(contract.owner, card, 'aff-undead', null, null);
      this.fx('sacrifice', card.id, undefined, { seat: card.owner });
      this.log(`${this.cardName(contract.num)}: ${def.name} is held at 1 health, its statuses are gone, it gains 3 Overheal and 2 Decay, and it is Undead now.`, contract.owner);
      this.discardFromPlay(contract);
      return true;
    }
    return false;
  }
  /** Melstone Terror: from Building to Flying Troop (its SP damage is cleared; it has its Troop health). */
  private becomeTroop(card: FieldCard, def: CardDef) {
    card.troopForm = true;
    this.removeTokens(this.tokensOn(card).filter((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)) || this.effectOf(t) === CONFIG.tokenRules.spDecayToken));
    if (card.rot === CONFIG.arrival.entrenchedRotation) card.rot = 0;
    this.fx('play', card.id);
    this.log(`${def.name} is a Building no more: it becomes a Flying Troop with ${this.fullForm(card, def).health} health and may attack.`, card.owner);
  }
  /** Illusionist: one of the pair goes (the one damaged first, or the one that didn't attack first); the other is the card from then on. */
  private illusionGoes(card: FieldCard, gone: FieldCard) {
    const s = this.state;
    const other = card.illusion ? this.findField(card.illusion) : undefined;
    const keep = gone === card ? other : card;
    delete card.illusion; if (other) delete other.illusion;
    if (!s.field.includes(gone)) return;
    this.log(`${this.cardName(gone.num)}: one of the pair is only an illusion, and it is removed from play.${keep ? '' : ''}`, gone.owner);
    this.fx('sacrifice', gone.id, undefined, { seat: gone.owner });
    if (gone.spawned) { this.clearTokensOn(gone); this.removeField(gone.id); this.closeUp(gone); }
    else this.discardFromPlay(gone);
  }
  /** Void Walker: the ones due back at the end of this player's turn come out of the discard pile into their lane. */
  private returnWalkers(seat: Seat) {
    const s = this.state; const p = s.players[seat];
    for (const r of [...(s.returns ?? [])]) {
      if (this.turnOf(r.owner) !== seat || r.turn > p.turns) continue;
      s.returns = s.returns!.filter((x) => x !== r);
      const i = p.discard.findIndex((c) => c.num === r.num);
      const lane = laneOrder().find((l) => l.name === r.lane);
      if (i < 0 || !lane) continue;
      const [back] = p.discard.splice(i, 1);
      const owned = this.owners();
      const u: FieldCard = { ...back, owner: seat, x: lane.x * r.side, z: lane.z * r.side, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, walkStage: r.stage, hp: r.stage === 1 ? 10 : 4, dmgNow: r.stage === 1 ? 2 : 1 };
      s.field.push(u);
      this.giveStatus(seat, u, r.stage === 1 ? 'aff-spectral' : 'aff-undead', null, null);
      this.fx('play', u.id);
      this.layout(owned);
      this.log(`${this.cardName(u.num)} returns to ${lane.name} as ${r.stage === 1 ? 'a Spectral Troop with 10 health and 2 damage' : 'an Undead Troop with 4 health and 1 damage'}.`, seat);
    }
    if (s.returns && !s.returns.length) delete s.returns;
  }
  /** Valley of Waiting: the Souls whose time is up go at the end of their owner's turn. */
  private soulsGo(seat: Seat) {
    const s = this.state; const p = s.players[seat];
    const done = s.field.filter((c) => c.soul && this.turnOf(c.owner) === seat && c.soul.until <= p.turns);
    for (const c of done) { this.clearTokensOn(c); this.removeField(c.id); this.closeUp(c); }
    if (done.length) this.log(`${done.length === 1 ? 'A Soul fades' : `${done.length} Souls fade`} away (Valley of Waiting).`, seat);
  }
  /** Valley of Waiting: a player with 2 or more Souls on their side as their turn begins gains 1 Gerzite. */
  private soulsPay(seat: Seat) {
    const s = this.state;
    const valley = s.field.find((c) => c.faceUp && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.words[0] === 'soul'));
    if (!valley) return;
    const n = s.field.filter((c) => c.soul && this.allies(c.owner, seat)).length;
    if (n < 2) return;
    const got = this.gain(seat, CONFIG.tokenRules.fractalGives, 1);
    this.log(`${this.cardName(valley.num)}: ${s.players[seat].name} has ${n} Souls on their side and gains ${this.gainText(CONFIG.tokenRules.fractalGives, 1, got)}.`, seat);
  }
  /** Valley of Waiting has gone: every Soul goes with it. */
  private soulsWithoutValley() {
    const s = this.state;
    if (!s.field.some((c) => c.soul)) return;
    if (s.field.some((c) => c.faceUp && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.words[0] === 'soul'))) return;
    for (const c of s.field.filter((x) => x.soul)) { this.clearTokensOn(c); this.removeField(c.id); this.closeUp(c); }
    this.log('Valley of Waiting is gone: every Soul is destroyed.', null);
  }
  /** Eldritch Realm Crosser leaves the Biome slot: its owner searches their deck for a Biome, and it is shuffled into their deck. */
  private crosserLeaves(card: FieldCard) {
    const s = this.state; const p = s.players[card.owner];
    const name = this.cardName(card.num);
    this.clearTokensOn(card);
    this.removeField(card.id);
    this.closeUp(card);
    p.deck.push(this.rehide({ id: card.id, num: card.num, spawned: false }));
    this.log(`${name} leaves the Biome slot: it is shuffled into ${p.name}’s deck, and they search it for a Biome.`, card.owner);
    this.startSearch(card.owner, name, ['deck', 'type', 'biome']);
  }
  /** Transmissive Death: each Unit carrying one of its tokens takes 1 Decay as its owner's turn begins; the token counts its turns down. */
  private transmitTick(seat: Seat) {
    const s = this.state;
    for (const t of s.tokens.filter((x) => this.effectOf(x) === 'transmission')) {
      const c = this.cardUnder(t);
      if (!c || this.turnOf(c.owner) !== seat) continue;
      this.giveStatus(t.by ?? seat, c, 'decay', 1, null);
      t.value -= 1;
      if (t.value <= 0) this.removeTokens([t]);
    }
    // a Transmissive Death with no token of its own left on the table is discarded
    for (const sp of [...s.field]) if (sp.faceUp && this.cards.byNumber.get(sp.num)?.effects.some((e) => e.words[0] === 'transmit') && sp.transmitting && !s.tokens.some((t) => this.effectOf(t) === 'transmission' && t.from === sp.id)) { this.log(`${this.cardName(sp.num)} has no Unit left to carry it: it is discarded.`, sp.owner); this.discardFromPlay(sp); }
  }
  /** Transmissive Death: a Unit carrying its token was destroyed while it is in play: the token passes to two Units beside it. */
  private transmitOn(dead: FieldCard) {
    const s = this.state;
    for (const t of this.tokensOn(dead).filter((x) => this.effectOf(x) === 'transmission' && x.from)) {
      const sp = this.findField(t.from!);
      if (!sp) continue;
      const at = this.standsAt(dead);
      if (!at || slotKind(at.slot.name) !== 'Lane') continue;
      sp.chainAt = { x: dead.x, z: dead.z }; // (it jumps from the fallen Unit to each new one)
      const row = this.laneRow(at.side); const i = row.indexOf(at.slot);
      const near = [row[i - 1], row[i + 1]].filter(Boolean).flatMap((l) => this.unitsAt(l, at.side)).filter((u) => u.faceUp && this.isUnit(this.cards.byNumber.get(u.num)) && !this.untouchable(u));
      if (near.length <= 2 || s.bots?.includes(sp.owner)) { for (const u of near.slice(0, 2)) this.transmitTo(sp, u); continue; }
      const eff: CardEffect[] = [
        { when: 'x', passive: false, words: ['transmit'], on: 'pick_any', conds: [['pool', 'listed']] },
        { when: 'x', passive: false, words: ['transmit'], on: 'pick_any', conds: [['pool', 'listed'], ['pool', 'new']] },
      ];
      this.transmitPool = near.map((u) => u.id);
      this.pickedNow = [];
      this.runEffects(sp, eff, {});
    }
  }
  private transmitPool: string[] = [];
  private transmitTo(sp: FieldCard, u: FieldCard) {
    const s = this.state;
    const tdef = this.tokenDb.byId.get('transmission');
    if (!tdef || !s.field.includes(u)) return;
    const sc = u.scale ?? 1;
    s.tokens.push({ id: this.newId('t'), tokenId: tdef.id, x: u.x - 1.6 * sideOf(u.owner) * sc, z: u.z + 1.4 * sideOf(u.owner) * sc, value: 4, layer: s.nextLayer++, by: sp.owner, from: sp.id });
    this.removeTokens(this.tokensOn(u).filter((o) => this.tokenDb.byId.get(o.tokenId)?.category === 'affiliation'));
    this.giveStatus(sp.owner, u, 'aff-undead', null, null);
    sp.transmitting = true;
    // (passed on from a Unit it destroyed: the curse jumps from where that Unit stood to this one)
    if (sp.chainAt && CONFIG.looks.enabled) {
      const hop = this.hopsNow.get(sp.id) ?? 0;
      this.hopsNow.set(sp.id, hop + 1);
      this.fx('spell', sp.id, undefined, { target: u.id, from: sp.chainAt, tint: this.damageAffOf(sp)[0] ?? this.affOf(sp)[0] ?? 'undead', seat: sp.owner, effect: 'hop', hop: 0 });
    }
    this.fx('status', u.id, undefined, { effect: 'notice' });
    this.log(`${this.cardName(sp.num)} passes to ${this.cardName(u.num)}: Undead, with 1 Decay each turn for 4 turns.`, sp.owner);
  }

  // ---------------- damage that goes somewhere else (Spirit Tree, Spatial Rift, Gravitation, Spirit Rod, Reflection) ----------------

  /** Spirit Rod: the opposing Rod an opponent's Spell is pulled toward, if one is active (undefined = none, or not a Spell). */
  private magnetFor(card: FieldCard): FieldCard | undefined {
    if (!this.cards.byNumber.get(card.num)?.types.includes('spell') || !CONFIG.combat.enabled) return undefined;
    const side = this.sideOfCard(card);
    return this.state.field.find((c) => c.faceUp && this.sideOfCard(c) !== side && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && !!this.cards.byNumber.get(c.num)?.effects.length && this.effectsOf(c, 'always').some((e) => e.words[0] === 'spell_magnet'));
  }
  /** Spirit Tree: the Tree guarding the lane this card stands in (never the Tree itself). */
  private guardOf(card: FieldCard): FieldCard | undefined {
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane') return undefined;
    return this.unitsAt(at.slot, at.side).find((c) => c !== card && c.faceUp && !!this.cards.byNumber.get(c.num)?.effects.length && this.effectsOf(c, 'always').some((e) => e.words[0] === 'guardian'));
  }
  /** Vast Expanse: is a Biome active that lets every lane hold two cards (while_in_play double_lanes)? */
  wideLanes(): boolean {
    return this.state.field.some((c) => c.faceUp && !c.atZero && this.cards.byNumber.get(c.num)?.whileInPlay.includes('double_lanes') && slotAt(c)?.slot.name === 'Biome' && !this.isExpired(c));
  }
  /** Grave Reinforcements: is this one of its Undead Guards (always: bulwark)? */
  private isBulwark(card: FieldCard) {
    return card.faceUp && !card.atZero && !this.hasOn(card, CONFIG.turn.lifetime.expiredToken) && !!this.cards.byNumber.get(card.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'bulwark');
  }
  /** The Undead Guard standing in this card's lane that takes the damage aimed at it (never itself, never for an opponent's unit), or undefined. */
  private bulwarkOf(card: FieldCard): FieldCard | undefined {
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane') return undefined;
    // (only for its own side: an opposing unit standing in this lane, a Royal Guard, isn't shielded by these Guards)
    return this.unitsAt(at.slot, at.side).find((c) => c !== card && this.isBulwark(c) && !this.opposed(c.owner, card.owner));
  }
  /** Spatial Rift: the lane damage into this card's lane comes out of instead (or null). */
  private riftFor(card: FieldCard): { slot: Slot; side: 1 | -1; rift: FieldCard } | null {
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane') return null;
    for (const r of this.state.field) {
      if (!r.faceUp || !r.lanes || r.lanes.side !== at.side || r.lanes.names.length < 2 || this.isExpired(r)) continue;
      if (!this.cards.byNumber.get(r.num)?.effects.some((e) => e.words[0] === 'rift')) continue;
      const i = r.lanes.names.indexOf(at.slot.name);
      if (i < 0) continue;
      const other = laneOrder().find((l) => l.name === r.lanes!.names[1 - i]);
      if (other) return { slot: other, side: at.side, rift: r };
    }
    return null;
  }
  /** Where a hit aimed at `to` really goes, by a Spatial Rift or a shift: a Unit in the other lane, else that side's Emperor if the damage is Direct, else nowhere. */
  private intoLane(slot: Slot, side: 1 | -1, direct: boolean): FieldCard | null {
    const there = this.unitsAt(slot, side).filter((u) => !this.intruder(u));
    if (there.length) return there[0];
    return direct ? (this.emperorOn(side, { x: slot.x * side }) ?? null) : null;
  }
  /** Gravitation: the active one guarding the side this card stands on during this turn (its owner's opponent's turn). */
  private gravityFor(card: FieldCard): FieldCard | undefined {
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane' || !this.isUnit(this.cards.byNumber.get(card.num))) return undefined;
    return this.state.field.find((g) => g.faceUp && this.sideOfCard(g) === at.side && !this.isExpired(g) && this.opposed(g.owner, this.state.turn) && !!this.cards.byNumber.get(g.num)?.effects.some((e) => e.words[0] === 'gravity'));
  }

  /**
   * Gravitation and a Spell's damage: before it lands on a Unit Gravitation guards, that Unit's side chooses which
   * lane it is shifted into (each time damage arrives). Returns true when it has asked, and the effect waits.
   */
  private gravityHold(card: FieldCard, e: CardEffect, ctx: FxCtx, rest: CardEffect[]): boolean {
    const s = this.state;
    if (e.words[0] !== 'damage' || !this.cards.byNumber.get(card.num)?.types.includes('spell') || e.on.startsWith('pick_')) return false;
    for (const t of this.whoOf(card, e.on || 'target', ctx)) {
      if (ctx.shifts && t.id in ctx.shifts) continue;
      const grav = this.gravityFor(t);
      const at = this.standsAt(t);
      if (!grav || !at) continue;
      const row = this.laneRow(at.side);
      const i = row.indexOf(at.slot);
      const lanes = [row[i - 1], row[i + 1]].filter(Boolean).map((l) => ({ slot: l.name, side: at.side }));
      if (s.bots?.includes(grav.owner) || lanes.length <= 1) { (ctx.shifts ??= {})[t.id] = lanes[0] ?? null; this.gravityLook(t, lanes[0]); continue; }
      const labels = [i > 0 ? `Left: ${row[i - 1].name}` : '', i < row.length - 1 ? `Right: ${row[i + 1].name}` : ''].filter(Boolean);
      (s.picks ??= []).push({ id: s.nextId++, seat: grav.owner, name: this.cardName(grav.num), text: `Gravitation: ${this.cardName(card.num)} is about to damage ${this.cardName(t.num)}. Shift it one lane`, options: [], choices: labels, gravLanes: { target: t.id, lanes }, src: { ...card, merged: [] }, rest: [{ when: 'x', passive: false, words: ['choose'], on: '', conds: [] }, e, ...rest], ctx: this.packCtx(ctx) });
      this.fx('status', grav.id, undefined, { effect: 'notice' });
      return true;
    }
    return false;
  }

  // ---------------- damage dealt point by point (Dividend, Super Split, Primordial Seastorm, Bloodburst) ----------------

  /** The cards a point of split damage may go on right now. */
  private splitOptions(card: FieldCard, sp: NonNullable<PendingPick['split']>): FieldCard[] {
    const s = this.state;
    const side = this.sideOfCard(card);
    const live = (c: FieldCard) => c.faceUp && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken);
    const isEmp = (c: FieldCard) => !!this.cards.byNumber.get(c.num)?.types.includes('emperor');
    const isMinion = (c: FieldCard) => this.standsAt(c)?.slot.name === 'Minion';
    if (sp.kind === 'wound') {
      // its caster's own Troops: at most `most` different ones
      let own = s.field.filter((c) => live(c) && this.sideOfCard(c) === side && !!this.cards.byNumber.get(c.num)?.types.includes('troop'));
      if (sp.most !== undefined && new Set(sp.hit).size >= sp.most) own = own.filter((c) => sp.hit.includes(c.id));
      return own;
    }
    const capped = sp.cap !== null && sp.cap <= 0;
    const units = s.field.filter((c) => live(c) && this.isUnit(this.cards.byNumber.get(c.num)) && (sp.any || this.sideOfCard(c) !== side) && !(capped && isMinion(c) && this.sideOfCard(c) !== side));
    const emp = this.emperorOn(-side as 1 | -1, card);
    const rod = this.magnetFor(card);
    if (rod) return [rod];
    return [...units, ...(emp && live(emp) && isEmp(emp) && !capped ? [emp] : [])];
  }
  /** Puts the next point of a split to its player (or, for a bot, spends it all at once). */
  private splitAsk(card: FieldCard, sp: NonNullable<PendingPick['split']>, ctx: FxCtx, freeAmount = false, rest: CardEffect[] = []) {
    const s = this.state; const seat = card.owner;
    const name = this.cardName(card.num);
    let opts = this.splitOptions(card, sp);
    if (s.bots?.includes(seat)) {
      // a bot: the opposing Emperor up to the cap, then the weakest opposing Unit, again and again
      while (sp.left > 0 && opts.length && !freeAmount) {
        const emp = opts.find((c) => this.cards.byNumber.get(c.num)?.types.includes('emperor'));
        const t = emp ?? [...opts].sort((a, b) => this.healthLeft(a) - this.healthLeft(b))[0];
        this.splitPoint(card, sp, t);
        opts = this.splitOptions(card, sp);
      }
      this.splitEnd(card, sp, ctx);
      this.runEffects(card, rest, ctx);
      return;
    }
    if (sp.left <= 0 || !opts.length) { this.splitEnd(card, sp, ctx); this.runEffects(card, rest, ctx); return; }
    const capText = sp.cap !== null ? ` · ${sp.cap} more may go on the opposing Emperor or Minion` : '';
    const text = sp.kind === 'wound'
      ? `${name}: click one of your Troops to take health from it${freeAmount ? '' : ` (${sp.left} left)`}${sp.most !== undefined ? `, up to ${sp.most} Troops` : ''}`
      : `${name}: click a ringed card to deal it damage (${sp.left} left${capText})`;
    (s.picks ??= []).push({ id: s.nextId++, seat, name, text, options: opts.map((c) => c.id), optional: true, skip: 'Done', split: sp, src: { ...card, merged: [] }, rest: [{ when: 'enter', passive: false, words: ['divide'], on: '', conds: [] }, ...rest], ctx: this.packCtx(ctx) });
  }
  /** One point of a split lands. */
  private splitPoint(card: FieldCard, sp: NonNullable<PendingPick['split']>, t: FieldCard) {
    const seat = card.owner;
    const isEmp = !!this.cards.byNumber.get(t.num)?.types.includes('emperor');
    const isMinion = this.standsAt(t)?.slot.name === 'Minion' && this.sideOfCard(t) !== this.sideOfCard(card);
    sp.left--; sp.total++;
    sp.hit.push(t.id);
    if (sp.kind !== 'wound') this.spellAim(card, [t], true);
    else if (CONFIG.looks.woundLook[this.cardName(card.num)]) this.look(CONFIG.looks.woundLook[this.cardName(card.num)], t); // (Bloodburst: blood drawn out of it)
    if (sp.kind === 'wound') {
      const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
      if (damageId) { this.addDamage(t, damageId, 1, null); this.mirrorDamage(t, damageId, 1); }
    } else if (isEmp) {
      sp.emp++;
      if (sp.cap !== null) sp.cap--;
    } else {
      if (isMinion && sp.cap !== null) sp.cap--;
      this.landHit(seat, t, 1, { from: card, spell: true }, []);
    }
    this.fx('status', t.id, undefined, { effect: 'damage' });
    this.settleFallen();
  }
  /** The split is over: what went on the Emperor lands now, halved; ctx.amount is how many points were spent. */
  private splitEnd(card: FieldCard, sp: NonNullable<PendingPick['split']>, ctx: FxCtx) {
    const name = this.cardName(card.num);
    const emp = this.emperorOn(-this.sideOfCard(card) as 1 | -1, card);
    let empGot = 0;
    if (sp.emp > 0 && emp) empGot = this.landHit(card.owner, emp, sp.down ? Math.floor(sp.emp / 2) : Math.round(sp.emp / 2), { from: card, spell: true }, []);
    ctx.amount = sp.total;
    const where = [...new Set(sp.hit)].map((id) => this.findField(id)).filter((c): c is FieldCard => !!c && !this.cards.byNumber.get(c.num)?.types.includes('emperor')).map((c) => this.cardName(c.num));
    this.log(sp.kind === 'wound'
      ? `${name}: ${sp.total} health taken from ${[...new Set(where)].join(', ') || 'no Troop'}.`
      : `${name}: ${sp.total} damage dealt point by point${where.length ? ` (${[...new Set(where)].join(', ')})` : ''}${sp.emp ? `; ${sp.emp} at the Emperor, which takes ${empGot}` : ''}.`, card.owner);
    this.settleFallen();
  }
  /** The Units in the lanes either side of the one this card's host stands in, on the host's side (Cursed Chains' recast). */
  private besideHost(card: FieldCard): FieldCard[] {
    const h = card.host ? this.findField(card.host) : undefined;
    const at = h ? this.standsAt(h) : null;
    if (!h || !at || slotKind(at.slot.name) !== 'Lane') return [];
    const row = laneOrder().map((slot) => ({ slot, x: slot.x * at.side })).sort((a, b) => a.x - b.x);
    const i = row.findIndex((l) => l.slot === at.slot);
    return [i - 1, i + 1].filter((j) => j >= 0 && j < row.length).flatMap((j) => this.unitsAt(row[j].slot, at.side)).filter((u) => u.faceUp && this.isUnit(this.cards.byNumber.get(u.num)) && !this.untouchable(u));
  }
  /** What an effect knows, as it is kept with a choice waiting for its answer (cards by id). */
  private packCtx(ctx: FxCtx): NonNullable<PendingPick['ctx']> {
    return { target: ctx.target?.id, attacker: ctx.attacker?.id, it: ctx.it?.id, amount: ctx.amount, chose: ctx.chose, lane: ctx.lane, laneFoe: ctx.laneFoe, actor: ctx.actor, count: ctx.count, ...(ctx.tossed !== undefined ? { tossed: ctx.tossed } : {}), ...(ctx.picked ? { picked: [...ctx.picked] } : {}), ...(ctx.shifts ? { shifts: { ...ctx.shifts } } : {}) };
  }
  private unpackCtx(k: PendingPick['ctx']): FxCtx {
    const find = (id?: string) => (id ? this.findField(id) : undefined);
    return { target: find(k?.target), attacker: find(k?.attacker), it: find(k?.it), amount: k?.amount, chose: k?.chose, lane: k?.lane, laneFoe: k?.laneFoe, actor: k?.actor, count: k?.count, ...(k?.tossed !== undefined ? { tossed: k.tossed } : {}), ...(k?.picked ? { picked: [...k.picked] } : {}), ...(k?.shifts ? { shifts: { ...k.shifts } } : {}) };
  }
  /** What a choice is for, in a few words ("choose an opposing card for Burn"). */
  private pickWords(e: CardEffect) {
    const [verb, a, b] = e.words;
    const tok = (x: string) => this.tokenDb.list.find((d) => d.id === x || d.effect === x)?.name ?? x;
    const whose = e.on.includes('foe') || e.on === 'pick_front' ? 'an opposing card' : e.on.includes('ally') ? 'one of your cards' : 'a card';
    // (the effects with their own words; any other just says which card to choose)
    const named: Record<string, string> = { bell_on: 'a Bell', wail: 'it to copy from', burn_move: 'the Burn', unburn: 'its Burn to be removed', berry: 'healing', tierup: 'a Tier up', tier3: 'Tier III', lock: `a Lock (${a} Gerzite)`, link: 'a Link', heal: `healing ${a}`, attach_brought: 'its token', encore: 'another action', swap: 'the swap', pull: 'the pull' };
    const what = verb === 'status' ? `${tok(a)}${b && /^\d+$/.test(b) ? ` ${b}` : ''}` : verb === 'damage' || verb === 'wound' ? (/^\d+$/.test(a) ? `${a} damage` : 'its damage') : named[verb] ?? '';
    return what ? `choose ${whose} for ${what}` : `choose ${whose}`;
  }
  /** The cards an "on pick_…" effect may be done to. "if pool …" conditions narrow them (pool affiliation forested, pool troop). */
  private pickPool(card: FieldCard, e: CardEffect, ctx: FxCtx = {}): FieldCard[] {
    const s = this.state;
    const side = this.sideOfCard(card);
    const def = this.cards.byNumber.get(card.num);
    const at = this.standsAt(card);
    const row = (sd: number) => laneOrder().map((slot) => ({ slot, x: slot.x * sd })).sort((a, b) => a.x - b.x);
    const i = at && slotKind(at.slot.name) === 'Lane' ? row(side).findIndex((l) => l.slot === at.slot) : -1;
    const laneOf = (c: FieldCard) => { const t = this.standsAt(c); return t && slotKind(t.slot.name) === 'Lane' ? row(t.side).findIndex((l) => l.slot === t.slot) : -1; };
    const range = def ? this.rangeNow(card, def) : 0;
    const unit = (c: FieldCard) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && !this.untouchable(c) && !this.intangible(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && !this.laneGuard(c, card.owner);
    const emperor = (c: FieldCard) => c.faceUp && !!this.cards.byNumber.get(c.num)?.types.includes('emperor');
    const mine = this.fightsOn(card);
    const theirs = (c: FieldCard) => this.fightsOn(c) !== mine;
    let pool: FieldCard[] = [];
    switch (e.on) {
      case 'pick_foe': pool = s.field.filter((c) => unit(c) && theirs(c)); break;
      case 'pick_foe_all': pool = s.field.filter((c) => (unit(c) || emperor(c)) && theirs(c)); break;
      case 'pick_foe_range': pool = s.field.filter((c) => unit(c) && theirs(c) && i >= 0 && laneOf(c) >= 0 && Math.abs(laneOf(c) - i) <= range); break;
      case 'pick_front': pool = s.field.filter((c) => unit(c) && theirs(c) && i >= 0 && laneOf(c) === i); break;
      case 'pick_ally': pool = s.field.filter((c) => unit(c) && !theirs(c)); break;
      case 'pick_ally_all': pool = s.field.filter((c) => (unit(c) || emperor(c)) && !theirs(c)); break;
      case 'pick_ally_range': pool = s.field.filter((c) => unit(c) && !theirs(c) && i >= 0 && laneOf(c) >= 0 && Math.abs(laneOf(c) - i) <= range); break;
      case 'pick_foe_open': pool = s.field.filter((c) => (unit(c) && theirs(c)) || (emperor(c) && theirs(c) && laneOrder().some((l) => !this.unitsAt(l, -side as 1 | -1).filter((u) => !this.intruder(u)).length))); break; // an Emperor's reach: any opposing Unit, and their Emperor once a lane stands empty
      case 'pick_any': pool = s.field.filter((c) => unit(c) && c.id !== card.id); break;
      case 'pick_support': pool = s.field.filter((c) => theirs(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && (c.faceUp ? !!this.cards.byNumber.get(c.num)?.types.some((t) => ['biome', 'tome', 'trigger'].includes(t)) : slotKind(slotAt(c)?.slot.name ?? '') === 'Support' || !!this.cards.byNumber.get(c.num)?.types.includes('trigger'))); break;
    }
    pool = pool.filter((u) => !this.shrugsOff(u, card, e.words[0])); // (Blood Dragon)
    if (e.words[0] === 'status' && NEEDS_ATTACK.includes(e.words[1])) pool = pool.filter((u) => !this.neverAttacks(u)); // (Blacksmith's Damage Boost: never offered for a unit that never attacks)
    // Spirit Rod: a Spell aimed at its owner's cards can only be aimed at it
    const rod = e.on.includes('foe') || e.on === 'pick_any' ? this.magnetFor(card) : undefined;
    if (rod) pool = pool.includes(rod) || this.isUnit(this.cards.byNumber.get(rod.num)) ? [rod] : pool;
    for (const c of e.conds.filter((k) => k[0] === 'pool')) {
      if (c[1] === 'affiliation') pool = pool.filter((u) => c.slice(2).some((a) => this.affOf(u).includes(a)));
      else if (c[1] === 'troop' || c[1] === 'building') pool = pool.filter((u) => this.cards.byNumber.get(u.num)?.types.includes(c[1]));
      else if (c[1] === 'hurt') pool = pool.filter((u) => this.damageOn(u) > 0);
      else if (c[1] === 'beside') { const last = this.findField(this.pickedNow.at(-1) ?? ''); const j = last ? laneOf(last) : -9; pool = pool.filter((u) => Math.abs(laneOf(u) - j) === 1 && !!last && this.sideOfCard(u) === this.sideOfCard(last)); }
      else if (c[1] === 'near') { const last = this.findField(this.pickedNow.at(-1) ?? ''); const j = last ? laneOf(last) : -9; pool = pool.filter((u) => Math.abs(laneOf(u) - j) <= 1 && !!last && this.sideOfCard(u) === this.sideOfCard(last)); }
      else if (c[1] === 'laned') pool = pool.filter((u) => laneOf(u) >= 0);
      else if (c[1] === 'beside_host') { const near = this.besideHost(card); pool = pool.filter((u) => near.includes(u)); }
      else if (c[1] === 'movable') pool = pool.filter((u) => !this.unmovable(u));
      else if (c[1] === 'listed') pool = pool.filter((u) => this.transmitPool.includes(u.id));
      else if (c[1] === 'burned') pool = pool.filter((u) => this.hasOn(u, 'burn'));
      else if (c[1] === 'reaches') pool = pool.filter((u) => this.reachOf(u, true).includes(card)); // (a Unit able to attack this card: Jouster)
      else if (c[1] === 'half_of_target') { const t = ctx.target; pool = pool.filter((u) => !!t && this.healthLeft(u) * 2 >= this.healthLeft(t) && this.healthLeft(t) * 2 >= this.healthLeft(u)); } // (Shadow of Death)
      else if (c[1] === 'host_reach') { const h = card.host ? this.findField(card.host) : undefined; const r = h ? this.reachOf(h, true) : []; pool = pool.filter((u) => r.includes(u)); }
      else if (c[1] === 'within') { const n2 = Number(c[2]) || 0; pool = pool.filter((u) => i >= 0 && laneOf(u) >= 0 && Math.abs(laneOf(u) - i) <= n2); }
      else if (c[1] === 'beside_target') {
        // the Units in the lanes either side of where the target stood, on its side (Maelstrom Tarragon's pull)
        const at2 = ctx.target ? slotAt(ctx.target) : null;
        const j = at2 && slotKind(at2.slot.name) === 'Lane' ? row(at2.side).findIndex((l) => l.slot === at2.slot) : -9;
        pool = pool.filter((u) => { const t = this.standsAt(u); return !!at2 && !!t && t.side === at2.side && Math.abs(laneOf(u) - j) === 1; });
      }
      else if (c[1] === 'other') pool = pool.filter((u) => u.id !== card.id);
      else if (c[1] === 'new') pool = pool.filter((u) => !this.pickedNow.includes(u.id));
    }
    return pool;
  }
  // one wave of damage (a hit and its splash, or one effect): a swarm in the Minion slot passes it to its Emperor once
  private linkWave: Set<string> | null = null;
  private searchThen: SearchThen | undefined; // set while a search effect starts (see runEffects)
  private pickedNow: string[] = []; // the cards already chosen while one list of effects is carried out ("3 different Units")
  /** A player answers the choice put to them: the effect is done to the card they chose, and the rest of its list follows. */
  private answerPick(seat: Seat, a: { pick: number; card?: string; yes?: boolean; choice?: number; numbers?: number[]; order?: string[]; drop?: string[]; amount?: number }): string | null {
    const s = this.state;
    const k = s.picks?.find((q) => q.id === a.pick);
    if (!k) return null; // already answered
    if (k.seat !== seat) return 'That choice isn’t yours to make.';
    const done = () => { s.picks = s.picks!.filter((q) => q !== k); if (!s.picks.length) delete s.picks; };
    if (k.strike) {
      done();
      this.reactThenStrike(k.strike, !!a.yes);
      return null;
    }
    if (k.lava) {
      if (k.lava === 'ask') {
        if (a.choice !== 1 && a.choice !== 2) return 'Choose one of the options.';
        done();
        const p = s.players[seat];
        if (a.choice === 1 && p.hand.length) (s.picks ??= []).push({ id: s.nextId++, seat, name: k.name, text: `${k.name}: choose a card from your hand to discard`, options: [], cards: p.hand.map((c) => ({ id: c.id, num: c.num })), lava: 'hand' });
        else this.lavaDiscard(seat, k.name);
        return null;
      }
      if (!a.card || !k.cards?.some((c) => c.id === a.card)) return 'Choose a card to discard.';
      done();
      this.lavaDiscard(seat, k.name, a.card);
      return null;
    }
    if (k.showing !== undefined) {
      // Continue: now the cards turned up go where they go (to the hand, or the discard pile), and the effect goes on
      done();
      const plan = s.showing && s.showing.id === k.showing ? s.showing : null;
      if (plan) { this.revealApply(plan); s.showing = null; }
      const src3 = this.findField(k.src!.id) ?? k.src!;
      this.runEffects(src3, k.rest ?? [], this.unpackCtx(k.ctx ?? {}));
      this.settleFallen();
      return null;
    }
    if (k.blind) {
      const i = Math.round(a.choice ?? 0) - 1;
      const hand = s.players[k.blind.foe].hand;
      if (!(i >= 0 && i < hand.length)) return 'Choose one of the cards.';
      done();
      const src2 = this.findField(k.src!.id) ?? k.src!;
      const [c] = hand.splice(i, 1);
      this.insertAt(s.players[k.blind.foe].discard, this.rehide(c), 'top');
      this.fx('revealCard', '', undefined, { seat: k.blind.foe, num: c.num, text: `${k.name}: ${s.players[k.blind.foe].name} discards ${this.cardName(c.num)}.` });
      this.log(`${k.name}: ${s.players[k.blind.foe].name} discards ${this.cardName(c.num)} from their hand.`, seat);
      this.springTriggers(k.blind.foe, 'discard');
      this.blindAsk(src2, k.blind.foe, k.blind.left - 1, (k.blind.gone ?? 0) + 1);
      return null;
    }
    if (k.arrange) {
      // the order the cards go back in (top first), and the Undead ones discarded for 1 damage each to its player's Emperor
      const ids = k.cards!.map((c) => c.id);
      const order = (a.order ?? ids).filter((id) => ids.includes(id));
      if (order.length !== ids.length) return 'Put every card in the order you want.';
      const drop = (a.drop ?? []).filter((id) => k.arrange!.undead.includes(id));
      done();
      const deck = s.players[k.arrange.foe].deck;
      const taken = ids.map((id) => deck.find((c) => c.id === id)).filter((c): c is HeldCard => !!c);
      for (const c of taken) deck.splice(deck.indexOf(c), 1);
      const keep = order.filter((id) => !drop.includes(id)).map((id) => taken.find((c) => c.id === id)!).filter(Boolean);
      deck.unshift(...keep);
      for (const id of drop) { const c = taken.find((x) => x.id === id); if (c) this.insertAt(s.players[k.arrange.foe].discard, this.rehide(c), 'top'); }
      if (drop.length) {
        const own = this.emperorOf(seat);
        const src = k.src ? this.findField(k.src.id) : undefined;
        if (own) this.fx('spell', src?.id ?? k.src?.id ?? '', undefined, { target: own.id, from: k.src ? { x: k.src.x, z: k.src.z } : undefined, tint: 'spectral', seat, effect: 'strike' });
        this.damageEmperor(seat, drop.length);
        this.log(`${k.name}: ${s.players[seat].name} discards ${drop.map((id) => this.cardName(taken.find((c) => c.id === id)!.num)).join(', ')} from the top of ${s.players[k.arrange.foe].name}’s deck; their own Emperor takes ${drop.length} damage.`, seat);
      }
      this.log(`${k.name}: ${s.players[seat].name} puts ${keep.length} card${keep.length === 1 ? '' : 's'} back on top of ${s.players[k.arrange.foe].name}’s deck.`, seat);
      return null;
    }
    if (k.repeat) {
      const src2 = this.findField(k.src!.id) ?? k.src!;
      const ctx2 = this.unpackCtx(k.ctx);
      const left = k.ctx?.count ?? 99;
      if (!a.card) { done(); this.repeatEnd(src2, k.repeat, { ...ctx2, count: left }); return null; }
      if (!k.options.includes(a.card)) return 'Choose one of the ringed cards.';
      done();
      const t = this.findField(a.card);
      if (t) { ctx2.it = t; this.doEffect(src2, { when: 'x', passive: false, words: [k.repeat], on: 'it', conds: [] }, ctx2); this.settleFallen(); }
      this.repeatAsk(src2, k.repeat, k.rest?.[0]?.on ?? 'pick_ally', ctx2, left - 1);
      return null;
    }
    if (k.celestial) {
      const cel = this.findField(k.celestial);
      if (!a.card) { done(); return null; }
      if (!k.cards?.some((c) => c.id === a.card) || !cel) return 'Choose one of the Troops.';
      done();
      const p = s.players[seat];
      const i = p.discard.findIndex((c) => c.id === a.card);
      if (i < 0) return null;
      const [back] = p.discard.splice(i, 1);
      const bDef = this.cards.byNumber.get(back.num)!;
      const at = { x: cel.x, z: cel.z };
      this.discardFromPlay(cel);
      const owned = this.owners();
      const u: FieldCard = { ...back, owner: seat, ...at, rot: CONFIG.arrival.entrenchedRotation, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null };
      s.field.push(u);
      this.addCount(u); this.addStartCounters(u); this.bring(u);
      this.giveStatus(seat, u, CONFIG.tokenRules.entrenchedToken, 1, null);
      const decay = Math.ceil((bDef.cost ?? 0) / 2);
      if (decay > 0) this.giveStatus(seat, u, 'decay', decay, null);
      this.fx('play', u.id);
      this.layout(owned);
      this.log(`${k.name}: ${bDef.name} comes back in its place, Entrenched, with ${decay} Decay; ${k.name} is discarded.`, seat);
      return null;
    }
    if (k.split) {
      const sp = k.split; const src2 = this.findField(k.src!.id) ?? k.src!;
      const ctx2 = this.unpackCtx(k.ctx);
      const rest2 = (k.rest ?? []).slice(1);
      if (a.card && !k.options.includes(a.card)) return 'Choose one of the ringed cards.';
      done();
      const t = a.card ? this.findField(a.card) : undefined;
      if (t) {
        // as many points as its player set (1 a click, or more at once), while the card can still take them
        const many = Math.max(1, Math.round(Number(a.amount) || 1));
        for (let i = 0; i < many && sp.left > 0 && s.field.includes(t) && this.splitOptions(src2, sp).includes(t); i++) this.splitPoint(src2, sp, t);
        this.splitAsk(src2, sp, ctx2, sp.left > 50, rest2);
        return null;
      }
      this.splitEnd(src2, sp, ctx2);
      this.runEffects(src2, rest2, ctx2);
      this.settleFallen();
      return null;
    }
    if (k.hold) {
      // an attack waiting for numbers: what is paid (Dark Gerzite Titan) or added (Volcanic Disciple)
      const h = k.hold;
      const from = this.findField(h.from); const to = this.findField(h.to);
      const nums = (a.numbers ?? []).map((x) => Math.max(0, Math.round(Number(x) || 0)));
      if (h.kind === 'gravity' && !(Math.round(a.choice ?? 0) >= 1 && Math.round(a.choice ?? 0) <= (h.lanes?.length ?? 0))) return 'Choose left or right.';
      if (h.kind !== 'gravity' && a.numbers === undefined && !k.optional) return 'Set the numbers, then confirm.';
      if (k.numbers && nums.some((x, i2) => x > k.numbers![i2].max)) return 'That is more than you can use.';
      done();
      if (!from || !to) return null;
      if (h.kind === 'gravity') {
        const lane = h.lanes?.[Math.round(a.choice ?? 0) - 1];
        from.shiftTo = lane ?? null;
        if (lane) { this.log(`Gravitation: ${s.players[seat].name} shifts the attack into ${lane.slot}.`, seat); this.gravityLook(to, lane); }
      } else if (h.kind === 'titan') {
        const [g2, d2] = [nums[0] ?? 0, nums[1] ?? 0];
        if (d2 > 0 && this.darkHeld(seat, d2)) return this.darkHeld(seat, d2);
        if (this.barTokens(seat, 'gerzite').length < g2 || this.barTokens(seat, 'dark').length < d2) return 'Not enough on your bar for that.';
        this.payBar(seat, g2, false); this.payBar(seat, d2, true);
        from.paidHit = 3 * g2 + 5 * d2;
        this.log(`${s.players[seat].name} pays ${g2} Gerzite and ${d2} Dark Gerzite for ${this.cardName(from.num)}’s attack: ${from.paidHit} damage.`, seat);
      } else if (h.kind === 'disciple') {
        const n2 = nums[0] ?? 0;
        const dis = h.by ? this.findField(h.by) : undefined;
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        if (n2 > 0 && dis && damageId) {
          this.bufferedDamage(dis, damageId, n2, null);
          from.boostHit = (from.boostHit ?? 0) + n2;
          this.log(`${this.cardName(dis.num)} takes ${n2} damage: ${this.cardName(from.num)}’s attack deals ${n2} more.`, seat);
          this.settleFallen();
        }
      }
      if (s.field.includes(from) && s.field.includes(to)) this.strike(h.seat, from, to, false, h.stage + 1);
      return null;
    }
    if (k.swap) {
      // Coral Reef: the first Unit, then the second; then the swap is paid for and made
      if (!a.card) { done(); return null; }
      if (!k.options.includes(a.card)) return 'Choose one of the ringed cards.';
      done();
      const biome = this.findField(k.swap.by);
      if (!k.swap.first) { this.coralAsk(seat, biome, a.card); return null; }
      if (biome) this.coralSwap(seat, biome, k.swap.first, a.card);
      return null;
    }
    if (k.search) {
      if (a.card && !k.cards?.some((c) => c.id === a.card)) return 'That isn’t one of the cards you can take.';
      done();
      const w = k.search.w;
      const pile: PileName = w[0] === 'discard' ? 'discard' : 'deck';
      const then: SearchThen | undefined = k.src && k.rest ? { src: k.src, rest: k.rest, ctx: k.ctx ?? {} } : undefined;
      const before = s.picks?.length ?? 0;
      if (a.card) {
        this.takeFound(seat, k.name, pile, w.includes('to') && w[w.indexOf('to') + 1] === 'top' ? 'top' : w.includes('to') && w[w.indexOf('to') + 1] === 'bottom' ? 'bottom' : 'hand', a.card, w.includes('payhalf'));
        this.startSearch(seat, k.name, w, k.search.left - 1, then);
      } else this.startSearch(seat, k.name, w, 0, then);
      if (then && (s.picks?.length ?? 0) <= before) {
        // the search is over: what came after it happens now (a Spell is discarded)
        this.runEffects(this.findField(then.src.id) ?? then.src, then.rest, this.unpackCtx(then.ctx));
        this.settleFallen();
      }
      return null;
    }
    if (k.entomb !== undefined) {
      const sarc = this.findField(k.entomb);
      if (a.card && sarc) {
        if (!k.cards?.some((c) => c.id === a.card)) return 'Choose one of your Spells.';
        done();
        this.entombSpell(sarc, a.card);
        return null;
      }
      done();
      return null;
    }
    const src = k.src!; const rest = k.rest ?? [];
    const ctx: FxCtx = this.unpackCtx(k.ctx);
    const card = this.findField(src.id) ?? src;
    if (k.toss) {
      if (!a.card && k.optional) {
        done();
        ctx.tossed = false;
        this.runEffects(card, rest.slice(1), ctx);
        this.settleFallen();
        return null;
      }
      if (!a.card || !k.cards?.some((c) => c.id === a.card)) return 'Choose a card to discard.';
      done();
      ctx.tossed = true;
      this.tossCard(seat, k.name, a.card, ctx);
      this.runEffects(card, rest.slice(1), ctx);
      this.settleFallen();
      return null;
    }
    if (k.laneChoices) {
      const n = Math.round(a.choice ?? 0);
      const o = k.laneChoices[n - 1];
      if (!o) return 'Choose one of the lanes.';
      done();
      ctx.lane = o.name; ctx.laneFoe = o.side !== this.sideOfCard(card);
      this.runEffects(card, rest.slice(1), ctx);
      this.settleFallen();
      return null;
    }
    if (k.choices) {
      const n = Math.round(a.choice ?? 0);
      if (!(n >= 1 && n <= k.choices.length)) return 'Choose one of the options.';
      done();
      if (k.gravLanes) {
        const lane = k.gravLanes.lanes[n - 1] ?? null;
        (ctx.shifts ??= {})[k.gravLanes.target] = lane;
        const aimed = this.findField(k.gravLanes.target);
        if (aimed) this.gravityLook(aimed, lane);
        if (lane) this.log(`Gravitation: ${s.players[seat].name} shifts the Spell’s damage into ${lane.slot}.`, seat);
      } else if (rest[0]?.words[1] === 'lane' || rest[0]?.words[1] === 'lanes') {
        const all = this.laneChoiceGroups(card, rest[0].words);
        const g = all.find((x) => x.label === k.choices![n - 1]) ?? all[n - 1];
        ctx.lane = g?.names.join(',');
        ctx.laneFoe = g ? g.foe : rest[0].words[2] !== 'own';
      } else ctx.chose = n;
      this.runEffects(card, rest.slice(1), ctx);
      this.settleFallen();
      return null;
    }
    if (!a.card) {
      if (!k.optional) return 'Choose one of the ringed cards.';
      done();
      if ('usedAt' in card) delete card.usedAt; // (an effect that was passed up can still be used this turn)
      // passed: the same "you may" offered again straight after is passed too (Fleshstealer: "up to 3", and that's enough)
      let next = 1;
      while (rest[next] && rest[next].words[0] === rest[0].words[0] && rest[next].conds.some((c) => c[0] === 'may')) next++;
      this.runEffects(card, rest.slice(next), ctx);
      this.settleFallen();
      return null;
    }
    if (!k.options.includes(a.card)) return 'That isn’t one of the cards you can choose.';
    done();
    const chosen = this.findField(a.card);
    if (chosen) {
      ctx.it = chosen;
      this.pickedNow.push(chosen.id);
      if (this.gravityHold(card, { ...rest[0], on: 'it' }, ctx, rest.slice(1))) return null;
      this.fxDepth++;
      try { this.doEffect(card, { ...rest[0], on: 'it' }, ctx); } finally { this.fxDepth--; }
    }
    this.runEffects(card, rest.slice(1), ctx);
    this.settleFallen();
    return null;
  }
  /** Fires one moment on every face-up card that passes `which`. */
  private fireAll(when: string, which: (c: FieldCard) => boolean, ctx: FxCtx = {}) {
    const swarms = new Set<string>(); // (a swarm answers some moments once, as one: CONFIG.swarms.once — Charred Clan's offer to tap)
    for (const c of [...this.state.field]) if (c.faceUp && which(c) && this.state.field.includes(c) && !(c.swarm && CONFIG.swarms.once.includes(when) && (swarms.has(c.swarm) || !swarms.add(c.swarm))) && (this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === when) || !!c.copied || !!c.soul || !!c.borrowed?.length)) this.fire(c, when, { ...ctx }); // (or effects it has from another card: Woods Wailer, a Soul, Soul Collector)
  }
  /** Vacuum: the active one keeping this positive status off a Unit in its lane, or undefined. */
  private vacuumFor(card: FieldCard, effect: string): FieldCard | undefined {
    if (!CONFIG.positiveStatuses.includes(effect) || !card.faceUp || !this.isUnit(this.cards.byNumber.get(card.num))) return undefined;
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane') return undefined;
    return this.state.field.find((v) => v.faceUp && !!v.lanes && v.lanes.side === at.side && v.lanes.names.includes(at.slot.name) && !this.isExpired(v) && this.effectsOf(v, 'always').some((e) => e.words[0] === 'vacuum'));
  }
  /** Puts a status token on a card (onto the one already there, if it is a counter). Returns false if the card shrugs it off. */
  private giveStatus(by: Seat, target: FieldCard, effect: string, n: number | null, turns: number | null, price?: number): boolean {
    const s = this.state;
    const tdef = this.tokenDb.list.find((d) => d.id === effect) ?? this.tokenDb.list.find((d) => d.effect === effect);
    if (!tdef || !s.field.includes(target) || this.untouchable(target)) return false;
    if (this.laneGuard(target, by)) return false; // (Protective Petals: nothing of an opponent's reaches its lane)
    if (this.vacuumFor(target, tdef.effect || tdef.id)) return false; // (Vacuum: no positive statuses in its lane)
    if (NEEDS_ATTACK.some((x) => x === tdef.id || x === tdef.effect) && this.neverAttacks(target)) return false; // (a Skeleton Dropship never attacks: Damage Boost and Overclock do nothing for it)
    const have = price === undefined ? this.tokensOn(target).find((t) => t.tokenId === tdef.id) : undefined;
    if (have) {
      if (tdef.counter) {
        const cap = CONFIG.tokenRules.caps[tdef.effect];
        const types = this.cards.byNumber.get(target.num)?.types ?? [];
        const most = cap ? (types.map((ty) => cap[ty]).find((v) => v !== undefined) ?? cap.other ?? 999) : 999;
        // a turn count (Frozen 2, then Frozen 3) takes the longer; an amount adds up (Overshield too: its points fill one bar)
        have.value = Math.min(most, tdef.effect !== 'overshield' && (CONFIG.turn.tickDown.end.includes(tdef.effect) || CONFIG.turn.tickDown.summon.includes(tdef.effect)) ? Math.max(have.value, n ?? tdef.start) : have.value + (n ?? tdef.start));
      }
      if (turns) have.turns = Math.max(have.turns ?? 0, turns);
      this.fx('status', target.id, undefined, { token: have.id, effect: 'notice' });
      return true;
    }
    const k = this.tokensOn(target).length;
    const sc = target.scale ?? 1; const side = sideOf(target.owner);
    const t: Token = { id: this.newId('t'), tokenId: tdef.id, x: target.x + ((k % 3) - 1) * 2 * sc, z: target.z - side * 1.2 * sc + Math.floor(k / 3) * 1.6 * sc * side, value: tdef.counter && price === undefined ? (n ?? tdef.start) : tdef.start, layer: s.nextLayer++, by, since: s.players[by].turns };
    if (turns) t.turns = turns;
    if (price !== undefined) t.price = Math.min(CONFIG.tokenRules.lockMaxCost, price);
    s.tokens.push(t);
    if (this.landToken(by, t)) {
      this.removeTokens([t]);
      return false;
    }
    if (s.tokens.includes(t)) this.mirrorStatus(by, t, undefined);
    this.fx('status', target.id, undefined, { token: t.id, effect: 'notice' });
    return true;
  }
  /**
   * A player has just done something their opponents' Triggers may be waiting for ("trigger: attack", "trigger: aff
   * duned"): each such Trigger lying face-down is flipped, pays its owner its Gerzite, and goes to their discard pile.
   */
  private springTriggers(actor: Seat, event: string, arg?: string) {
    const s = this.state;
    if (!CONFIG.combat.enabled || s.mode === 'solo') return;
    for (const card of [...s.field]) {
      const def = this.cards.byNumber.get(card.num);
      if (!def?.types.includes('trigger') || !this.opposed(card.owner, actor) || !s.field.includes(card)) continue;
      if (this.blind?.has(card.id)) continue; // (a bot's copy of the table: it can't know what this face-down card is)
      if (!def.effects.some((e) => e.when === 'trigger' && e.words[0] === event && (arg === undefined || e.words[1] === arg))) continue;
      const owner = s.players[card.owner];
      const amount = triggerPayout(def);
      // Glacial Wyrm: a Trigger it revealed has gone off: its owner gains 2
      const wyrm = card.revealedBy ? this.findField(card.revealedBy) : undefined;
      if (wyrm && wyrm.faceUp && !this.passiveStopper(wyrm) && this.cards.byNumber.get(wyrm.num)?.effects.some((e) => e.words[0] === 'wyrm_pays')) { const got2 = this.gain(wyrm.owner, CONFIG.tokenRules.fractalGives, 2); this.log(`${this.cardName(wyrm.num)}: the Trigger it revealed went off, so ${s.players[wyrm.owner].name} gains ${this.gainText(CONFIG.tokenRules.fractalGives, 2, got2)}.`, wyrm.owner); }
      const gerzite = CONFIG.tokenRules.fractalGives;
      // Igloo Village: while it shows every Trigger, one that goes off costs its owner 1 Gerzite, or it is discarded for nothing
      const igloo = s.field.find((c) => c.faceUp && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'show_triggers'));
      const toll = igloo ? Number(this.cards.byNumber.get(igloo.num)!.effects.find((e) => e.words[0] === 'show_triggers')!.words[1]) || 0 : 0;
      let unpaid = false;
      if (toll > 0) {
        const mine = this.barTokens(card.owner, 'gerzite');
        if (mine.length >= toll) this.removeTokens(mine.slice(-toll));
        else unpaid = true;
      }
      card.faceUp = true;
      this.fx('reveal', card.id);
      this.fx('sacrifice', card.id, undefined, { seat: card.owner });
      const got = unpaid ? 0 : this.gain(card.owner, gerzite, amount);
      const from = { x: card.x, z: card.z };
      this.clearTokensOn(card);
      this.removeField(card.id);
      this.insertAt(owner.discard, this.rehide(card), 'top');
      this.closeUp(from);
      const text = unpaid
        ? `${owner.name}’s ${def.name} is sprung, but they can’t pay the ${toll} Gerzite ${this.cardName(igloo!.num)} asks: it is discarded and pays nothing.`
        : `${owner.name}’s ${def.name} is sprung${toll > 0 ? `: they pay ${toll} Gerzite (${this.cardName(igloo!.num)}) and gain` : ': they gain'} ${this.gainText(gerzite, amount, got)}.`;
      this.fx('revealCard', '', undefined, { seat: card.owner, num: card.num, text });
      this.log(text, card.owner);
    }
  }
  /** A card from a player's hand is discarded for an effect, and its cost added to what the effect deals (Sacrifice). */
  private tossCard(seat: Seat, name: string, handId: string, ctx: FxCtx) {
    const p = this.state.players[seat];
    const i = p.hand.findIndex((c) => c.id === handId);
    if (i < 0) return;
    const [c] = p.hand.splice(i, 1);
    this.insertAt(p.discard, this.rehide(c), 'top');
    ctx.amount = (ctx.amount ?? 0) + (this.cards.byNumber.get(c.num)?.cost ?? 0);
    this.log(`${name}: ${p.name} discards ${this.cardName(c.num)} from their hand.`, seat);
    this.springTriggers(seat, 'discard');
  }
  /** Molten Lava River: the top card of a player's deck (or the hand card they chose) goes to their discard pile. */
  private lavaDiscard(seat: Seat, name: string, handId?: string) {
    const p = this.state.players[seat];
    const from = handId ? p.hand : p.deck;
    const i = handId ? p.hand.findIndex((c) => c.id === handId) : p.deck.findIndex((c) => !this.jumper(c));
    if (i < 0) return;
    const [c] = from.splice(i, 1);
    this.insertAt(p.discard, this.rehide(c), 'top');
    // (from the deck: everyone sees the card jump, face-up, from the deck to the discard pile — The River Keeper's
    // Bell in a pale green glow)
    if (!handId) this.fx('pileMove', '', undefined, { seat, effect: 'deck', num: c.num, ...(name === 'The Bell' && CONFIG.looks.enabled ? { tint: 'bell-mill' } : {}) });
    this.log(`${name}: ${p.name} discards ${this.cardName(c.num)} from ${handId ? 'their hand' : 'the top of their deck'}.`, seat);
    this.springTriggers(seat, 'discard');
  }
  /** A unit that has just arrived gets Ambush instead of being Entrenched: it stands up, and may attack this turn. */
  private giveAmbush(card: FieldCard) {
    const dug = this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken);
    this.removeTokens(dug);
    if (card.rot === CONFIG.arrival.entrenchedRotation) card.rot = 0;
    if (!this.hasOn(card, CONFIG.tokenRules.ambushToken)) this.giveStatus(card.owner, card, CONFIG.tokenRules.ambushToken, null, null);
    this.fx('ambush', card.id);
  }
  /** A card is destroyed outright (an effect says so): it is marked, what it leaves behind appears, and it goes. */
  private destroyNow(card: FieldCard) {
    const s = this.state;
    const skull = this.tokenDb.list.find((d) => d.effect === CONFIG.turn.lifetime.expiredToken);
    const def = this.cards.byNumber.get(card.num);
    if (!skull || !def || !s.field.includes(card) || isEmperor(def)) return;
    if (this.intangible(card)) return; // a Spirit Soldier is only ever taken away by its General's fall (Split Chasm passes it by)
    if (!this.hasOn(card, skull.effect)) {
      s.tokens.push({ id: this.newId('t'), tokenId: skull.id, x: card.x, z: card.z, value: skull.start, layer: s.nextLayer++, by: null });
      card.atZero = true;
      this.laneMark(card, 'destroyed');
      this.log(`${def.name} is destroyed.`, card.owner);
      this.summonUnits(card, 'destroyed');
      this.whenDestroyed(card);
    }
    if (CONFIG.combat.autoDiscard) this.fall(card);
  }
  /** A card has just been destroyed: its own "destroyed" effects, and those of the cards that watch for it. */
  private whenDestroyed(card: FieldCard) {
    const side = this.sideOfCard(card);
    for (const t of this.state.tokens) if (this.effectOf(t) === 'life-bank' && t.value > 0) t.value += 2; // a Unit destroyed: 2 more in every life bank
    if (card.faceUp && this.cards.byNumber.get(card.num)?.types.includes('troop') && !card.spawned) this.state.lastFallen = { num: card.num, owner: card.owner };
    this.transmitOn(card); // (Transmissive Death passes on)
    if (card.faceUp && !card.spawned && this.cards.byNumber.get(card.num)?.types.includes('troop')) { (this.state.graves ??= []).push({ num: card.num, owner: card.owner, at: this.turnStamp() }); if (this.state.graves.length > 30) this.state.graves.shift(); }
    this.pairFalls(card); // (Shadow of Death, Branch Out, Symbiotic Vines, Impervious Smite)
    this.necrosisSpreads(card); // (Barragand's Burning Necrosis: Burn on a Unit in its lane)
    // a Unit with a Bell on it is destroyed: its owner discards a card, from their hand or the top of their deck (The River Keeper)
    const bell = this.tokensOn(card).find((t) => this.effectOf(t) === 'bell' && t.by !== null && t.by !== undefined && this.opposed(t.by, card.owner));
    if (bell) {
      this.look('bell-ring', null, { at: { x: bell.x, z: bell.z } }); // the Bell tolls where it was
      const who2 = card.owner; const p2 = this.state.players[who2];
      if (this.state.bots?.includes(who2) || !p2.hand.length) this.lavaDiscard(who2, 'The Bell');
      else (this.state.picks ??= []).push({ id: this.state.nextId++, seat: who2, name: 'The Bell', text: `The Bell on ${this.cardName(card.num)} tolls: discard a card`, options: [], choices: ['A card from your hand', 'The top card of your deck'], lava: 'ask' });
    }
    this.fire(card, 'destroyed');
    this.fireAll('ally_destroyed', (c) => c !== card && this.sideOfCard(c) === side, { it: card });
    this.fireAll('foe_destroyed', (c) => c !== card && this.sideOfCard(c) !== side, { it: card });
    this.fireAll('any_destroyed', (c) => c !== card, { it: card, actor: card.owner });
  }
  /** The Spell → card pairs already shown flying during this action (one look for each, however many effects it has). */
  private aimed = new Set<string>();
  /** A Spell's look, flying from it to the Units and Emperors it is aimed at (CONFIG.combat.spellFx). */
  /** Where a lane is, on the table: its middle. */
  private laneSpot(name: string, side: 1 | -1) {
    const sl = laneOrder().find((l) => l.name === name);
    return sl ? { x: sl.x * side, z: sl.z * side } : null;
  }
  /** A Spell's light flying from it to lanes (marked ones, or empty ones it reached), not to a card. */
  private spellAimLanes(card: FieldCard, L: { side: 1 | -1; names: string[] } | null) {
    if (!L || !this.cards.byNumber.get(card.num)?.types.includes('spell')) return;
    const tint = this.damageAffOf(card)[0] ?? this.affOf(card)[0] ?? 'neutral';
    for (const n of L.names) {
      const at = this.laneSpot(n, L.side);
      if (at) this.fx('spell', card.id, undefined, { at, from: { x: card.x, z: card.z }, tint, seat: card.owner });
    }
  }
  /** A look of its own played over each of a card's lanes (Timed Blast going off, Crystalline Discharge's burst). */
  private laneLook(effect: string, L: { side: 1 | -1; names: string[] } | null) {
    if (!L || !CONFIG.looks.enabled) return;
    for (const n of L.names) {
      const at = this.laneSpot(n, L.side);
      if (at) this.fx('look', '', undefined, { effect, at });
    }
  }
  /** Gravitation shifts damage into another lane: a blue and black wind blows from the card that was aimed at to that lane. */
  private gravityLook(from: FieldCard, lane: { slot: string; side: 1 | -1 } | null | undefined) {
    const at = lane ? this.laneSpot(lane.slot, lane.side) : null;
    if (at && CONFIG.looks.gravityGust) this.look(CONFIG.looks.gravityGust, from, { at });
  }
  private spellAim(card: FieldCard, targets: FieldCard[], damage: boolean, anyCard = false) {
    // a Spell, or an Emperor's ability (CONFIG.looks.abilityBolts: its light flies from the Emperor to each card it reaches)
    const srcDef = this.cards.byNumber.get(card.num);
    if (!srcDef || !(srcDef.types.includes('spell') || (CONFIG.looks.abilityBolts && isEmperor(srcDef)))) return;
    if (CONFIG.looks.enabled && CONFIG.looks.noAim.includes(this.cardName(card.num))) return; // (a Spell with a look of its own instead: Enigma Beam's beams)
    const tint = this.damageAffOf(card)[0] ?? this.affOf(card)[0] ?? 'neutral';
    let n = 0;
    let vk = -1; // (a volley: which strike of this update it is)
    for (const t of targets) {
      const tDef = t.faceUp ? this.cards.byNumber.get(t.num) : undefined;
      if (t === card || ((!tDef || !(this.isUnit(tDef) || isEmperor(tDef))) && !anyCard)) continue;
      const key = `${card.id}>${t.id}`;
      // (a Spell that strikes again and again — Piercing Arrows, Finishing Feast, 1000 Knives: each strike flies on its own, one after another)
      if (damage && CONFIG.looks.enabled && CONFIG.looks.volley.includes(this.cardName(card.num))) {
        if (vk < 0) { vk = this.hopsNow.get(`v:${card.id}`) ?? 0; this.hopsNow.set(`v:${card.id}`, vk + 1); }
        this.aimed.add(key);
        if (vk >= 12) continue; // (a long run of 1000 Knives: the first dozen waves are shown)
        this.fx('spell', card.id, undefined, { target: t.id, from: { x: card.x, z: card.z }, tint, seat: card.owner, effect: 'volley', hop: vk });
        continue;
      }
      if (CONFIG.looks.enabled && CONFIG.looks.volley.includes(this.cardName(card.num)) && !damage) continue;
      if (this.aimed.has(key) || n >= CONFIG.combat.spellFx.most) continue;
      this.aimed.add(key);
      n++;
      // (a Spell that jumps from Unit to Unit — Chain Lightning, Bouncing Magma Blob — sets off each time from the one it hit last)
      const chain = CONFIG.looks.enabled && CONFIG.looks.chain.includes(this.cardName(card.num));
      if (chain && card.chainAt) {
        const hop = this.hopsNow.get(card.id) ?? 0;
        this.hopsNow.set(card.id, hop + 1);
        this.fx('spell', card.id, undefined, { target: t.id, from: card.chainAt, tint, seat: card.owner, effect: 'hop', hop });
      } else this.fx('spell', card.id, undefined, { target: t.id, from: { x: card.x, z: card.z }, tint, seat: card.owner, ...(damage ? { effect: 'strike' } : {}) });
      if (chain) card.chainAt = { x: t.x, z: t.z };
    }
  }
  private hopsNow = new Map<string, number>(); // (how many jumps each chaining Spell has made in this update)
  /**
   * What a bot's card effect aims at, when it has a choice. Martin (easy) takes any of them; Practice Buddy
   * (skilled) and Creator (expert) weigh them: damage where it destroys something (or the opposing Emperor, if it
   * would finish it), else where it hurts most for what the card is worth; healing on the Unit that has lost the
   * most; anything else on the opposing card worth most, or on their own strongest. Practice Buddy is a little
   * less exact. A bot with no level set takes the dearest opposing card, or its own first.
   */
  /** How much a bot would lose by sacrificing this Troop: its cost and damage (a token counts for little). */
  private devourWorth(c: FieldCard): number {
    const d = this.cards.byNumber.get(c.num);
    if (!d) return 0;
    return (d.cost ?? 0) + (Number.parseInt(d.damage ?? '', 10) || 0) * 0.5;
  }
  private botTarget(seat: Seat, e: CardEffect, pool: FieldCard[]): FieldCard {
    const level = this.state.botLevels?.[seat];
    const foeSide = e.on.includes('foe') || e.on === 'pick_front';
    if (!level) return foeSide ? [...pool].sort((a, b) => (this.cards.byNumber.get(b.num)?.cost ?? 0) - (this.cards.byNumber.get(a.num)?.cost ?? 0))[0] : pool[0];
    if (level === 'easy') return pool[Math.floor(this.rng() * pool.length)];
    const verb = e.words[0];
    const n = Number(e.words[1]) || 0;
    const worth = (c: FieldCard) => { const d = this.cards.byNumber.get(c.num); return (d?.cost ?? 3) + (Number.parseInt(d?.damage ?? '', 10) || 0) * 1.5; };
    const score = (c: FieldCard) => {
      const d = this.cards.byNumber.get(c.num);
      const left = Math.max(1, this.healthLeft(c));
      if (verb === 'heal') return Math.min(this.damageOn(c), n || 99) * (1 + worth(c) / 10);
      if ((verb === 'damage' || verb === 'divide') && foeSide) {
        if (d && isEmperor(d)) return Math.round(n / 2) >= left ? 1000 : n * 0.6;
        return n >= left ? 30 + worth(c) : (n / left) * worth(c);
      }
      return worth(c) + Math.min(20, left) * 0.1;
    };
    const noise = () => (level === 'skilled' ? this.rng() * 3 : 0);
    return pool.map((c) => ({ c, v: score(c) + noise() })).sort((a, b) => b.v - a.v)[0].c;
  }
  /**
   * A copy of the game to try moves on without touching the real one: a bot weighing what an attack or a card
   * would do. Nothing in it is random (no misses), and nothing done in it reaches the real game.
   */
  /** In a bot's copy of the table (sandbox): the opposing face-down cards it can't know, which never go off there. */
  private blind?: Set<string>;
  sandbox(viewer?: Seat): Game {
    const copy = Object.assign(Object.create(Game.prototype), this) as Game;
    const c = copy as unknown as Record<string, unknown>;
    copy.state = structuredClone(this.state);
    // a copy for one player (a bot) to try its moves on: what it can't know stays unknown there. The opposing side's
    // face-down cards (an unrevealed Trigger) never go off in it, and their decks are in no order it could learn from.
    if (viewer !== undefined) {
      c.blind = new Set(copy.state.field.filter((f) => !f.faceUp && !f.revealedBy && this.opposed(f.owner, viewer)).map((f) => f.id));
      copy.state.players.forEach((p, o) => { if (this.opposed(o as Seat, viewer)) for (let i = p.deck.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [p.deck[i], p.deck[j]] = [p.deck[j], p.deck[i]]; } });
    } else c.blind = undefined;
    c.rng = () => 0.99;
    c.transmitPool = [...this.transmitPool];
    c.pickedNow = [];
    c.aimed = new Set();
    c.gifts = [];
    c.guardHits = new Set();
    c.reached = new Set();
    c.fallenBefore = new Set(this.fallenBefore);
    return copy;
  }
  /**
   * For a bot looking ahead: the attacks this seat could make in its next Attack Phase as the table stands now (its
   * cards stood back up and no longer Entrenched), with the damage each would deal in all.
   */
  nextAttacks(seat: Seat): { from: string; to: string; damage: number }[] {
    const sb = this.sandbox();
    const s = sb.state;
    s.turn = seat;
    s.phase = 2;
    s.players[seat].turns = Math.max(1, s.players[seat].turns + 1);
    const out: { from: string; to: string; damage: number }[] = [];
    for (const c of s.field) {
      if (c.owner !== seat) continue;
      c.rot = 0;
      delete c.acted;
      delete c.swings;
      sb.removeTokens(sb.tokensOn(c).filter((t) => sb.effectOf(t) === CONFIG.tokenRules.entrenchedToken));
    }
    for (const c of s.field) {
      const def = c.faceUp && c.owner === seat ? sb.cards.byNumber.get(c.num) : undefined;
      if (!def || sb.attackWhy(seat, c)) continue;
      const { per, hits } = sb.attackOf(c, def);
      for (const t of sb.attackTargets(c)) out.push({ from: c.id, to: t.id, damage: per * hits });
    }
    return out;
  }
  /** How much health a card on the table has left (for a bot weighing its moves). */
  healthOf(id: string): number {
    const c = this.findField(id);
    return c ? this.healthLeft(c) : 0;
  }
  /** One effect, carried out (with the hand glow of its card, if it has one: CONFIG.looks.handTint). */
  private doEffect(card: FieldCard, e: CardEffect, ctx: FxCtx) {
    const was = this.handTint;
    const tint = card.faceUp ? CONFIG.looks.handTint[this.cardName(card.num)] : undefined;
    if (tint) this.handTint = tint;
    const wave = this.linkWave;
    this.linkWave = new Set();
    try { this.doEffectNow(card, e, ctx); } finally { this.handTint = was; this.linkWave = wave; }
  }
  private doEffectNow(card: FieldCard, e: CardEffect, ctx: FxCtx) {
    const s = this.state;
    const name = this.cardName(card.num);
    const seat = card.owner;
    const [verb, ...w] = e.words;
    const names = (list: FieldCard[]) => list.map((c) => (c.faceUp ? this.cardName(c.num) : 'a face-down card')).join(', ');
    // ("affiliation forested" among an effect's words: only the cards of that affiliation)
    const affAt = e.words.indexOf('affiliation');
    const affs = affAt > 0 && e.words[0] !== 'search' && e.words[0] !== 'discount' && e.words[0] !== 'reveal' ? e.words.slice(affAt + 1) : null;
    const who = (fallback: string) => this.whoOf(card, e.on || fallback, ctx).filter((c) => (!affs || affs.some((a) => this.affOf(c).includes(a))) && !this.shrugsOff(c, card, verb));
    const twice = this.biomeDoubling(card); // (Murkfin: a Submerged Biome's stated effects are doubled)
    const num = (word: string | undefined, d = 1) => (word === 'count' ? (ctx.count ?? 0) : word === 'half' ? Math.ceil((ctx.amount ?? 0) / 2) : word === 'lost' ? (ctx.amount ?? 0) : word !== undefined && /^\d+$/.test(word) ? Number(word) * twice : d);
    const say = (text: string) => {
      this.fx('status', card.id, undefined, { effect: 'notice' });
      this.log(`${name}: ${text}`, seat);
    };
    // a Spell aimed at Units or Emperors: something flies from it to each of them, so both players see what it targets
    // (its damage is shown where it lands below, once Gravitation has had its say)
    if (verb !== 'damage' && e.on && e.on !== 'self') this.spellAim(card, who('target'), false);
    switch (verb) {
      case 'status': {
        const tdef = this.tokenDb.list.find((d) => d.id === w[0]) ?? this.tokenDb.list.find((d) => d.effect === w[0]);
        if (!tdef) return;
        const n = w[1] === 'count' ? (ctx.count ?? 0) : w[1] === 'third' ? Math.floor((ctx.count ?? 0) / 3) : w[1] !== undefined && /^\d+$/.test(w[1]) ? Number(w[1]) : null;
        if ((w[1] === 'count' || w[1] === 'third') && !n) return;
        const f = w.indexOf('for');
        // ("with_spell": it lasts as long as the Spell is in play, and goes when the Spell is discarded: Buffer)
        const lend = w.includes('with_spell') && !!this.cards.byNumber.get(card.num)?.types.includes('spell');
        const turns = f >= 0 && !lend ? num(w[f + 1]) : null;
        // "cap 3": never more than that much from this card on any one card (Armorsmith); what it gave is remembered
        const capAt = w.includes('cap') ? num(w[w.indexOf('cap') + 1], 99) : 0;
        const key = (t: FieldCard) => `${tdef.id}:${t.id}`;
        const room = (t: FieldCard) => !capAt || (card.gave?.[key(t)] ?? 0) + (n ?? 1) <= capAt;
        const full = capAt ? who('target').filter((t) => !room(t)) : [];
        if (full.length) say(`${names(full)} already ${full.length === 1 ? 'has' : 'have'} ${capAt} ${tdef.name} from it: no more.`);
        const got = who('target').filter((t) => room(t) && this.giveStatus(seat, t, tdef.id, n, turns));
        this.spellAim(card, got, false); // (a Spell's light reaches each card it gives the status to: Powertrip)
        // "until_end": it comes off as this turn ends · "until_next_end": as its caster's next turn ends
        if (w.includes('until_end') || w.includes('until_next_end')) {
          const p0 = s.players[seat];
          const turn = w.includes('until_end') ? (s.turn === seat ? p0.turns : p0.turns) : (s.turn === seat ? p0.turns + 1 : p0.turns);
          for (const t of got) for (const tok of this.tokensOn(t).filter((o) => o.tokenId === tdef.id)) tok.until = { seat, turn };
        }
        if (capAt || w.includes('track')) for (const t of got) (card.gave ??= {})[key(t)] = (card.gave?.[key(t)] ?? 0) + (n ?? 1);
        if (lend) for (const t of got) { const tok = this.tokensOn(t).find((o) => o.tokenId === tdef.id); if (tok) (tok.lent ??= []).push({ spell: card.id, n: tdef.counter ? (n ?? tdef.start) : tok.value }); }
        // (drain: the amount is temporary and wears off 1 a turn: Cagurn's Razor Point)
        if (w.includes('drain') && n) for (const t of got) { const tok = this.tokensOn(t).find((o) => o.tokenId === tdef.id); if (tok) { tok.drain = (tok.drain ?? 0) + n; tok.drainSince = s.players[t.owner].turns; } }
        if (got.length) say(`${tdef.name}${n !== null && tdef.counter ? ` ${n}` : ''}${turns ? ` for ${turns} turn${turns === 1 ? '' : 's'}` : ''} on ${names(got)}.`);
        return;
      }
      case 'lock': {
        const got = who('attacker').filter((t) => this.giveStatus(seat, t, CONFIG.tokenRules.lockToken, null, w.includes('for') ? num(w[w.indexOf('for') + 1]) : null, num(w[0], 0)));
        // ("paid gain 2" / "paid status susceptible 1 weakness 1": what happens when the Lock is paid off)
        if (w.includes('paid')) for (const t of got) { const lock = this.tokensOn(t).filter((o) => this.effectOf(o) === CONFIG.tokenRules.lockToken).at(-1); if (lock) lock.onPaid = { by: seat, w: w.slice(w.indexOf('paid') + 1) }; }
        // "tied": the card ends once its Lock is gone however it went (paid off, run out, its Unit gone: Grasping Twine);
        // "ground": a Unit under this Lock can't be Flying while it lasts
        if (w.includes('tied') || w.includes('ground')) {
          const locks = got.map((t) => this.tokensOn(t).filter((o) => this.effectOf(o) === CONFIG.tokenRules.lockToken).at(-1)).filter((o): o is Token => !!o);
          for (const lock of locks) { lock.from = card.id; if (w.includes('ground')) lock.grounds = true; }
          if (w.includes('tied') && locks.length) card.brought = [...(card.brought ?? []), ...locks.map((o) => ({ id: o.id, name: 'Locked' }))];
        }
        if (got.length) say(`${names(got)} ${got.length === 1 ? 'is' : 'are'} Locked for ${num(w[0], 0)} Gerzite.`);
        return;
      }
      case 'damage': {
        const srcDef = this.cards.byNumber.get(card.num);
        const spell = !!srcDef?.types.includes('spell');
        const dark = spell && s.field.includes(card) && this.hasOn(card, CONFIG.turn.darkgale.token);
        let hit = who('target');
        const stamp = this.turnStamp();
        // (Gravitation: a target whose side chose to shift it: the damage goes into that lane instead, under the
        // Spell's own rules: into an empty lane only Direct damage goes on, to the Emperor)
        if (ctx.shifts) {
          const direct = !!srcDef?.traits.some((t) => /direct/i.test(t));
          hit = hit.flatMap((t) => {
            const sh = ctx.shifts![t.id];
            if (!sh) return [t];
            delete ctx.shifts![t.id];
            const sl = laneOrder().find((l) => l.name === sh.slot);
            const out = sl ? this.intoLane(sl, sh.side, direct) : null;
            if (!out) { this.log(`${name}: its damage is shifted into ${sh.slot} by Gravitation, where there is nothing it can hit.`, seat); return []; }
            return [out];
          });
        }
        // how much: a number, or "wane 4" (1 less each turn since it was played), "charge 3" (times its count-up timer),
        // "enigma 20 2" (2 less for each Unit past the first), "paid" (what was paid to play it), "cost" (the cost of the card in question); "x4" multiplies
        // (a Spell aimed at whole lanes that deals splash, or area, damage reaches every unit of a Count it hits: Toxic Cloud on Gerzite Crystallites)
        const laneSplash = e.on === 'in_lanes' && !!srcDef?.traits.some((t) => /splash|area of effect/i.test(t));
        let n = w[0] === 'wane' ? Math.max(0, num(w[1], 0) - (s.players[seat].turns - (card.playedTurn ?? s.players[seat].turns)))
          : w[0] === 'charge' ? num(w[1], 1) * this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.turn.countUp.token).reduce((a, t) => a + t.value, 0)
          : w[0] === 'enigma' ? Math.max(0, num(w[1], 0) - num(w[2], 0) * Math.max(0, hit.length - 1))
          : w[0] === 'paid' ? this.lastPaid : w[0] === 'cost' ? (this.cards.byNumber.get(ctx.it?.num ?? -1)?.cost ?? 0) : num(w[0], 0);
        const times = w.find((x) => /^x\d+$/.test(x));
        if (times) n *= Number(times.slice(1));
        // "quake 3": that much more for each Earthquake its caster has active in their Spell slots (Underrealm Sinkhole)
        if (w.includes('quake')) n += num(w[w.indexOf('quake') + 1], 0) * s.field.filter((c) => c !== card && c.faceUp && c.owner === seat && this.cardName(c.num) === 'Earthquake' && !this.isExpired(c) && slotKind(slotAt(c)?.slot.name ?? '') === 'Spell').length;
        if (spell || isEmperor(srcDef!)) n = this.spellScale(card, n);
        const rawHalf = this.halvedFrom;
        const notes: string[] = [];
        // a look of its own over its lanes as it goes off (Timed Blast, Crystalline Discharge), and its light flies to
        // any of them it reaches with nothing in them
        if (e.on === 'in_lanes' && n > 0) {
          const L = this.lanesFor(card, ctx);
          const blast = CONFIG.looks.enabled ? CONFIG.looks.laneBlast[name] : undefined;
          if (blast) this.laneLook(blast, L);
          if (L) this.spellAimLanes(card, { side: L.side, names: L.names.filter((ln) => !hit.some((t) => this.standsAt(t)?.slot.name === ln)) });
        }
        if (n <= 0 || !hit.length) return;
        let total = 0;
        ctx.hurt = [];
        const amountFor = (t: FieldCard) => {
          const tDef = this.cards.byNumber.get(t.num);
          let m = n;
          if (w.includes('bld') && tDef?.types.includes('building')) m *= 2; // (twice as much to Buildings)
          if (w.includes('air') && this.fliesNow(t)) m = Math.max(0, m - 1); // (1 less to Flying)
          if (w.includes('ground') && this.fliesNow(t)) return 0; // (Ground Units only)
          if (spell) {
            if (t.spellWardAt === stamp) { notes.push(`${this.cardName(t.num)} is warded against Spells`); return 0; } // Impervious Shield
            if (tDef?.types.includes('emperor') && !w.includes('exact') && !dark) m = rawHalf !== null ? Math.round(rawHalf / 4) : Math.round(m / 2); // an Emperor takes half of a Spell's damage (a quarter after Frozen Peaks halved it)
            if (tDef?.effects.some((e2) => e2.when === 'take' && e2.words[0] === 'half' && e2.words[2] === 'spell')) m = Math.round(m / 2); // Snow Dweller
          }
          return m;
        };
        this.spellAim(card, hit, true);
        // (splash: everything in the lane of the card it is aimed at)
        // (only the units fighting on the same side as it: Zap on an opposing Royal Guard in your lane doesn't splash your own units)
        if (w.includes('splash')) for (const t of [...hit]) { const at = this.standsAt(t); if (at && slotKind(at.slot.name) === 'Lane') for (const u of this.unitsAt(at.slot, at.side, undefined, true)) if (!hit.includes(u) && (s.mode === 'solo' || this.fightsOn(u) === this.fightsOn(t))) hit.push(u); }
        // (how many units it hurt is kept for what follows: "status overheal count" — Gleaming Strike)
        ctx.count = 0;
        // (a Spell with a strike of its own on each card: Enigma Beam's beam from the sky, grander the more it deals)
        const strike = CONFIG.looks.enabled ? CONFIG.looks.strike[name] : undefined;
        if (strike) for (const t of hit) { const m = amountFor(t); const tier = m >= 16 ? 4 : m >= 10 ? 3 : m >= 5 ? 2 : 1; if (m > 0) this.look(`${strike}-${tier}`, t); }
        if (spell) this.spellHitting++;
        try {
          for (const t of hit) {
            const units = Math.max(1, this.valueOn(t, CONFIG.tokenRules.countToken));
            this.reached.delete(t.id);
            const got = this.landHit(seat, t, amountFor(t), { pierce: w.includes('pierce'), splash: w.includes('splash') || laneSplash, from: card, spell }, notes);
            total += got;
            // ("on damaged": every card the damage reached, even one its Buffer or Entrenched left at 0 — Zap's Stun still lands)
            if (got > 0 || this.reached.has(t.id)) ctx.hurt.push(t);
            if (got > 0) { ctx.count += units; if (spell && s.field.includes(t)) this.fire(t, 'spell_hurt', { attacker: card, amount: got }); }
          }
        } finally { if (spell) this.spellHitting--; }
        say(`${n} damage to ${names(hit)}${notes.length ? ` (${notes.join('; ')})` : ''}.`);
        return;
      }
      case 'wound': {
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        const n = num(w[0], 0);
        const hit = who('self');
        if (!damageId || (n <= 0 && w[0] !== 'cost') || !hit.length) return;
        const amt = w[0] === 'cost' ? (this.cards.byNumber.get(ctx.it?.num ?? -1)?.cost ?? 0) : n;
        for (const t of hit) { this.addDamage(t, damageId, amt, null); this.mirrorDamage(t, damageId, amt); }
        ctx.count = hit.length;
        if (w.includes('reap')) {
          // Soul Reap: 1 more for each Unit it destroyed
          this.settleFallen();
          ctx.count += hit.filter((t) => this.isUnit(this.cards.byNumber.get(t.num)) && (!s.field.includes(t) || this.hasOn(t, CONFIG.turn.lifetime.expiredToken))).length;
        }
        say(`${names(hit)} lose${hit.length === 1 ? 's' : ''} ${n} ${hit.every((t) => this.cards.byNumber.get(t.num)?.types.includes('building')) ? 'SP' : 'health'}.`);
        return;
      }
      case 'heal': {
        const n = num(w[0], 0);
        const healed: FieldCard[] = [];
        for (const t of who('self')) {
          let left = n;
          for (const d of this.tokensOn(t).filter((o) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(o)))) {
            const take = Math.min(left, d.value);
            d.value -= take; left -= take;
            if (d.value <= 0) this.removeTokens([d]);
          }
          if (left < n) { healed.push(t); this.spellAim(card, [t], false); this.fx('status', t.id, undefined, { effect: 'heal' }); }
          else if (w.includes('overheal') && this.giveStatus(seat, t, 'overheal', n, null)) healed.push(t); // at full health: Overheal instead
        }
        if (healed.length) say(`heals up to ${n} on ${names(healed)}.`);
        return;
      }
      case 'health': {
        const hdef = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.healthToken);
        if (!hdef) return;
        for (const t of who('self')) this.giveStatus(seat, t, hdef.id, num(w[0]), null);
        say(`gains ${num(w[0])} health.`);
        return;
      }
      case 'gain': {
        if (w[1] === 'fractal') { this.addFractals(seat, num(w[0])); say(`${s.players[seat].name} gains ${num(w[0])} Fractal${num(w[0]) === 1 ? '' : 's'}.`); return; } // (Recall Rift)
        const id = w[1] === 'dark' ? this.tokenDb.list.find((d) => d.bar === 'dark')?.id : CONFIG.tokenRules.fractalGives;
        if (!id) return;
        const n = num(w[0]);
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat));
        const to = w.includes('actor') && ctx.actor !== undefined ? ctx.actor : w.includes('foe') && foeSeat >= 0 ? (foeSeat as Seat) : seat; // ("actor": whoever's turn or move it is, for a Biome)
        say(`${s.players[to].name} gains ${this.gainText(id, n, this.gain(to, id, n))}.`);
        if (CONFIG.looks.gainFly[name]) this.look(CONFIG.looks.gainFly[name], null, { seat: to, at: { x: card.x, z: card.z } }); // (Gerzite Mine: it flies from the Mine to the bar)
        return;
      }
      case 'draw': {
        const p = s.players[seat];
        let drew = 0;
        const draws = w[0] === 'dark' ? this.barTokens(seat, 'dark').length : num(w[0]); // ("draw dark": 1 for each Dark Gerzite its player has — Warlord's Wrath)
        for (let k = 0; k < draws; k++) {
          if (!this.canDraw(p) || p.hand.length >= CONFIG.handLimit) break;
          const got = this.drawTop(p)!;
          p.hand.push(this.rehide(got));
          drew++;
          this.drew(seat, got.num);
        }
        if (drew) this.fx('toHand', '', undefined, { effect: 'deck', seat, count: drew });
        say(drew ? `${p.name} draws ${drew} card${drew === 1 ? '' : 's'}.` : `${p.name} can’t draw (a full hand, or no cards left).`);
        return;
      }
      case 'summon': {
        const stop = w.findIndex((x) => /^x\d+$/i.test(x) || ['max', 'front', 'ambush', 'lane', 'lanes', 'entrenched', 'xtroops', 'emerge'].includes(x.toLowerCase()));
        const unitName = (stop < 0 ? w : w.slice(0, stop)).join(' ').toLowerCase();
        const flags = (stop < 0 ? [] : w.slice(stop)).map((x) => x.toLowerCase());
        const unit = this.cards.spawnOnly.find((d) => d.name.toLowerCase() === unitName);
        if (!unit) return;
        const times = flags.includes('xtroops') ? s.players[seat].discard.filter((c) => this.cards.byNumber.get(c.num)?.types.includes('troop')).length : Number(flags.find((x) => /^x\d+$/.test(x))?.slice(1) ?? 1);
        const max = flags.includes('max') ? num(flags[flags.indexOf('max') + 1], 99) : 99;
        const have = s.field.filter((u) => u.summonedBy === card.id && u.num === unit.number).length;
        const n = Math.min(times, max - have);
        if (n <= 0) {
          // nothing to summon (Dead Rising with no Troops in the discard pile): its light still goes to the lane chosen
          if (flags.includes('lane') || flags.includes('lanes')) this.spellAimLanes(card, this.lanesFor(card, ctx));
          return;
        }
        // in its own lane, or (front) in the lane across from it
        const at = this.standsAt(card);
        let spot = { x: card.x, z: card.z };
        if (flags.includes('front') && at && slotKind(at.slot.name) === 'Lane') {
          const row = (sd: number) => laneOrder().map((slot) => ({ slot, x: slot.x * sd })).sort((a, b) => a.x - b.x);
          const across = row(-at.side)[row(at.side).findIndex((l) => l.slot === at.slot)];
          if (across) spot = { x: across.slot.x * -at.side, z: across.slot.z * -at.side };
        }
        // "lane": in the lane chosen for it · "lanes": that many in each of the lanes chosen for it
        const L = flags.includes('lane') || flags.includes('lanes') ? this.lanesFor(card, ctx) : null;
        const spots = L ? laneOrder().filter((l) => L.names.includes(l.name)).slice(0, flags.includes('lanes') ? 9 : 1).map((l) => ({ x: l.x * L.side, z: l.z * L.side })) : [spot];
        if (!spots.length) return;
        // (the cards already in that lane keep their own tokens: the lane is laid out again at once)
        let brought = 0;
        for (let k = 0; k < n * spots.length; k++) {
          const owned = this.owners(); // (taken again for each: the one summoned just before keeps the tokens it came with — Undead Guards' Shields)
          const sp = spots[k % spots.length];
          const u: FieldCard = { ...this.held(unit.number, true), owner: card.owner, x: sp.x, z: sp.z, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, summonedBy: card.id, bornIn: s.turn };
          s.field.push(u);
          // (on the solo table every card is yours: one summoned into the other side's lane — a Skeleton Dropship's
          // Skell-Mini, Royal Delivery's Royal Guard — still fights for the side that summoned it)
          if (s.mode === 'solo') { const ua = this.standsAt(u); const by = this.sideOfCard(card); if (ua && ua.side !== by) u.fightsFor = by; }
          this.addCount(u);
          this.addStartCounters(u);
          brought += this.bring(u, true);
          if (flags.includes('ambush')) this.giveStatus(seat, u, CONFIG.tokenRules.ambushToken, null, null);
          if (flags.includes('entrenched')) { u.rot = CONFIG.arrival.entrenchedRotation; this.giveStatus(seat, u, CONFIG.tokenRules.entrenchedToken, 1, null); }
          // ("emerge": it rises up out of the table, in a ripple of water; Infestation's Sand Crawlers out of the sand)
          const ground = CONFIG.looks.enabled ? CONFIG.looks.emerge[name] : undefined;
          this.fx(flags.includes('emerge') || ground ? 'emerge' : 'play', u.id, undefined, ground && !flags.includes('emerge') ? { effect: ground } : {});
          this.layout(owned);
        }
        const made = n * spots.length;
        if (made <= 0 && L) this.spellAimLanes(card, L);
        // a Spell that summons (Chicken Barrel, Grave Reinforcements): its affiliation's light flies from it to every unit
        // it summoned (up to CONFIG.combat.spellFx.most of them)
        if (!(CONFIG.looks.enabled && CONFIG.looks.emerge[name]) && !!this.cards.byNumber.get(card.num)?.types.includes('spell')) {
          const fresh = s.field.filter((u) => u.summonedBy === card.id && u.num === unit.number).slice(-made);
          this.spellAim(card, fresh, false);
        }
        const what = `summons ${made === 1 ? `a ${unit.name}` : `${made} ${unit.name}s`}${L ? ` in ${L.names.join(', ')}` : flags.includes('front') ? ' into the lane across from it' : ' in its lane'}`;
        say(`${what}.`);
        const sl = CONFIG.looks.summon[name];
        if (sl) this.look(sl, card); // (Frozen Scarecrow: birds circle it as it calls an Icewing)
        // one line on the screen for the lot, instead of one for every token each of them brought (Leviathan Body Segments)
        if (brought) this.fx('status', card.id, undefined, { effect: 'notice', seat, text: `${name} ${what}, with ${brought === 1 ? 'its token' : 'their tokens'} in place.` });
        return;
      }
      case 'destroy': {
        const doomed = who('self').filter((t) => !w.includes('ground') || !this.fliesNow(t));
        if (!doomed.length) return;
        say(doomed.length === 1 && doomed[0] === card ? 'it is destroyed.' : `${names(doomed)} ${doomed.length === 1 ? 'is' : 'are'} destroyed.`);
        for (const t of doomed) this.destroyNow(t);
        return;
      }
      case 'shuffle': {
        for (let i = 0; i < s.players.length; i++) {
          if (!this.opposed(seat, i as Seat)) continue;
          this.shuffle(s.players[i].deck);
          s.players[i].shuffles.deck++;
          say(`${s.players[i].name} shuffles their deck.`);
        }
        return;
      }
      case 'transfer': {
        const to = who('target')[0];
        if (!to) return;
        const mine = this.tokensOn(card).filter((t) => this.tokenDb.byId.get(t.tokenId)?.category === 'status' && this.effectOf(t) !== CONFIG.tokenRules.entrenchedToken);
        const moved: string[] = [];
        for (const t of mine) {
          const tdef = this.tokenDb.byId.get(t.tokenId)!;
          this.removeTokens([t]);
          // (it goes on as the new card's own: its owner's turn clock, not the one who passed it on)
          if (this.giveStatus(to.owner, to, tdef.id, tdef.counter ? t.value : null, t.turns ?? null)) moved.push(tdef.name);
        }
        if (moved.length) say(`its ${moved.join(', ')} ${moved.length === 1 ? 'passes' : 'pass'} to ${names([to])}.`);
        return;
      }
      case 'steal': {
        const from = who('target')[0];
        const gz = this.tokenDb.byId.get(CONFIG.tokenRules.fractalGives);
        const dk = this.tokenDb.list.find((d) => d.bar === 'dark');
        if (!from || from.owner === seat || !gz) return;
        const theirs = this.barTokens(from.owner, 'gerzite');
        const dark = this.barTokens(from.owner, 'dark');
        if (theirs.length && this.placeBarToken(seat, gz)) { this.removeTokens(theirs.slice(-1)); say(`steals 1 ${gz.name} from ${s.players[from.owner].name}.`); }
        else if (dark.length && dk && this.placeBarToken(seat, dk)) { this.removeTokens(dark.slice(-1)); say(`steals 1 ${dk.name} from ${s.players[from.owner].name}.`); }
        return;
      }
      case 'done': {
        // the Spell has done all it does: it goes to its owner's discard pile
        if (!s.field.includes(card)) return;
        for (const c of s.field) if (c.cloakBy === card.id) delete c.cloakBy;
        // (a Spell that goes out its own way: Arcane Explosion rises, shakes and blows apart)
        const blast = card.faceUp && CONFIG.looks.enabled ? CONFIG.looks.spellEnd[name] : undefined;
        if (blast) this.fx('sacrifice', card.id, undefined, { seat: card.owner, effect: blast, num: card.num, from: { x: card.x, z: card.z }, value: card.rot }); // (num and where: a Spell that goes off at once is never drawn on the table before it does)
        this.discardFromPlay(card);
        this.log(`${name} is discarded.`, seat);
        return;
      }
      case 'rehand': {
        // the Spell goes back to its owner's hand instead of the discard pile (Gerzite Dagger, if what it hit was destroyed)
        if (!s.field.includes(card) || s.players[seat].hand.length >= CONFIG.handLimit) return;
        this.clearTokensOn(card);
        this.removeField(card.id);
        this.closeUp(card);
        s.players[seat].hand.push(this.rehide(card));
        say('it returns to its owner’s hand.');
        return;
      }
      case 'secret': {
        // Timed Blast: it lies upside down, and its opponents can't see what it is or which lane it aims at until it goes off
        if (w[0] === 'off') { delete card.secret; return; }
        card.secret = true;
        if (card.rot % 2 === 0) card.rot = 2;
        return;
      }
      case 'mark': {
        const L = this.lanesFor(card, ctx);
        if (L) card.lanes = { side: L.side, names: [...L.names] };
        if (L && CONFIG.looks.enabled && CONFIG.looks.markAim.includes(name)) this.spellAimLanes(card, L); // (Energy Surge, Spatial Rift: the Spell's light flies to the lanes it marks)
        return;
      }
      case 'mark_add': {
        // one more lane marked on the card (Spatial Rift's second lane)
        const L = this.lanesFor(card, ctx);
        if (L && card.lanes && card.lanes.side === L.side) for (const n2 of L.names) { if (!card.lanes.names.includes(n2)) card.lanes.names.push(n2); }
        else if (L) card.lanes = { side: L.side, names: [...L.names] };
        if (L && CONFIG.looks.enabled && CONFIG.looks.markAim.includes(name)) this.spellAimLanes(card, L);
        delete ctx.lane; delete ctx.laneFoe; // (what follows reads every lane marked on the card)
        return;
      }
      case 'wrath': {
        // Wrathful Ghost: an attack with its Special Hit Entrenches every Unit it damaged; the token comes back for every third attack after
        const sh = this.tokensOn(card).filter((t) => this.effectOf(t) === 'special-hit');
        if (sh.length) {
          this.removeTokens(sh);
          card.wrath = 0;
          const dug = (ctx.hurt ?? []).filter((t) => s.field.includes(t) && !t.atZero && this.isUnit(this.cards.byNumber.get(t.num)) && !isEmperor(this.cards.byNumber.get(t.num)!));
          for (const t of dug) { if (t.rot % 2 === 0) t.rot = CONFIG.arrival.entrenchedRotation; this.giveStatus(seat, t, CONFIG.tokenRules.entrenchedToken, 1, null); }
          if (CONFIG.looks.enabled) for (const t of dug) this.fx('status', t.id, undefined, { effect: 'notice' });
          say(dug.length ? `its Special Hit Entrenches ${names(dug)}.` : 'its Special Hit damaged nothing it could Entrench.');
        } else {
          card.wrath = (card.wrath ?? 0) + 1;
          if (card.wrath >= 2) { this.bring(card); card.wrath = 0; say('its next attack is a Special Hit.'); }
        }
        return;
      }
      case 'rewind': {
        // Time Bender: back to how it stood as its opponent's last turn began (its damage, statuses and counters)
        const snap = card.rewind;
        card.rewoundAt = s.players[seat].turns;
        if (!snap?.to || snap.ready !== s.players[seat].turns) { say('there is no turn of its opponent’s to undo.'); return; }
        delete card.rewind;
        // only what changed during that turn is undone: what it gained then is taken off, what it lost then is put back
        // (damage, statuses and counters it got before that turn, or since in this one, stay as they are)
        const A = new Map(snap.from.map((t) => [t.id, t])); const B = new Map(snap.to.map((t) => [t.id, t]));
        const cur = new Map(this.tokensOn(card).map((t) => [t.id, t]));
        const counter = (t: Token) => !!this.tokenDb.byId.get(t.tokenId)?.counter;
        const gone: Token[] = [];
        for (const [id, b] of B) {
          const a = A.get(id); const c = cur.get(id);
          if (!c) continue;
          const grew = b.value - (a?.value ?? 0);
          if (!a && !counter(c)) gone.push(c);
          else if (grew > 0 && counter(c)) { c.value -= grew; if (c.value <= 0) gone.push(c); }
        }
        this.removeTokens(gone);
        const dx = card.x - snap.x; const dz = card.z - snap.z;
        for (const [id, a] of A) {
          const b = B.get(id); const c = cur.get(id);
          if (!b) { if (!c || gone.includes(c)) s.tokens.push({ ...a, id: this.newId('t'), twin: undefined, x: a.x + dx, z: a.z + dz, layer: s.nextLayer++ }); }
          else if (counter(a) && b.value < a.value && c && !gone.includes(c)) c.value += a.value - b.value;
        }
        card.atZero = false;
        if (CONFIG.looks.enabled) this.fx('status', card.id, undefined, { effect: 'heal' });
        say('everything done to it during its opponent’s last turn is undone.');
        return;
      }
      case 'sever': {
        // Sever the Spirits: every link and connection the Unit has is cut
        for (const t of who('it')) {
          const cut: string[] = [];
          // Link counters (a Duned Brain's Troops, Voodoo Magic, Link tokens of any card): the pair goes from both cards
          const L = CONFIG.tokenRules.linkToken;
          const nos = new Set(this.tokensOn(t).filter((o) => this.effectOf(o) === L).map((o) => o.value));
          if (nos.size) { this.removeTokens(s.tokens.filter((o) => this.effectOf(o) === L && nos.has(o.value))); cut.push('its Links'); }
          // a Leviathan and its Body Segments: they stand apart from now on (a Segment no longer passes hits on, nor does the Leviathan reach past it)
          const segs = s.field.filter((c) => c.summonedBy === t.id && !!this.cards.byNumber.get(c.num)?.traits.some((x) => /body segment/i.test(x)));
          const isSeg = !!t.summonedBy && !!this.cards.byNumber.get(t.num)?.traits.some((x) => /body segment/i.test(x));
          for (const c of [...segs, ...(isSeg ? [t] : [])]) delete c.summonedBy;
          if (segs.length || isSeg) cut.push(isSeg ? 'its Leviathan' : 'its Body Segments');
          // a Soul Collector: the Troops it holds go back to their owners' discard piles, and it loses their mechanics
          const held = s.field.filter((c) => c.heldBy === t.id);
          for (const h of held) { this.removeField(h.id); this.insertAt(s.players[h.owner].discard, this.rehide(h), 'top'); }
          if (t.borrowed?.length) { for (const n2 of t.borrowed.slice(held.length)) this.insertAt(s.players[t.owner].discard, { id: this.newId('c'), num: n2, spawned: false }, 'top'); t.borrowed = []; cut.push('the Troops it holds'); }
          // a unit riding with another (a Zombie Horse under its Knight): set apart, a unit of its own in the lane
          const riders = s.field.filter((c) => c.attachedTo === t.id);
          const rode = !!t.attachedTo;
          for (const c of [...riders, ...(rode ? [t] : [])]) { delete c.attachedTo; delete c.attachOrder; delete c.summonedBy; }
          if (riders.length) cut.push('what rides with it');
          if (rode) cut.push('the unit it rode with');
          // Symbiotic Vines (and any other Spell joining it to another Unit): that Spell ends
          for (const v of s.field.filter((c) => c.pair?.includes(t.id) && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.some((e2) => e2.words[0] === 'vines'))) { this.endCard(v, `${names([t])} was severed from it`); cut.push(this.cardName(v.num)); }
          this.layout(this.owners());
          say(cut.length ? `${names([t])} is cut off from ${cut.join(', ')}.` : `${names([t])} has nothing joined to it.`);
        }
        return;
      }
      case 'recall': {
        // Recall Rift: one of its player's Units goes back to their hand, cleared of everything on it (a summoned unit just goes)
        const p = s.players[seat];
        for (const t of who('it')) {
          const units = t.swarm ? this.swarmOf(t) : [t];
          const real = units.filter((u) => !u.spawned && !u.spanOf);
          const copies = real.flatMap((u) => this.copiesOf(u));
          // a pink and blue swirl winds up over it as the Rift's light reaches it, and it is gone (the table keeps it in view
          // until the swirl is done, and the Spell until after that: see holdFor in TableView)
          for (const u of units) this.look('recall-swirl', u);
          for (const r of s.field.filter((c) => units.some((u) => c.attachedTo === u.id))) { this.clearTokensOn(r); this.removeField(r.id); }
          for (const u of units) { delete u.intercede; this.clearTokensOn(u); this.removeField(u.id); this.closeUp(u); }
          let back = 0;
          for (const c of copies) { if (p.hand.length < CONFIG.handLimit) { p.hand.push(this.rehide(c)); back++; } else this.insertAt(p.discard, this.rehide(c), 'top'); }
          say(copies.length ? `${names([t])} returns to ${p.name}’s hand${back < copies.length ? ` (${copies.length - back} to the discard pile: the hand is full)` : ''}.` : `${names([t])} was summoned: it simply leaves the field.`);
        }
        this.layout(this.owners());
        return;
      }
      case 'reactor': {
        // Star Reactor: twice the SP Decay on it to every Unit on both sides, half of that to both Emperors
        const decay = this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.tokenRules.spDecayToken).reduce((n2, t) => n2 + t.value, 0);
        const n = 2 * decay;
        if (n <= 0) { say('it had no SP Decay on it: nothing happens.'); return; }
        const notes: string[] = [];
        const units = s.field.filter((c) => c !== card && c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
        const emps = s.field.filter((c) => c.faceUp && isEmperor(this.cards.byNumber.get(c.num)!) && this.healthLeft(c) > 0);
        for (const u of [...units, ...emps]) this.fx('spell', card.id, undefined, { target: u.id, from: { x: card.x, z: card.z }, tint: 'astral', seat });
        let total = 0;
        for (const u of units) total += this.landHit(seat, u, n, { splash: true, from: card }, notes);
        for (const em of emps) total += this.landHit(seat, em, Math.round(n / 2), { from: card }, notes);
        this.markAgain = true;
        say(`it bursts: ${n} damage to every Unit and ${Math.round(n / 2)} to each Emperor (${total} in all)${notes.length ? `. ${notes.join('; ')}` : ''}.`);
        return;
      }
      case 'warlord': {
        // Vaelith's Warlord's Wrath: 2 damage to the Unit chosen (once for each Dark Gerzite: see repeat)
        const notes: string[] = [];
        for (const t of who('it')) { this.fx('spell', card.id, undefined, { target: t.id, from: { x: card.x, z: card.z }, tint: this.affOf(card)[0] ?? 'astral', seat, effect: 'strike' }); const got = this.landHit(seat, t, 2, { from: card }, notes); say(`${got} damage to ${names([t])}${notes.length ? ` (${notes.join('; ')})` : ''}.`); }
        return;
      }
      case 'conquest': {
        // Vaelith's Galactic Conquest: 2 to itself, 4 to every opposing Unit and Emperor; an opposing Emperor with a
        // Minion takes 8 instead, and its Minion takes those 8 over the Emperor–Minion link (it isn't hit for 4 as well)
        const notes: string[] = [];
        const tint = this.affOf(card)[0] ?? 'astral';
        const hitIt = (t: FieldCard, n: number) => { this.fx('spell', card.id, undefined, { target: t.id, from: { x: card.x, z: card.z }, tint, seat, effect: 'strike' }); return this.landHit(seat, t, n, { from: card }, notes); };
        const self = this.landHit(seat, card, 2, { status: true, from: card }, notes);
        const emps = s.field.filter((c) => c.faceUp && this.opposed(seat, c.owner) && isEmperor(this.cards.byNumber.get(c.num)!) && this.healthLeft(c) > 0);
        const minionOf = (em: FieldCard) => this.linkPartners(em).filter((p) => !p.via && this.isUnit(this.cards.byNumber.get(p.card.num))).map((p) => p.card);
        const minions = new Set(emps.flatMap(minionOf).map((c) => c.id));
        const units = s.field.filter((c) => c.faceUp && this.opposed(seat, c.owner) && this.isUnit(this.cards.byNumber.get(c.num)) && !minions.has(c.id) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
        let total = 0;
        for (const u of units) total += hitIt(u, 4);
        for (const em of emps) total += hitIt(em, minionOf(em).length ? 8 : 4);
        this.markAgain = true;
        say(`${self} damage to itself, and ${total} to its opponents${notes.length ? ` (${notes.join('; ')})` : ''}.`);
        return;
      }
      case 'aim': {
        // only chooses the card the rules after it speak of ("on it")
        return;
      }
      case 'intercede': {
        for (const t of who('it')) { card.intercede = t.id; this.fx('link', card.id, undefined, { target: t.id }); say(`if ${names([t])} would be destroyed, ${name} is destroyed instead.`); }
        return;
      }
      case 'end_summoner': {
        // the card that summoned this one ends now (a Spirit Tree destroyed early: its Spell ends)
        const by = card.summonedBy ? this.findField(card.summonedBy) : undefined;
        if (by && !this.isExpired(by)) this.endCard(by, `its ${name} was destroyed`);
        return;
      }
      case 'hold_it': {
        ctx.target = ctx.it;
        return;
      }
      case 'host': {
        if (ctx.it) card.host = ctx.it.id;
        return;
      }
      case 'cloak': {
        for (const t of who('it')) { t.cloakBy = card.id; say(`${names([t])} can’t be attacked, and doesn’t fill its lane, until it attacks or is damaged.`); }
        return;
      }
      case 'taunt': {
        for (const t of who('it')) { t.tauntAt = this.turnStamp() + 1; this.look('taunt-cast', t); say(`during the next turn, ${names([t])} is the only card that can be attacked.`); }
        return;
      }
      case 'unlink': {
        const L2 = CONFIG.tokenRules.linkToken;
        this.removeTokens(s.tokens.filter((t) => this.effectOf(t) === L2 && t.value === card.mode));
        return;
      }
      case 'cleanse': {
        const got = who('all_cards');
        for (const t of got) {
          const had = this.tokensOn(t).filter((o) => this.tokenDb.byId.get(o.tokenId)?.category === 'status' && !o.ability);
          if (had.some((o) => this.effectOf(o) === CONFIG.tokenRules.entrenchedToken) && t.rot === CONFIG.arrival.entrenchedRotation) t.rot = 0;
          if (had.length) this.spellAim(card, [t], false); // (the Bell's light reaches each card it cleanses)
          this.removeTokens(had);
        }
        say(`every status effect is removed from ${got.length} card${got.length === 1 ? '' : 's'}.`);
        return;
      }
      case 'recycle': {
        const p = s.players[seat];
        const top = p.discard.shift();
        if (!top) return;
        this.insertAt(p.deck, this.rehide(top), 'top');
        // (both players see it: a sheen wipes over the discard pile, then the card flies to the deck in a white glow — Glace Glass)
        this.fx('pileMove', '', undefined, { seat, effect: 'discard', tint: 'glace-sheen', num: top.num });
        say(`${this.cardName(top.num)} goes from the top of ${p.name}’s discard pile to the top of their deck.`);
        return;
      }
      case 'clear_spells': {
        const gone = s.field.filter((c) => c !== card && !!this.cards.byNumber.get(c.num)?.types.includes('spell'));
        // a pulse spreads out from All Stop, and each Spell bursts apart as it reaches it
        if (CONFIG.looks.enabled) {
          this.look('allstop-pulse', card);
          for (const c of gone) this.fx('sacrifice', c.id, undefined, { seat: c.owner, effect: 'allstop-burst', at: { x: card.x, z: card.z } });
        }
        for (const c of gone) this.discardFromPlay(c);
        say(`every other Spell in play is discarded (${gone.length}).`);
        return;
      }
      case 'rehand_foe': {
        for (let i = 0; i < s.players.length; i++) {
          if (!this.opposed(seat, i as Seat)) continue;
          // ("less 1": they draw that many fewer than they had: Trickster Mage)
          const p = s.players[i]; const n2 = Math.max(0, p.hand.length - (w.includes('less') ? num(w[w.indexOf('less') + 1], 0) : 0));
          // its light flies toward that player's hand
          this.fx('spell', card.id, undefined, { at: seatPoint(i as Seat, { x: 0, z: CONFIG.zones.opponentHandRow.z }), from: { x: card.x, z: card.z }, tint: this.affOf(card)[0] ?? 'magical', seat });
          p.deck.push(...p.hand.splice(0).map((c) => this.rehide(c)));
          this.shuffle(p.deck); p.shuffles.deck++;
          for (let k = 0; k < n2 && this.canDraw(p); k++) p.hand.push(this.rehide(this.drawTop(p)!));
          this.fx('toHand', '', undefined, { effect: 'deck', seat: i as Seat, count: n2 });
          say(`${p.name} shuffles their hand into their deck and draws ${n2} card${n2 === 1 ? '' : 's'}.`);
        }
        return;
      }
      case 'aff': {
        const pick2 = w[(ctx.chose ?? 1) - 1];
        for (const t of who('it')) {
          this.removeTokens(this.tokensOn(t).filter((o) => this.tokenDb.byId.get(o.tokenId)?.category === 'affiliation'));
          if (pick2 && this.giveStatus(seat, t, `aff-${pick2}`, null, null)) say(`${names([t])} is now ${pick2[0].toUpperCase()}${pick2.slice(1)}.`);
        }
        return;
      }
      case 'peek_hand': {
        // Questioning: its player sees their opponent's hand, the top card of their deck and what lies face-down
        for (let i = 0; i < s.players.length; i++) {
          if (!this.opposed(seat, i as Seat)) continue;
          const p = s.players[i];
          s.reveals[seat] = { from: i as Seat, cards: p.hand.map((c) => ({ ...c })) };
          const top = p.deck.find((c) => !this.jumper(c));
          const down = s.field.filter((c) => c.owner === i && !c.faceUp).map((c) => this.cardName(c.num));
          this.fx('peek', '', undefined, { effect: 'deck', seat: i as Seat, count: 1 }); // (both players see a look taken at the top of the deck)
          this.fx('status', card.id, undefined, { effect: 'notice', seat, text: `${p.name}’s top card: ${top ? this.cardName(top.num) : 'none'}. Face-down: ${down.length ? down.join(', ') : 'nothing'}.` });
        }
        this.log(`${name}: ${s.players[seat].name} looks at their opponent’s hand, the top of their deck and their face-down cards.`, seat);
        return;
      }
      case 'toss': {
        // a card from the hand is discarded, and its cost added to what follows (Sacrifice)
        const p = s.players[seat];
        const kinds = w.filter((x) => x !== 'may');
        const fits = p.hand.filter((c) => !kinds.length || kinds.some((k) => this.cards.byNumber.get(c.num)?.types.includes(k)));
        if (!fits.length) { ctx.tossed = false; return; }
        ctx.tossed = true;
        const cheapest = [...fits].sort((a, b) => (this.cards.byNumber.get(a.num)?.cost ?? 0) - (this.cards.byNumber.get(b.num)?.cost ?? 0))[0];
        this.tossCard(seat, name, cheapest.id, ctx); // (a bot's choice; a player is asked instead: see runEffects)
        return;
      }
      case 'feast': {
        // Finishing Feast: instances of 1 damage on one Unit; once it is destroyed, a Faerie for it and one for each instance left over
        const t = who('it')[0];
        const unit = this.cards.spawnOnly.find((d) => d.name.toLowerCase() === 'faerie');
        if (!t) return;
        const notes: string[] = [];
        let left = num(w[0], 9); let dealt = 0;
        while (left > 0 && s.field.includes(t) && !this.hasOn(t, CONFIG.turn.lifetime.expiredToken)) { left--; this.spellAim(card, [t], true); dealt += this.landHit(seat, t, 1, { pierce: true, from: card, spell: true }, notes); this.settleFallen(); }
        const dead = !s.field.includes(t) || this.hasOn(t, CONFIG.turn.lifetime.expiredToken);
        say(`${dealt} damage to ${names([t])} in instances of 1${dead ? `: destroyed, with ${left} left over` : ''}.`);
        const L = this.lanesFor(card, ctx);
        const lane = L ? laneOrder().find((l) => L.names.includes(l.name)) : undefined;
        if (dead && unit && L && lane) {
          const owned = this.owners();
          for (let k = 0; k < 1 + left; k++) {
            const u: FieldCard = { ...this.held(unit.number, true), owner: seat, x: lane.x * L.side, z: lane.z * L.side, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, summonedBy: card.id, bornIn: s.turn };
            s.field.push(u); this.addCount(u); this.addStartCounters(u); this.bring(u); this.fx('play', u.id); this.layout(owned);
          }
          say(`${1 + left} ${unit.name}${left ? 's' : ''} appear in ${lane.name}.`);
        }
        return;
      }
      case 'knives': {
        // 1000 Knives: 1 damage to every opposing Unit and their Emperor, again and again, until a Shield is lost or a Unit destroyed; past 6 rounds, 1 to your own Emperor each time
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        const mine = this.whoOf(card, 'emperor', ctx)[0];
        let rounds = 0; let stop = false;
        while (!stop && rounds < 40) {
          const foes = [...this.whoOf(card, 'foes', ctx), ...this.whoOf(card, 'foe_emperor', ctx)];
          if (!foes.length) break;
          rounds++;
          const shields = () => foes.reduce((a, t) => a + (s.field.includes(t) ? this.valueOn(t, 'shield') + this.valueOn(t, 'overshield') : 0), 0);
          const before = shields(); const notes: string[] = [];
          this.spellAim(card, foes, true); // (each round of knives flies on its own)
          for (const t of foes) this.landHit(seat, t, 1, { from: card, spell: true }, notes);
          if (rounds > 6 && mine && damageId) this.bufferedDamage(mine, damageId, 1, null);
          const lost = shields() < before;
          this.settleFallen();
          stop = lost || foes.some((t) => !s.field.includes(t) || this.hasOn(t, CONFIG.turn.lifetime.expiredToken));
        }
        say(`${rounds} round${rounds === 1 ? '' : 's'} of 1 damage to every opposing Unit and their Emperor${rounds > 6 ? `, and ${rounds - 6} damage to your own Emperor` : ''}.`);
        return;
      }
      case 'blob': {
        // Bouncing Magma Blob: 6 to the Unit chosen, then 1 less each time to a Unit beside the last that it hasn't hit; the last one is Burned
        let cur = who('it')[0]; let n2 = num(w[0], 6);
        const done2: FieldCard[] = []; const notes: string[] = [];
        const laneIx = (c: FieldCard) => { const t = this.standsAt(c); return t && slotKind(t.slot.name) === 'Lane' ? laneOrder().map((slot) => ({ slot, x: slot.x * t.side })).sort((a, b) => a.x - b.x).findIndex((l) => l.slot === t.slot) : -9; };
        while (cur && n2 > 0) {
          this.spellAim(card, [cur], true);
          this.landHit(seat, cur, n2, { pierce: true, from: card, spell: true }, notes);
          done2.push(cur); n2--;
          const here = cur;
          cur = this.whoOf(card, 'foes', ctx).filter((u) => !done2.includes(u) && Math.abs(laneIx(u) - laneIx(here)) <= 1).sort((a, b) => Math.abs(laneIx(a) - laneIx(here)) - Math.abs(laneIx(b) - laneIx(here)))[0];
        }
        const last = done2.at(-1);
        if (last && s.field.includes(last)) this.giveStatus(seat, last, 'burn', null, null);
        say(`it bounces across ${names(done2)}${last ? `, and Burns ${names([last])}` : ''}.`);
        return;
      }
      case 'devilcoin': {
        const roll = this.rollDie(); const heads = roll % 2 === 0;
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat));
        const gz = this.tokenDb.byId.get(CONFIG.tokenRules.fractalGives);
        const drawFor = (who2: Seat, k: number) => { const p = s.players[who2]; let d = 0; for (let i = 0; i < k && this.canDraw(p) && p.hand.length < CONFIG.handLimit; i++) { const c = this.drawTop(p)!; p.hand.push(this.rehide(c)); d++; } if (d) this.fx('toHand', '', undefined, { effect: 'deck', seat: who2, count: d }); };
        if (heads) {
          let stolen = 0;
          let from: { x: number; z: number } | null = null;
          for (let k = 0; k < 2 && foeSeat >= 0 && gz; k++) { const theirs = this.barTokens(foeSeat as Seat, 'gerzite'); if (theirs.length && this.placeBarToken(seat, gz)) { from ??= { x: theirs.at(-1)!.x, z: theirs.at(-1)!.z }; this.removeTokens(theirs.slice(-1)); stolen++; } }
          // (the stolen Gerzite flies across to its new bar, burning)
          if (from && CONFIG.looks.gainFly[name]) this.look(CONFIG.looks.gainFly[name], null, { seat, at: from, count: stolen });
          drawFor(seat, 1);
          if (CONFIG.looks.enabled) this.look('coin-win-devil', card); // a red pulse on the coin
          say(`heads (a ${roll}): ${s.players[seat].name} steals ${stolen} Gerzite and draws a card.`);
        } else {
          if (foeSeat >= 0) drawFor(foeSeat as Seat, 2);
          if (CONFIG.looks.enabled) this.fx('sacrifice', card.id, undefined, { seat, effect: 'coin-shatter' }); // it rises and shatters
          say(`tails (a ${roll}): ${foeSeat >= 0 ? s.players[foeSeat].name : 'their opponent'} draws two cards.`);
        }
        return;
      }
      case 'spend': {
        // Forsaken Conversion: 1 to 3 Gerzite (2 damage each) or Dark Gerzite (3 each), as chosen; what it bought is the damage of what follows
        const k = ctx.chose ?? 1;
        const dark = k > 3; const n = ((k - 1) % 3) + 1;
        if ((dark && this.darkHeld(seat, n)) || !this.payBar(seat, n, dark)) { ctx.amount = 0; say(`not enough ${dark ? 'Dark Gerzite' : 'Gerzite'} for that: nothing happens.`); return; }
        ctx.amount = n * (dark ? 3 : 2);
        say(`${s.players[seat].name} spends ${n} ${dark ? 'Dark Gerzite' : 'Gerzite'}: ${ctx.amount} damage.`);
        return;
      }
      case 'spellward': {
        // Impervious Shield: Spells can't damage it during the opponent's next turn (kept for when the table deals Spell damage)
        const got = who('self');
        for (const t of got) { t.spellWardAt = this.turnStamp() + 1; this.look('royal-ward-cast', t); } // (a regal gold flourish as it is given)
        if (got.length) say(`${names(got)} can’t be damaged by Spells during the next turn.`);
        return;
      }
      case 'biome_off': {
        s.biomeOff = { side: this.sideOfCard(card), at: this.turnStamp() };
        this.look('map-cast', card);
        for (const b of s.field.filter((c) => c.faceUp && !!this.cards.byNumber.get(c.num)?.types.includes('biome') && !this.isExpired(c))) this.look('map-cast', b);
        say('the Biome in play does nothing to its side of the field this turn.');
        return;
      }
      case 'lava': {
        // Molten Lava River: every player discards a card, from their hand or the top of their deck
        for (let i = 0; i < s.players.length; i++) {
          const p = s.players[i]; const who2 = i as Seat;
          if (p.out) continue;
          if (s.bots?.includes(who2) || !p.hand.length) { this.lavaDiscard(who2, name); continue; }
          (s.picks ??= []).push({ id: s.nextId++, seat: who2, name, text: `${name}: discard a card`, options: [], choices: ['A card from your hand', 'The top card of your deck'], lava: 'ask' });
        }
        return;
      }
      case 'mark_look': {
        // "mark_look coat on it": the table shows a look of its own on the card while what the ability gave it lasts
        // (Molten Coat: while its Shield holds; Burning Necrosis: while its Bleed or Weakness does), and a flourish now
        for (const t of who(e.on || 'it')) { (t.marks ??= {})[w[0]] = card.owner; this.look(`${w[0]}-cast`, t); }
        return;
      }
      case 'cooldown': {
        // one of the opposing Emperor's abilities that is on cooldown waits that much longer
        const emp = this.whoOf(card, 'foe_emperor', ctx)[0];
        const timer = emp ? s.tokens.filter((t) => t.ability?.card === emp.id && this.abilityTokenKind(t) === 'cooldown' && t.value > 0).sort((a, b) => a.value - b.value)[0] : undefined;
        if (!timer) { say('no opposing ability is on cooldown: nothing happens.'); return; }
        timer.value += num(w[0]);
        this.look('heat-flare', emp!); // (a burst of heat on that Emperor; its timer flares too)
        if (CONFIG.looks.enabled) this.fx('look', '', undefined, { effect: 'heat-timer', at: { x: timer.x, z: timer.z } });
        say(`an ability of ${names([emp!])} is on cooldown for ${num(w[0])} more turn${num(w[0]) === 1 ? '' : 's'}.`);
        return;
      }
      case 'tierup': {
        const got = who('target');
        for (const t of got) { t.tierBoost = 1; if (w.includes('turn')) t.tierBoostAt = this.turnStamp(); else delete t.tierBoostAt; this.refreshTierToken(t); }
        if (got.length) say(`${names(got)} ${got.length === 1 ? 'is' : 'are'} a tier higher${w.includes('turn') ? ' for the rest of this turn' : ''}.`);
        return;
      }
      case 'veil': {
        if (e.on === 'in_lanes') {
          // Protective Petals: the lane chosen (marked on the card) is guarded for as long as the Spell is active
          const L = this.lanesFor(card, ctx);
          if (L) card.lanes = { side: L.side, names: [...L.names] };
          card.veilLanes = true;
          if (card.lanes) say(`${card.lanes.names.join(' and ')} can’t be targeted or damaged by ${s.players[card.owner].name}’s opponents while it lasts.`);
          return;
        }
        const got = who('self');
        for (const t of got) { t.veilAt = this.turnStamp() + 1; this.look('veil-cast', t); }
        if (got.length) say(`${names(got)} can’t be attacked during the next turn.`);
        return;
      }
      case 'exit': {
        card.negating = num(w[0], 2);
        this.look('exit-cast', card);
        say('until this ability is off cooldown, only Pierce damage lands on it.');
        return;
      }
      case 'ambush': {
        for (const t of who('it')) this.giveAmbush(t);
        return;
      }
      case 'mode': {
        card.mode = ctx.chose ?? 1;
        return;
      }
      case 'snare': {
        // damage counters laid in one of the opponent's lanes: whatever is next put down there has them on it
        const lane = laneOrder().find((l) => l.name === ctx.lane);
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        if (!lane || !damageId) return;
        // "snare 3 2": the next Unit summoned into it takes 3 hits of 2 damage
        const foe = -this.sideOfCard(card) as 1 | -1;
        const hits = num(w[0], 3), amount = num(w[1], 1);
        s.snares = [...(s.snares ?? []).filter((n) => !(n.lane === lane.name && n.side === foe)), { lane: lane.name, side: foe, by: seat, from: card.id, hits, amount }];
        say(`a Thorny Snare lies in wait in ${lane.name}: the next Unit summoned there takes ${hits} hits of ${amount} damage.`);
        return;
      }
      case 'raise': {
        // Beyond Death: if what it hit was destroyed, a unit of yours rises where it stood
        const it = ctx.it;
        const unitName = w.filter((x) => x !== 'direct').join(' ');
        const unit = this.cards.spawnOnly.find((d) => d.name.toLowerCase() === unitName);
        this.settleFallen();
        if (!it || !unit || (s.field.includes(it) && !this.hasOn(it, CONFIG.turn.lifetime.expiredToken))) return;
        const owned = this.owners();
        const u: FieldCard = { ...this.held(unit.number, true), owner: seat, x: it.x, z: it.z, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, summonedBy: card.id, bornIn: s.turn };
        s.field.push(u);
        this.addCount(u); this.addStartCounters(u); this.bring(u);
        if (w.includes('direct')) this.giveStatus(seat, u, 'direct', null, null);
        this.fx('play', u.id);
        this.layout(owned);
        say(`a ${unit.name} rises where ${names([it])} fell.`);
        return;
      }
      case 'resurrect': {
        // Dead Rising: the Troop most recently destroyed comes back in one of your lanes, Undead, for half its cost, with Decay equal to its cost
        const last = s.lastFallen;
        const lane = laneOrder().find((l) => l.name === ctx.lane);
        const lDef = last ? this.cards.byNumber.get(last.num) : undefined;
        if (!last || !lane || !lDef) { say('no Troop has been destroyed yet: nothing happens.'); return; }
        const pile = s.players[last.owner].discard;
        const i = pile.findIndex((c) => c.num === last.num);
        const cost = Math.round((lDef.cost ?? 0) / 2);
        if (i < 0) { say(`${lDef.name} is no longer in a discard pile: nothing happens.`); return; }
        if (!this.payBar(seat, cost, false)) { say(`bringing ${lDef.name} back costs ${cost} Gerzite: not enough, so nothing happens.`); return; }
        const [back] = pile.splice(i, 1);
        const side = this.sideOfCard(card);
        const owned = this.owners();
        const u: FieldCard = { ...back, owner: seat, x: lane.x * side, z: lane.z * side, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, bornIn: s.turn };
        s.field.push(u);
        this.addCount(u); this.addStartCounters(u); this.bring(u);
        this.layout(owned);
        this.giveStatus(seat, u, 'aff-undead', null, null);
        if ((lDef.cost ?? 0) > 0) this.giveStatus(seat, u, 'decay', lDef.cost!, null);
        this.fx('play', u.id);
        delete s.lastFallen;
        say(`${lDef.name} rises in ${lane.name} for ${cost} Gerzite: Undead, with ${lDef.cost ?? 0} Decay.`);
        return;
      }
      case 'copy': {
        // Spectral Clone: a copy of one of your Units in its lane (the clone the table already knows how to make)
        const it = who('it')[0];
        const by = w.join(' ').replace(/_/g, ' ');
        const src = CONFIG.cloneSources.find((c) => c.name.toLowerCase() === by);
        if (!it || !src) return;
        const before = new Set(s.field.map((c) => c.id));
        // (a card effect makes it, so the rule against making clones by hand in a game doesn't apply)
        this.byEffect = true;
        let refused: string | null;
        try { refused = this.run(seat, s.players[seat], { type: 'clone', id: it.id, by: src.name }); } finally { this.byEffect = false; }
        const made = s.field.find((c) => !before.has(c.id));
        if (refused || !made) { say(refused ?? 'no copy could be made.'); return; }
        this.giveStatus(seat, made, 'aff-spectral', null, null);
        return;
      }
      case 'bank': {
        const bdef = this.tokenDb.list.find((d) => d.effect === 'life-bank');
        if (!bdef) return;
        const side = sideOf(card.owner);
        s.tokens.push({ id: this.newId('t'), tokenId: bdef.id, x: card.x + 5.2 * side, z: card.z, value: num(w[0], 3), layer: s.nextLayer++, by: seat });
        say(`a life bank of ${num(w[0], 3)} health: damage to your Units is paid back from it, and it gains 2 whenever a Unit is destroyed.`);
        return;
      }
      case 'crypt': {
        // Ancient Crypt: whoever draws a Duned card shows it and draws another; if that one is Duned too, it is shown and they gain 1 Gerzite
        const who2 = ctx.actor; const first = this.cards.byNumber.get(ctx.amount ?? -1);
        if (who2 === undefined || !first?.affiliations.includes(w[0] ?? 'duned')) return;
        const p = s.players[who2];
        if (p.hand.length >= CONFIG.handLimit || !this.canDraw(p)) return;
        this.fx('revealCard', '', undefined, { seat: who2, num: first.number, text: `${p.name} reveals ${first.name} (${name}) and draws another card.` });
        const got = this.drawTop(p)!;
        p.hand.push(this.rehide(got));
        this.fx('toHand', '', undefined, { effect: 'deck', seat: who2, count: 1 });
        const second = this.cards.byNumber.get(got.num);
        if (second?.affiliations.includes(w[0] ?? 'duned')) {
          this.gain(who2, CONFIG.tokenRules.fractalGives, 1);
          this.fx('revealCard', '', undefined, { seat: who2, num: got.num, text: `${p.name} reveals ${second.name} as well, and gains 1 Gerzite.` });
        }
        this.log(`${name}: ${p.name} reveals ${first.name} and draws another card${second?.affiliations.includes(w[0] ?? 'duned') ? `, ${second.name}: 1 Gerzite` : ''}.`, who2);
        this.crypting = true;
        try { this.drew(who2, got.num); } finally { this.crypting = false; }
        return;
      }
      case 'templar': {
        // Cursed Templar: the owner of a destroyed Unit draws; a Spectral or Duned card is shown and kept, any other goes under their deck and costs their Emperor 1
        const it = ctx.it; if (!it || !this.isUnit(this.cards.byNumber.get(it.num))) return;
        const p = s.players[it.owner];
        if (!this.canDraw(p)) return;
        const got = this.drawTop(p)!;
        const d = this.cards.byNumber.get(got.num);
        if (d?.affiliations.some((a) => ['spectral', 'duned'].includes(a)) && p.hand.length < CONFIG.handLimit) {
          p.hand.push(this.rehide(got));
          this.fx('toHand', '', undefined, { effect: 'deck', seat: it.owner, count: 1 });
          const text = `${name}: ${p.name} draws ${d.name} and keeps it.`;
          this.fx('revealCard', '', undefined, { seat: it.owner, num: got.num, text });
          this.log(text, it.owner);
        } else {
          this.insertAt(p.deck, this.rehide(got), 'bottom');
          this.damageEmperor(it.owner, 1);
          this.log(`${name}: ${p.name} draws a card that is neither Spectral nor Duned: it goes under their deck, and their Emperor takes 1 damage.`, it.owner);
        }
        return;
      }
      case 'tundra': {
        // Tundra Basin: as a player's turn ends, each of their untapped Units and their Emperor takes 1 damage, or (Frigid) gains 1 Buffer
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        const mine = s.field.filter((c) => c.faceUp && c.owner === ctx.actor && c.rot % 2 === 0 && (this.isUnit(this.cards.byNumber.get(c.num)) || !!this.cards.byNumber.get(c.num)?.types.includes('emperor')) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
        const cold: string[] = []; const warm: string[] = [];
        for (const c of mine) {
          if (this.affOf(c).includes('frigid')) { if (this.giveStatus(seat, c, 'buffer', 1, null)) { (card.gave ??= {})[`buffer:${c.id}`] = (card.gave?.[`buffer:${c.id}`] ?? 0) + 1; warm.push(this.cardName(c.num)); } }
          else if (damageId) { this.bufferedDamage(c, damageId, 1, null); cold.push(this.cardName(c.num)); }
        }
        if (cold.length || warm.length) say(`${cold.length ? `1 damage to ${cold.join(', ')}` : ''}${cold.length && warm.length ? '; ' : ''}${warm.length ? `1 Buffer to ${warm.join(', ')}` : ''}.`);
        return;
      }
      case 'ungive': {
        // what this card gave of a status is taken back (it was destroyed, or its turns are over)
        const tdef = this.tokenDb.list.find((d) => d.id === w[0]) ?? this.tokenDb.list.find((d) => d.effect === w[0]);
        if (!tdef) return;
        for (const [k, n] of Object.entries(card.gave ?? {})) {
          const [tok, id] = k.split(':');
          const on = tok === tdef.id ? this.findField(id) : undefined;
          let left = n;
          for (const t of on ? this.tokensOn(on).filter((o) => o.tokenId === tdef.id) : []) { if (!tdef.counter) { this.removeTokens([t]); break; } const take = Math.min(left, t.value); t.value -= take; left -= take; if (t.value <= 0) this.removeTokens([t]); }
          if (tok === tdef.id) delete card.gave![k];
        }
        say(`the ${tdef.name} it gave is removed.`);
        return;
      }
      case 'lose': {
        // a swarm loses units by losing its cards (Flamebound Synergy destroying one of its units)
        if (w[0] === CONFIG.tokenRules.countToken && card.swarm) {
          const units = this.swarmOf(card).filter((m) => !m.atZero);
          const gone = units.filter((m) => m !== card).slice(0, num(w[1]));
          if (gone.length < num(w[1]) && units.length > gone.length) gone.push(card);
          for (const m of gone.slice(0, Math.max(0, units.length - 1))) { const keep = units.find((u) => !gone.includes(u)); if (keep && m.merged.length) { keep.merged.push(...m.merged); m.merged = []; } this.clearTokensOn(m); this.removeField(m.id); this.closeUp(m); }
          if (gone.length) for (const m of this.swarmOf(card)) this.fire(m, 'unit_lost');
          return;
        }
        let left = num(w[1]);
        for (const t of this.tokensOn(card).filter((o) => this.effectOf(o) === w[0])) {
          const take = Math.min(left, t.value);
          t.value -= take; left -= take;
          if (t.value <= 0) this.removeTokens([t]);
        }
        if (w[0] === CONFIG.tokenRules.countToken && left < num(w[1])) this.fire(card, 'unit_lost'); // (one of its units is gone)
        return;
      }
      case 'overload': {
        if (this.valueOn(card, 'damage-boost') < num(w[0], 3)) return;
        this.removeTokens(this.tokensOn(card).filter((t) => this.effectOf(t) === 'damage-boost'));
        const emp = this.whoOf(card, 'foe_emperor', ctx)[0];
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        if (emp && damageId) this.bufferedDamage(emp, damageId, num(w[1], 3), seat);
        say(`its Damage Boost reached ${num(w[0], 3)}: ${emp ? `${num(w[1], 3)} damage to ${names([emp])}` : 'no Emperor to hit'}, and the Damage Boost is gone.`);
        return;
      }
      case 'link': {
        // a pair of Link counters with a number no other Link has: the two cards share damage and statuses from then on
        const to = who('target')[0];
        const ldef = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.linkToken);
        const first = w.includes('pair') ? ctx.target : card; // ("link pair": the two cards chosen, not the card itself — Voodoo Magic)
        if (!to || !ldef || !first || first === to) return;
        const no = 1 + Math.max(0, ...s.tokens.filter((o) => this.effectOf(o) === ldef.effect).map((o) => o.value));
        if (w.includes('pair')) card.mode = no;
        if (CONFIG.looks.linkBeam[name]) this.look(CONFIG.looks.linkBeam[name], first, { target: to.id }); // (Voodoo Magic: a purple beam joins the two)
        for (const c of [first, to]) {
          const side = sideOf(c.owner); const sc = c.scale ?? 1;
          s.tokens.push({ id: this.newId('t'), tokenId: ldef.id, x: c.x + 1.8 * side * sc, z: c.z - 2.4 * side * sc, value: no, layer: s.nextLayer++, by: seat });
        }
        say(w.includes('pair') ? `${names([first])} and ${names([to])} are linked (Link ${no}): damage to one is dealt to the other.` : `it is linked to ${names([to])} (Link ${no}): they share damage and status effects, and attack as one.`);
        return;
      }
      case 'split': {
        // Soul Splinterer: what it hurt becomes two Units, each with half of the health it has left and half its damage
        const t = who('target')[0];
        const tDef = t ? this.cards.byNumber.get(t.num) : undefined;
        if (!t || !tDef || !s.field.includes(t) || this.uncloneable(t.num) || this.hasOn(t, CONFIG.turn.lifetime.expiredToken)) return;
        const left = this.healthLeft(t);
        if (left <= 0) return;
        const half = Math.ceil(left / 2);
        const dmg = Math.ceil(this.attackOf(t, tDef, true).per / 2);
        const owned = this.owners();
        this.removeTokens(this.tokensOn(t).filter((o) => countsAgainstHealth(this.effectOf(o)) || ['overheal', CONFIG.tokenRules.healthToken].includes(this.effectOf(o))));
        t.clone = { by: name, health: half, damage: dmg };
        const twin: FieldCard = { ...this.held(t.num, true), owner: t.owner, x: t.x, z: t.z, rot: t.rot, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, clone: { by: name, health: half, damage: dmg }, bornIn: s.turn };
        if (t.form) twin.form = t.form;
        s.field.push(twin);
        this.fx('play', twin.id);
        this.layout(owned);
        say(`${tDef.name} is split in two: each has ${half} health and ${dmg} damage.`);
        return;
      }
      case 'clone': {
        // Twilight Seedling: a Twilight Beast with as much health and damage as the Seedling had grown to; its Emperor heals half of that
        const def = this.cards.byNumber.get(card.num);
        const unit = this.cards.spawnOnly.find((d) => d.name.toLowerCase() === w.join(' '));
        if (!def || !unit) return;
        const grown = (this.fullForm(card, def).health ?? 0) + this.gainedHealth(this.tokensOn(card));
        const owned = this.owners();
        const u: FieldCard = { ...this.held(unit.number, true), owner: card.owner, x: card.x, z: card.z, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, clone: { by: name, health: grown, damage: grown }, summonedBy: card.id, bornIn: s.turn };
        s.field.push(u);
        this.fx('play', u.id);
        this.layout(owned);
        const emp = this.emperorOn(this.sideOfCard(card), card);
        const heal = Math.floor(grown / 2);
        if (emp && heal > 0) {
          let leftH = heal;
          for (const d of this.tokensOn(emp).filter((o) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(o)))) { const take = Math.min(leftH, d.value); d.value -= take; leftH -= take; if (d.value <= 0) this.removeTokens([d]); }
          this.fx('status', emp.id, undefined, { effect: 'heal' });
        }
        say(`a ${unit.name} rises with ${grown} health and ${grown} damage${emp && heal > 0 ? `, and ${this.cardName(emp.num)} heals ${heal}` : ''}.`);
        return;
      }
      case 'rebirth': {
        // Pile of Ashes: still here once its opponent's next turn has passed, the Phoenix it came from returns in its place
        card.wakes = (card.wakes ?? 0) + 1;
        if (card.wakes < (card.bornIn !== undefined && this.allies(card.bornIn, card.owner) ? 1 : 2)) return;
        const p = s.players[card.owner];
        const callers = this.cards.byNumber.get(card.num)?.summonedBy ?? [];
        const i = p.discard.findIndex((c) => callers.includes(this.cardName(c.num).toLowerCase()));
        if (i < 0) return;
        const [back] = p.discard.splice(i, 1);
        const owned = this.owners();
        const u: FieldCard = { ...back, owner: card.owner, x: card.x, z: card.z, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null };
        this.clearTokensOn(card);
        this.removeField(card.id);
        s.field.push(u);
        this.addCount(u);
        this.addStartCounters(u);
        this.bring(u);
        this.fx('play', u.id);
        this.layout(owned);
        say(`${this.cardName(u.num)} rises again from its ashes.`);
        return;
      }
      case 'search': {
        this.startSearch(seat, name, w, undefined, this.searchThen);
        return;
      }
      case 'recast': {
        // Cursed Chains, its Lock paid off: its owner pays its cost again; a new Lock comes with it (chose 1), or it can't (chose 2)
        const def = this.cards.byNumber.get(card.num);
        const cost = Math.max(0, def?.cost ?? 0);
        if (!def || (def.costDark && cost > 0 && this.darkHeld(seat, cost)) || !this.payBar(seat, cost, !!def.costDark)) { ctx.chose = 2; say(`not enough ${def?.costDark ? 'Dark Gerzite' : 'Gerzite'} to recast it.`); return; }
        this.removeTokens(this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.turn.lifetime.expiredToken));
        this.fx('play', card.id);
        say(`${s.players[seat].name} pays ${cost} ${def.costDark ? 'Dark Gerzite' : 'Gerzite'} to cast it again.`);
        this.bring(card);
        ctx.chose = 1;
        return;
      }
      case 'cordy': {
        // Cordyceps Hatchlings, hitting a card carrying its Susceptibility: that Susceptibility is used up, and it deals 1 more for good
        const t = ctx.target;
        const key = t ? `susceptible:${t.id}` : '';
        const had = t ? card.gave?.[key] ?? 0 : 0;
        if (!t || had <= 0) return;
        let left = had;
        for (const tok of this.tokensOn(t).filter((o) => this.effectOf(o) === 'susceptible')) { const k2 = Math.min(left, tok.value); tok.value -= k2; left -= k2; if (tok.value <= 0) this.removeTokens([tok]); }
        delete card.gave![key];
        card.bonusHit = (card.bonusHit ?? 0) + 1;
        say(`its Susceptibility on ${names([t])} is spent, and one of its Troops deals 1 more from now on.`);
        return;
      }
      case 'clan_go': {
        // Charred Clan: tapped so the Volcanic Unit just summoned arrives ready
        const it = ctx.it;
        if (!it || card.rot % 2 === 1 || card.acted === this.turnKey()) return;
        for (const m of this.swarmOf(card)) if (m.rot % 2 === 0) this.turnCard(m, 1, 'ability'); // (a swarm is tapped whole)
        this.removeTokens(this.tokensOn(it).filter((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken));
        if (it.rot === CONFIG.arrival.entrenchedRotation) it.rot = 0;
        say(`it is tapped: ${names([it])} is not Entrenched.`);
        return;
      }
      case 'burn_move': {
        const from = ctx.target; const to = who('it')[0];
        const b = from ? this.tokensOn(from).find((t) => this.effectOf(t) === 'burn') : undefined;
        if (!b || !to) return;
        this.removeTokens([b]);
        this.giveStatus(seat, to, 'burn', null, null);
        say(`the Burn on ${names([from!])} moves to ${names([to])}.`);
        return;
      }
      case 'unstatus': {
        // "unstatus buffer": that status comes off the card(s), all of it (Encased Skeleton's Buffer as its Shield breaks)
        const tdef = this.tokenDb.list.find((d) => d.id === w[0]) ?? this.tokenDb.list.find((d) => d.effect === w[0]);
        if (!tdef) return;
        for (const t of who('self')) {
          const had = this.tokensOn(t).filter((o) => o.tokenId === tdef.id);
          if (!had.length) continue;
          this.removeTokens(had);
          say(`${names([t])} loses its ${tdef.name}.`);
        }
        return;
      }
      case 'also_aff': {
        // "also_aff magical while overclock": the card is that affiliation as well as its own, while it has that status:
        // an affiliation counter of it goes on the card, and comes off with the status (Givellt's Arcane Ascension)
        const tdef = this.tokenDb.list.find((d) => d.id === `aff-${w[0]}`);
        const keep = w[w.indexOf('while') + 1] ?? 'overclock';
        if (!tdef || w.indexOf('while') < 0) return;
        for (const t of who(e.on || 'it')) {
          if (!this.tokensOn(t).some((o) => this.effectOf(o) === keep && o.value > 0)) continue;
          this.look('ascend-cast', t); // (dark magic bursting up round it as it is empowered)
          this.removeTokens(this.tokensOn(t).filter((o) => o.tokenId === tdef.id && o.alsoWhile));
          const k = this.tokensOn(t).length;
          const sc = t.scale ?? 1; const side = sideOf(t.owner);
          const tok: Token = { id: this.newId('t'), tokenId: tdef.id, x: t.x + ((k % 3) - 1) * 2 * sc, z: t.z - side * 1.2 * sc + Math.floor(k / 3) * 1.6 * sc * side, value: 0, layer: s.nextLayer++, by: seat, since: s.players[seat].turns, alsoWhile: keep };
          s.tokens.push(tok);
          this.fx('status', t.id, undefined, { token: tok.id, effect: 'notice' });
          say(`${names([t])} is ${tdef.name} as well as its own affiliation while its ${this.tokenDb.list.find((d) => d.effect === keep)?.name ?? keep} lasts.`);
        }
        return;
      }
      case 'unburn': {
        for (const t of who('it')) { this.removeTokens(this.tokensOn(t).filter((o) => this.effectOf(o) === 'burn')); say(`the Burn on ${names([t])} is removed.`); }
        return;
      }
      case 'eel': {
        // Generator Eel: every status on every other Unit is removed; each Unit that lost one takes 2
        const hitList: FieldCard[] = [];
        for (const u of s.field.filter((c) => c !== card && c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && !this.untouchable(c))) {
          const had = this.tokensOn(u).filter((t) => this.tokenDb.byId.get(t.tokenId)?.category === 'status' && !t.ability);
          if (!had.length) continue;
          if (had.some((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken) && u.rot === CONFIG.arrival.entrenchedRotation) u.rot = 0;
          this.removeTokens(had);
          hitList.push(u);
        }
        const notes: string[] = [];
        for (const u of hitList) this.landHit(seat, u, num(w[0], 2), { from: card }, notes);
        say(hitList.length ? `every status is removed; ${num(w[0], 2)} damage to ${names(hitList)}.` : 'no Unit had a status to remove.');
        return;
      }
      case 'clam': {
        // Cavity Clam: the first time it takes damage (and lives through it), its lanes either side get 2 Overclock and a turn of Frenzy
        if (card.hurtFirst) return;
        card.hurtFirst = true;
        if (this.healthLeft(card) <= 0) return;
        const near = this.whoOf(card, 'allies_adjacent', ctx);
        for (const u of near) { this.giveStatus(seat, u, 'overclock', 2, null); this.giveStatus(seat, u, 'frenzy', 1, null); }
        if (near.length) say(`it was hurt for the first time: 2 Overclock and a turn of Frenzy for ${names(near)}.`);
        return;
      }
      case 'berry': {
        // Berry Reserve: one of your Troops in its range heals what it took during the opponent's turn
        // (only the opponent's turn just before this one: what it took in an earlier turn that went unused is gone)
        const n2 = card.berryAt === this.turnStamp() - 1 ? (card.berry ?? 0) : 0;
        delete card.berry; delete card.berryAt;
        const t = who('it')[0];
        if (!t || n2 <= 0) return;
        this.doEffect(card, { when: 'x', passive: false, words: ['heal', String(n2)], on: 'it', conds: [] }, { ...ctx, it: t });
        return;
      }
      case 'sun_mark': {
        card.sun ??= { base: Number.parseInt(this.cards.byNumber.get(card.num)?.damage ?? '0', 10) || 0 };
        card.sun.attacked = this.turnKey();
        return;
      }
      case 'sun_reset': {
        card.sun ??= { base: Number.parseInt(this.cards.byNumber.get(card.num)?.damage ?? '0', 10) || 0 };
        card.sun.reset = true;
        return;
      }
      case 'sun_turn': {
        // Sun Magnifier, as its owner's turn ends: it attacked (and broke nothing, destroyed nothing): double its base, less 1; otherwise back to its first
        const first = Number.parseInt(this.cards.byNumber.get(card.num)?.damage ?? '0', 10) || 0;
        card.sun ??= { base: first };
        const was = card.sun.base;
        card.sun.base = card.sun.attacked === this.turnKey() && !card.sun.reset ? card.sun.base * 2 - 1 : first;
        delete card.sun.attacked; delete card.sun.reset;
        if (card.sun.base !== was) say(`its base damage is now ${card.sun.base}.`);
        return;
      }
      case 'wail': {
        // Woods Wailer: copy an effect from an opposing Unit, or use an ability of their Emperor (not the same one two turns running)
        const t = who('it')[0];
        const tDef = t ? this.cards.byNumber.get(t.num) : undefined;
        if (!t || !tDef) return;
        if (isEmperor(tDef)) {
          const abil = tDef.emperorAbilities.find((ab) => ab.effects.length);
          if (!abil) { say(`${names([t])} has no ability it can copy.`); return; }
          say(`it copies ${names([t])}’s ${abil.name}.`);
          this.runEffects(card, abil.effects.map((x) => ({ ...x, when: 'x' })), {});
          card.copied = { num: t.num, kind: 'enter', at: this.turnStamp() };
          return;
        }
        const kinds = (['enter', 'passive', 'tap'] as const).filter((k2) => (k2 === 'tap' ? this.tapEffects(tDef).length > 0 : tDef.effects.some((x) => (k2 === 'passive' ? x.passive : x.when === k2))));
        if (kinds.length > 1 && ctx.chose === undefined && !s.bots?.includes(seat)) {
          // its player picks which of that Unit's effects to copy
          const label = { enter: 'Copy its Entrance', passive: 'Copy its Passive', tap: 'Copy its Tap' } as const;
          (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: which of ${names([t])}’s effects does it copy`, options: [], choices: kinds.map((k2) => label[k2]), src: { ...card, merged: [] }, rest: [{ when: 'x', passive: false, words: ['choose'], on: '', conds: [] }, { ...e, on: 'it' }], ctx: this.packCtx({ ...ctx, it: t }) });
          return;
        }
        const kind = kinds[(ctx.chose ?? 1) - 1] ?? kinds[0];
        if (!kind) { say(`${names([t])} has no effect it can copy: it has no effect this time.`); return; }
        if (card.copied && card.copied.num === t.num && card.copied.kind === kind && card.copied.at >= this.turnStamp() - 2) { say(`it copied that last turn, so it can’t copy it again yet.`); return; }
        card.copied = { num: t.num, kind, at: this.turnStamp() };
        say(`it copies ${names([t])}’s ${kind === 'enter' ? 'Entrance' : kind === 'passive' ? 'Passive' : 'Tap'}.`);
        if (kind === 'enter') this.runEffects(card, tDef.effects.filter((x) => x.when === 'enter'), {});
        return;
      }
      case 'entomb': {
        // Cursed Sarcophagus: a Spell from its player's hand is laid face-down under it (it can't be hit, and waits)
        const p = s.players[seat];
        const spells = p.hand.filter((c) => this.cards.byNumber.get(c.num)?.types.includes('spell'));
        if (!spells.length) { say('there is no Spell in the hand to attach.'); return; }
        if (s.bots?.includes(seat)) {
          // a bot seals in its dearest Spell
          const best = [...spells].sort((a, b) => (this.cards.byNumber.get(b.num)?.cost ?? 0) - (this.cards.byNumber.get(a.num)?.cost ?? 0))[0];
          this.entombSpell(card, best.id);
          return;
        }
        (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: choose a Spell from your hand to attach face-down (it is cast when ${name} is destroyed)`, options: [], cards: spells.map((c) => ({ id: c.id, num: c.num })), optional: true, skip: 'No Spell', entomb: card.id });
        return;
      }
      case 'unentomb': {
        // …destroyed: the Spell sealed under it is turned face-up and cast, its lane as its Spell slot
        const sealed = s.field.find((c) => c.heldBy === card.id && !c.faceUp);
        if (!sealed) return;
        delete sealed.heldBy; delete sealed.attachedTo; delete sealed.attachOrder;
        const at = this.standsAt(card);
        const lane = at ? { x: at.slot.x * at.side, z: at.slot.z * at.side } : { x: card.x, z: card.z };
        Object.assign(sealed, { faceUp: true, rot: 0, layer: s.nextLayer++, ...lane });
        delete sealed.scale;
        sealed.playedTurn = s.players[sealed.owner].turns;
        this.fx('reveal', sealed.id);
        this.look('trigger', sealed); // (it springs, like a Trigger)
        say(`${this.cardName(sealed.num)}, sealed inside it, is cast.`);
        this.fire(sealed, 'enter');
        return;
      }
      case 'rest_hits': {
        // Marlin Knight: it didn't attack last turn, so its next attack has one more hit (until it attacks)
        card.restHits = (card.restHits ?? 0) + num(w[0], 1);
        say(`it rested: its next attack has ${card.restHits} extra hit${card.restHits === 1 ? '' : 's'}.`);
        return;
      }
      case 'more_actions': {
        // Cragged Skulker: one more attacking action, for good (a Passive: none while its Passive is switched off)
        card.actions = (card.actions ?? 1) + num(w[0], 1);
        this.look('skulker-pulse', card);
        say(`it gains an attacking action: ${card.actions} a turn now.`);
        return;
      }
      case 'look': {
        // a card's own effect, for both players to see ("look skulker-pulse": on this card; "on it": on another)
        // "look tectonic-sink lanes": in each lane chosen for it · "look seastorm": across the whole table, from a side picked at random
        if (w[1] === 'lanes') {
          const L = this.lanesFor(card, ctx) ?? card.lanes ?? null;
          for (const l of L ? laneOrder().filter((x) => L.names.includes(x.name)) : []) this.look(w[0], null, { at: { x: l.x * L!.side, z: l.z * L!.side } });
          return;
        }
        if (w[1] === 'biome') { const b = CONFIG.slots.find((sl) => sl.name === 'Biome'); if (b) this.look(w[0], null, { at: { x: b.x, z: b.z } }); return; } // ("look tectonic-sink biome": on the Biome slot)
        if (w[0] === 'seastorm') { this.look(w[0], card, { value: Math.floor(this.rng() * 4) }); return; }
        for (const t of e.on ? who('self') : [card]) this.look(w[0], t);
        return;
      }
      case 'archmage_peek': {
        // Archmage: the top cards of the opponent's deck, looked at; those of an affiliation go to the bottom
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat)) as Seat;
        if (foeSeat < 0) return;
        const q = s.players[foeSeat];
        const top = q.deck.filter((c) => !this.jumper(c)).slice(0, num(w[0], 4));
        if (!top.length) return;
        const affs = w.slice(1);
        const sunk = top.filter((c) => this.cards.byNumber.get(c.num)?.affiliations.some((a) => affs.includes(a)));
        s.peeks[seat] = { owner: foeSeat, pile: 'deck', cards: top.map((c) => ({ ...c })) };
        this.fx('peek', '', undefined, { effect: 'deck', seat, count: top.length });
        this.look('archmage-fire', card);
        for (const c of sunk) { q.deck.splice(q.deck.indexOf(c), 1); q.deck.push(this.rehide(c)); }
        if (sunk.length) this.look('archmage-steam', null, { seat: foeSeat, pile: 'deck' });
        say(`${s.players[seat].name} looks at the top ${top.length} cards of ${q.name}’s deck${sunk.length ? `; ${sunk.length} ${affs.map((a) => a[0].toUpperCase() + a.slice(1)).join('/')} card${sunk.length === 1 ? ' goes' : 's go'} to the bottom` : ''}.`);
        return;
      }
      case 'incinerate': {
        // Primordial Incinerator: every Biome and Tome in play cracks, shakes, crumbles and is discarded
        const gone = s.field.filter((b) => b !== card && b.faceUp && (b.asBiome || !!this.cards.byNumber.get(b.num)?.types.some((t) => t === 'biome' || t === 'tome')));
        for (const b of gone) { this.fx('sacrifice', b.id, undefined, { seat: b.owner, effect: 'burst-incinerate' }); this.discardFromPlay(b); }
        say(gone.length ? `${names(gone)} ${gone.length === 1 ? 'is' : 'are'} burned away.` : 'there is no Biome or Tome in play to burn away.');
        return;
      }
      case 'devour': {
        // Fleshstealer: a Troop of its own is sacrificed; the health it had left becomes this card's Overheal
        const t = who('it')[0];
        if (!t || t === card) return;
        const left = Math.max(0, this.healthLeft(t));
        this.look('flesh-blood', t, { target: card.id });
        this.destroyNow(t);
        if (left > 0) this.giveStatus(seat, card, 'overheal', left, null);
        say(`${names([t])} is sacrificed: ${left} Overheal.`);
        return;
      }
      case 'reflect': {
        // Great Hay Golem: half the damage it took is thrown back. At an attacker, the attack it made bounces back the
        // way it came; from a Spell, into the lane across from it, the Spell's look bouncing that way
        const amount = Math.ceil((ctx.amount ?? 0) / 2);
        const from = ctx.attacker;
        if (amount <= 0 || !from) return;
        this.look('golem-reflect', card);
        const spell = !!this.cards.byNumber.get(from.num)?.types.includes('spell');
        const notes: string[] = [];
        if (spell) {
          const hit = this.whoOf(card, 'front', ctx);
          const tint = this.damageAffOf(from)[0] ?? 'neutral';
          for (const t of hit) { this.fx('spell', card.id, undefined, { target: t.id, from: { x: card.x, z: card.z }, tint, seat, effect: 'bounce' }); this.landHit(seat, t, amount, { splash: true, from: card }, notes); }
          say(hit.length ? `${amount} damage thrown back into the lane across (${names(hit)}).` : 'the lane across is empty: nothing to throw it back at.');
        } else if (s.field.includes(from)) {
          this.fx('attack', card.id, undefined, { target: from.id, seat, effect: 'bounce', tint: this.attackTint(from) });
          this.landHit(seat, from, amount, { splash: true, from: card }, notes);
          say(`${amount} damage thrown back at ${names([from])}.`);
        }
        this.settleFallen();
        return;
      }
      case 'legion': {
        // Undead Legion: its General raises the Legion: two Undead Soldiers in its own lane, one in every other lane on its
        // side. They come up out of the ground; the General plants its battle banner. All of them attack together.
        const at = this.standsAt(card);
        if (!at || slotKind(at.slot.name) !== 'Lane') return;
        const before = new Set(s.field.map((c) => c.id));
        // one soldier in each of the two lanes either side of the General; one that would fall off the edge of the
        // table (the 2v2 table has an even number of lanes) goes in the end lane with the one already there
        const row = laneOrder();
        const i = row.indexOf(at.slot);
        const spots = [-2, -1, 1, 2].map((d) => row[Math.max(0, Math.min(row.length - 1, i + d))]).filter((l) => l !== at.slot);
        const sideOk = this.sideOfCard(card) === at.side;
        this.look('legion-banner', card);
        this.doEffect(card, { when: 'x', passive: false, words: ['summon', 'Undead', 'Soldier', 'x2'], on: '', conds: [] }, { ...ctx });
        if (sideOk) for (const lane of new Set(spots)) {
          const n = spots.filter((l) => l === lane).length;
          this.doEffect(card, { when: 'x', passive: false, words: ['summon', 'Undead', 'Soldier', 'lanes', ...(n > 1 ? [`x${n}`] : [])], on: '', conds: [] }, { ...ctx, lane: lane.name, laneFoe: false });
        }
        for (const c of s.field) if (!before.has(c.id) && c.summonedBy === card.id) c.legion = card.id;
        return;
      }
      case 'segments': {
        // Leviathan: Body Segments in the lanes running away from it on one side, 1 Gerzite each, as many as its
        // player likes and can pay for (from Lane 3: up to two, both left or both right; from Lane 5: up to four)
        const at = this.standsAt(card);
        if (!at || slotKind(at.slot.name) !== 'Lane') return;
        const row = this.laneRow(at.side);
        const i = row.indexOf(at.slot);
        const afford = this.barTokens(seat, 'gerzite').length;
        const opts: { label: string; lanes: string[] }[] = [];
        for (const [dir, side] of [[-1, 'Left'], [1, 'Right']] as const) {
          for (let n = 1; n <= afford && row[i + dir * n]; n++) {
            const lanes = Array.from({ length: n }, (_, k) => row[i + dir * (k + 1)].name);
            opts.push({ label: `${side}: ${n} Body Segment${n === 1 ? '' : 's'} (${lanes.join(', ')}) · ${n} Gerzite`, lanes });
          }
        }
        if (!opts.length) return;
        if (ctx.chose === undefined && !s.bots?.includes(seat)) {
          (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: pay 1 Gerzite for each Body Segment`, options: [], choices: [...opts.map((o) => o.label), 'No Body Segment'], src: { ...card, merged: [] }, rest: [{ when: 'x', passive: false, words: ['choose'], on: '', conds: [] }, e], ctx: this.packCtx(ctx) });
          return;
        }
        const o = opts[(ctx.chose ?? 1) - 1];
        delete ctx.chose;
        if (!o) return;
        this.payBar(seat, o.lanes.length, false);
        say(`${s.players[seat].name} pays ${o.lanes.length} Gerzite.`);
        this.doEffect(card, { when: 'x', passive: false, words: ['summon', 'Leviathan', 'Body', 'Segment', 'lanes', 'emerge'], on: '', conds: [] }, { ...ctx, lane: o.lanes.join(','), laneFoe: false });
        return;
      }
      case 'bell_on': {
        // The River Keeper: a Bell on an opposing Unit (when it is destroyed, its owner discards a card)
        const bdef = this.tokenDb.byId.get('bell');
        for (const t of who('it')) if (bdef) { const sc = t.scale ?? 1; s.tokens.push({ id: this.newId('t'), tokenId: bdef.id, x: t.x + 1.6 * sideOf(t.owner) * sc, z: t.z + 1.2 * sideOf(t.owner) * sc, value: bdef.start, layer: s.nextLayer++, by: seat }); say(`a Bell on ${names([t])}.`); }
        return;
      }
      case 'banish': {
        // Banishment: its caster discards cards from the opponent's hand, chosen by place, without seeing them
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat)) as Seat;
        if (foeSeat < 0) return;
        this.blindAsk(card, foeSeat, num(w[0], 3));
        return;
      }
      case 'gaze': {
        // Ephemeral Gaze: the top cards of the opponent's deck, put back in any order; an Undead one may be discarded for 1 damage to your own Emperor
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat)) as Seat;
        if (foeSeat < 0) return;
        const top = s.players[foeSeat].deck.filter((c) => !this.jumper(c)).slice(0, num(w[0], 4));
        if (!top.length) return;
        const undead = top.filter((c) => this.cards.byNumber.get(c.num)?.affiliations.includes('undead')).map((c) => c.id);
        this.log(`${name}: ${s.players[seat].name} looks at the top ${top.length} cards of ${s.players[foeSeat].name}’s deck and puts them back in any order.`, seat);
        if (s.bots?.includes(seat)) return;
        (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: put the top of ${s.players[foeSeat].name}’s deck in the order you like (top first)${undead.length ? '; an Undead card may be discarded for 1 damage to your Emperor each' : ''}`, options: [], cards: top.map((c) => ({ id: c.id, num: c.num })), arrange: { foe: foeSeat, undead } });
        return;
      }
      case 'bargain': {
        // Dark Bargain: an amount both players lose; both can change it, and both must agree
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat)) as Seat;
        if (foeSeat < 0) return;
        const max = Math.max(0, Math.min(this.barTokens(seat, 'gerzite').length, this.barTokens(foeSeat, 'gerzite').length) - 1);
        if (max <= 0) { say('neither player can lose any Gerzite without going under 1: nothing happens.'); return; }
        s.bargain = { by: name, seat, value: Math.min(1, max), max, agreed: [] };
        if (s.bots?.includes(foeSeat)) s.bargain.agreed.push(foeSeat);
        say(`both players set how much Gerzite they each lose (up to ${max}), and both press Deal.`);
        return;
      }
      case 'repeat': {
        // a choice asked again and again until its player says Done ("repeat resurge on pick_ally": Forbidden Resurgence)
        // ("dark": once for each Dark Gerzite its player has: Vaelith's Warlord's Wrath)
        this.repeatAsk(card, w[0], e.on, ctx, w[1] === 'chosen' ? (this.lastChoice ?? 99) : w[1] === 'dark' ? this.barTokens(seat, 'dark').length : num(w[1], 99));
        return;
      }
      case 'resurge': {
        // Forbidden Resurgence: the Unit is destroyed and discarded; its owner gains 1 less than its cost (Dark Gerzite for a Darkgale one) and may search their discard pile for a card of the same cost
        const t = who('it')[0];
        const tDef = t ? this.cards.byNumber.get(t.num) : undefined;
        if (!t || !tDef) return;
        const cost = formOf(tDef, t.form).cost ?? 0;
        const dark = this.hasOn(t, CONFIG.turn.darkgale.token);
        const id2 = dark ? this.tokenDb.list.find((d) => d.bar === 'dark')?.id : CONFIG.tokenRules.fractalGives;
        // (struck by purple lightning, it blows apart its own way)
        const bolt = CONFIG.looks.enabled ? CONFIG.looks.destroyLook[name] : undefined;
        if (bolt) this.look(bolt.look, t);
        this.burstOverride = bolt?.burst ?? null;
        try {
          this.destroyNow(t);
          if (s.field.includes(t)) this.fall(t);
        } finally { this.burstOverride = null; }
        const n2 = Math.max(0, cost - 1);
        if (id2 && n2 > 0) say(`${tDef.name} is destroyed: ${s.players[seat].name} gains ${this.gainText(id2, n2, this.gain(seat, id2, n2))}.`);
        else say(`${tDef.name} is destroyed.`);
        this.startSearch(seat, name, ['discard', 'cost', String(cost)]);
        return;
      }
      case 'vanish': {
        // Curse of Vanishing: the opponent's Biomes, Tomes and Triggers chosen are discarded (as many as were paid for)
        const t = who('it')[0];
        if (!t) return;
        const was = t.faceUp ? this.cardName(t.num) : 'a face-down card';
        // its light reaches the card, which shatters as it lands
        this.spellAim(card, [t], true, true);
        this.fx('sacrifice', t.id, undefined, { seat: t.owner, effect: 'destroyed' });
        this.discardFromPlay(t);
        say(`${was} is discarded.`);
        return;
      }
      case 'peek_top': {
        // Arctic Owlkin: a look at the top card of the opponent's deck; they see that it happens, not what it is
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat)) as Seat;
        const top = foeSeat >= 0 ? s.players[foeSeat].deck.find((c) => !this.jumper(c)) : undefined;
        if (!top) return;
        s.peeks[seat] = { owner: foeSeat, pile: 'deck', cards: [{ ...top }] };
        this.fx('peek', '', undefined, { effect: 'deck', seat, count: 1 });
        this.log(`${name}: ${s.players[seat].name} looks at the top card of ${s.players[foeSeat].name}’s deck.`, seat);
        return;
      }
      case 'reveal_triggers': {
        // Glacial Wyrm, Molten Stallion: the opponent's face-down Triggers are shown to this card's owner (they can still go off)
        const got = s.field.filter((c) => !c.faceUp && this.opposed(c.owner, seat) && !!this.cards.byNumber.get(c.num)?.types.includes('trigger'));
        for (const c of got) c.revealedBy = card.id;
        say(got.length ? `${s.players[seat].name} sees ${got.length === 1 ? 'the opponent’s Trigger' : `the opponent’s ${got.length} Triggers`}: ${got.map((c) => this.cardName(c.num)).join(', ')}.` : 'the opponent has no Trigger in play.');
        return;
      }
      case 'collect': {
        // Soul Collector: its discard pile is shuffled, and the first Troop of 6 Gerzite or less in it is attached: its mechanics are this card's too
        const p = s.players[seat];
        this.shuffle(p.discard); p.shuffles.discard++;
        const i = p.discard.findIndex((c) => { const d = this.cards.byNumber.get(c.num); return !!d && d.types.includes('troop') && (d.cost ?? 99) <= num(w[0], 6) && !this.jumper(c); });
        if (i < 0) { say(`${p.name} shuffles their discard pile, but finds no Troop of ${num(w[0], 6)} Gerzite or less.`); return; }
        const [got] = p.discard.splice(i, 1);
        (card.borrowed ??= []).push(got.num);
        const gotDef = this.cards.byNumber.get(got.num)!;
        // the card itself flies from the discard pile and is attached under the Collector, for both players to see
        // what it holds (it can't be hit and does nothing itself; it goes back with the Collector)
        const owned = this.owners();
        const heldCard: FieldCard = { id: got.id, num: got.num, spawned: false, owner: seat, x: card.x, z: card.z, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, attachedTo: card.id, heldBy: card.id, attachOrder: card.borrowed.length };
        s.field.push(heldCard);
        this.layout(owned);
        this.look('soul-take', null, { seat, pile: 'discard', target: heldCard.id });
        say(`${p.name} shuffles their discard pile and attaches ${gotDef.name}: it can use its mechanics now.`);
        this.look('souls-in', card); // souls fly into it from all around
        this.fx('revealCard', '', undefined, { seat, num: got.num, text: `${name} attaches ${gotDef.name}.` });
        // an Entrance it took on happens as it is taken on
        const enter = gotDef.effects.filter((x) => x.when === 'enter');
        if (enter.length) this.runEffects(card, enter, {});
        return;
      }
      case 'collect_tap': {
        // Soul Collector's Tap: pay its price (collect_tap 3) to attach another (or, with Troops attached that have a Tap, use one of those instead)
        const price = Number(e.words[1]) || 2;
        const taps = (card.borrowed ?? []).flatMap((n) => this.cards.byNumber.get(n)?.effects.filter((x) => x.when === 'tap') ?? []);
        if (taps.length && ctx.chose === undefined && !s.bots?.includes(seat)) {
          (s.picks ??= []).push({ id: s.nextId++, seat, name, text: `${name}: what does its Tap do this time`, options: [], choices: [`Pay ${price} Gerzite to attach another Troop`, 'Use an attached Troop’s Tap'], src: { ...card, merged: [] }, rest: [{ when: 'tap', passive: false, words: ['choose'], on: '', conds: [] }, e], ctx: this.packCtx(ctx) });
          return;
        }
        if (ctx.chose === 2) { this.runEffects(card, taps, {}); return; }
        if (!this.payBar(seat, price, false)) { say(`attaching another Troop costs ${price} Gerzite.`); return; }
        this.doEffect(card, { when: 'tap', passive: false, words: ['collect', '6'], on: '', conds: [] }, {});
        return;
      }
      case 'tribunal': {
        // Tribunal Trigger: its caster lays the Unit it paid for face-down (it waits in the hand: played for free)
        return;
      }
      case 'challenge': {
        // Jouster: the chosen Unit must attack it from now on
        for (const t of who('it')) { t.mustAttack = card.id; this.look('joust-mark', t); say(`${names([t])} must attack it, and nothing else, until either card is gone.`); }
        return;
      }
      case 'tier3': {
        // Fatal Attraction: the Unit is Tier III while the Spell is active
        for (const t of who('it')) { t.tierBoost = Math.max(0, 3 - (1 + t.merged.length)); delete t.tierBoostAt; this.refreshTierToken(t); card.host = t.id; say(`${names([t])} is Tier III while it is active: opposing Units in its range must attack it.`); }
        return;
      }
      case 'untier': {
        for (const t of who('host')) { delete t.tierBoost; delete t.tierBoostAt; this.refreshTierToken(t); }
        return;
      }
      case 'branch': {
        // Branch Out: the second Unit's health, Shield, Buffer and Thorns join the first's; its damage comes off their shared health
        const a2 = ctx.target; const b2 = who('it')[0];
        if (!a2 || !b2 || a2 === b2) return;
        const hdef = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.healthToken);
        const bDef = this.cards.byNumber.get(b2.num)!;
        const bLeft = this.healthLeft(b2) - this.valueOn(b2, 'overheal');
        if (hdef && bLeft > 0) { this.giveStatus(seat, a2, hdef.id, bLeft, null); const t = this.tokensOn(a2).find((o) => o.tokenId === hdef.id); if (t) { (card.gave ??= {})[`branchhp:${a2.id}`] = bLeft; } }
        for (const eff of ['shield', 'buffer', 'thorns']) for (const t of this.tokensOn(b2).filter((o) => this.effectOf(o) === eff)) { (card.gave ??= {})[`branch${eff}:${b2.id}`] = (card.gave?.[`branch${eff}:${b2.id}`] ?? 0) + t.value; this.giveStatus(seat, a2, t.tokenId, t.value, null); this.removeTokens([t]); }
        this.removeTokens(this.tokensOn(b2).filter((o) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(o))));
        b2.mergedInto = a2.id;
        card.pair = [a2.id, b2.id];
        say(`${names([a2])} and ${names([b2])} share their health (${this.healthLeft(a2)}), Shield, Buffer and Thorns for three turns.`);
        void bDef;
        return;
      }
      case 'unbranch': {
        // the three turns are over: what is left of the shared health is split in half between the two
        const [a2, b2] = (card.pair ?? []).map((id) => this.findField(id));
        delete card.pair;
        if (!a2 || !b2) return;
        delete b2.mergedInto;
        const hdef = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.healthToken);
        const left = Math.max(0, this.healthLeft(a2) - this.valueOn(a2, 'overheal'));
        const added = card.gave?.[`branchhp:${a2.id}`] ?? 0;
        let take = added;
        for (const t of this.tokensOn(a2).filter((o) => o.tokenId === hdef?.id)) { const k = Math.min(take, t.value); t.value -= k; take -= k; if (t.value <= 0) this.removeTokens([t]); }
        for (const eff of ['shield', 'buffer', 'thorns']) { let back = card.gave?.[`branch${eff}:${b2.id}`] ?? 0; for (const t of this.tokensOn(a2).filter((o) => this.effectOf(o) === eff)) { const k = Math.min(back, t.value); t.value -= k; back -= k; if (t.value <= 0) this.removeTokens([t]); } const gave = card.gave?.[`branch${eff}:${b2.id}`] ?? 0; if (gave - back > 0) this.giveStatus(seat, b2, eff, gave - back, null); }
        delete card.gave;
        const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
        const setLeft = (u: FieldCard, want: number) => { this.removeTokens(this.tokensOn(u).filter((o) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(o)))); const full = this.healthLeft(u) - this.valueOn(u, 'overheal'); if (damageId && full - want > 0) this.addDamage(u, damageId, full - want, null); };
        setLeft(a2, Math.ceil(left / 2)); setLeft(b2, Math.floor(left / 2));
        say(`the shared ${left} health is split: ${names([a2])} ${Math.ceil(left / 2)}, ${names([b2])} ${Math.floor(left / 2)}.`);
        this.settleFallen();
        return;
      }
      case 'shadow': {
        // Shadow of Death: the two are bound; if either is destroyed, so is the other
        const a2 = ctx.target; const b2 = who('it')[0];
        if (!a2 || !b2) return;
        card.pair = [a2.id, b2.id];
        say(`${names([a2])} and ${names([b2])} are bound: if either is destroyed, so is the other.`);
        return;
      }
      case 'vines': {
        // Symbiotic Vines: two of your Units, each with 1 Thorns, each reaching what the other reaches
        const a2 = ctx.target; const b2 = who('it')[0];
        if (!a2 || !b2 || a2 === b2) return;
        card.pair = [a2.id, b2.id];
        for (const u of [a2, b2]) if (this.giveStatus(seat, u, 'thorns', 1, null)) (card.gave ??= {})[`thorns:${u.id}`] = (card.gave?.[`thorns:${u.id}`] ?? 0) + 1;
        say(`${names([a2])} and ${names([b2])} are joined: 1 Thorns each, and each may attack what the other can reach.`);
        return;
      }
      case 'smite': {
        // Impervious Smite: its Unit made it through the opponent's turn and can still attack: 6 damage to a Unit in its reach, then it can't be damaged this turn
        const host = card.host ? this.findField(card.host) : undefined;
        const blocked = host ? this.tokensOn(host).find((t) => CONFIG.combat.blockers.includes(this.effectOf(t))) : undefined;
        if (!host || blocked) { say(host ? `${names([host])} can’t attack after the opponent’s turn: it is discarded.` : 'its Unit is gone: it is discarded.'); this.discardFromPlay(card); return; }
        host.wardAt = this.turnStamp();
        this.runEffects(card, [{ when: 'x', passive: false, words: ['damage', '6'], on: 'pick_foe', conds: [['pool', 'host_reach']] }, { when: 'x', passive: false, words: ['done'], on: '', conds: [] }], ctx);
        return;
      }
      case 'missmark': {
        // the Units in the lanes it holds have a chance to miss in their owner's next turn (Blizzard: 1 in 3)
        const [a3, b3] = (w[0] ?? '1/3').split('/').map(Number);
        const chance = (a3 || 1) / (b3 || 3);
        for (const t of who('in_lanes')) (s.missMarks ??= []).push({ card: t.id, chance, by: name, seat: t.owner, turns: s.players[t.owner].turns + (s.turn === t.owner ? 1 : 0) });
        return;
      }
      case 'clear_biome': {
        for (const b of [...s.field]) if (b !== card && b.faceUp && this.cards.byNumber.get(b.num)?.types.includes('biome')) { say(`${names([b])} is discarded.`); this.discardFromPlay(b); }
        for (const b of [...s.field]) if (b.asBiome) this.discardFromPlay(b);
        return;
      }
      case 'echo': {
        // Executioner: a follow-up attack on the lane it attacked, at the end of the opponent's next turn
        const t = ctx.target; const at = t ? this.standsAt(t) : null;
        const def = this.cards.byNumber.get(card.num);
        if (!t || !at || slotKind(at.slot.name) !== 'Lane' || !def) return;
        const foe = t.owner;
        (s.echoes ??= []).push({ by: name, seat, lane: at.slot.name, side: at.side, amount: this.attackOf(card, def).per, pierce: this.traitNow(card, def, 'Pierce'), foe, turn: s.turn === foe ? s.players[foe].turns + 1 : s.players[foe].turns });
        say(`its axe will come back to everything in ${at.slot.name} at the end of ${s.players[foe].name}’s next turn (even if it is gone by then).`);
        return;
      }
      case 'encore': {
        // Encore: one Unit of yours may act again this turn (1 turn of Frenzy; if it has already acted, it stands up again)
        this.spellAim(card, who('it'), false); // (its light goes to the Unit chosen)
        for (const t of who('it')) {
          this.giveStatus(seat, t, 'frenzy', 1, null);
          const key = this.turnKey();
          if (t.acted === key || t.swings?.key === key) { delete t.acted; delete t.swings; if (t.rot % 2 === 1 && !this.sitsSideways(t)) t.rot = 0; }
          say(`${names([t])} may attack or use its Tap ability again this turn.`);
        }
        return;
      }
      case 'become': {
        // Sheepify: the Troop is a Sheep for now (8/1, no effects); its counters stay
        const unit = this.cards.spawnOnly.find((d) => d.name.toLowerCase() === w.join(' ').toLowerCase());
        const t = who('it')[0];
        if (!unit || !t || t.trueNum !== undefined) return;
        const was = this.cardName(t.num);
        t.trueNum = t.num; t.num = unit.number;
        card.host = t.id;
        this.fx('play', t.id);
        say(`${was} is turned into a ${unit.name}.`);
        this.settleFallen();
        return;
      }
      case 'unbecome': {
        const t = who('host')[0];
        if (!t || t.trueNum === undefined) return;
        t.num = t.trueNum; delete t.trueNum;
        this.fx('play', t.id);
        say(`the Sheep turns back into ${this.cardName(t.num)}.`);
        this.settleFallen();
        return;
      }
      case 'void_walk': {
        // Void Walker, destroyed: it comes back at the end of its owner's next turn, in the same lane (twice at most)
        const stage = (card.walkStage ?? 0) + 1;
        const at = this.standsAt(card);
        if (stage > 2 || !at || slotKind(at.slot.name) !== 'Lane') return;
        const p = s.players[seat];
        (s.returns ??= []).push({ num: card.num, owner: seat, lane: at.slot.name, side: at.side, stage, turn: s.turn === seat ? p.turns + 1 : p.turns });
        say(`it will return at the end of ${p.name}’s next turn, as ${stage === 1 ? 'a Spectral Troop with 10 health and 2 damage' : 'an Undead Troop with 4 health and 1 damage'}.`);
        return;
      }
      case 'rise': {
        // Dark Sovereign: a Volcanic Unit of yours destroyed comes back at once, Undead with 1 health
        for (const t of who('it')) if (this.isUnit(this.cards.byNumber.get(t.num)) && !t.spawned) t.rise = true;
        return;
      }
      case 'melstone': {
        // Melstone Terror: an opposing Unit summoned across from it while it is a Building: it becomes a Troop at once, and deals 5 to that Unit
        const def = this.cards.byNumber.get(card.num);
        const it = ctx.it;
        if (!def || card.troopForm || !it) return;
        this.becomeTroop(card, def);
        this.landHit(seat, it, num(w[0], 5), { from: card }, []);
        say(`it rises as a Troop and deals ${num(w[0], 5)} damage to ${names([it])}.`);
        return;
      }
      case 'discard_top': {
        const p = s.players[seat];
        const i = p.deck.findIndex((c) => !this.jumper(c));
        if (i < 0) return;
        const [c] = p.deck.splice(i, 1);
        this.insertAt(p.discard, this.rehide(c), 'top');
        if (CONFIG.looks.pileStrike[name]) this.look(CONFIG.looks.pileStrike[name], null, { seat, pile: 'deck' }); // (Banishment: purple lightning strikes the deck)
        this.fx('pileMove', '', undefined, { seat, effect: 'deck' });
        say(`${p.name} discards ${this.cardName(c.num)} from the top of their deck.`);
        return;
      }
      case 'take_discard': {
        const p = s.players[seat];
        const i = p.discard.findIndex((c) => !this.jumper(c));
        if (i < 0 || p.hand.length >= CONFIG.handLimit) return;
        const [c] = p.discard.splice(i, 1);
        p.hand.push(this.rehide(c));
        this.fx('toHand', '', undefined, { effect: 'discard', seat, count: 1 });
        say(`${p.name} takes ${this.cardName(c.num)} from the top of their discard pile.`);
        return;
      }
      case 'crossing': {
        card.crossing = true; // (it stays on the table while its owner chooses: it is becoming the Biome)
        return;
      }
      case 'crosser': {
        // Eldritch Realm Crosser: any Biome in play is discarded, and it takes the Biome slot
        const slot = CONFIG.slots.find((sl) => sl.name === 'Biome');
        if (!slot || !s.field.includes(card)) return;
        for (const b of [...s.field]) if (b !== card && b.faceUp && slotAt(b)?.slot.name === 'Biome') this.discardFromPlay(b);
        const owned = this.owners();
        this.clearTokensOn(card);
        delete card.crossing; card.asBiome = true; card.atZero = false;
        card.sideAs = sideOf(seat);
        Object.assign(card, { x: slot.x, z: slot.z, rot: 1, layer: s.nextLayer++ });
        delete card.scale;
        this.layout(owned);
        this.fx('play', card.id);
        say('it takes the Biome slot: it deals 1 damage to every opposing Unit as each turn begins.');
        return;
      }
      case 'soul': {
        // Valley of Waiting: a destroyed non-Spectral Troop lingers as a Soul (3/0) where it fell until the end of its owner's next turn
        const dead = ctx.it;
        const dDef = dead?.faceUp ? this.cards.byNumber.get(dead.num) : undefined;
        const unit = this.cards.spawnOnly.find((d) => d.name === 'Soul');
        if (!dead || !dDef || !unit || !dDef.types.includes('troop') || dead.soul || this.affOf(dead).includes('spectral')) return;
        const at = this.standsAt(dead);
        if (!at || slotKind(at.slot.name) !== 'Lane') return;
        const p = s.players[dead.owner];
        const owned = this.owners();
        const u: FieldCard = { ...this.held(unit.number, true), owner: dead.owner, x: at.slot.x * at.side, z: at.slot.z * at.side, rot: 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, summonedBy: card.id, soul: { num: dead.num, until: s.turn === dead.owner ? p.turns + 1 : p.turns } };
        s.field.push(u);
        this.fx('play', u.id, undefined, { effect: 'soul' });
        this.layout(owned);
        say(`${dDef.name} lingers in ${at.slot.name} as a Soul until the end of ${p.name}’s next turn.`);
        return;
      }
      case 'illusion': {
        // Illusionist: a copy of it goes into the lane chosen; the first of the two to be damaged is removed
        const L = this.lanesFor(card, ctx);
        const lane = L ? laneOrder().find((l) => l.name === L.names[0]) : undefined;
        if (!lane || !L) return;
        const owned = this.owners();
        const twin: FieldCard = { ...this.held(card.num, true), owner: seat, x: lane.x * L.side, z: lane.z * L.side, rot: card.rot, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, clone: { by: name } };
        s.field.push(twin);
        this.addStartCounters(twin);
        twin.illusion = card.id; card.illusion = twin.id;
        this.fx('play', twin.id);
        this.layout(owned);
        say(`a copy of it appears in ${lane.name}. The first of the two to be damaged is only an illusion.`);
        return;
      }
      case 'transmit': {
        // Transmissive Death: the Unit becomes Undead and carries its token (1 Decay each turn, for 4 turns)
        for (const t of who('it')) this.transmitTo(card, t);
        return;
      }
      case 'move': {
        // the Unit is moved into the lane chosen for it ("move"), or the lane across from this card ("move front")
        const u = who('it')[0];
        if (!u) return;
        const why = this.unmovable(u);
        if (why) { say(why); return; }
        let dest: { slot: Slot; side: 1 | -1 } | null = null;
        if (w[0] === 'front') {
          const at = this.standsAt(card);
          if (at && slotKind(at.slot.name) === 'Lane') { const mine = this.laneRow(at.side); const foe = -at.side as 1 | -1; dest = { slot: this.laneRow(foe)[mine.indexOf(at.slot)], side: foe }; }
        } else {
          const L = this.lanesFor(card, ctx);
          const slot = L ? laneOrder().find((l) => l.name === L.names[0]) : undefined;
          if (L && slot) dest = { slot, side: L.side };
        }
        const from = this.standsAt(u);
        if (!dest || !from || (from.slot === dest.slot && from.side === dest.side)) return;
        this.moveUnit(u, dest.slot, dest.side);
        ctx.moved = true;
        say(`${names([u])} moves into ${dest.slot.name}${dest.side !== sideOf(seat) ? ` on ${s.players.find((_, k) => sideOf(k as Seat) === dest!.side)?.name ?? 'the other'}’s side` : ''}.`);
        return;
      }
      case 'swap': {
        // two Units change places: the one held (ctx.target) and the one chosen now
        const a2 = ctx.target; const b2 = who('it')[0];
        if (!a2 || !b2 || a2 === b2 || this.unmovable(a2) || this.unmovable(b2)) return;
        this.swapUnits(a2, b2);
        say(`${names([a2])} and ${names([b2])} swap places.`);
        return;
      }
      case 'hold_lane': {
        // the lane the target stands in is kept, for what follows (it may be gone by then: Maelstrom Tarragon's kill)
        const at = ctx.target ? slotAt(ctx.target) : null;
        if (at) { ctx.lane = at.slot.name; ctx.laneFoe = at.side !== this.sideOfCard(card); }
        return;
      }
      case 'tornado': {
        // Tornado: damage to every Unit in the lane chosen and the lanes beside it; then those beside are pulled into it
        const L = this.lanesFor(card, ctx);
        const mid = L ? laneOrder().find((l) => l.name === L.names[0]) : undefined;
        if (!L || !mid) return;
        const row = this.laneRow(L.side);
        const j = row.indexOf(mid);
        const near = [row[j - 1], row[j + 1]].filter(Boolean);
        const lanes3 = [mid, ...near].map((l) => l.name).join(',');
        // (the table plays a swirling tornado over the lane, with a ring round the lanes it reaches: `at` its middle,
        // `value` half its width; the Units pulled in are seen moving once the damage has landed)
        if (CONFIG.looks.enabled) {
          const xs = [mid, ...near].map((l) => l.x * L.side);
          this.look('tornado', card, { at: { x: (Math.min(...xs) + Math.max(...xs)) / 2, z: mid.z * L.side }, value: (Math.max(...xs) - Math.min(...xs)) / 2 });
        }
        this.doEffect(card, { when: e.when, passive: false, words: ['damage', w[0] ?? '2'], on: 'in_lanes', conds: [] }, { ...ctx, lane: lanes3 });
        this.settleFallen();
        const pulled: FieldCard[] = [];
        for (const l of near) for (const u of this.unitsAt(l, L.side, undefined, true)) if (u.faceUp && !this.unmovable(u)) { this.moveUnit(u, mid, L.side); pulled.push(u); }
        if (pulled.length) say(`${names(pulled)} ${pulled.length === 1 ? 'is' : 'are'} pulled into ${mid.name}.`);
        return;
      }
      case 'pull': {
        // the Unit chosen is pulled into the lane where the target stood (Maelstrom Tarragon, after a kill)
        const u = who('it')[0];
        const L = this.lanesFor(card, ctx);
        const kept = L ? laneOrder().find((l) => l.name === L.names[0]) : undefined;
        const at = ctx.target && s.field.includes(ctx.target) ? slotAt(ctx.target) : kept && L ? { slot: kept, side: L.side } : ctx.target ? slotAt(ctx.target) : null;
        if (!u || !at || slotKind(at.slot.name) !== 'Lane' || this.unmovable(u)) return;
        this.moveUnit(u, at.slot, at.side);
        ctx.moved = true;
        say(`${names([u])} is pulled into ${at.slot.name}.`);
        return;
      }
      case 'riptide': {
        // Crashing Riptide: a Unit was destroyed: a Unit beside it (not a Submerged one) is pulled into its lane. The Biome chooses: at random
        const dead = ctx.it;
        const at = dead ? slotAt(dead) : null;
        if (!dead || !at || slotKind(at.slot.name) !== 'Lane' || !this.isUnit(this.cards.byNumber.get(dead.num))) return;
        const row = this.laneRow(at.side);
        const j = row.indexOf(at.slot);
        const near = [row[j - 1], row[j + 1]].filter(Boolean).flatMap((l) => this.unitsAt(l, at.side, undefined, true)).filter((u) => u.faceUp && !this.unmovable(u) && !this.affOf(u).includes('submerged'));
        if (!near.length) return;
        const u = near[Math.floor(this.rng() * near.length)];
        this.moveUnit(u, at.slot, at.side);
        say(`${names([u])} is pulled into ${at.slot.name}, where ${names([dead])} fell.`);
        return;
      }
      case 'nospells': {
        // Split Chasm: its caster casts no more Spells this turn or their next
        const p = s.players[seat];
        p.noSpellsThrough = p.turns + num(w[0], 1);
        say(`${p.name} can’t cast Spells for the rest of this turn and their next.`);
        return;
      }
      case 'pest': {
        // Pestilence Approaching, on an odd roll: with Gerzite left, both players lose 1 and the damage comes again (chose 1)
        const foeSeat = s.players.findIndex((_, i) => this.opposed(seat, i as Seat));
        if (!this.barTokens(seat, 'gerzite').length) { ctx.chose = 0; this.endCard(card, `${s.players[seat].name} has no Gerzite left`); return; }
        for (const who2 of [seat, ...(foeSeat >= 0 ? [foeSeat as Seat] : [])]) this.payBar(who2, Math.min(1, this.barTokens(who2, 'gerzite').length), false);
        ctx.chose = 1;
        say('it spreads again: both players lose 1 Gerzite.');
        return;
      }
      case 'attach_brought': {
        // the token this card brought goes onto the card chosen for it (Tangleweed's Lock, Torch Pass's Burn)
        const to = who('it')[0];
        if (!to) return;
        const mine = s.tokens.filter((t) => t.from === card.id && this.cardUnder(t)?.id !== card.id && !(card.host && this.cardUnder(t)?.id === card.host));
        if (w.includes('paid')) for (const t of mine) if (this.effectOf(t) === CONFIG.tokenRules.lockToken) t.onPaid = { by: seat, w: w.slice(w.indexOf('paid') + 1) };
        const owned = this.owners();
        mine.forEach((t, k) => {
          const sc = to.scale ?? 1;
          Object.assign(t, { x: to.x + ((k % 3) - 1) * 2 * sc, z: to.z - sideOf(to.owner) * 1.2 * sc, layer: s.nextLayer++ });
          if (this.landToken(seat, t)) { this.removeTokens([t]); return; }
          if (s.tokens.includes(t)) this.mirrorStatus(seat, t, undefined);
        });
        card.host = to.id;
        this.layout(owned);
        const names2 = mine.filter((t) => s.tokens.includes(t)).map((t) => this.tokenDb.byId.get(t.tokenId)?.name ?? 'token');
        if (names2.length) say(`${names2.join(' and ')} on ${names([to])}.`);
        return;
      }
      case 'reveal': {
        this.revealTop(seat, name, w);
        return;
      }
      case 'discount': {
        // the next card of that kind its player plays costs less ("turn": only this turn)
        const p = s.players[seat];
        (p.discounts ??= []).push({ amount: num(w[0]), w: w.slice(1), ...(w.includes('turn') ? { stamp: this.turnStamp() } : {}), by: card.id });
        say(`${p.name}’s next ${w.slice(1).filter((x) => !['affiliation', 'type', 'turn'].includes(x)).join(' / ') || 'card'} costs ${num(w[0])} less${w.includes('turn') ? ' this turn' : ''}.`);
        return;
      }
      case 'hold': {
        card.holdTapped = num(w[0]);
        say(`it stays tapped for ${num(w[0]) === 1 ? 'an extra turn' : `${num(w[0])} extra turns`}.`);
        return;
      }
      case 'pay': {
        const bar = w[1] === 'dark' ? 'dark' : 'gerzite';
        const mine = this.barTokens(seat, bar);
        this.removeTokens(mine.slice(-num(w[0])));
        say(`${s.players[seat].name} pays ${num(w[0])} ${bar === 'dark' ? 'Dark Gerzite' : 'Gerzite'}.`);
        return;
      }
    }
  }
  // ---------------- searching a pile, looking at the top of a deck, and what cards cost ----------------

  /** Does a card (by its sheet row) pass a list of filter words: "affiliation forested type spell biome"? "support" = Biome, Tome or Trigger. */
  private passes(def: CardDef | undefined, w: string[]): boolean {
    if (!def) return false;
    const stop = ['affiliation', 'type', 'to', 'turn', 'first', 'foe', 'per', 'gain', 'else', 'look', 'keep', 'maxcost', 'gift', 'payhalf', 'cost'];
    const list = (key: string) => { const i = w.indexOf(key); if (i < 0) return null; const out: string[] = []; for (let k = i + 1; k < w.length && !stop.includes(w[k]) && !/^x\d+$/.test(w[k]); k++) out.push(w[k]); return out; };
    const affs = list('affiliation'); const types = list('type');
    const mc = w.indexOf('maxcost');
    if (mc >= 0 && (def.cost ?? 99) > (Number(w[mc + 1]) || 0)) return false;
    const ec = w.indexOf('cost');
    if (ec >= 0 && (def.cost ?? -1) !== (Number(w[ec + 1]) || 0)) return false;
    if (affs && !affs.some((a) => defAffiliations(def).includes(a))) return false;
    if (types && !types.some((t) => (t === 'support' ? def.types.some((x) => ['biome', 'tome', 'trigger'].includes(x)) : t === 'unit' ? this.isUnit(def) : def.types.includes(t)))) return false;
    return true;
  }
  /** "search deck affiliation forested type spell [to top] [x2]": its player picks a matching card out of the pile. */
  private startSearch(seat: Seat, name: string, w: string[], left?: number, then?: SearchThen) {
    const s = this.state; const p = s.players[seat];
    const pile: PileName = w[0] === 'discard' ? 'discard' : 'deck';
    const to: 'top' | 'hand' | 'bottom' = !w.includes('to') ? 'hand' : w[w.indexOf('to') + 1] === 'top' ? 'top' : w[w.indexOf('to') + 1] === 'bottom' ? 'bottom' : 'hand';
    const times = left ?? Number(w.find((x) => /^x\d+$/.test(x))?.slice(1) ?? 1);
    const topN = w[0] === 'top' ? Number(w[1]) || 1 : 0; // "search top 4": only the top cards of the deck, which isn't shuffled after
    const half = (c: HeldCard) => Math.round((this.cards.byNumber.get(c.num)?.cost ?? 0) / 2);
    const found = (topN ? p.deck.filter((c) => !this.jumper(c)).slice(0, topN) : p[pile].filter((c) => !this.jumper(c) && this.passes(this.cards.byNumber.get(c.num), w))).filter((c) => !w.includes('payhalf') || half(c) <= this.barTokens(seat, 'gerzite').length);
    const where = pile === 'deck' ? 'deck' : 'discard pile';
    const finish = () => { if (pile === 'deck' && !topN) { this.shuffle(p.deck); p.shuffles.deck++; } };
    if (!found.length || times <= 0) {
      if (left === undefined) this.log(`${name}: ${p.name} searches their ${where} and finds nothing to take.`, seat);
      finish();
      return;
    }
    if (s.bots?.includes(seat)) {
      const best = [...found].sort((a, b) => (this.cards.byNumber.get(b.num)?.cost ?? 0) - (this.cards.byNumber.get(a.num)?.cost ?? 0))[0];
      this.takeFound(seat, name, pile, to, best.id, w.includes('payhalf'));
      this.startSearch(seat, name, w, times - 1, then);
      return;
    }
    if (left === undefined) this.fx('peek', '', undefined, { effect: pile, seat, count: Math.min(5, found.length) }); // (its player looks through the pile)
    (s.picks ??= []).push({ id: s.nextId++, seat, name, optional: true, options: [], cards: found.map((c) => ({ id: c.id, num: c.num })), search: { w, left: times }, ...(then ? { src: then.src, rest: then.rest, ctx: then.ctx } : {}), text: `${name}: choose a card from your ${where}${to === 'top' ? ' to put on top of your deck' : to === 'bottom' ? ' to put at the bottom of your deck' : ' to put in your hand'}` });
  }
  private takeFound(seat: Seat, name: string, pile: PileName, to: 'hand' | 'top' | 'bottom', id: string, payHalf = false) {
    const s = this.state; const p = s.players[seat];
    const i = p[pile].findIndex((c) => c.id === id);
    if (i < 0) return;
    const where = pile === 'deck' ? 'deck' : 'discard pile';
    if (to === 'hand' && p.hand.length >= CONFIG.handLimit) {
      this.log(`${name}: ${p.name}’s hand is full, so nothing is taken from their ${where}.`, seat);
      return;
    }
    const [c] = p[pile].splice(i, 1);
    if (payHalf) { const cost = Math.round((this.cards.byNumber.get(c.num)?.cost ?? 0) / 2); this.payBar(seat, cost, false); this.log(`${name}: ${p.name} pays ${cost} Gerzite (half its cost).`, seat); }
    if (to === 'hand') {
      p.hand.push(this.rehide(c));
      this.fx('toHand', '', undefined, { effect: pile, seat, count: 1, ...(CONFIG.looks.handTint[name] ? { tint: CONFIG.looks.handTint[name] } : {}) });
    } else this.insertAt(p.deck, this.rehide(c), to === 'bottom' ? 'bottom' : 'top');
    // (what was taken is shown: a search is for a card of a stated kind)
    const text = `${name}: ${p.name} takes ${this.cardName(c.num)} from their ${where}${to === 'top' ? ' and puts it on top of their deck' : to === 'bottom' ? ' and puts it at the bottom of their deck' : ''}.`;
    this.fx('revealCard', '', undefined, { seat, num: c.num, text });
    this.log(text, seat);
  }
  /**
   * "reveal 2 keep affiliation magical gain 2 else discard" (Occult Sorcerer) · "reveal 1 look keep affiliation
   * duned submerged" (Dunehauler: only its player sees it, and a card that isn't kept stays where it is).
   */
  /** Cursed Sarcophagus: the Spell (from the hand) goes face-down under it, held there until it is destroyed. */
  private entombSpell(sarc: FieldCard, handId: string) {
    const s = this.state; const p = s.players[sarc.owner];
    const i = p.hand.findIndex((c) => c.id === handId);
    if (i < 0) return;
    const [c] = p.hand.splice(i, 1);
    const owned = this.owners();
    s.field.push({ id: c.id, num: c.num, spawned: false, owner: sarc.owner, x: sarc.x, z: sarc.z, rot: 0, faceUp: false, layer: s.nextLayer++, merged: [], tierToken: null, attachedTo: sarc.id, heldBy: sarc.id, attachOrder: 0 });
    this.layout(owned);
    this.log(`${this.cardName(sarc.num)}: ${p.name} seals a Spell inside it, face-down.`, sarc.owner);
  }
  /** What turning up the top cards would do, card by card, without doing it yet (Occult Sorcerer). */
  private revealPlan(seat: Seat, name: string, w: string[]): Showing | null {
    const p = this.state.players[seat];
    const n = Number(w[0]) || 1;
    const gainAt = w.indexOf('gain');
    const cards: Showing['cards'] = [];
    let room = CONFIG.handLimit - p.hand.length;
    for (const top of p.deck.filter((c) => !this.jumper(c)).slice(0, n)) {
      const keep = this.passes(this.cards.byNumber.get(top.num), w.slice(w.indexOf('keep'))) && room > 0;
      const to = keep ? 'hand' : w.includes('discard') ? 'discard' : 'stays';
      if (keep) room--;
      cards.push({ id: top.id, num: top.num, to });
      if (to === 'stays') break;
    }
    if (!cards.length) return null;
    return { id: this.state.nextId++, by: seat, name, cards, ...(gainAt >= 0 ? { gain: Number(w[gainAt + 1]) || 1 } : {}), waiting: true };
  }
  /** The cards turned up go where the plan says (the plan was shown to both players first). */
  private revealApply(plan: Showing) {
    const s = this.state; const seat = plan.by; const p = s.players[seat];
    const gz = CONFIG.tokenRules.fractalGives;
    for (const c of plan.cards) {
      const i = p.deck.findIndex((d) => d.id === c.id);
      if (i < 0) continue;
      const what = this.cardName(c.num);
      if (c.to === 'hand' && p.hand.length < CONFIG.handLimit) {
        const [top] = p.deck.splice(i, 1);
        p.hand.push(this.rehide(top));
        this.fx('toHand', '', undefined, { effect: 'deck', seat, count: 1, ...(CONFIG.looks.handTint[plan.name] ? { tint: CONFIG.looks.handTint[plan.name] } : {}) });
        const got = plan.gain ? this.gain(seat, gz, plan.gain) : 0;
        this.log(`${plan.name}: ${p.name} reveals ${what} and takes it${plan.gain ? `, gaining ${this.gainText(gz, plan.gain, got)}` : ''}.`, seat);
        this.drew(seat, top.num);
      } else if (c.to === 'discard') {
        const [top] = p.deck.splice(i, 1);
        this.insertAt(p.discard, this.rehide(top), 'top');
        this.fx('pileMove', '', undefined, { seat, effect: 'deck' });
        this.log(`${plan.name}: ${p.name} reveals ${what} and discards it.`, seat);
      } else this.log(`${plan.name}: ${p.name} reveals ${what}; it stays on top of their deck.`, seat);
    }
  }
  private revealTop(seat: Seat, name: string, w: string[]) {
    const s = this.state; const p = s.players[seat];
    const n = Number(w[0]) || 1;
    const look = w.includes('look');
    const gainAt = w.indexOf('gain');
    const gz = CONFIG.tokenRules.fractalGives;
    for (let k = 0; k < n; k++) {
      const top = p.deck.find((c) => !this.jumper(c));
      if (!top) break;
      const def = this.cards.byNumber.get(top.num);
      const keep = this.passes(def, w.slice(w.indexOf('keep'))) && p.hand.length < CONFIG.handLimit;
      const what = this.cardName(top.num);
      if (keep) {
        p.deck.splice(p.deck.indexOf(top), 1);
        p.hand.push(this.rehide(top));
        this.fx('toHand', '', undefined, { effect: 'deck', seat, count: 1 });
        const got = gainAt >= 0 ? this.gain(seat, gz, Number(w[gainAt + 1]) || 1) : 0;
        const text = `${name}: ${p.name} reveals ${what} and takes it${gainAt >= 0 ? `, gaining ${this.gainText(gz, Number(w[gainAt + 1]) || 1, got)}` : ''}.`;
        this.fx('revealCard', '', undefined, { seat, num: top.num, text });
        this.log(text, seat);
        this.drew(seat, top.num);
      } else if (w.includes('discard')) {
        p.deck.splice(p.deck.indexOf(top), 1);
        this.insertAt(p.discard, this.rehide(top), 'top');
        const text = `${name}: ${p.name} reveals ${what} and discards it.`;
        this.fx('revealCard', '', undefined, { seat, num: top.num, text });
        this.log(text, seat);
      } else {
        const text = `${name}: the top card of your deck is ${what}. It stays there.`;
        if (look) this.fx('status', '', undefined, { effect: 'notice', seat, text });
        else this.fx('revealCard', '', undefined, { seat, num: top.num, text });
        this.log(look ? `${name}: ${p.name} looks at the top card of their deck.` : text, seat);
        break;
      }
    }
  }
  /** A player has drawn a card: their cards that watch for that act ("drawn: draw 1 if card frigid if my_turn": Mountaineer). */
  private drew(seat: Seat, num: number) {
    if (!CONFIG.combat.enabled || this.fxDepth > 6) return;
    const def = this.cards.byNumber.get(num);
    if (!this.crypting) this.fireAll('any_drawn', () => true, { actor: seat, amount: num });
    for (const c of [...this.state.field]) {
      if (c.owner !== seat || !this.state.field.includes(c)) continue;
      const list = this.effectsOf(c, 'drawn').filter((e) => e.conds.filter((k) => k[0] === 'card').every((k) => k.slice(1).some((a) => def?.affiliations.includes(a))));
      if (!list.length) continue;
      const text = `${this.state.players[seat].name} reveals ${this.cardName(num)} (${this.cardName(c.num)}).`;
      this.fx('revealCard', '', undefined, { seat, num, text });
      this.log(text, seat);
      this.runEffects(c, list, {});
    }
  }
  /**
   * What the cards in play, and the discounts a player holds, change a card's cost by, for this player:
   *   "cost: -1 type tome" on one of your cards (Arcane Library) · "cost: +1 foe affiliation spectral if untapped"
   *   on an opponent's (Tarnished Souls) · "per count" = that much for each unit left of its Count ·
   *   "first" = only the first such card you play each turn (Mystical Cleric)
   * and a discount held from an effect ("discount 1 affiliation submerged turn": Oasis Nymph's Tap).
   */
  private crypting = false; // (Ancient Crypt's second draw doesn't set it off again)
  private lastPaid = 0; // what was paid for the card being played ("damage paid x4": Crystalline Discharge)
  private lastChoice: number | null = null; // the number its player gave for a cost formula as it was played (Bloodburst: health removed)
  private gifts: string[] = []; // what the discount just spent also gives the card played with it ("gift ambush overclock 2": Bellowing Horn)
  private costChange(seat: Seat, def: CardDef | undefined, spend = false): number {
    const s = this.state; const p = s.players[seat];
    if (!def || !CONFIG.combat.enabled) return 0;
    let delta = 0;
    const stamp = this.turnStamp();
    const swarmsSeen = new Set<string>(); // (a swarm's cost rule counts once for the whole swarm)
    for (const c of s.field) {
      if (!c.faceUp || this.hasOn(c, CONFIG.turn.lifetime.expiredToken)) continue;
      if (c.swarm) { if (swarmsSeen.has(c.swarm)) continue; swarmsSeen.add(c.swarm); }
      for (const e of this.cards.byNumber.get(c.num)?.effects.length ? this.effectsOf(c, 'cost') : []) {
        if (e.words.includes('self')) continue; // (a rule about the card's own cost: below)
        const foe = e.words.includes('foe');
        if (foe ? !this.opposed(c.owner, seat) : !this.allies(c.owner, seat)) continue;
        if (!this.passes(def, e.words) || !e.conds.every((k) => this.condHolds(c, k, {}))) continue;
        if (e.words.includes('first') && p.playedNow?.stamp === stamp && def.types.some((t) => p.playedNow!.types.includes(t))) continue;
        delta += (Number(e.words[0]) || 0) * (e.words.includes('per') ? Math.max(1, this.unitsLeft(c)) : 1);
      }
    }
    // the card's own cost rules ("cost: -2 self if more_units": Pyreblast costs 2 less while you control more Units)
    for (const e of def.effects.filter((x) => x.when === 'cost' && x.words.includes('self'))) {
      const units = (sd: number) => s.field.filter((c) => c.faceUp && this.isUnit(this.cards.byNumber.get(c.num)) && this.sideOfCard(c) === sd && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken)).length;
      const holds = e.conds.every((k) => (k[0] === 'more_units' ? units(sideOf(seat)) > units(-sideOf(seat)) : true));
      if (holds) delta += Number(e.words[0]) || 0;
    }
    for (const d of [...(p.discounts ?? [])]) {
      if (d.stamp !== undefined && d.stamp !== stamp) { p.discounts = p.discounts!.filter((x) => x !== d); continue; }
      if (!this.passes(def, d.w)) continue;
      delta -= d.amount;
      if (spend) { p.discounts = p.discounts!.filter((x) => x !== d); if (d.w.includes('gift')) this.gifts.push(...d.w.slice(d.w.indexOf('gift') + 1)); if (d.by) this.discountFrom = d.by; }
    }
    if (p.discounts && !p.discounts.length) delete p.discounts;
    return delta;
  }
  /** What a card's own `deal` effects, and the auras round it, add to one hit on this target. */
  private dealMod(from: FieldCard, to: FieldCard | undefined, fresh: boolean) {
    let add = 0; let mult = 1; let pierce = false; let vs = false;
    const toDef = to?.faceUp ? this.cards.byNumber.get(to.num) : undefined;
    const fromDef = this.cards.byNumber.get(from.num);
    for (const e of this.effectsOf(from, 'deal')) {
      if (!e.conds.every((c) => this.condHolds(from, c, { target: to, fresh }))) continue;
      const [amt, kind, what, ...list] = e.words;
      if (amt === 'pierce') { pierce = true; continue; }
      let times = 1;
      if (kind === 'vs') {
        if (!to) continue;
        if (what === 'flying' ? !this.fliesNow(to) : what === 'rarity' ? !list.includes(toDef?.rarity ?? '') : !list.some((a) => this.affOf(to).includes(a))) continue; // ("vs flying": Midnight Hunter)
        if (what === 'rarity') vs = true; // (Highcard Assassin: its X slashes across the high card it strikes)
      } else if (kind === 'per') {
        if (what === 'affiliation') times = this.state.field.filter((c) => c.faceUp && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && list.some((a) => this.affOf(c).includes(a))).length;
        else if (what === 'lost') times = Math.max(0, (fromDef?.count ?? 0) - this.unitsLeft(from));
      }
      if (amt.startsWith('x')) mult *= Number(amt.slice(1)) || 1;
      else add += (Number(amt) || 0) * times;
    }
    return { add: add + this.auraFor(from, 'damage'), mult, pierce, vs };
  }
  /** What the auras of the cards round it give this card: `damage`, `range` or `buffer` ("aura: damage 2": own units within its range). */
  /** `lent`: collects the cards whose boost reaches this one only through a Sand Wyrm (for the table's sand scenes). */
  private auraFor(card: FieldCard, what: string, lent?: Set<string>): number {
    if (!CONFIG.combat.enabled) return 0;
    const at = this.standsAt(card);
    if (!at) return 0;
    const row = laneOrder().map((slot) => ({ slot, x: slot.x * at.side })).sort((a, b) => a.x - b.x);
    const i = row.findIndex((l) => l.slot === at.slot);
    let n = 0;
    for (const src of this.state.field) {
      if (src === card || !src.faceUp) continue;
      const def = this.cards.byNumber.get(src.num);
      for (const e of def?.effects.length ? this.effectsOf(src, 'aura') : []) {
        if (e.words[0] !== what || this.hasOn(src, CONFIG.turn.lifetime.expiredToken)) continue;
        if (this.shrugsOff(card, src, 'aura')) continue; // (Blood Dragon: no Undead card's aura reaches it, boost or penalty)
        if (!e.words.includes('both') && this.sideOfCard(src) !== at.side) continue;
        if (def!.types.includes('biome') && this.biomeOffFor(at.side)) continue; // Traveler's Map // ("both": a Biome's, for both sides of the field)
        if (e.words.includes('unentrenched') && this.hasOn(card, CONFIG.tokenRules.entrenchedToken)) continue;
        const k = e.words.indexOf('affiliation');
        // (Sand Wyrm: its player's own Support cards give their boosts, never their penalties, to Duned cards too)
        const wyrmed = def!.types.some((t) => ['biome', 'tome', 'trigger'].includes(t)) && this.allies(src.owner, card.owner) && !((Number(e.words[1]) || 0) < 0) && this.affOf(card).includes('duned') && this.sandWyrmFor(card.owner);
        const ownAff = k < 0 || e.words.slice(k + 1).some((a) => this.affOf(card).includes(a));
        if (!ownAff && !wyrmed) continue;
        if (e.words.includes('units') && !this.isUnit(this.cards.byNumber.get(card.num))) continue;
        if (!e.words.includes('field') && !e.words.includes('both')) {
          const sat = this.standsAt(src);
          const j = sat ? row.findIndex((l) => l.slot === sat.slot) : -1;
          if (i < 0 || j < 0 || Math.abs(i - j) > this.rangeNow(src, def!, true)) continue;
        }
        n += (Number(e.words[1]) || 0) * this.biomeDoubling(src);
        if (!ownAff) lent?.add(src.id);
      }
    }
    return n;
  }
  /** Sand Wyrm at work: the Support cards lending their boosts to Duned cards right now, and the Wyrms doing it. */
  private wyrmLending(): Set<string> {
    const lent = new Set<string>();
    if (!CONFIG.combat.enabled) return lent;
    for (const c of this.state.field) {
      if (!c.faceUp || !this.affOf(c).includes('duned') || !this.isUnit(this.cards.byNumber.get(c.num)) || !this.sandWyrmFor(c.owner)) continue;
      for (const what of ['damage', 'range', 'buffer', 'thorns', 'frenzy', 'antiair']) this.auraFor(c, what, lent);
    }
    if (lent.size) {
      const owners = new Set([...lent].map((id) => this.findField(id)?.owner));
      for (const c of this.state.field) if (c.faceUp && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'sand_wyrm') && [...owners].some((o) => o !== undefined && this.allies(c.owner, o))) lent.add(c.id);
    }
    return lent;
  }
  /**
   * "always: tally health affiliation forested" (Forest Hivemind): the card carries a Health counter equal to the
   * number of cards of that affiliation in play, kept up to date after every move.
   */
  private syncTallies() {
    const s = this.state;
    if (!CONFIG.combat.enabled) return;
    const hdef = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.healthToken);
    for (const card of s.field) {
      const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
      const e = def?.effects.find((x) => x.when === 'always' && x.words[0] === 'tally');
      if (!e || !hdef) continue;
      const affs = e.words.slice(3);
      const n = s.field.filter((c) => c.faceUp && !c.heldBy && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && affs.some((a) => this.affOf(c).includes(a))).length;
      const mine = s.tokens.find((t) => t.tally === card.id);
      if (mine && this.cardUnder(mine)?.id !== card.id) delete mine.tally;
      const have = s.tokens.find((t) => t.tally === card.id);
      // (Forest Hivemind glows as it gains or loses: a look for more, another for less)
      const was = have?.value ?? 0;
      if (n !== was && CONFIG.looks.tally.up) this.look(n > was ? CONFIG.looks.tally.up : CONFIG.looks.tally.down, card);
      if (n <= 0) { if (have) this.removeTokens([have]); continue; }
      if (have) have.value = n;
      else {
        const side = sideOf(card.owner); const sc = card.scale ?? 1;
        s.tokens.push({ id: this.newId('t'), tokenId: hdef.id, x: card.x - 1.6 * side * sc, z: card.z - 2.2 * side * sc, value: n, layer: s.nextLayer++, by: card.owner, tally: card.id });
      }
    }
  }
  /** Does one of its own effects give this card Frenzy right now? ("frenzy: on if health<=10") */
  private frenzied(card: FieldCard) {
    return this.effectsOf(card, 'frenzy').some((e) => e.conds.every((c) => this.condHolds(card, c, {})));
  }

  // ---------------- attacks: the table deals the damage (CONFIG.combat) ----------------

  /** This turn, as a key: what a card did "this turn" is kept against it. */
  private turnKey() {
    const s = this.state;
    return `${s.turn}:${s.players[s.turn].turns}`;
  }
  private valueOn(card: FieldCard, effect: string) {
    return this.tokensOn(card).filter((t) => this.effectOf(t) === effect).reduce((n, t) => n + t.value, 0);
  }
  private hasOn(card: FieldCard, effect: string) {
    return this.tokensOn(card).some((t) => this.effectOf(t) === effect);
  }
  private isUnit(def: CardDef | undefined) {
    return !!def && (def.types.includes('troop') || def.types.includes('building'));
  }
  /** Wingwrap: an opposing Spell holds every Troop on this card's side to the ground. */
  private wingwrapped(card: FieldCard) {
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    return !!def?.types.includes('troop') && this.state.field.some((c) => c.faceUp && this.sideOfCard(c) !== this.sideOfCard(card) && !!this.cards.byNumber.get(c.num)?.effects.length && this.effectsOf(c, 'always').some((e) => e.words[0] === 'ground_foes'));
  }
  /** Gravity Well: the active one an opponent of this Unit's player has in play (it grounds the Unit, and keeps its attacks to the lane in front of it). */
  private wellOn(card: FieldCard): FieldCard | undefined {
    if (!card.faceUp || !this.isUnit(this.cards.byNumber.get(card.num))) return undefined;
    const by = this.possessor(card) ?? card.owner;
    return this.state.field.find((g) => g.faceUp && this.opposed(g.owner, by) && !this.isExpired(g) && !!this.cards.byNumber.get(g.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'well') && this.effectsOf(g, 'always').some((e) => e.words[0] === 'well'));
  }
  private fliesNow(card: FieldCard) {
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    if (this.wingwrapped(card)) return false; // Wingwrap
    if (this.wellOn(card)) return false; // Gravity Well grounds it
    if (this.tokensOn(card).some((t) => t.grounds)) return false; // Grasping Twine's Lock holds it down
    // (Flamebound Synergy: Flying only while both of its units stand)
    if (def && def.count && def.traits.some((t) => /flying\/ground/i.test(t)) && this.unitsLeft(card) < def.count) return this.hasOn(card, 'flying') && !this.hasOn(card, 'ground');
    const troopFlies = !!def && def.types.includes('building') && def.types.includes('troop') && !!card.troopForm && def.traits.some((t) => /ground\/flying/i.test(t)); // Melstone Terror: a Flying Troop once its SP is gone
    return (def?.movement === 'flying' || troopFlies || this.hasOn(card, 'flying')) && !this.hasOn(card, 'ground');
  }
  /** Where a card stands: its own slot, or (a summoned unit attached to a card) that card's. */
  private standsAt(card: FieldCard) {
    const parent = card.attachedTo ? this.findField(card.attachedTo) : undefined;
    return slotAt(parent ?? card);
  }
  /**
   * One of a card's traits right now. A trait written "A/B" is one of two attacks: a card with two damage numbers
   * (Firelance 12/3) uses the first while it has its Special Hit and the second without. A Direct, Pierce or
   * Target token on the card says so whatever its sheet row does.
   */
  private traitNow(card: FieldCard, def: CardDef, word: string) {
    const w = word.toLowerCase();
    if (['direct', 'pierce', 'target'].includes(w)) {
      const set = ['direct', 'pierce', 'target'].filter((e) => this.hasOn(card, e));
      if (set.length) return set.includes(w);
    }
    const special = numbersIn(def.damage).length > 1 && this.hasOn(card, 'special-hit');
    return def.traits.some((t) =>
      t.split('|').some((part) => {
        const opts = part.split('/').map((o) => o.trim().toLowerCase());
        return (opts.length > 1 ? (special ? opts[0] : opts[opts.length - 1]) : opts[0]) === w;
      }),
    );
  }
  /** A card's range right now: its sheet row (the first of two numbers with its Special Hit), plus Range tokens. */
  private rangeNow(card: FieldCard, def: CardDef, own = false) {
    const special = this.hasOn(card, 'special-hit');
    const nums = numbersIn(def.rangeText ?? '');
    const two = nums.length > 1;
    let base = two ? (special ? nums[0] : nums[nums.length - 1]) : (def.range ?? 0);
    const m = special ? /first attack deals \d+[^.]*?has (\d+) range/i.exec(def.text) : null;
    if (m) base = Math.max(base, Number(m[1]));
    const extra = this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.reach.rangeToken && !(two && t.from === card.id)).reduce((n, t) => n + t.value, 0);
    return Math.max(0, Math.floor(base + extra + (own ? 0 : this.auraFor(card, 'range'))));
  }
  /**
   * What one attack of this card is: how many hits, and the damage of each. A Count is one hit per unit still
   * standing; "attacks in two instances of 2 damage each" is two hits of 2; Frenzy doubles the hits. The damage
   * is the sheet row's (Tier III: more; a Darkgale unit: double), plus Damage Boost and Overclock, less Weakness;
   * a Minion deals half, rounded up.
   */
  /** Does this card lead others into its attacks, and take the hits meant for the one riding with it? (always: leads) */
  private isLeader(card: FieldCard) {
    return card.faceUp && !!this.cards.byNumber.get(card.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'leads');
  }
  /** Sandstone Colossus (always: colossus): it hits the two lanes beside the one across, never that one. */
  private isColossus(card: FieldCard) {
    return card.faceUp && !!this.cards.byNumber.get(card.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'colossus');
  }
  /** Is this card one that fights as one with the cards Linked to it? (always: brain — Duned Brain) */
  private isBrain(card: FieldCard) {
    return card.faceUp && !!this.cards.byNumber.get(card.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'brain');
  }
  /** The cards joined to this one by Link counters (not the Emperor–Minion link). */
  private linked(card: FieldCard) {
    return this.linkPartners(card).filter((p) => p.via).map((p) => p.card).filter((c) => c.faceUp && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken));
  }
  private attackOf(card: FieldCard, def: CardDef, alone = false) {
    const nums = numbersIn(tieredDamage(def, this.tierOf(card)));
    let per = !nums.length ? 0 : nums.length > 1 && !this.hasOn(card, 'special-hit') ? nums[nums.length - 1] : nums[0];
    if (def.forms && nums.length === def.forms.length && !this.hasOn(card, 'special-hit')) per = nums[Math.min(nums.length - 1, card.form ?? 0)]; // a card summoned one of two ways: the damage of the way it came (Forgotten Monarch 4/5, Clay Soldiers 3/2)
    if (card.clone?.damage !== undefined) per = card.clone.damage; // a card whose damage was set as it appeared (a split unit, a Twilight Beast)
    if (card.dmgNow !== undefined) per = card.dmgNow; // the damage it came back with (Void Walker)
    if (card.sun) per = card.sun.base; // Sun Magnifier's base damage as it grows
    if (card.paidHit !== undefined) per = card.paidHit; // an attack paid for as it was declared (Dark Gerzite Titan)
    if (card.boostHit) per += card.boostHit; // damage added to this attack (Volcanic Disciple)
    let hits = Math.max(1, this.valueOn(card, CONFIG.tokenRules.countToken) || 1);
    const WORDS: Record<string, number> = { two: 2, three: 3, four: 4, five: 5, six: 6 };
    const multi = /attacks in (\w+) instances of (\d+) damage/i.exec(def.text);
    const stages = multi ? (WORDS[multi[1].toLowerCase()] ?? Number(multi[1])) : 0;
    if (multi && stages > 1 && per > 0) {
      per = Math.max(1, Math.round(per / stages));
      hits *= stages;
    }
    if (card.restHits && per > 0) hits += card.restHits; // (Marlin Knight: a hit more for each turn it went without attacking)
    if (per > 0 && this.hasOn(card, CONFIG.turn.darkgale.token)) per *= CONFIG.combat.darkgaleDeals;
    if (per > 0 || isEmperor(def) || card.paidHit !== undefined) per = Math.max(0, per + this.valueOn(card, 'damage-boost') + this.valueOn(card, 'overclock') - this.valueOn(card, 'weakness'));
    if (this.standsAt(card)?.slot.name === 'Minion') per = Math.ceil(per / 2);
    if (this.hasOn(card, 'frenzy') || this.frenzied(card) || this.auraFor(card, 'frenzy') > 0) hits *= 2;
    // Vialmaster: one more attack for each Spell its owner has cast this turn before it was tapped
    if (this.effectsOf(card, 'always').some((e) => e.words[0] === 'vial_attacks')) { const p = this.state.players[card.owner]; hits += p.playedNow?.stamp === this.turnStamp() ? p.playedNow.types.filter((t) => t === 'spell').length : 0; }
    // Duned Brain: the damage of every Troop linked to it is its own as well
    if (!alone && this.isBrain(card)) for (const c of this.linked(card)) per += this.attackOf(c, this.cards.byNumber.get(c.num)!, true).per;
    return { per, hits };
  }
  /**
   * A unit that can never attack: its printed damage is 0 (or none) and nothing has given it any of its own
   * (Skeleton Dropship, Purifier Spirit). Its menu offers no Attack, and Damage Boost and Overclock never go on it.
   */
  private neverAttacks(card: FieldCard): boolean {
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    if (!def || !this.isUnit(def) || isEmperor(def)) return false;
    if (def.types.includes('building') && def.types.includes('troop')) return false; // (Melstone Terror)
    if (!/^\s*0*\s*$/.test(def.damage) || numbersIn(tieredDamage(def, this.tierOf(card))).some((n) => n > 0)) return false;
    if (this.isBrain(card) || this.effectsOf(card, 'attack').some((e) => e.words[0] === 'pay_any')) return false;
    return !((card.clone?.damage ?? 0) > 0 || (card.dmgNow ?? 0) > 0 || (card.sun?.base ?? 0) > 0 || (card.paidHit ?? 0) > 0);
  }
  /** Does this card have a Tap ability of its own (not one that is simply how it attacks: Firelance)? */
  private hasTapAbility(def: CardDef) {
    return (def.abilities.includes('tap') || def.effects.some((e) => e.when === 'tap')) && !CONFIG.combat.tapIsAttack.includes(def.name);
  }
  /**
   * Frenzy: two actions this turn, each an attack of its own (a new target each time) or its Tap. How many it has
   * left (null = it has no Frenzy). One action is half of its hits (a Count's units each).
   */
  private frenzyLeft(card: FieldCard): number | null {
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    if (!def || (card.actions ?? 0) > 1) return null;
    if (!(this.hasOn(card, 'frenzy') || this.frenzied(card) || this.auraFor(card, 'frenzy') > 0)) return null;
    const hits = this.attackOf(card, def).hits;
    const per = Math.max(1, Math.floor(hits / 2));
    const key = this.turnKey();
    const left = card.swings?.key === key ? card.swings.left : card.acted === key ? 0 : hits;
    return Math.floor(left / per);
  }
  /** A Frenzied unit uses its Tap as one of its two actions: half of its hits this turn are spent on it. */
  private frenzyTapSpend(card: FieldCard) {
    const def = this.cards.byNumber.get(card.num)!;
    const hits = this.attackOf(card, def).hits;
    const key = this.turnKey();
    if (card.swings?.key !== key) card.swings = { key, left: hits };
    card.swings.left = Math.max(0, card.swings.left - Math.max(1, Math.floor(hits / 2)));
  }
  /** Why this card can't attack right now, for this player (null = it can). */
  private attackWhy(seat: Seat, card: FieldCard): string | null {
    const s = this.state;
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    if (!CONFIG.combat.enabled) return 'Attacks are dealt by hand at this table.';
    if (!def || !(this.isUnit(def) || isEmperor(def)) || this.untouchable(card)) return 'Only Troops, Buildings, Minions and Emperors attack.';
    const name = def.name;
    const at = this.standsAt(card);
    if (!at || !['Lane', 'Minion', 'Emperor'].includes(slotKind(at.slot.name))) return `${name} isn’t in a lane.`;
    if (s.turn !== seat) return 'Units attack on your own turn.';
    if (s.players[seat].turns === 0) return 'No unit may attack on your first turn.';
    if (s.phase < 1) return 'Finish your Overdraw Phase first.';
    if (s.phase > 2) return 'Your Attack Phase is over.';
    const abilityLocks = CONFIG.emperorAbilities.enabled && !!def.emperorAbilities.length; // a Lock on an Emperor locks an ability
    const stop = this.tokensOn(card).find((t) => CONFIG.combat.blockers.includes(this.effectOf(t)) && !(abilityLocks && this.effectOf(t) === CONFIG.tokenRules.lockToken));
    if (stop) return `${name} can’t attack: ${this.tokenDb.byId.get(stop.tokenId)?.name ?? 'a status'}.`;
    if (def.types.includes('building') && def.types.includes('troop')) { if (!card.troopForm) return `${name} is a Building for now: it can’t attack.`; } // (Melstone Terror)
    else if (/cannot attack until its shield reaches 0/i.test(def.text) ? this.valueOn(card, 'shield') > 0 : /\bcannot attack\b(?! during your next turn)/i.test(def.text)) return `${name} can’t attack (see its text).`; // ("cannot attack during your next turn" is a rest after attacking: Slag Launcher stays tapped)
    const brain = this.isBrain(card) ? undefined : this.linked(card).find((c) => this.isBrain(c));
    if (brain) return `${name} is part of ${this.cardName(brain.num)} now: attack with ${this.cardName(brain.num)}.`;
    if (this.neverAttacks(card)) return `${name} never attacks.`;
    const paysPer = this.effectsOf(card, 'attack').some((e) => e.words[0] === 'pay_any');
    if (!paysPer && this.attackOf(card, def).per <= 0) return `${name} has no attack damage.`;
    for (const e of this.effectsOf(card, 'attack')) {
      // an attack that has to be paid for (Templar Beam: 1 Dark Gerzite)
      if (e.words[0] !== 'pay') continue;
      const bar = e.words[2] === 'dark' ? 'dark' : 'gerzite';
      if (this.barTokens(seat, bar).length < (Number(e.words[1]) || 1)) return `${name} needs ${e.words[1]} ${bar === 'dark' ? 'Dark Gerzite' : 'Gerzite'} to attack.`;
    }
    const key = this.turnKey();
    if (card.swings?.key === key) return card.swings.left > 0 ? null : `${name} has already attacked this turn.`;
    if (card.acted === key) return `${name} has already been used this turn.`;
    if (card.rot % 2 === 1) return `${name} is tapped.`;
    return null;
  }
  /** Why this card's Tap ability can't be used right now (null = it can). */
  private tapWhy(seat: Seat, card: FieldCard): string | null {
    const s = this.state;
    const def = card.faceUp ? this.cards.byNumber.get(card.num) : undefined;
    if (!def?.abilities.includes('tap') && !def?.effects.some((e) => e.when === 'tap')) return 'It has no Tap ability.';
    if (s.turn !== seat) return 'A Tap ability is used on your own turn.';
    const stop = this.tokensOn(card).find((t) => CONFIG.combat.blockers.includes(this.effectOf(t)));
    if (stop) return `${def.name} can’t use its Tap: ${this.tokenDb.byId.get(stop.tokenId)?.name ?? 'a status'}.`;
    const tapFx = this.effectsOf(card, 'tap');
    if (tapFx.length && tapFx.every((e) => e.conds.some((c) => c[0] === 'form')) && !tapFx.some((e) => e.conds.filter((c) => c[0] === 'form').every((c) => this.condHolds(card, c, {})))) return `${def.name} has no Tap ability the way it was summoned.`;
    const pay = this.effectsOf(card, 'tap').find((e) => e.words[0] === 'pay' && !e.conds.length);
    const bar0 = pay?.words[2] === 'dark' ? 'dark' : 'gerzite';
    if (pay && this.barTokens(seat, bar0).length < (Number(pay.words[1]) || 1)) return `${def.name}’s Tap costs ${pay.words[1]} ${bar0 === 'dark' ? 'Dark Gerzite' : 'Gerzite'}.`;
    // Frenzy: its Tap may be one or both of its two actions this turn (an attack and its Tap, or its Tap twice)
    const fl = this.frenzyLeft(card);
    if (fl !== null && card.acted === this.turnKey() && card.swings?.key === this.turnKey()) return fl > 0 ? null : `${def.name} has used both of its Frenzy actions this turn.`;
    const retap = this.retapCost(card);
    if (card.rot % 2 === 1 || card.acted === this.turnKey()) {
      // (a Tap ability that may be used while already tapped, for a price: Wandering Spirit)
      if (retap !== null && card.retapAt !== this.turnKey()) return this.barTokens(seat, 'gerzite').length >= retap ? null : `${def.name} is tapped: using its Tap anyway costs ${retap} Gerzite.`;
    }
    if (card.acted === this.turnKey()) return `${def.name} has already been used this turn.`;
    if (card.rot % 2 === 1) return `${def.name} is tapped.`;
    if (card.rewoundAt !== undefined && card.rewoundAt === s.players[seat].turns - 1 && tapFx.some((e) => e.words[0] === 'rewind')) return `${def.name}’s Tap can’t be used two turns in a row.`; // (Time Bender)
    return null;
  }
  /** A Tap ability that may also be used while its card is already tapped, and what that costs ("tap: retap 1"), or null. */
  private retapCost(card: FieldCard): number | null {
    const e = this.effectsOf(card, 'tap').find((x) => x.words[0] === 'retap');
    return e ? Number(e.words[1]) || 0 : null;
  }
  /** The units standing in a slot on one side that an attack could be aimed at (not a destroyed or untouchable card). */
  private unitsAt(slot: Slot, side: 1 | -1, except?: FieldCard, spirits = false) {
    return this.state.field.filter((c) => {
      if (c === except) return false;
      if (!spirits && this.intangible(c)) return false; // (a Spirit Soldier can't be aimed at and doesn't fill its lane: only what reaches a whole lane finds it)
      const at = this.standsAt(c);
      if (!at || at.slot !== slot || at.side !== side) return false;
      if (c.tribunalOf) return false; // (Tribunal Trigger's face-down Unit can't be targeted and blocks nothing)
      if (!c.faceUp) return true; // a face-down card in a lane counts as a unit
      const def = this.cards.byNumber.get(c.num);
      return (this.isUnit(def) || (!!def && isEmperor(def))) && !this.untouchable(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken);
    });
  }
  /**
   * The cards this card may attack right now. A unit in a lane reaches the lane in front of it, and with range
   * the lanes that far either side; a ground unit needs range to hit a Flying one. With Direct and nothing in
   * front of it, it reaches the opposing Emperor and Minion. An Emperor or a Minion reaches every opposing unit,
   * and the Emperor and Minion once a lane stands empty. A Possessed unit attacks its owner's other units.
   */
  private attackTargets(card: FieldCard, alone = false): FieldCard[] {
    let out = this.reachOf(card, alone);
    // Symbiotic Vines: it may also attack any opposing Unit within its partner's reach
    for (const v of this.state.field) if (v.pair?.includes(card.id) && v.faceUp && !this.isExpired(v) && this.cards.byNumber.get(v.num)?.effects.some((e) => e.words[0] === 'vines')) {
      const mate = this.findField(v.pair.find((id) => id !== card.id) ?? '');
      if (mate) out = [...new Set([...out, ...this.reachOf(mate, true).filter((u) => this.sideOfCard(u) !== this.sideOfCard(card) && this.isUnit(this.cards.byNumber.get(u.num)))])];
    }
    // Jouster, Fatal Attraction: a Unit that must attack one card can attack nothing else (and nothing at all if it can't reach it)
    const forced = this.forcedTarget(card);
    if (forced) return out.includes(forced) ? [forced] : [];
    // Taunt: while one of the cards it could attack is taunting, it can attack nothing else
    const stamp = this.turnStamp();
    const side = this.sideOfCard(card);
    if (this.state.field.some((u) => u.tauntAt === stamp && this.sideOfCard(u) !== side)) return out.filter((u) => u.tauntAt === stamp);
    return out;
  }
  /** The card this Unit is made to attack right now (Jouster's challenge, Fatal Attraction), or undefined. */
  private forcedTarget(card: FieldCard): FieldCard | undefined {
    const s = this.state;
    if (card.mustAttack) { const j = this.findField(card.mustAttack); if (j && !this.hasOn(j, CONFIG.turn.lifetime.expiredToken)) return j; delete card.mustAttack; }
    const at = this.standsAt(card);
    for (const fa of s.field) {
      if (!fa.faceUp || !fa.host || this.isExpired(fa) || !this.cards.byNumber.get(fa.num)?.effects.some((e) => e.words[0] === 'attract')) continue;
      const host = this.findField(fa.host);
      const ha = host ? this.standsAt(host) : null;
      if (!host || !ha || !at || this.sideOfCard(host) === this.sideOfCard(card) || slotKind(ha.slot.name) !== 'Lane' || slotKind(at.slot.name) !== 'Lane') continue;
      const r = this.rangeNow(host, this.cards.byNumber.get(host.num)!);
      if (Math.abs(this.laneRow(ha.side).indexOf(ha.slot) - this.laneRow(at.side).indexOf(at.slot)) <= r) return host;
    }
    return undefined;
  }
  private reachOf(card: FieldCard, alone = false): FieldCard[] {
    // Duned Brain reaches whatever any Troop linked to it can reach
    if (!alone && this.isBrain(card)) return [...new Set([card, ...this.linked(card)].flatMap((c) => this.reachOf(c, true)))];
    const def = this.cards.byNumber.get(card.num);
    const at = this.standsAt(card);
    if (!def || !at) return [];
    const kind = slotKind(at.slot.name);
    const row = (side: number) => laneOrder().map((slot) => ({ slot, x: slot.x * side })).sort((a, b) => a.x - b.x);
    // the units that belong in a lane, and the intruders standing in it (see intruder): an intruder never fills or blocks a lane
    // (a unit riding with its leader can't be aimed at while the leader stands: the Zombie Horse under its Knight.
    // Splash still reaches it.)
    const riding = (u: FieldCard) => { const lead = u.attachedTo ? this.findField(u.attachedTo) : undefined; return !!lead && this.isLeader(lead) && !this.hasOn(lead, CONFIG.turn.lifetime.expiredToken); };
    const by = this.possessor(card) ?? card.owner;
    const veiled = (u: FieldCard) => (u.veilAt !== undefined && u.veilAt === this.turnStamp()) || !!u.cloakBy || !!this.laneGuard(u, by); // (Hidden Veil, Arctic Prowler; an Invisibility Cloak; Protective Petals' lane)
    const home = (slot: Slot, side: 1 | -1) => this.unitsAt(slot, side, card).filter((u) => !this.intruder(u) && !riding(u) && !veiled(u) && !u.soul && !u.tribunalOf);
    // (a lane is empty, for reaching past it to the Emperor, only if nothing stands there: a Unit guarded by
    // Protective Petals can't be targeted, but it still holds its lane)
    const held = (slot: Slot, side: 1 | -1) => home(slot, side).length > 0 || this.unitsAt(slot, side, card).some((u) => !this.intruder(u) && !riding(u) && !u.soul && !u.tribunalOf && !!this.laneGuard(u, by));
    const guests = (slot: Slot, side: 1 | -1) => this.unitsAt(slot, side, card).filter((u) => this.intruder(u) && !riding(u) && !veiled(u) && !u.soul);
    const leadersOf = (side: 1 | -1) => CONFIG.slots.filter((sl) => ['Emperor', 'Minion'].includes(slotKind(sl.name))).flatMap((sl) => this.unitsAt(sl, side, card));
    const range = this.rangeNow(card, def);
    // Gravity Well: an opposing Unit only reaches the lane in front of it · Midnight Hunter: its range only reaches Flying Units
    const reach = this.wellOn(card) ? 0 : range;
    const airOnly = this.effectsOf(card, 'always').some((e) => e.words[0] === 'air_range');
    const leader = kind === 'Emperor' || kind === 'Minion';
    const hitsFlying = leader || this.fliesNow(card) || (range > 0 && !new RegExp(CONFIG.reach.noFlying, 'i').test(def.text)) || this.auraFor(card, 'antiair') > 0; // (Treetop Aviary)
    const canHit = (u: FieldCard) => hitsFlying || !this.fliesNow(u);
    const out: FieldCard[] = [];
    if (this.intruder(card)) {
      // a unit in the other side's lane (summoned there, or Possessed): it fights the units of the lanes it can
      // reach on that side, and their Emperor and Minion once its own lane holds none of them
      if (kind !== 'Lane') return out;
      const theirs = row(at.side);
      const i = theirs.findIndex((l) => l.slot === at.slot);
      theirs.forEach((l, j) => { if (Math.abs(j - i) <= reach) out.push(...home(l.slot, at.side).filter((u) => canHit(u) && (j === i || !airOnly || this.fliesNow(u)))); });
      if (!held(at.slot, at.side)) out.push(...leadersOf(at.side));
      return out;
    }
    const foe = -at.side as 1 | -1;
    const theirs = row(foe);
    const mine = row(at.side);
    if (leader) {
      for (const l of theirs) out.push(...home(l.slot, foe));
      if (theirs.some((l) => !held(l.slot, foe))) out.push(...leadersOf(foe));
      for (const l of mine) out.push(...guests(l.slot, at.side)); // …and any intruder on its own side
      return out;
    }
    if (kind !== 'Lane') return out;
    const i = mine.findIndex((l) => l.slot === at.slot); // the lane across the mat is the one at the same place along the table
    if (this.isColossus(card)) {
      // Sandstone Colossus: only the lanes either side of the one across (never that one, whatever its range); an empty one means the Emperor
      const sides = [i - 1, i + 1];
      for (const j of sides) if (theirs[j]) out.push(...home(theirs[j].slot, foe).filter(canHit));
      if (sides.some((j) => !theirs[j] || !held(theirs[j].slot, foe))) out.push(...leadersOf(foe).filter((u) => isEmperor(this.cards.byNumber.get(u.num)!)));
      return out;
    }
    theirs.forEach((l, j) => { if (Math.abs(j - i) <= reach) out.push(...home(l.slot, foe).filter((u) => canHit(u) && (j === i || !airOnly || this.fliesNow(u)))); });
    // Leviathan: it also reaches the lane across from each of its Body Segments
    for (const seg of this.state.field) {
      if (seg.summonedBy !== card.id || !seg.faceUp || !this.cards.byNumber.get(seg.num)?.traits.some((t) => /body segment/i.test(t))) continue;
      const sa = this.standsAt(seg);
      const j = sa && slotKind(sa.slot.name) === 'Lane' ? mine.findIndex((l) => l.slot === sa.slot) : -1;
      if (j >= 0 && theirs[j]) out.push(...home(theirs[j].slot, foe).filter(canHit));
    }
    if (this.traitNow(card, def, 'Direct') && i >= 0 && !held(theirs[i].slot, foe) && !this.ashActive()) out.push(...leadersOf(foe)); // (Ashy Wasteland: empty lanes can't be targeted)
    // an intruder in its own lane (or one within its range) can be attacked as well as the lane across
    mine.forEach((l, j) => { if (Math.abs(j - i) <= reach) out.push(...guests(l.slot, at.side).filter((u) => canHit(u) && (j === i || !airOnly || this.fliesNow(u)))); });
    return out;
  }
  /** Which side of the mat controls a card: its owner's, or (Possessed) the side of the player who Possessed it. */
  private controlSide(card: FieldCard): 1 | -1 {
    const by = this.possessor(card);
    return sideOf(by !== null ? by : card.owner);
  }
  /**
   * Is this unit standing in the other side's lane? (one summoned there: a Royal Guard from Royal Delivery, a
   * Skeleton Dropship's Skell-Mini; or one that is Possessed). It doesn't fill or block that lane. On the solo
   * table every card is yours, so nothing is.
   */
  private intruder(card: FieldCard) {
    const at = this.standsAt(card);
    if (this.state.mode === 'solo') return !!at && slotKind(at.slot.name) === 'Lane' && card.fightsFor !== undefined && card.fightsFor !== at.side;
    return !!at && slotKind(at.slot.name) === 'Lane' && this.controlSide(card) !== at.side;
  }
  /**
   * One hit lands on a card, by the rules: an Overshield takes it whole (and loses 1), so does a Shield unless
   * the hit is Pierce or Direct; an Entrenched or Frozen card takes half, a Darkgale one double (the two cancel);
   * Buffer comes off it unless it is Pierce; Susceptible adds to what is left. `status` = damage that isn't an
   * attack (Thorns thrown back): never halved or doubled. Returns the damage that went on the card.
   */
  private broke = false; // the last hit broke a Shield
  private reached = new Set<string>(); // the cards hits have reached (see landHit), even those left undamaged
  private guardHits = new Set<string>(); // Spirit Tree: the sources it has already taken a hit from in this action
  /**
   * Protective Petals: is this card standing in a lane a Spell guards against `by` (one of its caster's
   * opponents)? Returns the guarding card, or undefined.
   */
  private laneGuard(target: FieldCard, by: Seat | null | undefined): FieldCard | undefined {
    if (by === null || by === undefined) return undefined;
    const at = this.standsAt(target);
    if (!at || slotKind(at.slot.name) !== 'Lane') return undefined;
    return this.state.field.find((c) => c.veilLanes && c.faceUp && !!c.lanes && c.lanes.side === at.side && c.lanes.names.includes(at.slot.name) && this.opposed(c.owner, by) && !this.isExpired(c));
  }
  private landHit(seat: Seat, target: FieldCard, amount: number, o: { pierce?: boolean; direct?: boolean; status?: boolean; splash?: boolean; from?: FieldCard; spell?: boolean; routed?: boolean }, notes: string[]): number {
    const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
    if (this.intangible(target)) return 0; // a Spirit Soldier: nothing ever damages it
    const petals = this.laneGuard(target, seat);
    if (petals) { notes.push(`${this.cardName(target.num)}’s lane is guarded by ${this.cardName(petals.num)}`); return 0; }
    if (!o.status && !o.routed && CONFIG.combat.enabled && target.faceUp && this.isUnit(this.cards.byNumber.get(target.num))) {
      // Undead Guards take every hit aimed at their lane, splash too (one hit from each source, however many cards it was aimed at)
      const wall = this.isBulwark(target) ? target : this.bulwarkOf(target);
      if (wall && o.from && this.striking === 0) { // (an attack is aimed at the Guard itself: each of its hits lands)
        const key = `${o.from.id}:${wall.id}`;
        if (this.guardHits.has(key)) return 0;
        this.guardHits.add(key);
      }
      if (wall && wall !== target) {
        notes.push(`an Undead Guard takes it for ${this.cardName(target.num)}`);
        return this.landHit(seat, wall, amount, { ...o, splash: false, routed: true }, notes);
      }
    }
    if (o.spell && !o.routed && CONFIG.combat.enabled && this.isUnit(this.cards.byNumber.get(target.num))) {
      // a Spell's damage into a Spatial Rift's lane comes out of the other; into a Spirit Tree's lane, the Tree takes it (once per Spell)
      const rift = this.riftFor(target);
      if (rift) {
        const fromDef = o.from ? this.cards.byNumber.get(o.from.num) : undefined;
        const out = this.intoLane(rift.slot, rift.side, !!fromDef?.traits.some((t) => /\bdirect\b/i.test(t)));
        notes.push(`${this.cardName(rift.rift.num)} sends it to ${rift.slot.name}${out ? '' : ', where there is nothing to hit'}`);
        return out ? this.landHit(seat, out, amount, { ...o, routed: true }, notes) : 0;
      }
      const tree = this.guardOf(target);
      if (tree) {
        const key = `${o.from?.id ?? ''}:${tree.id}`;
        if (this.guardHits.has(key)) return 0;
        this.guardHits.add(key);
        notes.push(`the Spirit Tree takes it for ${this.cardName(target.num)}`);
        return this.landHit(seat, tree, amount, { ...o, splash: false, routed: true }, notes);
      }
    }
    if (!damageId || amount <= 0 || this.untouchable(target) || !this.state.field.includes(target)) return 0;
    if (target.wardAt !== undefined && target.wardAt === this.turnStamp()) { notes.push(`${this.cardName(target.num)} can’t be damaged this turn`); return 0; } // (Impervious Smite)
    const def = target.faceUp ? this.cards.byNumber.get(target.num) : undefined;
    const name = def?.name ?? 'a face-down card';
    const on = this.tokensOn(target);
    if (target.negating) {
      // Emergency Exit: nothing but Pierce lands while that ability is on cooldown; Pierce ends it
      if (o.pierce || !this.abilityToken(target, target.negating, 'cooldown')) delete target.negating;
      else { notes.push(`${name} shrugs it off`); return 0; }
    }
    // the hit has reached this card (whatever Overshield, Shield, Buffer or Entrenched then leave of it): what comes
    // with the hit — a Stun, an Entrench — goes on it even if it takes no damage
    this.reached.add(target.id);
    // A Shield is one layer: it soaks up a whole hit, losing as much as the hit
    // was, and breaks once a hit is as big as what is left of it (what goes past it is lost, it doesn't reach the
    // card). A card with several separate Shield counters (Ghostly Armor, Ice Golemite) has that many layers.
    const absorb = (effect: string, label: string, soak?: number) => {
      // (a Shield from Gerzite Armor sits on top of any other: it is the first to go)
      const t = on.find((x) => this.effectOf(x) === effect && x.value > 0 && x.tokenId === 'gerzite-shield') ?? on.find((x) => this.effectOf(x) === effect && x.value > 0);
      if (!t) return false;
      t.value -= soak === undefined ? 1 : Math.min(t.value, soak);
      if (t.value <= 0) {
        // it shatters into shards where it sat (shown once the hit lands: the look is on the card)
        if (CONFIG.looks.enabled && CONFIG.looks.shieldBreak) this.fx('look', target.id, undefined, { effect: CONFIG.looks.shieldBreak, at: { x: t.x, z: t.z } });
        this.removeTokens([t]);
        this.broke = true;
        // its last Shield is gone ("shield_gone": Encased Skeleton loses its Buffer with it)
        if (effect === 'shield' && !this.tokensOn(target).some((x) => this.effectOf(x) === 'shield' && x.value > 0)) this.fire(target, 'shield_gone');
        // Gerzite Armor: the opponent who breaks it gains 1 Gerzite
        if (t.tokenId === 'gerzite-shield' && t.by !== null && t.by !== undefined && this.opposed(seat, t.by)) {
          const got = this.gain(seat, CONFIG.tokenRules.fractalGives, 1);
          notes.push(`breaking the Gerzite Shield gives ${this.state.players[seat].name} ${this.gainText(CONFIG.tokenRules.fractalGives, 1, got)}`);
        }
      }
      this.fx('status', target.id, undefined, { effect: 'notice' });
      notes.push(`${name}’s ${label} takes the hit${t.value > 0 ? ` (${t.value} left)` : ' and breaks'}`);
      return true;
    };
    let hit = amount;
    if (!o.status) {
      const halved = on.some((t) => CONFIG.combat.halvedBy.includes(this.effectOf(t)));
      const dark = on.some((t) => this.effectOf(t) === CONFIG.turn.darkgale.token);
      if (halved && !dark) hit = Math.ceil(hit / 2);
      else if (dark && !halved) hit *= CONFIG.combat.darkgaleTakes;
    }
    if (!o.pierce) hit = Math.max(0, hit - this.valueOn(target, 'buffer') - this.auraFor(target, 'buffer'));
    // the card's own `take` effects: less from some attackers (Algae Zombie), or never more than so much at once
    for (const e of hit > 0 ? this.effectsOf(target, 'take') : []) {
      const [amt, kind, what, ...list] = e.words;
      if (amt === 'cap') {
        const most = Number(kind) || 0;
        if (hit > most && (what !== 'once' || target.cappedAt !== this.turnStamp())) {
          notes.push(`${name} takes no more than ${most} of it`);
          hit = most;
          target.cappedAt = this.turnStamp();
        }
      } else if (kind === 'from' && what === 'affiliation' && o.from && list.some((a) => this.damageAffOf(o.from!).includes(a))) hit = Math.max(0, hit + (Number(amt) || 0));
    }
    if (hit <= 0) {
      notes.push(`${name}’s Buffer stops it`);
      return 0;
    }
    hit += this.valueOn(target, 'susceptible');
    // Overshield is a Shield bar over the Shield: one bar however many times it was given (its points add up), it soaks
    // up a whole hit, losing as much as the hit was, and breaks once a hit is as big as what is left. Nothing gets past
    // it, Pierce or not.
    const overs = on.filter((x) => this.effectOf(x) === 'overshield' && x.value > 0);
    if (overs.length > 1) { overs[0].value += overs.slice(1).reduce((a, x) => a + x.value, 0); this.removeTokens(overs.slice(1)); on.splice(0, on.length, ...this.tokensOn(target)); }
    if (absorb('overshield', 'Overshield', hit)) { this.mirrorShieldHit(target, damageId, o.spell && this.standsAt(target)?.slot.name === 'Minion' ? Math.round(hit / 2) : hit, seat, notes); return 0; }
    if (!o.pierce && absorb('shield', 'Shield', hit)) { this.mirrorShieldHit(target, damageId, o.spell && this.standsAt(target)?.slot.name === 'Minion' ? Math.round(hit / 2) : hit, seat, notes); return 0; } // (only Pierce goes through a Shield; Direct only reaches further)
    // (damage dealt to an opposing Emperor by an attack or a Spell is kept: it settles a game called, or ended by Overtime, that is otherwise even)
    if (!o.status && def && isEmperor(def) && this.opposed(seat, target.owner)) { const h = (this.state.emperorHits ??= this.state.players.map(() => 0)); h[seat] = (h[seat] ?? 0) + hit; }
    // splash reaches every unit of a Count: it goes on a counter of its own that stays as units fall
    const count = on.find((t) => this.effectOf(t) === CONFIG.tokenRules.countToken);
    this.addDamage(target, damageId, hit, seat, !!o.splash && !!count && count.value > 1);
    // (a Spell on a Minion: the Minion takes it all, its Emperor only half)
    this.mirrorDamage(target, damageId, o.spell && this.standsAt(target)?.slot.name === 'Minion' ? Math.round(hit / 2) : hit);
    return hit;
  }
  /**
   * A hit an Emperor's or a Minion's Shield (or Overshield) soaked up is shared with the card it is linked to, as damage
   * on it would be (Scjemet and an Encased Skeleton in the Minion slot): the partner's own Overshield or Shield takes it
   * the same way, or, with none, its health does.
   */
  private mirrorShieldHit(card: FieldCard, damageId: string, amount: number, seat: Seat, notes: string[]) {
    if (amount <= 0 || !CONFIG.combat.enabled) return;
    for (const p of this.linkPartners(card)) {
      if (p.via) continue; // (Link counters share the damage that gets through, as before)
      const partner = p.card;
      if (!this.state.field.includes(partner) || this.untouchable(partner)) continue;
      const pname = partner.faceUp ? this.cardName(partner.num) : 'a face-down card';
      const bar = ['overshield', 'shield'].map((eff) => this.tokensOn(partner).find((x) => this.effectOf(x) === eff && x.value > 0)).find(Boolean);
      if (bar) {
        bar.value -= Math.min(bar.value, amount);
        const label = this.effectOf(bar) === 'overshield' ? 'Overshield' : 'Shield';
        if (bar.value <= 0) {
          if (CONFIG.looks.enabled && CONFIG.looks.shieldBreak) this.fx('look', partner.id, undefined, { effect: CONFIG.looks.shieldBreak, at: { x: bar.x, z: bar.z } });
          this.removeTokens([bar]);
          if (label === 'Shield' && !this.tokensOn(partner).some((x) => this.effectOf(x) === 'shield' && x.value > 0)) this.fire(partner, 'shield_gone');
        }
        this.fx('status', partner.id, undefined, { effect: 'notice' });
        this.fx('link', card.id, undefined, { target: partner.id });
        notes.push(`it is shared with ${pname}, whose ${label} takes it${bar.value > 0 ? ` (${bar.value} left)` : ' and breaks'} (${p.why})`);
      } else {
        this.addDamage(partner, damageId, amount, null);
        this.fx('link', card.id, undefined, { target: partner.id });
        notes.push(`it is shared with ${pname}, which takes ${amount} (${p.why})`);
      }
    }
  }
  /** A card destroyed in a fight leaves the table by itself: to its owner's discard pile (a summoned unit just goes). */
  private fall(card: FieldCard) {
    const def = this.cards.byNumber.get(card.num);
    if (!def || isEmperor(def) || !this.state.field.includes(card)) return;
    if (card.rise) {
      // Dark Sovereign: it is re-summoned at once where it stood, Undead, with 1 health (or SP); not Entrenched
      delete card.rise;
      this.clearTokensOn(card);
      card.hp = 1; card.atZero = false; card.rot = 0;
      this.giveStatus(card.owner, card, 'aff-undead', null, null);
      this.fx('play', card.id);
      this.log(`${def.name} is re-summoned as an Undead Unit with 1 ${def.types.includes('building') && !def.types.includes('troop') ? 'SP' : 'health'} (Dark Sovereign).`, card.owner);
      return;
    }
    if (card.crossing) return; // (Eldritch Realm Crosser: it is about to become the Biome)
    if (card.soul) { this.clearTokensOn(card); this.removeField(card.id); this.closeUp(card); return; } // a Soul just goes
    const due = this.dueOnLeaving(card);
    // Undead Legion: an Undead Soldier that falls while its General stands rises again as a Spirit Soldier (it breaks
    // into blue shards, and the Spirit rises out of them)
    const spirit = card.legion && this.generalStands(card.legion) && !this.intangible(card) ? this.cards.spawnOnly.find((d) => d.whileInPlay.includes('intangible') && d.summonedBy.some((n) => n.toLowerCase() === this.cardName(this.findField(card.legion!)!.num).toLowerCase())) : undefined;
    const burst = spirit ? 'spirit-shatter' : (this.burstOverride ?? CONFIG.looks.burst[this.cardName(card.num)]); // (a card that bursts apart its own way: Cursed Sarcophagus in gold)
    this.fx('sacrifice', card.id, undefined, { seat: card.owner, effect: burst ?? 'destroyed' }); // (destroyed: it shatters, unlike a card sacrificed for Dark Gerzite)
    if (card.swarm) {
      // one unit of a swarm: it just goes, while others of the swarm are still on the table; the card itself goes to
      // its owner's discard pile with the last of them
      const rest = this.swarmOf(card).filter((m) => m !== card);
      if (rest.length) {
        if (card.merged.length) { rest[0].merged.push(...card.merged); card.merged = []; } // (the copies tiered into it go with the swarm's last unit)
        this.clearTokensOn(card);
        this.removeField(card.id);
        this.closeUp(card);
        if (def.destroyedGains?.each) this.destroyedGain(card, due);
        for (const m of rest) if (!m.atZero) this.fire(m, 'unit_lost');
        return;
      }
      if (card.spawned) card.spawned = false; // (the last one standing is the card)
    }
    if (card.spawned || card.spanOf) {
      this.clearTokensOn(card);
      this.removeField(card.id);
      this.closeUp(card);
    } else this.discardFromPlay(card);
    this.destroyedGain(card, due);
    if (spirit) this.raiseSpirit(card, spirit.number);
    this.legionSweep(); // (a General that falls takes its Spirits with it)
  }
  /**
   * What sacrificing an Undead Legion is worth: its cost in Dark Gerzite at full strength, by the share left of the
   * General's health and Shield and its Undead Soldiers' health (3 + 3 + 6 x 2 = 18: 6 Dark Gerzite). Spirit Soldiers
   * count for nothing. Rounded like any sacrifice.
   */
  legionWorth(general: FieldCard): number {
    const def = this.cards.byNumber.get(general.num);
    if (!def) return 0;
    const soldier = this.cards.spawnOnly.find((d) => d.name === 'Undead Soldier');
    const each = soldier?.health ?? 2;
    const full = (def.health ?? 0) + (Number.parseFloat(def.shield) || 0) + 6 * each;
    const left = (g: FieldCard) => Math.max(0, this.healthLeft(g));
    const now = left(general) + this.valueOn(general, 'shield') + this.state.field.filter((c) => c.legion === general.id && !this.intangible(c)).reduce((n, c) => n + Math.min(each, left(c)), 0);
    return full > 0 ? Math.round(((def.cost ?? 0) * Math.min(now, full)) / full) : 0;
  }
  /** A fallen Undead Soldier's Spirit Soldier, where it stood: a fresh card (no damage, no statuses), still one of its Legion. */
  private raiseSpirit(was: FieldCard, num: number) {
    const s = this.state;
    const u: FieldCard = { ...this.held(num, true), owner: was.owner, x: was.x, z: was.z, rot: was.rot, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, summonedBy: was.summonedBy, legion: was.legion, bornIn: s.turn };
    // (one that has already attacked this turn has done so as a Spirit too)
    if (was.acted) u.acted = was.acted;
    if (was.swings) u.swings = { ...was.swings };
    if (was.swarmHit) u.swarmHit = was.swarmHit;
    s.field.push(u);
    this.addStartCounters(u);
    this.fx('emerge', u.id, undefined, { effect: 'spirit' });
    this.log(`An Undead Soldier rises again as a ${this.cardName(num)}: it can’t be damaged or targeted while its General stands.`, was.owner);
  }
  /** Spirit Soldiers whose General is no longer standing fade away: they shrink and drift off in a pale blue light. */
  private legionSweep() {
    const s = this.state;
    const gone = s.field.filter((c) => c.legion && this.intangible(c) && !this.generalStands(c.legion));
    if (!gone.length) return;
    for (const c of gone) {
      this.fx('sacrifice', c.id, undefined, { seat: c.owner, effect: 'spirit-away' });
      this.clearTokensOn(c);
      this.removeField(c.id);
      this.closeUp(c);
    }
    this.log(`With its General gone, ${gone.length === 1 ? 'a Spirit Soldier fades' : `${gone.length} Spirit Soldiers fade`} away.`, gone[0].owner);
  }
  /** What each of this player's cards can do right now, for their table (GameView.combat). */
  private combatView(seat: Seat): GameView['combat'] {
    const out: GameView['combat'] = {};
    if (!CONFIG.combat.enabled || this.state.result) return out;
    const key = this.turnKey();
    for (const c of this.state.field) {
      const def = c.faceUp ? this.cards.byNumber.get(c.num) : undefined;
      if (!def || !(this.isUnit(def) || isEmperor(def)) || this.untouchable(c)) continue;
      if (!this.canControl(seat, c) || this.possessedAway(seat, c) || this.mateHeld(seat, c)) continue;
      if (this.state.bots?.includes(c.owner) && !this.state.bots.includes(seat)) continue; // a bot's cards are the bot's to use
      const why = this.lockedBy(c) ?? this.attackWhy(seat, c);
      // (a card whose Tap is what it does when it attacks — Firelance's first shot — offers only the attack)
      const hasTap = this.hasTapAbility(def);
      const never = this.neverAttacks(c);
      const tapWhy = hasTap ? (this.lockedBy(c) ?? this.tapWhy(seat, c)) : null;
      out[c.id] = {
        targets: why ? [] : this.attackTargets(c).map((t) => t.id),
        ...(why ? { why } : {}),
        ...(c.swings?.key === key ? { left: c.swings.left } : {}),
        ...(hasTap ? { tap: true } : {}),
        ...(tapWhy ? { tapWhy } : {}),
        ...(c.acted === key ? { used: true } : {}),
        ...(never ? { never: true } : {}),
      };
    }
    return out;
  }
  /** After damage in a fight: the table marks what has fallen, and (CONFIG.combat.autoDiscard) clears it away. */
  private fallenBefore = new Set<string>();
  private settleFallen() {
    this.markDestroyed();
    if (!CONFIG.combat.autoDiscard) return;
    const skull = CONFIG.turn.lifetime.expiredToken;
    for (const c of [...this.state.field]) if (c.faceUp && c.atZero && !this.fallenBefore.has(c.id) && this.isUnit(this.cards.byNumber.get(c.num)) && this.hasOn(c, skull)) this.fall(c);
  }
  /**
   * An attack: `from` hits `to` with every hit it has left this turn (a Count's units one after another, each
   * felling one unit at most; hits left over when the target is gone stay for another target). The table rolls
   * any chance to miss, deals the damage, throws Thorns back, and clears away what is destroyed.
   */
  private strike(seat: Seat, from: FieldCard, to: FieldCard, answered = false, stage = 0): string | null {
    this.striking++;
    let err: string | null;
    try { err = this.strikeNow(seat, from, to, answered, stage); } finally { this.striking--; }
    // a swarm: once one of its units has attacked, the rest must attack this turn too (or are tapped as the Attack
    // Phase ends); each picks its own target. If every one of them went at the same target, a bonus for that
    // (Fleet of Archers) is dealt as the last one's attack lands
    if (!err && !from.swarm && from.acted === this.turnKey()) {
      // a Lanespanner: the same obligation for the rest of its units (each picks its own target, no bonus)
      const group = this.attackGroup(from);
      if (group.length > 1) for (const m of group) if (!m.atZero) m.swarmHit = this.turnKey();
    }
    if (!err && from.swarm && from.acted === this.turnKey()) {
      const key = this.turnKey();
      from.swarmAim = { key, target: to.id };
      const units = this.swarmOf(from).filter((m) => !m.atZero);
      for (const m of units) m.swarmHit = key;
      if (units.length > 1 && units.every((m) => m.swarmAim?.key === key && m.swarmAim.target === to.id) && this.state.field.includes(to) && !this.hasOn(to, CONFIG.turn.lifetime.expiredToken)) {
        const extra = (this.dealMod(from, to, true).add - this.dealMod(from, to, false).add) * units.length;
        if (extra > 0) {
          const notes: string[] = [];
          const got = this.landHit(seat, to, extra, { from }, notes);
          this.settleFallen();
          this.log(`Every unit of ${this.cardName(from.num)} attacked ${to.faceUp ? this.cardName(to.num) : 'it'}: ${got} extra damage${notes.length ? ` (${notes.join('; ')})` : ''}.`, seat);
        }
      }
    }
    return err;
  }
  private strikeNow(seat: Seat, from: FieldCard, to: FieldCard, answered = false, stage = 0): string | null {
    const s = this.state;
    // what has to be settled as an attack is declared, before it lands: Dark Gerzite Titan's payment (stage 0),
    // then a Volcanic Disciple's boost for a Volcanic attacker (stage 1)
    if (!answered && stage <= 0 && this.effectsOf(from, 'attack').some((e) => e.words[0] === 'pay_any')) {
      const g2 = this.barTokens(seat, 'gerzite').length; const d2 = this.darkHeld(seat) ? 0 : Math.min(this.barTokens(seat, 'dark').length, this.darkCap(seat)?.left ?? Infinity);
      if (s.bots?.includes(seat)) {
        // a bot pays by its level: Martin a little, at random; Practice Buddy half of what he can spare; Creator what
        // destroys the target, as far as he can spare it (he keeps 3 back)
        const lvl = s.botLevels?.[seat];
        const spare = Math.max(0, g2 - 3);
        const need = Math.ceil(Math.max(1, this.healthLeft(to)) / 3);
        const pay = !lvl ? Math.min(g2, 2) : lvl === 'easy' ? Math.floor(this.rng() * (Math.min(g2, 3) + 1)) : lvl === 'skilled' ? Math.min(g2, Math.max(1, Math.floor(spare / 2))) : Math.min(g2, Math.max(1, Math.min(spare, need)));
        this.payBar(seat, pay, false);
        from.paidHit = 3 * pay;
        if (lvl) this.log(`${s.players[seat].name} pays ${pay} Gerzite for ${this.cardName(from.num)}’s attack: ${from.paidHit} damage.`, seat);
      }
      else {
        (s.picks ??= []).push({ id: s.nextId++, seat, name: this.cardName(from.num), text: `${this.cardName(from.num)}: pay for this attack — 3 damage for each Gerzite, 5 for each Dark Gerzite (0 is an attack without paying)`, options: [], numbers: [{ label: 'Gerzite', min: 0, max: g2, value: Math.min(1, g2) }, { label: 'Dark Gerzite', min: 0, max: d2, value: 0 }], confirm: 'Attack', hold: { seat, from: from.id, to: to.id, stage: 0, kind: 'titan' } });
        this.fx('attack', from.id, undefined, { target: to.id, seat });
        return null;
      }
    }
    if (!answered && stage <= 1 && this.affOf(from).includes('volcanic') && this.isUnit(this.cards.byNumber.get(from.num))) { // (a Volcanic Unit: never an Emperor)
      const dis = s.field.find((c) => c !== from && c.faceUp && this.cardName(c.num) === 'Volcanic Disciple' && this.allies(c.owner, seat) && !this.passiveStopper(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && this.healthLeft(c) > 0);
      if (dis && !s.bots?.includes(seat)) {
        (s.picks ??= []).push({ id: s.nextId++, seat, name: this.cardName(dis.num), text: `Volcanic Disciple: you may damage it by any amount, to add that much to ${this.cardName(from.num)}’s attack`, options: [], numbers: [{ label: 'Damage to Volcanic Disciple', min: 0, max: this.healthLeft(dis), value: 0 }], confirm: 'Attack', hold: { seat, from: from.id, to: to.id, stage: 1, kind: 'disciple', by: dis.id } });
        this.fx('attack', from.id, undefined, { target: to.id, seat });
        return null;
      }
    }
    if (!answered && stage <= 2 && from.shiftTo === undefined) {
      // Gravitation: the player it guards chooses, for this attack, whether it shifts one lane left or right (as they see it)
      const grav = this.gravityFor(to);
      const at = this.standsAt(to);
      if (grav && at) {
        const row = this.laneRow(at.side);
        const i = row.indexOf(at.slot);
        const lanes = [row[i - 1], row[i + 1]].filter(Boolean).map((l) => ({ slot: l.name, side: at.side }));
        const labels = [i > 0 ? `Left: ${row[i - 1].name}` : '', i < row.length - 1 ? `Right: ${row[i + 1].name}` : ''].filter(Boolean);
        if (s.bots?.includes(grav.owner) || lanes.length === 1) { from.shiftTo = lanes[0] ?? null; this.gravityLook(to, from.shiftTo); }
        else {
          (s.picks ??= []).push({ id: s.nextId++, seat: grav.owner, name: this.cardName(grav.num), text: `Gravitation: ${this.cardName(from.num)} attacks ${this.cardName(to.num)}. Shift the attack one lane`, options: [], choices: labels, hold: { seat, from: from.id, to: to.id, stage: 2, kind: 'gravity', lanes } });
          this.fx('attack', from.id, undefined, { target: to.id, seat });
          this.fx('status', grav.id, undefined, { effect: 'notice' });
          return null;
        }
      }
    }
    if (!answered) {
      this.fireAll('host_attacks', (c) => c.host === from.id);
      this.springTriggers(seat, 'attack');
      if (isEmperor(this.cards.byNumber.get(from.num)!)) this.springTriggers(seat, 'emperor');
    }
    // a target that may strike first if its owner pays for it (Orc Patroller): the attack waits for their answer
    const react = answered ? undefined : this.effectsOf(to, 'targeted').find((e) => e.words[0] === 'counter');
    if (react) {
      const cost = Number(isEmperor(this.cards.byNumber.get(from.num)!) ? (react.words[2] ?? react.words[1]) : react.words[1]) || 0;
      const have = this.barTokens(to.owner, 'gerzite').length;
      const toDefR = this.cards.byNumber.get(to.num)!;
      if (have >= cost && this.attackOf(to, toDefR).per > 0) {
        const hold = { seat, from: from.id, to: to.id, cost };
        if (s.bots?.includes(to.owner)) this.reactThenStrike(hold, have >= cost + 2);
        else {
          (s.picks ??= []).push({ id: s.nextId++, seat: to.owner, name: toDefR.name, yesNo: true, options: [], strike: hold, text: `${this.cardName(from.num)} is attacking ${toDefR.name}. Pay ${cost} Gerzite to have ${toDefR.name} attack it first?` });
          this.fx('attack', from.id, undefined, { target: to.id, seat });
          this.log(`${s.players[seat].name}: ${this.cardName(from.num)} moves to attack ${toDefR.name}. ${s.players[to.owner].name} may pay ${cost} Gerzite to strike first.`, seat);
        }
        return null;
      }
    }
    const def = this.cards.byNumber.get(from.num)!;
    const key = this.turnKey();
    if (from.illusion) { const other = this.findField(from.illusion); if (other) this.illusionGoes(from, other); } // Illusionist: it attacks before either was damaged, so the other is the illusion
    const plan = this.attackOf(from, def);
    const actions = from.actions ?? 0; // (Cragged Skulker: several attacks a turn, each its own)
    if (from.swings?.key !== key) from.swings = { key, left: plan.hits * Math.max(1, actions) };
    // (Frenzy: each of its two attacks is its own action, at a target of its own; with a Tap ability, its Tap may be the other)
    const frenzyHalf = actions <= 1 && this.frenzyLeft(from) !== null ? Math.max(1, Math.floor(plan.hits / 2)) : 0;
    let budget = actions > 1 ? plan.hits : frenzyHalf || Infinity; // (this attack's own hits; the rest wait for its next attacks)
    // this attack is used up without landing (a miss…): with several attacking actions, only this one is gone
    const spend = () => {
      from.swings!.left = actions > 1 ? Math.max(0, from.swings!.left - plan.hits) : frenzyHalf ? Math.max(0, from.swings!.left - frenzyHalf) : 0;
      if ((actions > 1 || frenzyHalf) && from.swings!.left <= 0 && from.rot % 2 === 0 && s.field.includes(from)) this.turnCard(from, 1, 'attack');
    };
    from.acted = key;
    if (from.rot % 2 === 0 && !(actions > 1 && from.swings.left > plan.hits) && !(frenzyHalf && from.swings.left > frenzyHalf)) this.turnCard(from, 1, 'attack'); // (it turns as its last attack goes)
    if (this.isBrain(from)) for (const c of this.linked(from)) { c.acted = key; if (c.rot % 2 === 0) this.turnCard(c, 1, 'attack'); } // its linked Troops are tapped with it
    const fromName = def.name;
    const toDef = to.faceUp ? this.cards.byNumber.get(to.num) : undefined;
    const toName = toDef?.name ?? 'a face-down card';
    const who = s.players[seat].name;
    this.rollMiss(seat, from, to);
    const missed = !!s.lastMiss && s.lastMiss.from === from.id && s.lastMiss.to === to.id && s.lastMiss.missed;
    // (the table shows the arrow for a moment, then the attack itself, in its attacker's affiliation)
    this.fx('attack', from.id, undefined, { target: to.id, seat, effect: missed ? 'miss' : 'strike', tint: this.attackTint(from) });
    if (missed) {
      spend();
      this.log(`${who}: ${fromName} attacks ${toName} and misses.`, seat);
      return null;
    }
    const pierce = this.traitNow(from, def, 'Pierce');
    const direct = this.traitNow(from, def, 'Direct');
    let splash = this.traitNow(from, def, 'Splash Damage');
    // where the attack really lands: shifted a lane by Gravitation, out of a Spatial Rift's other lane, or onto a
    // Spirit Tree guarding the lane (as single target damage). Into an empty lane only Direct damage goes on, to the Emperor
    const aimed = to;
    const shift = from.shiftTo;
    delete from.shiftTo;
    if (shift) {
      const sl = laneOrder().find((l) => l.name === shift.slot);
      const out = sl ? this.intoLane(sl, shift.side, direct) : null;
      if (!out) { spend(); this.log(`${who}: ${fromName}’s attack is shifted into ${shift.slot} by Gravitation, where there is nothing it can hit.`, seat); return null; }
      to = out;
    }
    if (this.isUnit(to.faceUp ? this.cards.byNumber.get(to.num) : undefined) || !to.faceUp) {
      const rift = this.riftFor(to);
      if (rift) {
        const out = this.intoLane(rift.slot, rift.side, direct);
        if (!out) { spend(); this.log(`${who}: ${fromName}’s attack goes through ${this.cardName(rift.rift.num)} into ${rift.slot.name}, where there is nothing it can hit.`, seat); return null; }
        to = out;
      }
      const tree = this.guardOf(to);
      if (tree) { to = tree; splash = false; }
      const wall = this.isBulwark(to) ? to : this.bulwarkOf(to); // (Undead Guards: the attack is theirs, splash and all)
      if (wall) { to = wall; splash = false; }
      // a Leviathan Body Segment that has lost its Shield: the attack goes to its Leviathan
      if (to.summonedBy && this.cardName(to.num) === 'Leviathan Body Segment' && this.valueOn(to, 'shield') <= 0) { const lev = this.findField(to.summonedBy); if (lev && !this.hasOn(lev, CONFIG.turn.lifetime.expiredToken)) to = lev; }
    }
    // (sent on somewhere else — out of a Spatial Rift's other lane, onto a Spirit Tree…: once it reaches the card it was aimed at, it travels on from there)
    if (to !== aimed) this.fx('attack', from.id, undefined, { target: to.id, seat, effect: 'relay', from: { x: aimed.x, z: aimed.z }, tint: this.attackTint(from) });
    const emperorHit = !!(to.faceUp ? this.cards.byNumber.get(to.num) : undefined) && isEmperor(this.cards.byNumber.get(to.num)!);
    const possessed = this.possessor(from) !== null;
    // (a card that can never hit Flying Units — Cannon, Ballista Artillery — doesn't splash them either)
    const hitsFlying = !new RegExp(CONFIG.reach.noFlying, 'i').test(def.text) && (this.fliesNow(from) || this.rangeNow(from, def) > 0 || ['Emperor', 'Minion'].includes(slotKind(this.standsAt(from)?.slot.name ?? '')));
    const lane = this.standsAt(to);
    const notes: string[] = [];
    let total = 0; let used = 0;
    const allIn = from.swarm ? false : from.swings.left === plan.hits; // every hit it has is going at this target (a swarm's bonus for all of it at one target is dealt as its last unit attacks: see strike)
    // a Possessed unit that reaches its owner's Emperor deals half of everything it has, in one hit
    let per = plan.per;
    if (possessed && emperorHit) {
      per = Math.round((plan.per * from.swings.left) / 2);
      from.swings.left = 1;
    }
    const countToken = CONFIG.tokenRules.countToken;
    const alive = (c: FieldCard) => s.field.includes(c) && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken);
    if (this.isColossus(from)) {
      // Sandstone Colossus: each hit lands on everything in the two lanes beside the one across; an empty one sends its share to the Emperor
      const at0 = this.standsAt(from);
      const foeSide = (at0 ? -at0.side : -sideOf(seat)) as 1 | -1;
      const mine0 = at0 ? this.laneRow(at0.side) : [];
      const theirs0 = this.laneRow(foeSide);
      const i0 = at0 ? mine0.indexOf(at0.slot) : -1;
      while (from.swings.left > 0 && s.field.includes(from)) {
        from.swings.left--; used++;
        const outerWave = this.linkWave;
        this.linkWave = new Set();
        for (const j of [i0 - 1, i0 + 1]) {
          const there = theirs0[j] ? this.unitsAt(theirs0[j], foeSide, from).filter((u) => !this.intruder(u) && (hitsFlying || !this.fliesNow(u))) : [];
          const victims0 = there.length ? there : [this.emperorOn(foeSide, from)].filter((c): c is FieldCard => !!c);
          for (const v of victims0.filter(alive)) {
            const mod = this.dealMod(from, v, allIn);
            const got = this.landHit(seat, v, Math.max(0, Math.round(per * mod.mult) + mod.add), { pierce: pierce || mod.pierce, direct, splash: true, from }, notes);
            total += got;
            if (got > 0) { if (mod.vs) this.look('assassin-x', v); this.fire(v, 'hurt', { attacker: from, amount: got }); }
          }
        }
        this.linkWave = outerWave;
        this.settleFallen();
      }
      this.fire(from, 'attack', { target: to, amount: total });
      this.settleFallen();
      this.log(`${who}: ${fromName} attacks the lanes either side of the one across: ${total} damage${notes.length ? `. ${notes.join('; ')}` : ''}.`, seat);
      delete from.paidHit; delete from.boostHit;
      return null;
    }
    const hurtAll: FieldCard[] = []; // every card this attack damaged (Wrathful Ghost Entrenches them)
    while (from.swings.left > 0 && budget > 0 && s.field.includes(from)) {
      budget--;
      // (splash hits everything in the lane but the attacker's own side's units standing there)
      const victims = (splash && lane && (slotKind(lane.slot.name) === 'Lane' || lane.slot.name === CONFIG.linkedSlots[1]) ? this.unitsAt(lane.slot, lane.side, from, true).filter((u) => u === to || ((hitsFlying || !this.fliesNow(u)) && (this.state.mode === 'solo' || this.controlSide(u) !== this.controlSide(from)))) : [to]).filter(alive);
      if (!victims.includes(to) && !splash) break;
      if (!victims.length) break;
      from.swings.left--; used++;
      const outerWave = this.linkWave;
      this.linkWave = new Set(); // (this hit and its splash: a swarm in the Minion slot passes it to its Emperor once)
      const struck: { v: FieldCard; count: number }[] = [];
      for (const v of victims) {
        const vDef = v.faceUp ? this.cards.byNumber.get(v.num) : undefined;
        // a Special Hit that strikes back the first time its card is damaged (Jungle Explorer)
        const back = vDef && this.hasOn(v, 'special-hit') ? /first time this troop receives damage, deal (\d+)[^.]*?damage(?: and inflict (\w+))? on the unit that damaged/i.exec(vDef.text) : null;
        const thorns = this.valueOn(v, 'thorns') + this.auraFor(v, 'thorns');
        const mod = this.dealMod(from, v, allIn);
        const amount = Math.max(0, Math.round(per * mod.mult) + mod.add + (used === 1 && v === to ? (from.bonusHit ?? 0) : 0));
        const count = this.valueOn(v, countToken);
        this.broke = false;
        this.reached.delete(v.id);
        const dealt = this.landHit(seat, v, amount, { pierce: pierce || mod.pierce, direct, splash, from }, notes);
        total += dealt;
        if (this.broke) this.fire(from, 'shieldbreak', { target: v });
        if (dealt <= 0 && this.reached.has(v.id) && !hurtAll.includes(v)) hurtAll.push(v); // (reached but left at 0: what comes with the hit still lands — Wrathful Ghost's Entrench)
        if (dealt <= 0) continue;
        struck.push({ v, count });
        if (!hurtAll.includes(v)) hurtAll.push(v);
        if (mod.vs) this.look('assassin-x', v);
        this.fire(from, 'hit', { target: v, amount: dealt });
        this.fire(v, 'hurt', { attacker: from, amount: dealt });
        // Thorns throw damage back at whatever hit them: a Troop, a Building or an Emperor
        if (thorns > 0 && s.field.includes(from)) {
          const got = this.landHit(seat, from, thorns, { status: true }, notes);
          if (got > 0) notes.push(`${vDef?.name ?? 'its target'}’s Thorns deal ${got} back to ${fromName}`);
        }
        if (back && (s.field.includes(v) && this.healthLeft(v) > 0)) {
          // (an Emperor that attacked it is hit too; a Volcanic attack flies back from it once the attack has landed)
          this.fx('attack', v.id, undefined, { target: from.id, seat: v.owner, effect: 'bounce', tint: 'volcanic' });
          const got = this.landHit(v.owner, from, Number(back[1]), { splash: true, from: v }, notes);
          const status = back[2] ? this.tokenDb.list.find((d) => d.name.toLowerCase() === back[2].toLowerCase()) : undefined;
          if (status) this.giveStatus(v.owner, from, status.id, null, null);
          notes.push(`${vDef!.name}’s Special Hit strikes back: ${got} damage${status ? ` and ${status.name}` : ''} on ${fromName}`);
        }
      }
      this.linkWave = outerWave;
      this.settleFallen();
      // what it destroyed with that hit (a whole card, or one of a Count's units)
      for (const k of struck) {
        if (!s.field.includes(from)) break;
        if (!alive(k.v) || this.valueOn(k.v, countToken) < k.count) this.fire(from, 'kill', { target: k.v });
      }
      this.settleFallen();
    }
    if (used > 0) {
      this.fire(from, 'attack', { target: to, amount: total, hurt: hurtAll });
      // the card it attacked answers being attacked, whatever the damage (Apparition)
      if (s.field.includes(to) && s.field.includes(from)) this.fire(to, 'attacked', { attacker: from, amount: total });
      this.settleFallen();
      if (from.restHits && (!from.swings || from.swings.left <= 0)) delete from.restHits; // (Marlin Knight: back to its 2 hits once its attack is used)
    }
    // the units it summoned, and the one riding with it, attack in the same action (Necromancer's Skell-Minis, Zombie Knight's Horse)
    const follows = this.isLeader(from) ? s.field.filter((c) => c.summonedBy === from.id || c.attachedTo === from.id) : [];
    const hitsText = used > 1 ? `${used} hits of ${per}` : `${per}`;
    const followUp = () => { for (const f of follows) if (s.field.includes(f) && s.field.includes(to) && !this.attackWhy(seat, f) && this.attackTargets(f).includes(to)) this.strike(seat, f, to, true); };
    delete from.paidHit; delete from.boostHit;
    // (several attacking actions: the card turns once the last of them is used)
    if ((actions > 1 || frenzyHalf) && (!from.swings || from.swings.left <= 0) && from.rot % 2 === 0 && s.field.includes(from)) this.turnCard(from, 1, 'attack');
    const landedName = to === aimed ? toName : `${to.faceUp ? this.cardName(to.num) : 'a face-down card'} (aimed at ${toName})`;
    this.log(`${who}: ${fromName} attacks ${splash && !emperorHit ? `${landedName}’s lane` : landedName} (${hitsText}): ${total} damage${notes.length ? `. ${notes.join('; ')}` : ''}.${from.swings && from.swings.left > 0 && s.field.includes(from) ? ` ${fromName} has ${from.swings.left} hit${from.swings.left === 1 ? '' : 's'} left.` : ''}`, seat);
    followUp();
    return null;
  }
  /** The answer to "pay to strike first?" (Orc Patroller): if yes, its hit lands first; then the attack that was on hold goes ahead. */
  private reactThenStrike(hold: { seat: Seat; from: string; to: string; cost: number }, yes: boolean) {
    const s = this.state;
    const from = this.findField(hold.from); const to = this.findField(hold.to);
    if (!from || !to) return;
    if (yes && this.payBar(to.owner, hold.cost, false)) {
      const def = this.cards.byNumber.get(to.num)!;
      const notes: string[] = [];
      const plan = this.attackOf(to, def);
      const got = this.landHit(to.owner, from, plan.per, { pierce: this.traitNow(to, def, 'Pierce'), direct: this.traitNow(to, def, 'Direct'), from: to }, notes);
      this.log(`${s.players[to.owner].name} pays ${hold.cost} Gerzite: ${def.name} strikes ${this.cardName(from.num)} first for ${got} damage${notes.length ? ` (${notes.join('; ')})` : ''}.`, to.owner);
      this.settleFallen();
    }
    if (!s.field.includes(from) || !s.field.includes(to) || this.attackWhy(hold.seat, from)) return;
    this.strike(hold.seat, from, to, true);
  }
  /**
   * Turns a card a quarter turn. `as`: what the tap is for. 'ability' = its Tap ability is being used (a
   * tap_harvest card gains its Harvest); 'attack' = it has attacked. Either way it has been used this turn and
   * can't be untapped again before the turn is over. A plain tap (no `as`) only turns the card.
   */
  private turnCard(card: FieldCard, dir: 1 | -1, as?: 'attack' | 'ability') {
    const wasTapped = card.rot % 2 === 1;
    card.rot = (card.rot + dir + 4) % 4;
    const tappedNow = !wasTapped && card.rot % 2 === 1;
    if (wasTapped && card.rot % 2 === 0) this.untapped(card);
    if (as) card.acted = this.turnKey();
    if (tappedNow && as === 'ability') this.harvest(card);
    // a tap_relock card (Cannoneer): used, it is given the tokens it arrived with again (its Lock), unless one is still on it
    if (tappedNow && as && card.faceUp && this.cards.byNumber.get(card.num)?.whileInPlay.includes('tap_relock') && !this.tokensOn(card).some((t) => t.from === card.id)) this.bring(card);
    // turning a card out of the Entrenched position ends its entrenchment
    const dug = card.rot !== CONFIG.arrival.entrenchedRotation ? this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken) : [];
    this.removeTokens(dug);
    // tapping a card uses its Ambush: the Ambush token comes off
    if (CONFIG.tokenRules.ambushEndsOnTap) this.removeTokens(this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.tokenRules.ambushToken));
    return dug.length > 0;
  }
  /** A unit a card effect summoned (or one from the Spawn menu): not a card played from a hand, a swarm's or Lanespanner's own unit, or a clone. */
  private isSummonedUnit(c: FieldCard): boolean {
    if (c.clone || c.spanOf || c.swarm || c.legion) return false;
    return !!c.summonedBy || !!this.cards.byNumber.get(c.num)?.spawnOnly;
  }
  /** Barragand's Burning Necrosis is still at work on this Unit: the Bleed or Weakness it gave is still on it. */
  private necrosisHolds(card: FieldCard) {
    return card.marks?.necrosis !== undefined && (this.hasOn(card, 'bleed') || this.hasOn(card, 'weakness'));
  }
  /**
   * A Unit under Burning Necrosis is destroyed while its Bleed or Weakness lasts: the player whose ability it was
   * picks a Unit in the same lane, on either side of the field (the lane and the one across from it), and it is
   * Burned. With one to pick from it is simply taken; a bot picks for itself (an opposing Unit first).
   */
  private necrosisSpreads(card: FieldCard) {
    const s = this.state;
    const by = card.marks?.necrosis;
    if (by === undefined || !this.necrosisHolds(card)) return;
    delete card.marks!.necrosis;
    const at = this.standsAt(card);
    if (!at || slotKind(at.slot.name) !== 'Lane') return;
    const row = (sd: 1 | -1) => laneOrder().map((slot) => ({ slot, x: slot.x * sd })).sort((a, b) => a.x - b.x);
    const j = row(at.side).findIndex((l) => l.slot === at.slot);
    const across = row(-at.side as 1 | -1)[j]?.slot;
    const ok = (u: FieldCard) => u !== card && u.faceUp && s.field.includes(u) && !u.atZero && this.isUnit(this.cards.byNumber.get(u.num)) && !this.untouchable(u) && !this.intangible(u) && !this.hasOn(u, CONFIG.turn.lifetime.expiredToken);
    const pool = [...this.unitsAt(at.slot, at.side, undefined, true), ...(across ? this.unitsAt(across, -at.side as 1 | -1, undefined, true) : [])].filter(ok);
    const name = this.cardName(card.num);
    if (!pool.length) { this.log(`${name} was destroyed under Burning Necrosis, but there is no Unit in its lane to Burn.`, by as Seat); return; }
    const burn = (u: FieldCard) => {
      this.giveStatus(by as Seat, u, 'burn', null, null);
      this.look('necrosis-cast', u);
      this.log(`Burning Necrosis: ${name} was destroyed, and ${this.cardName(u.num)} in its lane is Burned.`, by as Seat);
    };
    const theirs = pool.filter((u) => this.opposed(by as Seat, u.owner));
    if (pool.length === 1 || s.bots?.includes(by as Seat)) { burn(theirs[0] ?? pool[0]); return; }
    const src = this.emperorOf(by as Seat) ?? card;
    (s.picks ??= []).push({ id: s.nextId++, seat: by as Seat, name: 'Burning Necrosis', text: `Burning Necrosis: ${name} was destroyed. Choose a Unit in its lane to Burn`, options: pool.map((u) => u.id), src: { ...src, merged: [] }, rest: [{ when: 'use', passive: false, words: ['status', 'burn'], on: 'it', conds: [] }], ctx: {} });
  }
  /** Is this unit part of something spread across lanes: a Lanespanner's units, or Undead Legion's General and soldiers? */
  private spansLanes(card: FieldCard): boolean {
    const def = this.cards.byNumber.get(card.num);
    if (card.spanOf || card.legion || this.tokensOn(card).some((t) => this.effectOf(t) === CONFIG.tokenRules.lanespannerToken)) return true;
    return !!def && (def.effects.some((e) => e.words[0] === 'legion') || isLanespanner(def, card.form ?? 0));
  }
  /** The status on this card that stops its Passive effects (CONFIG.passiveStoppedBy), by name, or null. */
  private passiveStopper(card: FieldCard): string | null {
    const t = this.tokensOn(card).find((o) => CONFIG.passiveStoppedBy.includes(this.effectOf(o)));
    return t ? (this.tokenDb.byId.get(t.tokenId)?.name ?? 'a status') : null;
  }
  /**
   * Why this player can't spend this much Dark Gerzite right now, or null: a no_dark_spend card of theirs is in
   * play, or a dark_cap card of theirs (Gemstone Hoarder: 4) and they have already spent up to it this turn.
   */
  private darkHeld(seat: Seat, n = 1): string | null {
    const by = this.inPlayWith(seat, 'no_dark_spend')[0];
    if (by) return `You can’t spend Dark Gerzite while ${this.cardName(by.num)} is in play.`;
    const cap = this.darkCap(seat);
    if (cap && n > cap.left) return `${this.cardName(cap.by.num)} lets you spend only ${cap.max} Dark Gerzite a turn: you can spend ${cap.left} more this turn.`;
    return null;
  }
  /** The most Dark Gerzite this player may still spend this turn, from their dark_cap card in play (Gemstone Hoarder), or null. */
  private darkCap(seat: Seat): { by: FieldCard; max: number; left: number } | null {
    let best: { by: FieldCard; max: number } | null = null;
    for (const c of this.state.field) {
      if (!c.faceUp || !this.allies(c.owner, seat) || this.isExpired(c)) continue;
      const e = this.cards.byNumber.get(c.num)?.effects.find((x) => x.when === 'always' && x.words[0] === 'dark_cap');
      const max = e ? Number(e.words[1]) || 0 : null;
      if (max !== null && (!best || max < best.max)) best = { by: c, max };
    }
    if (!best) return null;
    const spent = this.state.players[seat].darkSpent;
    const used = spent && spent.stamp === this.turnStamp() ? spent.n : 0;
    return { ...best, left: Math.max(0, best.max - used) };
  }
  /** Dark Gerzite this player just spent, counted toward this turn's cap. */
  private spentDark(seat: Seat, n: number) {
    if (n <= 0) return;
    const p = this.state.players[seat]; const stamp = this.turnStamp();
    p.darkSpent = { stamp, n: (p.darkSpent?.stamp === stamp ? p.darkSpent.n : 0) + n };
  }
  /** A player just gained Dark Gerzite: each opposing steal_dark card in play (Gemstone Hoarder) takes 1 of it for its owner. */
  private darkGained(seat: Seat) {
    const s = this.state;
    const def = this.tokenDb.list.find((d) => d.bar === 'dark');
    if (!def) return;
    for (const thief of s.field) {
      if (!thief.faceUp || !this.opposed(thief.owner, seat) || !this.inPlayWith(thief.owner, 'steal_dark').includes(thief)) continue;
      if (this.passiveStopper(thief)) continue; // a Passive: held back while its card is Entrenched, Comatose, Frozen, Locked or Stunned
      const theirs = this.barTokens(seat, 'dark');
      if (!theirs.length) return;
      // onto the thief's bar; with no room there, nothing is taken
      if (!this.placeBarToken(thief.owner, def)) continue;
      this.removeTokens(theirs.slice(-1));
      const text = `${this.cardName(thief.num)} steals 1 ${def.name} from ${s.players[seat].name} for ${s.players[thief.owner].name}.`;
      this.fx('status', thief.id, undefined, { effect: 'notice', text });
      this.log(text, thief.owner);
    }
  }
  private payBar(seat: Seat, cost: number, dark: boolean) {
    if (cost <= 0) return true;
    const mine = this.barTokens(seat, dark ? 'dark' : 'gerzite');
    if (mine.length < cost) return false;
    this.removeTokens(mine.slice(-cost));
    if (dark) this.spentDark(seat, cost);
    return true;
  }
  /**
   * Cosmic Beyonder's Entrance (on_play = discard_recycle): with more cards in your discard pile than your
   * opponent (2v2: than either opponent), the card is discarded, your discard pile is shuffled, its top
   * CONFIG.discardRecycle.toDeck cards go into your deck, your deck is shuffled and you draw its top card (or, with a
   * full hand, it waits for you to choose where it goes). Otherwise it stays in play, with no Entrance effect.
   * Returns the line for the log.
   */
  private discardRecycle(seat: Seat, card: FieldCard): string {
    const s = this.state; const p = s.players[seat];
    const name = this.cardName(card.num);
    const theirs = Math.max(0, ...s.players.map((q, i) => (this.allies(seat, i as Seat) ? -1 : q.discard.length)));
    const mine = p.discard.length;
    if (mine <= theirs) return `${name}: ${p.name} has ${mine} card${mine === 1 ? '' : 's'} in their discard pile and their opponent ${theirs}, so it stays in play without its Entrance effect.`;
    this.discardFromPlay(card);
    this.shuffle(p.discard);
    const moved = p.discard.splice(0, CONFIG.discardRecycle.toDeck);
    p.deck.push(...moved.map((c) => this.rehide(c)));
    this.shuffle(p.deck);
    p.shuffles.discard++;
    p.shuffles.deck++;
    let drew = '';
    if (this.canDraw(p)) {
      if (p.hand.length < CONFIG.handLimit) {
        p.hand.push(this.rehide(this.drawTop(p)!));
        this.fx('toHand', '', undefined, { effect: 'deck', seat, count: 1, tint: CONFIG.looks.handTint[name] });
        drew = ' and drew the top card';
      } else if (!p.overflow) {
        p.overflow = this.rehide(this.drawTop(p)!);
        drew = ' and drew the top card (their hand is full: it waits for them to choose where it goes)';
      }
    }
    return `${name}’s Entrance: ${p.name} has more cards in their discard pile (${mine} to ${theirs}), so ${name} is discarded. They shuffled their discard pile, put its top ${moved.length} card${moved.length === 1 ? '' : 's'} into their deck, shuffled it${drew}.`;
  }
  /** A card leaves play for its OWNER's discard pile, whoever caused it, with every copy merged under it. */
  private discardFromPlay(card: FieldCard) {
    if (card.asBiome) { this.crosserLeaves(card); return; }
    const from = { x: card.x, z: card.z };
    const copies = this.copiesOf(card);
    this.clearTokensOn(card);
    this.removeField(card.id);
    // (a Spell that has done what it does: the table shows it on the mat until what it set off has played out, even one
    // that went off the moment it was played and so was never seen lying there)
    if (card.faceUp && this.cards.byNumber.get(card.num)?.types.includes('spell')) this.fx('spent', card.id, undefined, { num: card.num, seat: card.owner, from, value: card.rot });
    const to = card.reflectedFrom ?? card.owner; // (a Spell Reflection turned back goes to its caster's pile)
    for (const c of copies) this.insertAt(this.state.players[to].discard, this.rehide(c), 'top');
    // (the Troops Soul Collector held go with it, back to the discard pile)
    const heldCards = this.state.field.filter((c) => c.heldBy === card.id);
    for (const h of heldCards) { this.removeField(h.id); this.insertAt(this.state.players[card.owner].discard, this.rehide(h), 'top'); }
    for (const n of (card.borrowed ?? []).slice(heldCards.length)) this.insertAt(this.state.players[card.owner].discard, { id: this.newId('c'), num: n, spawned: false }, 'top');
    this.closeUp(from);
    this.guardSweep();
  }
  /**
   * Undead Guards whose Grave Reinforcements has left the table: those still standing rise and float away, like a
   * Spirit Soldier whose General has fallen. They aren't destroyed (nothing that watches for a destroyed Troop stirs).
   */
  private guardSweep() {
    const s = this.state;
    const gone = s.field.filter((c) => c.summonedBy && !this.findField(c.summonedBy) && c.faceUp && !!this.cards.byNumber.get(c.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'bulwark'));
    if (!gone.length) return;
    for (const c of gone) {
      this.fx('sacrifice', c.id, undefined, { seat: c.owner, effect: 'spirit-away' });
      this.clearTokensOn(c);
      this.removeField(c.id);
      this.closeUp(c);
    }
    this.log(`With their Grave Reinforcements gone, ${gone.length === 1 ? 'an Undead Guard rises and floats' : `${gone.length} Undead Guards rise and float`} away.`, gone[0].owner);
  }
  /**
   * May this player play this card from their hand right now? Every card can be played in its owner's Summon
   * Phase; after that only Spells, in the Attack Phase (playPhases in the settings). A free play, for a card
   * effect that puts a card into play at some other moment, isn't held to this.
   */
  /**
   * What card effects say about playing this card face-up right now (null = nothing stops it): Glacial Creep only
   * as the first thing you do in a turn, no Spells after Split Chasm. A free play (Ctrl) is never held to them.
   */
  private playBarred(seat: Seat, def: CardDef | undefined, free: boolean): string | null {
    if (!def || free || !CONFIG.combat.enabled) return null;
    const s = this.state; const p = s.players[seat];
    if (def.types.includes('spell') && p.noSpellsThrough !== undefined && p.turns <= p.noSpellsThrough) return `You can’t cast Spells until the end of your next turn (Split Chasm).`;
    if (def.effects.some((e) => e.when === 'play_if' && e.words[0] === 'first')) {
      const stamp = this.turnStamp();
      const key = this.turnKey();
      const played = p.playedNow?.stamp === stamp && p.playedNow.types.length > 0;
      const attacked = s.field.some((c) => this.turnOf(c.owner) === seat && (c.swings?.key === key));
      if (played || attacked) return `${def.name} can only be played before you have played any other card or attacked this turn.`;
    }
    return null;
  }
  /** Tectonic Shift: no Biome can be played while it is active, and no Ground Unit into the lanes it holds. */
  private shutBy(def: CardDef | undefined, x: number, z: number): string | null {
    if (!def || !CONFIG.combat.enabled) return null;
    for (const c of this.state.field) {
      if (!c.faceUp || this.isExpired(c)) continue;
      const fx2 = this.cards.byNumber.get(c.num)?.effects ?? [];
      if (def.types.includes('biome') && fx2.some((e) => e.when === 'always' && e.words[0] === 'no_biomes')) return `No Biome can be in play while ${this.cardName(c.num)} is active.`;
      if (this.isUnit(def) && def.movement !== 'flying' && c.lanes && fx2.some((e) => e.when === 'always' && e.words[0] === 'no_ground_lanes')) {
        const at = slotAt({ x, z });
        if (at && at.side === c.lanes.side && c.lanes.names.includes(at.slot.name)) return `${at.slot.name} has sunk away (${this.cardName(c.num)}): no Ground Unit can be summoned into it.`;
      }
    }
    return null;
  }
  private playWindow(seat: Seat, def: { types: string[]; name: string } | undefined, free: boolean): string | null {
    if (free || !def) return null;
    const why = playWindowRefusal(def, this.state.turn, this.state.phase, seat);
    return why ? `${why} (If a card effect lets you, hold Ctrl, or ⌘ on a Mac, as you put it down.)` : null;
  }
  /** A turn timer ran out on a Biome or Tome: a Destroyed token marks it as no longer active. Nothing is discarded. */
  /** As a player's turn ends: their Tomes whose turns are over and that weren't restarted go to the discard pile. */
  private discardSpentTomes(seat: Seat) {
    const s = this.state;
    const spent = s.field.filter((c) => this.turnOf(c.owner) === seat && c.faceUp && this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.types.some((t) => CONFIG.turn.restartable.includes(t)));
    for (const c of spent) {
      this.log(`${this.cardName(c.num)}’s turns were over and it wasn’t restarted, so it is discarded.`, seat);
      this.discardFromPlay(c);
    }
  }
  private expire(card: FieldCard) {
    const s = this.state;
    const def = this.tokenDb.list.find((d) => d.effect === CONFIG.turn.lifetime.expiredToken);
    if (!def || !this.hasLifetime(card) || !s.field.includes(card)) return false;
    if (this.tokensOn(card).some((t) => this.effectOf(t) === def.effect)) return true; // already marked
    this.fire(card, 'expired'); // what it does as its turns run out (Tome of Gerzite, Self-Destructing Tome)
    if (!s.field.includes(card)) return true; // (a Spell that discards itself as it ends)
    if (card.brought?.length) return true; // (cast again as it ended, with a new token to keep: Cursed Chains)
    s.tokens.push({ id: this.newId('t'), tokenId: def.id, x: card.x, z: card.z, value: def.start, layer: s.nextLayer++, by: card.owner });
    return true;
  }
  /**
   * An Entrenched token just left a card that is still on the table: it ran out, the card was turned out of
   * the Entrenched position, or the token was taken off by hand. Some cards are given tokens at that moment
   * (the freed_gains cell in cards.csv — Hexed Mummy gains 2 Decay, 2 Weakness and 2 Susceptible).
   */
  private freed(card: FieldCard | undefined) {
    const s = this.state;
    if (!card || !s.field.includes(card)) return;
    if (this.tokensOn(card).some((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken)) return; // still dug in
    const gains = this.cards.byNumber.get(card.num)?.freedGains ?? [];
    if (!gains.length) return;
    const side = sideOf(card.owner);
    const got: string[] = [];
    gains.forEach((g, i) => {
      const def = this.tokenDb.list.find((d) => d.effect === g.effect);
      if (!def) return;
      // in a row across the middle of the card
      const t: Token = { id: this.newId('t'), tokenId: def.id, x: card.x + (i - (gains.length - 1) / 2) * 2 * side, z: card.z + 0.2 * side, value: def.counter ? g.value : def.start, layer: s.nextLayer++, by: card.owner, since: s.players[card.owner].turns };
      s.tokens.push(t);
      if (this.landToken(card.owner, t)) this.removeTokens([t]); // the card can't take it (Divinity, an immunity)
      else if (s.tokens.includes(t)) got.push(def.counter ? `${t.value} ${def.name}` : def.name);
    });
    if (!got.length) return;
    const list = got.length > 1 ? `${got.slice(0, -1).join(', ')} and ${got.at(-1)}` : got[0];
    this.fx('status', card.id, undefined, { effect: 'notice' });
    this.log(`${card.faceUp ? this.cardName(card.num) : 'A face-down card'} is no longer Entrenched: it gains ${list}.`, card.owner);
  }
  /** A card just took damage: the statuses that end on damage come off it (CONFIG.tokenRules.endsOnDamage — Comatose). */
  private wake(card: FieldCard) {
    this.firstDamage(card);
    const list = CONFIG.tokenRules.endsOnDamage;
    const gone = this.tokensOn(card).filter((t) => list.includes(this.effectOf(t)));
    if (!gone.length) return;
    const names = [...new Set(gone.map((t) => this.tokenDb.byId.get(t.tokenId)?.name ?? 'a status'))];
    this.removeTokens(gone);
    this.fx('status', card.id, undefined, { effect: 'notice' });
    this.log(`${card.faceUp ? this.cardName(card.num) : 'A face-down card'} took damage, so ${names.join(' and ')} ends.`, card.owner);
  }
  /**
   * A Special Hit that is a passive set off by the first damage the card takes ("the first time this Troop
   * receives damage, …": Jungle Explorer): the first damage uses it up. Its token comes off, and both players are
   * reminded what it does, for the card's owner to carry out.
   */
  private striking = 0; // an attack is being dealt (its Special Hits strike back at the attacker there)
  private spellHitting = 0; // a Spell's damage is being dealt (Jungle Explorer then hits the lane in front of it)
  private firstDamage(card: FieldCard) {
    if (!card.faceUp) return;
    if (this.spellHitting > 0 && this.hasOn(card, 'special-hit') && this.healthLeft(card) > 0) {
      // Jungle Explorer hit by a Spell: its 4 Volcanic splash and Burn go to the lane in front of it
      const def0 = this.cards.byNumber.get(card.num);
      const m0 = def0 ? /first time this troop receives damage, deal (\d+)[^.]*?damage(?: and inflict (\w+))?/i.exec(def0.text) : null;
      const front = m0 ? this.whoOf(card, 'front', {}) : [];
      if (m0 && /damaged by a spell, execute this effect on the lane in front/i.test(def0!.text) && front.length) {
        this.removeTokens(this.tokensOn(card).filter((t) => this.effectOf(t) === 'special-hit'));
        const notes: string[] = [];
        const status = m0[2] ? this.tokenDb.list.find((d) => d.name.toLowerCase() === m0[2].toLowerCase()) : undefined;
        for (const u of front) { this.fx('spell', card.id, undefined, { target: u.id, from: { x: card.x, z: card.z }, tint: 'volcanic', seat: card.owner, effect: 'bounce' }); this.landHit(card.owner, u, Number(m0[1]), { splash: true, from: card }, notes); if (status) this.giveStatus(card.owner, u, status.id, null, null); }
        this.log(`${def0!.name} took Spell damage for the first time: ${m0[1]} Volcanic splash${status ? ` and ${status.name}` : ''} on the lane in front of it.`, card.owner);
        return;
      }
    }
    const def = this.cards.byNumber.get(card.num);
    const m = def ? /the first time this (?:troop|unit|building) receives damage,\s*([^.]*\.(?:[^.]*\bif\b[^.]*\.)?)/i.exec(def.text) : null;
    if (!m) return;
    const special = this.tokensOn(card).filter((t) => this.effectOf(t) === 'special-hit');
    if (!special.length) return;
    this.removeTokens(special);
    const name = this.cardName(card.num);
    const what = m[1].trim().replace(/^./, (ch) => ch.toUpperCase());
    const text = `${name} took damage for the first time: its Special Hit goes off. ${what}`;
    this.fx('status', card.id, undefined, { effect: 'notice', text });
    this.log(text, null);
  }
  /** The cards on the table as slot limits see them, leaving some out (the ones being moved). */
  private placed(except: string[] = []): Placed[] {
    return this.state.field.filter((c) => !except.includes(c.id)).map((c) => ({ x: c.x, z: c.z, types: this.cards.byNumber.get(c.num)?.types ?? null, shares: this.shares(c) }));
  }
  /** Biomes (CONFIG.arrival.sidewaysTypes) lie sideways while in play: for them sideways isn't "tapped". */
  private sitsSideways(card: { num: number; asBiome?: boolean }) {
    return !!card.asBiome || sitsSideways(this.cards.byNumber.get(card.num)); // (Eldritch Realm Crosser in the Biome slot lies sideways like a Biome)
  }
  /** Every card standing in a named slot on one side of the mat (not one riding on another card). */
  private allInSlot(name: string, side: 1 | -1, hand?: number): FieldCard[] {
    return this.state.field.filter((c) => { const at = slotAt(c); return !c.attachedTo && !!at && at.slot.name === name && at.slot.hand === hand && at.side === side; });
  }
  /** The card in a named slot on one side of the mat (the top one, if several are stacked there). */
  private cardInSlot(name: string, side: 1 | -1, hand?: number): FieldCard | undefined {
    let best: FieldCard | undefined;
    for (const c of this.state.field) {
      const at = slotAt(c);
      if (at && at.slot.name === name && at.slot.hand === hand && at.side === side && (!best || c.layer > best.layer)) best = c;
    }
    return best;
  }

  /** Damage straight onto a card (not an attack or a Spell's hit), less its Buffer, and shared across its links. "True damage" (wound) is not this: it ignores Buffer. */
  private bufferedDamage(card: FieldCard, tokenId: string, amount: number, by: Seat | null) {
    const n = Math.max(0, amount - this.valueOn(card, 'buffer') - this.auraFor(card, 'buffer'));
    if (n <= 0) return;
    this.addDamage(card, tokenId, n, by);
    this.mirrorDamage(card, tokenId, n);
  }
  /** Adds damage to a card: onto the damage counter already on it, or a new one. */
  private addDamage(card: FieldCard, tokenId: string, amount: number, by: Seat | null, all = false, straight = false) {
    const s = this.state;
    if (this.untouchable(card)) return; // Calling Pillar: it cannot be damaged
    if (this.intangible(card)) return; // nor can a Spirit Soldier
    if (card.wardAt !== undefined && card.wardAt === this.turnStamp()) return; // Impervious Smite: nothing damages it this turn
    // Branch Out: the second Unit's damage comes off the health the two share, held on the first
    const into = card.mergedInto ? this.findField(card.mergedInto) : undefined;
    if (into && into !== card) { this.addDamage(into, tokenId, amount, by, all, straight); return; }
    // Overheal takes the damage first, and only what is left over goes onto the card's health (straight: status
    // damage, Deck-Out and Overtime go to the health itself)
    let rest = amount;
    if (amount > 0 && !straight && CONFIG.combat.overhealFirst) {
      for (const t of this.tokensOn(card).filter((o) => this.effectOf(o) === 'overheal')) {
        if (rest <= 0) break;
        const take = Math.min(rest, t.value);
        t.value -= take; rest -= take;
        if (t.value <= 0) this.removeTokens([t]);
      }
    }
    // (splash on a card of several units is kept on a counter of its own: every unit has taken it, so it stays as units fall)
    const have = this.tokensOn(card).find((t) => t.tokenId === tokenId && !!t.all === all);
    if (rest <= 0) { /* all of it went on the Overheal */ }
    else if (have) have.value = Math.min(999, have.value + rest);
    else {
      const side = sideOf(card.owner);
      s.tokens.push({ id: this.newId('t'), tokenId, ...this.near(card, (all ? -1.2 : 1.2) * side, 1.6 * side), value: rest, layer: s.nextLayer++, by, ...(all ? { all: true } : {}) });
    }
    this.fx('damage', card.id);
    if (amount > 0 && card.faceUp && this.opposed(card.owner, s.turn) && this.cards.byNumber.get(card.num)?.effects.some((e) => e.words[0] === 'berry')) { if (card.berryAt !== this.turnStamp()) card.berry = 0; card.berry = (card.berry ?? 0) + amount; card.berryAt = this.turnStamp(); } // (Berry Reserve: only what it takes in this one turn)
    if (amount > 0) {
      this.wake(card);
      card.hurtAt = this.turnStamp();
      this.fireAll('host_damaged', (c) => c.host === card.id); // a Spell attached to it (Balloons, Invisibility Cloak)
      // Perish Bank: a life bank on its side pays the damage back at once, for as long as it has health in it
      const bank = card.faceUp && this.isUnit(this.cards.byNumber.get(card.num)) ? s.tokens.find((t) => this.effectOf(t) === 'life-bank' && t.value > 0 && t.by !== null && sideOf(t.by) === this.sideOfCard(card)) : undefined;
      if (bank && rest > 0) {
        const back = Math.min(rest, bank.value);
        const dmg = this.tokensOn(card).find((t) => t.tokenId === tokenId && !!t.all === all);
        if (dmg) { dmg.value -= back; if (dmg.value <= 0) this.removeTokens([dmg]); }
        bank.value -= back;
        this.log(`The life bank gives ${this.cardName(card.num)} ${back} health back (${bank.value} left in it).`, bank.by);
        if (bank.value <= 0) this.removeTokens([bank]);
      }
      this.fire(card, 'damaged', { amount });
      if (card.illusion) this.illusionGoes(card, card); // Illusionist: the first of the pair to be damaged goes
      if (card.faceUp && this.cards.byNumber.get(card.num)?.types.includes('emperor')) {
        const side = this.sideOfCard(card);
        this.fireAll('emperor_damaged', (c) => c !== card && this.sideOfCard(c) === side);
      }
    }
  }
  /**
   * The cards linked to this one: its Emperor or Minion (the linked slots), and any card carrying a Link counter
   * with the same number as one on this card. `via` = the Link counter on the partner (null for Emperor and
   * Minion), which says whether that card takes half of the damage copied to it.
   */
  private linkPartners(card: FieldCard): { card: FieldCard; via: Token | null; why: string }[] {
    const out: { card: FieldCard; via: Token | null; why: string }[] = [];
    const at = slotAt(card);
    const [a, b] = CONFIG.linkedSlots;
    // (a unit summoned into the Minion slot by the Minion — a Necromancer's Skell-Minis — stands beside it, but isn't
    // the Minion: it isn't linked to the Emperor)
    if (at && a && b && (at.slot.name === a || at.slot.name === b) && !card.summonedBy) {
      const other = at.slot.name === a ? b : a;
      // (2v2: each player's own Emperor and Minion.) Into the Minion slot it reaches everything standing there, like
      // splash: each unit of a swarm (Skell-Mateys ×2) takes it. Into the Emperor slot, the Emperor.
      const partners = (other === b ? this.allInSlot(other, at.side, at.slot.hand) : [this.cardInSlot(other, at.side, at.slot.hand)].filter((c): c is FieldCard => !!c)).filter((c) => !c.summonedBy);
      for (const partner of partners) out.push({ card: partner, via: null, why: 'Emperor and Minion are linked' });
    }
    const L = CONFIG.tokenRules.linkToken;
    for (const mine of this.tokensOn(card).filter((t) => this.effectOf(t) === L)) {
      for (const other of this.state.tokens) {
        if (other === mine || this.effectOf(other) !== L || other.value !== mine.value) continue;
        const partner = this.cardUnder(other);
        if (!partner || partner.id === card.id || out.some((o) => o.card.id === partner.id)) continue;
        out.push({ card: partner, via: other, why: `Link ${mine.value}` });
      }
    }
    return out;
  }
  /**
   * Linked cards share damage: what one takes, the other takes too (your Emperor and your Minion, and two cards
   * carrying Link counters with the same number). A Link counter set to half gives its card half, rounded up.
   */
  private mirrorDamage(card: FieldCard, tokenId: string, amount: number) {
    if (amount <= 0) return;
    const name = (c: FieldCard) => (c.faceUp ? this.cardName(c.num) : 'a face-down card');
    const said: { line: string; why: string; n: number }[] = [];
    // a swarm in the Minion slot hit all together (splash): its Emperor takes the hit once, not once per unit
    const inMinion = !!card.swarm && this.standsAt(card)?.slot.name === CONFIG.linkedSlots[1];
    for (const p of this.linkPartners(card)) {
      if (inMinion && !p.via && this.linkWave) {
        const key = `${card.swarm}>${p.card.id}:${tokenId}`;
        if (this.linkWave.has(key)) continue;
        this.linkWave.add(key);
      }
      let give = amount;
      if (p.via?.half) {
        // half, rounded up over all the hits so far: 1 then 1 more makes 1, not 2
        const before = p.via.linkSum ?? 0;
        p.via.linkSum = before + amount;
        give = Math.ceil(p.via.linkSum / 2) - Math.ceil(before / 2);
      }
      if (give <= 0) {
        this.log(`${amount} damage on ${name(card)}: half of it is owed to ${name(p.card)} and carries over to the next hit (${p.why}).`, card.owner);
        continue;
      }
      // Buffer takes off all damage coming in, the damage that comes across a link too
      const buffer = this.valueOn(p.card, 'buffer') + this.auraFor(p.card, 'buffer');
      if (buffer > 0) {
        const was = give;
        give = Math.max(0, give - buffer);
        if (give <= 0) { this.log(`${amount} damage on ${name(card)} would be shared with ${name(p.card)}, but its Buffer takes all ${was} of it (${p.why}).`, card.owner); continue; }
      }
      this.addDamage(p.card, tokenId, give, null);
      this.fx('link', card.id, undefined, { target: p.card.id });
      const line = `${amount} damage on ${name(card)} is shared with ${name(p.card)}${give !== amount ? (buffer > 0 ? `, which takes ${give} after its Buffer` : `, which takes half: ${give}`) : ''}`;
      const same = said.find((x) => x.line === line && x.why === p.why);
      if (same) same.n++;
      else said.push({ line, why: p.why, n: 1 });
    }
    // (one line for every unit of a swarm in the Minion slot: "… shared with Skell-Minis, each of its 3 units")
    for (const x of said) this.log(`${x.line}${x.n > 1 ? `, each of its ${x.n} units` : ''} (${x.why}).`, card.owner);
  }
  /**
   * A status token your opponent puts on your Emperor or your Minion is copied onto the other one
   * (statuses you place on your own cards are not), and so is one put on a card linked by Link counters.
   * The two copies then keep the same number.
   */
  private mirrorStatus(seat: Seat, t: Token, wasOn: FieldCard | undefined) {
    const old = t.twin ? this.state.tokens.find((o) => o.id === t.twin) : undefined;
    if (old) old.twin = undefined; // moved: the old pairing is over
    t.twin = undefined;
    const def = this.tokenDb.byId.get(t.tokenId);
    if (!CONFIG.linkMirrorsStatus || def?.category !== 'status') return;
    if ([CONFIG.tokenRules.entrenchedToken, CONFIG.tokenRules.ambushToken].includes(def.effect)) return;
    const card = this.cardUnder(t);
    if (!card || card.id === wasOn?.id) return;
    // Only a status that comes from the opponent is copied. Players usually put tokens on their own cards
    // themselves, whoever caused them, so a harmful status counts as the opponent's doing no matter who
    // places it; anything the opponent places by hand counts too.
    const hostile = CONFIG.hostileStatuses.includes(def.effect) || this.opposed(card.owner, seat);
    if (!hostile) return;
    for (const { card: partner, why } of this.linkPartners(card)) this.copyStatus(seat, t, card, partner, why);
  }
  /** Puts a copy of a status token on a linked card; the first copy is paired with it so their numbers stay together. */
  private copyStatus(seat: Seat, t: Token, card: FieldCard, partner: FieldCard, why: string) {
    const def = this.tokenDb.byId.get(t.tokenId);
    if (!def) return;
    const copy: Token = { id: this.newId('t'), tokenId: t.tokenId, ...this.near(partner, (t.x - card.x) / (card.scale ?? 1), (t.z - card.z) / (card.scale ?? 1)), value: t.value, layer: this.state.nextLayer++, by: t.by, since: t.since };
    if (t.price) Object.assign(copy, { price: t.price, priceDark: t.priceDark }); // a Lock's cost crosses the link with it
    if (t.turns) Object.assign(copy, { turns: t.turns, turnsSince: t.turnsSince }); // and so does a turn clock
    if (t.drain) Object.assign(copy, { drain: t.drain, drainSince: t.drainSince }); // …and a wear-off timer
    this.state.tokens.push(copy);
    const name = (c: FieldCard) => (c.faceUp ? this.cardName(c.num) : 'a face-down card');
    const refused = this.landToken(seat, copy);
    if (refused) {
      this.removeTokens([copy]);
      this.log(`${def.name} on ${name(card)} is not copied to ${name(partner)}: ${refused}`, card.owner);
      return;
    }
    if (!this.state.tokens.includes(copy)) return;
    if (copy.tokenId === t.tokenId && !t.twin) {
      t.twin = copy.id;
      copy.twin = t.id;
    }
    this.fx('link', card.id, undefined, { target: partner.id });
    this.log(`${def.name} on ${name(card)} is also placed on ${name(partner)} (${why}).`, card.owner);
  }
  /** How many units the Lanespanner with this id has (its card's Count). */
  private spanCount(id: string) {
    const card = this.state.field.find((c) => c.id === id);
    const def = card ? this.cards.byNumber.get(card.num) : undefined;
    return (card && def && formOf(def, card.form).count) || 1;
  }
  /** The refusal while a Lanespanner is waiting for its owner to say where the rest of it goes. */
  private spanWaiting(span: { card: string }) {
    const card = this.state.field.find((c) => c.id === span.card);
    return `First choose where the rest of ${card ? this.cardName(card.num) : 'your Lanespanner'} goes.`;
  }
  /** Puts a Lanespanner token on one of a Lanespanner's units (on its left edge, where a Count counter would go). */
  private spanToken(card: FieldCard, span: string) {
    const def = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.lanespannerToken);
    if (!def) return;
    const side = sideOf(card.owner);
    this.state.tokens.push({ id: this.newId('t'), tokenId: def.id, ...this.near(card, -1.7 * side, 0), value: def.start, layer: this.state.nextLayer++, by: card.owner, span });
  }
  /**
   * The rest of a Lanespanner's units go into the lanes from `start` on (0 = Lane 1), one per lane, beside the one
   * played from the hand. They may go into lanes that are already taken. Each is a copy of the card that arrives
   * the way it did (its Entrenched or Ambush, its Darkgale mark) with a Lanespanner token, and no Count counter.
   */
  private placeSpan(seat: Seat, start: number) {
    const s = this.state; const p = s.players[seat];
    const main = p.span ? s.field.find((c) => c.id === p.span!.card) : undefined;
    p.span = null;
    if (!main) return;
    const def = this.cards.byNumber.get(main.num);
    const lanes = laneOrder();
    const at = slotAt(main);
    const middle = at ? lanes.indexOf(at.slot) : -1;
    const n = def ? (formOf(def, main.form).count ?? 1) : 1;
    const marks = this.tokensOn(main).filter((t) => [CONFIG.tokenRules.entrenchedToken, CONFIG.tokenRules.ambushToken, CONFIG.turn.darkgale.token].includes(this.effectOf(t)));
    // the extra units are the summoned unit named in its spread_unit cell (Royal Line → Royal Guard), or copies of it
    const unitNum = (def?.spreadUnit && this.cards.spawnOnly.find((d) => d.name.toLowerCase() === def.spreadUnit.toLowerCase())?.number) || main.num;
    // (a Lanespanner wider than the table, Flesh-Eating Locusts on the 2v2 table: a unit past the end goes in the end lane)
    const wide = n > lanes.length;
    const where: number[] = [];
    for (let li = start; li < start + n; li++) {
      if (li === middle) continue;
      if (!wide && (li < 0 || li >= lanes.length)) continue;
      where.push(Math.max(0, Math.min(lanes.length - 1, li)));
    }
    for (const li of where) {
      // (the lanes of the side it stands on, as the mat has them: on the 2v2 table a right-hand player's seat is mirrored left
      // to right, but its lanes are the same four the whole side shares)
      const side = at?.side ?? sideOf(seat);
      const pos = { x: lanes[li].x * side, z: lanes[li].z * side };
      const unit: FieldCard = { ...this.held(unitNum, true), owner: seat, ...pos, rot: main.rot, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, spanOf: main.id };
      s.field.push(unit);
      for (const m of marks) s.tokens.push({ id: this.newId('t'), tokenId: m.tokenId, x: m.x - main.x + pos.x, z: m.z - main.z + pos.z, value: m.value, layer: s.nextLayer++, by: m.by, since: m.since });
      this.spanToken(unit, main.id);
      this.addStartCounters(unit);
      this.fx('play', unit.id);
    }
    const names = [...new Set([...where, middle].filter((li) => li >= 0))].sort((a, b) => a - b).map((li) => lanes[li].name.replace(/^Lane\s*/, ''));
    this.log(`${def?.name ?? 'The Lanespanner'} spreads across Lanes ${names.slice(0, -1).join(', ')} and ${names.at(-1)}.`, seat);
  }
  /** One of a Lanespanner's extra units leaves the table: it is a copy, so it is cleared away, never put in a pile or a hand. */
  private clearSpanUnit(card: FieldCard) {
    this.clearTokensOn(card);
    this.removeField(card.id);
    this.closeUp(card);
    this.log(`One of ${this.cardName(card.num)}’s extra units is cleared away.`, card.owner);
    return null;
  }
  /** The Splash token a dropped token has landed on top of, if any (a die, a bar token or a Splash itself never splash). */
  private splashUnder(t: Token): Token | undefined {
    const effect = this.effectOf(t);
    const def = this.tokenDb.byId.get(t.tokenId);
    if (effect === CONFIG.tokenRules.splashToken || this.isDie(t) || def?.bar) return undefined;
    const reach = CONFIG.token.size * 0.75;
    return this.state.tokens.find((o) => o !== t && this.effectOf(o) === CONFIG.tokenRules.splashToken && Math.hypot(o.x - t.x, o.z - t.z) <= reach);
  }
  /**
   * A token dropped on a Splash token: it hits every unit in the Splash token's lane. Damage is dealt to each
   * (and shared across their links); anything else is put on each, as if dropped there. The dropped token is
   * used up. A unit with a Count has every one of its units hit: splash damage of at least one unit's health
   * fells them all. Returns why it can't be done, if it can't.
   */
  private splashOnto(seat: Seat, t: Token, splash: Token): string | null {
    const s = this.state;
    const at = slotAt(splash);
    if (!at || slotKind(at.slot.name) !== 'Lane') return 'A Splash token works only in a lane: put it in one first.';
    const units = s.field.filter((c) => {
      const h = slotAt(c);
      return !!h && h.slot === at.slot && h.side === at.side && (!c.faceUp || !!this.cards.byNumber.get(c.num)?.types.some((ty) => ty === 'troop' || ty === 'building'));
    });
    const def = this.tokenDb.byId.get(t.tokenId);
    if (!units.length) return `There are no units in ${at.slot.name} for the Splash to hit.`;
    const damage = CONFIG.tokenRules.damageTokens.includes(this.effectOf(t));
    this.removeTokens([t]);
    const name = (c: FieldCard) => (c.faceUp ? this.cardName(c.num) : 'a face-down card');
    for (const u of units) {
      if (damage) {
        const uDef = u.faceUp ? this.cards.byNumber.get(u.num) : undefined;
        const each = uDef ? this.fullForm(u, uDef).health : null;
        const count = this.tokensOn(u).find((o) => this.effectOf(o) === CONFIG.tokenRules.countToken);
        // every one of a Count's units takes the hit: enough to fell one fells them all
        if (count && count.value > 1 && each && t.value >= each + this.gainedHealth(this.tokensOn(u))) count.value = 1;
        this.addDamage(u, t.tokenId, t.value, seat);
        this.mirrorDamage(u, t.tokenId, t.value);
      } else {
        const side = sideOf(u.owner);
        const copy: Token = { id: this.newId('t'), tokenId: t.tokenId, ...this.near(u, 1.5 * side, -1.8 * side), value: t.value, layer: s.nextLayer++, by: seat, since: s.players[seat].turns };
        if (t.turns) Object.assign(copy, { turns: t.turns });
        if (t.drain) Object.assign(copy, { drain: t.drain });
        s.tokens.push(copy);
        const refused = this.landToken(seat, copy);
        if (refused) {
          this.removeTokens([copy]);
          this.log(`Splash: ${def?.name ?? 'the token'} can’t go on ${name(u)}: ${refused}`, seat);
          continue;
        }
        if (s.tokens.includes(copy)) this.mirrorStatus(seat, copy, undefined);
        this.fx('status', u.id, undefined, { effect: 'notice' });
      }
    }
    const what = def?.counter ? `${def.name} ${t.value}` : (def?.name ?? 'a token');
    this.log(`Splash in ${at.slot.name}: ${what} on every unit there (${units.map(name).join(', ')}).`, seat);
    return null;
  }
  /** The health a card has gained: the Health counters on it, added up (CONFIG.tokenRules.healthToken). */
  private gainedHealth(on: Token[]) {
    return on.filter((t) => this.effectOf(t) === CONFIG.tokenRules.healthToken).reduce((n, t) => n + t.value, 0);
  }
  /** Is a status token on hold because its card is Frozen? (Frozen itself keeps counting.) */
  private paused(t: Token, card: FieldCard) {
    if (!CONFIG.turn.frozenPauses || this.effectOf(t) === 'frozen') return false;
    return this.tokensOn(card).some((o) => this.effectOf(o) === 'frozen');
  }
  /** What status tokens do by themselves on the turn player's cards at one moment of the turn (CONFIG.turn.statusActs). */
  private statusActs(seat: Seat, when: 'start' | 'summon' | 'attack' | 'end') {
    const s = this.state; const p = s.players[seat];
    const notes: string[] = [];
    const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
    const ended: string[] = [];
    for (const act of CONFIG.turn.statusActs.filter((x) => x.when === when || x.choose?.includes(when as 'start' | 'end'))) {
      for (const t of [...s.tokens]) {
        if (!s.tokens.includes(t) || this.effectOf(t) !== act.effect) continue;
        if ((act.choose ? (t.actAt ?? act.when) : act.when) !== when) continue; // its player picked another moment
        const def = this.tokenDb.byId.get(t.tokenId)!;
        const card = this.cardUnder(t);
        if (!card || this.turnOf(card.owner) !== seat || this.paused(t, card)) continue;
        if (act.clocked && t.turns && CONFIG.turn.durations.startsNextTurn && t.turnsSince === p.turns) continue;
        const where = card.faceUp ? this.cardName(card.num) : 'a face-down card';
        let amount = act.amount === 'value' ? t.value : (act.amount ?? 1);
        // a Biome that makes Burn hit harder while it is active ("always: burn_damage 2": Dense Woodland)
        if (act.effect === 'burn' && CONFIG.combat.enabled) for (const c of s.field) if (c.faceUp && !this.isExpired(c) && !this.biomeOffFor(this.sideOfCard(card))) for (const e of this.cards.byNumber.get(c.num)?.effects.length ? this.effectsOf(c, 'always') : []) if (e.words[0] === 'burn_damage') amount = Math.max(amount, Number(e.words[1]) || amount);
        if (act.does === 'damage' && damageId && amount > 0) {
          this.addDamage(card, damageId, amount, null, false, true);
          // a status that can be cured by a die: its card's owner now owes that roll (a copy across the
          // Emperor–Minion link shares one roll with its twin)
          const twinAsks = !!t.twin && s.tokens.some((o) => o.id === t.twin && o.cure !== undefined);
          if (act.cure?.length && !twinAsks) t.cure = seat;
          const told = t.cure !== undefined ? `${def.name} dealt ${amount} damage to ${where}. Roll to cure it: ${cureWords(act.cure!)} cures it.` : act.then ? `${def.name} dealt ${amount} damage to ${where}: now ${act.then}.` : undefined;
          // (a roll that is owed is asked for on the table itself, so it needs no passing notice as well)
          this.fx('status', card.id, undefined, { token: t.id, effect: 'damage', ...(told && t.cure === undefined ? { text: told, seat } : {}) });
          if (told) this.log(told, seat);
          // status damage goes straight to the card's health: now below full health, it can't keep Overheal
          let ended = '';
          if (CONFIG.turn.statusDamageEndsOverheal) {
            const extra = this.tokensOn(card).filter((o) => this.effectOf(o) === 'overheal');
            if (extra.length) {
              this.removeTokens(extra);
              ended = ', which ends its Overheal';
            }
          }
          let after = '';
          if (act.thenDrop) {
            t.value = Math.max(0, t.value - act.thenDrop);
            if (t.value <= 0) this.removeTokens([t]);
            after = t.value > 0 ? ` (${def.name} is now ${t.value})` : ` (${def.name} ends)`;
          }
          notes.push(`${def.name} deals ${amount} damage to ${where}${ended}${after}`);
        } else if (act.does === 'heal') {
          let left = amount;
          for (const d of this.tokensOn(card).filter((o) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(o)))) {
            const take = Math.min(left, d.value);
            d.value -= take; left -= take;
            if (d.value <= 0) this.removeTokens([d]);
          }
          if (left < amount) {
            this.fx('status', card.id, undefined, { token: t.id, effect: 'heal' });
            notes.push(`${def.name} heals ${amount - left} damage on ${where}`);
          }
        } else if (act.does === 'notice') {
          const text = (act.text ?? `${def.name} on {card}.`).replace('{card}', where);
          this.fx('status', card.id, undefined, { token: t.id, effect: 'notice', text, seat });
          this.log(text, seat);
        }
        // its turn clock goes down as it acts (Regeneration 2 for 3 turns: 3 heals, then it is gone)
        if (act.clocked && t.turns && s.tokens.includes(t)) {
          t.turns -= 1;
          if (t.turns <= 0) {
            this.removeTokens([t]);
            this.fx('status', card.id, undefined, { effect: 'notice', text: `${def.name} has ended on ${where}.` });
            ended.push(`${def.name} ended on ${where}`);
          } else ended.push(`${def.name} on ${where}: ${t.turns} turn${t.turns === 1 ? '' : 's'} left`);
        }
      }
    }
    if (notes.length) this.log(`${p.name}’s status effects: ${notes.join('; ')}.`, seat);
    if (ended.length) this.log(`${p.name}’s turn clocks: ${ended.join('; ')}.`, seat);
  }
  /**
   * A card is about to leave the table: how many of its units its destroyed_gains cell pays for. 0 unless it is
   * face-up with a Destroyed token on it. An "each" card pays for the units still on its Count (the ones that
   * fell earlier were paid for as they fell); any other card pays once.
   */
  private dueOnLeaving(card: FieldCard): number {
    const G = this.cards.byNumber.get(card.num)?.destroyedGains;
    const on = this.tokensOn(card);
    if (!G || !card.faceUp || !on.some((t) => this.effectOf(t) === CONFIG.turn.lifetime.expiredToken)) return 0;
    if (!G.each) return 1;
    const count = on.find((t) => this.effectOf(t) === CONFIG.tokenRules.countToken);
    return count ? count.value : 1;
  }
  /** Pays a card's destroyed_gains cell for this many of its units (Gerzite Farmer: its owner gains 1 Gerzite). */
  private destroyedGain(card: FieldCard, units: number) {
    const def = this.cards.byNumber.get(card.num); const G = def?.destroyedGains; const s = this.state;
    const bar = G ? this.tokenDb.byId.get(G.token)?.bar : undefined;
    if (!def || !G || !bar || units <= 0) return;
    const to = (G.opponent ? s.players.findIndex((pl, i) => !pl.out && this.opposed(card.owner, i as Seat)) : card.owner) as Seat;
    if (to < 0) return;
    const want = G.double ? this.barTokens(to, bar as 'gerzite' | 'dark').length : G.amount * units;
    const tokenName = this.tokenDb.byId.get(G.token)!.name;
    const what = G.each ? `A unit of ${def.name}` : def.name;
    if (G.double && want === 0) this.log(`${def.name} was destroyed: ${s.players[to].name} has no ${tokenName} to double.`, to);
    else {
      const got = this.gain(to, G.token, want);
      this.log(`${what} was destroyed: ${s.players[to].name} ${G.double ? `doubles their ${tokenName} and ` : ''}gains ${this.gainText(G.token, want, got)}.`, to);
      // (it flies from the card to the bar: Gerzite Crystallites)
      if (got > 0 && bar === 'gerzite' && CONFIG.looks.enabled && CONFIG.looks.destroyedGainFly) this.look(CONFIG.looks.destroyedGainFly, null, { seat: to, at: { x: card.x, z: card.z }, count: got });
    }
  }
  /**
   * A card was just tapped: one whose sheet row says while_in_play = tap_harvest (Gerzite Farmer's Tap effect) gains Harvest,
   * once a turn (CONFIG.harvest). Tapping it again in the same turn, after untapping it, gains nothing more.
   */
  private harvest(card: FieldCard) {
    const H = CONFIG.harvest; const s = this.state;
    const tokenDef = H.token ? this.tokenDb.byId.get(H.token) : undefined;
    if (!tokenDef || !card.faceUp || !this.cards.byNumber.get(card.num)?.whileInPlay.includes('tap_harvest')) return;
    const turn = `${s.turn}:${s.players[s.turn].turns}`;
    if (card.harvested === turn) return;
    // a Tap effect: like a Passive, it can't be used while the card is Entrenched, Comatose, Frozen, Locked or Stunned
    const stopped = this.passiveStopper(card);
    if (stopped) {
      this.log(`${this.cardName(card.num)} gains no ${tokenDef.name}: its Tap effect can’t be used while it is ${stopped}.`, card.owner);
      return;
    }
    card.harvested = turn;
    const have = this.tokensOn(card).find((t) => t.tokenId === tokenDef.id);
    if (have) have.value = Math.min(999, have.value + H.perTap);
    else {
      const side = sideOf(card.owner);
      s.tokens.push({ id: this.newId('t'), tokenId: tokenDef.id, ...this.near(card, -1.2 * side, 1.6 * side), value: H.perTap, layer: s.nextLayer++, by: card.owner });
    }
    const now = have ? have.value : H.perTap;
    const text = `${this.cardName(card.num)} gained ${H.perTap} ${tokenDef.name}: now ${now}.`;
    this.fx('status', card.id, undefined, { effect: 'notice', text, seat: card.owner });
    this.log(text, card.owner);
  }
  /** Can this token carry a turn clock? (a counter that holds an amount: CONFIG.turn.durations.effects) */
  private canClock(t: Token) {
    const def = this.tokenDb.byId.get(t.tokenId);
    if (t.asCounter && def?.bar) return CONFIG.turn.durations.enabled; // a Gerzite counter on the table
    return CONFIG.turn.durations.enabled && !!def?.counter && CONFIG.turn.durations.effects.includes(def.effect);
  }
  /** Puts a turn clock on a counter, changes it, or (0) takes it off. A copy across the Emperor–Minion link follows. */
  private setTurns(t: Token, turns: number) {
    const n = Math.max(0, Math.min(CONFIG.turn.durations.max, Math.round(turns) || 0));
    const owner = this.cardUnder(t)?.owner ?? t.by;
    for (const k of [t, t.twin ? this.state.tokens.find((o) => o.id === t.twin) : undefined]) {
      if (!k) continue;
      if (n > 0) {
        k.turns = n;
        k.turnsSince = owner !== null && this.state.turn === owner ? this.state.players[owner].turns : undefined;
      } else {
        delete k.turns;
        delete k.turnsSince;
      }
    }
  }
  /**
   * Sets how much of a counter's number wears off, 1 a turn (never more than the number itself), or (0) takes
   * the wear-off timer away. A copy across the Emperor–Minion link follows.
   */
  private setDrain(t: Token, drain: number) {
    const n = Math.max(0, Math.min(t.value, CONFIG.turn.durations.max, Math.round(drain) || 0));
    const owner = this.cardUnder(t)?.owner ?? t.by;
    for (const k of [t, t.twin ? this.state.tokens.find((o) => o.id === t.twin) : undefined]) {
      if (!k) continue;
      if (n > 0) {
        k.drain = n;
        k.drainSince = owner !== null && this.state.turn === owner ? this.state.players[owner].turns : undefined;
      } else {
        delete k.drain;
        delete k.drainSince;
      }
    }
  }
  /**
   * Turn clocks on the player's own cards go down by 1 (CONFIG.turn.durations); at 0 their counter is removed.
   * At `drainWhen` a wear-off timer takes 1 off its counter's number (and off itself), and goes away once the
   * temporary part is gone; a counter worn down to 0 is removed.
   * A counter with a clock that isn't on a card follows the turns of whoever put it down.
   */
  private tickClocks(seat: Seat, when: 'start' | 'summon' | 'attack' | 'end') {
    const rules = CONFIG.turn.durations;
    if (!rules.enabled || (rules.when !== when && rules.drainWhen !== when)) return;
    const s = this.state; const p = s.players[seat];
    const notes: string[] = [];
    for (const t of rules.drainWhen === when ? [...s.tokens] : []) {
      if (!t.drain || !s.tokens.includes(t)) continue;
      const def = this.tokenDb.byId.get(t.tokenId);
      const card = this.cardUnder(t);
      if (this.turnOf(card ? card.owner : t.by) !== seat) continue;
      if (card && this.paused(t, card)) continue;
      if (rules.startsNextTurn && t.drainSince === p.turns) continue; // set this very turn: it starts wearing off next turn
      const where = !card ? '' : card.faceUp ? this.cardName(card.num) : 'a face-down card';
      const on = where ? ` on ${where}` : '';
      const name = def?.name ?? 'Counter';
      t.value = Math.max(0, t.value - 1);
      t.drain -= 1;
      if (t.drain <= 0) {
        delete t.drain;
        delete t.drainSince;
      }
      if (t.value <= 0) {
        this.removeTokens([t]);
        if (card) this.fx('status', card.id, undefined, { effect: 'notice', text: `${name} has worn off${on}.` });
        notes.push(`${name} wore off${on}`);
      } else {
        notes.push(`${name}${on} wore down to ${t.value}${t.drain ? ` (${t.drain} more to wear off)` : ''}`);
      }
    }
    for (const t of rules.when === when ? [...s.tokens] : []) {
      if (!t.turns || !s.tokens.includes(t)) continue;
      const def = this.tokenDb.byId.get(t.tokenId);
      if (CONFIG.turn.statusActs.some((x) => x.clocked && x.effect === def?.effect)) continue; // its clock goes down as it acts
      if (t.asCounter && t.flow && def?.bar) continue; // a Gerzite counter that adds or removes each turn: its clock goes down as it does (gerziteFlows)
      const card = this.cardUnder(t);
      if (this.turnOf(card ? card.owner : t.by) !== seat) continue;
      if (card && this.paused(t, card)) continue;
      if (rules.startsNextTurn && t.turnsSince === p.turns) continue; // set this very turn: it starts counting next turn
      const where = !card ? '' : card.faceUp ? this.cardName(card.num) : 'a face-down card';
      const on = where ? ` on ${where}` : '';
      const name = `${def?.name ?? 'Counter'}${def?.counter ? ` ${t.value}` : ''}`;
      t.turns -= 1;
      if (t.turns > 0) notes.push(`${name}${on}: ${t.turns} turn${t.turns === 1 ? '' : 's'} left`);
      else {
        this.removeTokens([t]);
        if (card) this.fx('status', card.id, undefined, { effect: 'notice', text: `${name} has ended${on}.` });
        notes.push(`${name} ended${on}`);
      }
    }
    if (notes.length) this.log(`${p.name}’s turn clocks: ${notes.join('; ')}.`, seat);
  }
  /**
   * A spot just off a card where a die can land without sitting on any card: in front of it (its owner's
   * side) if that is free, else behind it, else to its right or left.
   */
  private besideCard(card: FieldCard) {
    const side = sideOf(card.owner);
    const turned = card.rot % 2 === 1;
    const reach = CONFIG.dice.size / 2 + CONFIG.turn.cureDie.gap + 0.3;
    const halfW = (turned ? CONFIG.card.height : CONFIG.card.width) / 2 + reach;
    const halfD = (turned ? CONFIG.card.width : CONFIG.card.height) / 2 + reach;
    const spots = [
      { x: card.x, z: card.z + side * halfD },
      { x: card.x, z: card.z - side * halfD },
      { x: card.x + side * halfW, z: card.z },
      { x: card.x - side * halfW, z: card.z },
    ].map((pt) => this.clampToTable(pt.x, pt.z));
    return spots.find((pt) => !this.cardUnder(pt)) ?? spots[0];
  }
  /** The status tokens this player still owes a cure roll for (Burn, after it has dealt its damage). */
  private curesOwed(seat: Seat) {
    return this.state.tokens.filter((t) => t.cure === seat && !!this.cardUnder(t));
  }
  /** Buildings shed all their Overheal as their owner's turn begins. */
  private shedOverheal(seat: Seat) {
    if (!CONFIG.turn.buildingsShedOverheal) return;
    for (const c of this.state.field) {
      if (this.turnOf(c.owner) !== seat || !this.cards.byNumber.get(c.num)?.types.includes('building')) continue;
      const extra = this.tokensOn(c).filter((t) => this.effectOf(t) === 'overheal');
      if (!extra.length || extra.every((t) => this.paused(t, c))) continue;
      this.removeTokens(extra);
      this.log(`Overheal is removed from ${c.faceUp ? this.cardName(c.num) : 'a face-down card'}: Buildings lose it as their owner’s turn begins.`, seat);
    }
  }
  /** A player's Emperor, wherever it is on the table. */
  private emperorOf(seat: Seat): FieldCard | undefined {
    return this.state.field.find((c) => c.owner === seat && !c.spawned && !!this.cards.byNumber.get(c.num)?.types.includes('emperor'));
  }
  /** Deck-Out: something flies off the empty deck and into its Emperor (the client draws it; the damage shows as it lands). */
  private deckOutLook(seat: Seat) {
    const emperor = this.emperorOf(seat);
    if (emperor) this.fx('look', emperor.id, undefined, { effect: 'deck-out', seat, pile: 'deck' });
  }
  /** Damage dealt to an Emperor by the table itself (Deck-Out, Overtime). */
  private damageEmperor(seat: Seat, amount: number) {
    const emperor = this.emperorOf(seat);
    const tokenId = this.tokenDb.list.find((t) => CONFIG.tokenRules.damageTokens.includes(t.effect))?.id;
    if (!emperor || !tokenId || amount <= 0) return false;
    this.addDamage(emperor, tokenId, amount, null, false, true);
    this.mirrorDamage(emperor, tokenId, amount);
    return true;
  }
  /**
   * A face-up card whose health (or SP) has just reached 0 is destroyed: a Destroyed skull goes on it. Once
   * only, as it reaches 0: healing it later changes nothing, and a skull taken off by hand stays off (the
   * players settle any disagreement themselves). Nothing is discarded for anyone.
   */
  private markAgain = false; // a card was destroyed in another's place (Grave Intercessor): look again once this pass is done
  private markDestroyed(depth = 0) {
    const s = this.state;
    const skull = this.tokenDb.list.find((d) => d.effect === CONFIG.turn.lifetime.expiredToken);
    if (!CONFIG.tokenRules.destroyAtZero || !skull) return;
    for (const card of s.field) {
      if (!card.faceUp) continue;
      const def = this.cards.byNumber.get(card.num);
      const full = def ? this.fullForm(card, def).health : null;
      if (!def || !full) continue;
      const on = this.tokensOn(card);
      const overheal = on.filter((t) => this.effectOf(t) === 'overheal').reduce((n, t) => n + t.value, 0);
      const left = Math.max(0, full + this.gainedHealth(on) - this.damageOn(card)) + overheal;
      if (left > 0) {
        card.atZero = false;
        continue;
      }
      // a card that is several units (a Count: Wraiths ×4): its health is each unit's. One unit falls, the
      // Count goes down by 1 and the damage that felled it is cleared; the card goes only with its last unit.
      // (One attack is aimed at one unit, so damage beyond that unit's health is lost.)
      const count = on.find((t) => this.effectOf(t) === CONFIG.tokenRules.countToken);
      if (count && count.value > 1) {
        // (damage aimed at one unit goes with it; splash, which every unit took, stays: if that alone is enough, the next falls too)
        let fell = 0;
        const leftNow = () => { const now = this.tokensOn(card); return full + this.gainedHealth(now) - this.damageOn(card) + now.filter((t) => this.effectOf(t) === 'overheal').reduce((n, t) => n + t.value, 0); };
        do {
          count.value -= 1;
          fell++;
          this.removeTokens(this.tokensOn(card).filter((t) => CONFIG.tokenRules.damageTokens.includes(this.effectOf(t)) && !t.all));
          if (def.destroyedGains?.each) this.destroyedGain(card, 1);
          this.summonUnits(card, 'destroyed'); // what each unit leaves behind (Gerzite Clusters: two Gerzite Crystallites)
        } while (count.value > 1 && leftNow() <= 0);
        this.fx('status', card.id, undefined, { effect: 'notice' });
        this.log(fell === 1 ? `One of ${def.name} falls: ${count.value} left.` : `${fell} of ${def.name} fall: ${count.value} left.`, card.owner);
        this.fire(card, 'unit_lost'); // (Flamebound Synergy)
        if (leftNow() > 0) {
          card.atZero = false;
          continue;
        }
      }
      if (!card.atZero && this.cheatDeath(card, def)) { card.atZero = false; continue; } // (Melstone Terror becomes a Troop; Demonic Contract)
      const lastUnits = count?.value ?? 0;
      if (count) count.value = 0;
      if (def.destroyedGains?.each) this.destroyedGain(card, lastUnits); // its last unit has fallen (nothing the second time: the Count is already 0)
      if (card.atZero) continue; // already dealt with when it got here
      card.atZero = true;
      if (on.some((t) => t.tokenId === skull.id)) continue; // it was destroyed before (healed since, perhaps): nothing new
      s.tokens.push({ id: this.newId('t'), tokenId: skull.id, x: card.x, z: card.z, value: skull.start, layer: s.nextLayer++, by: null });
      this.laneMark(card, 'destroyed'); // Ash Beetle
      const what = def.types.includes('building') ? 'SP' : 'health';
      this.fx('status', card.id, undefined, { effect: 'notice' });
      this.log(`${def.name} is destroyed: it has no ${what} left.`, card.owner);
      this.summonUnits(card, 'destroyed'); // what it leaves behind (Battering Ram: two Skell-Minis)
      this.whenDestroyed(card); // its own "destroyed" effects, and the cards that watch for it
    }
    if (this.markAgain && depth < 3) { this.markAgain = false; this.markDestroyed(depth + 1); }
  }
  /** Is this a token that can belong to an Emperor ability? A Locked token locks one; a turn timer is one's cooldown. */
  private abilityTokenKind(t: Token): 'lock' | 'cooldown' | null {
    const effect = this.effectOf(t);
    if (effect === CONFIG.tokenRules.lockToken) return 'lock';
    return CONFIG.turn.timers.includes(effect) ? 'cooldown' : null;
  }
  /** The token of this kind that belongs to one ability of an Emperor, if there is one. */
  private abilityToken(card: FieldCard, slot: number, kind: 'lock' | 'cooldown'): Token | undefined {
    return this.state.tokens.find((t) => t.ability?.card === card.id && t.ability.slot === slot && this.abilityTokenKind(t) === kind && (kind === 'lock' || t.value > 0));
  }
  /** Everything a card has lost: the damage sitting on it, plus (on a Building) the SP it has lost to decay. */
  private damageOn(card: FieldCard) {
    return this.tokensOn(card).filter((t) => countsAgainstHealth(this.effectOf(t))).reduce((n, t) => n + t.value, 0);
  }
  /**
   * SP decay: as a player ends their turn, each of their face-up Buildings loses SP. It goes on the blue SP
   * Decay counter, apart from damage. Only Entrenched holds it off; Crumble makes it 3 instead of 2.
   */
  private spDecay(seat: Seat) {
    const s = this.state;
    const D = CONFIG.turn.spDecay;
    const counter = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.spDecayToken);
    if (!D.enabled || D.amount <= 0 || !counter) return;
    const notes: string[] = [];
    for (const card of [...s.field]) {
      if (this.turnOf(card.owner) !== seat || !card.faceUp) continue;
      const def = this.cards.byNumber.get(card.num);
      if (!def?.types.includes('building') || this.untouchable(card) || card.troopForm) continue;
      if (this.effectsOf(card, 'always').some((e) => e.words[0] === 'no_decay')) continue; // Mysterious Artifact
      const on = this.tokensOn(card);
      const effects = on.map((t) => this.effectOf(t));
      if (effects.some((e) => D.stoppedBy.includes(e))) continue;
      const worse = effects.filter((e) => D.more[e] !== undefined);
      let amount = worse.length ? Math.max(...worse.map((e) => D.more[e])) : D.amount;
      // it can't lose more SP than it has left
      const full = this.fullForm(card, def).health;
      if (full) amount = Math.min(amount, Math.max(0, full - this.damageOn(card)));
      if (amount <= 0) continue;
      const have = on.find((t) => t.tokenId === counter.id);
      if (have) have.value = Math.min(999, have.value + amount);
      else {
        const side = sideOf(card.owner);
        // bottom-left of the card, beside the damage counter (bottom-right)
        s.tokens.push({ id: this.newId('t'), tokenId: counter.id, ...this.near(card, -1.2 * side, 1.6 * side), value: amount, layer: s.nextLayer++, by: null });
      }
      this.fx('decay', card.id, undefined, { ref: amount });
      const why = worse.length ? ` (${this.tokenDb.list.find((d) => d.effect === worse[0])?.name ?? worse[0]})` : '';
      notes.push(`${def.name} loses ${amount} SP${why}`);
    }
    if (notes.length) this.log(`${s.players[seat].name}’s Buildings decay: ${notes.join('; ')}.`, seat);
  }
  /** A card was just put down at `now`: if that is inside a slot it wasn't already in, play the slot's effect. */
  private enterSlot(cardId: string, before: { x: number; z: number } | null, now: { x: number; z: number }) {
    const at = slotAt(now);
    const was = before ? slotAt(before) : null;
    if (!at?.slot.effect || (was && was.slot === at.slot && was.side === at.side)) return;
    this.fx('slot', cardId, undefined, { effect: at.slot.effect });
  }
  /**
   * A card just left the spot `from` for good (discarded, picked up, deleted). If that emptied a slot
   * in a row that closes up (the Spell slots), the cards in the later slots move up to fill the gap.
   */
  private closeUp(from: { x: number; z: number }) {
    const at = slotAt(from);
    if (!at?.slot.row) return;
    const row = CONFIG.slots.filter((sl) => sl.row === at.slot.row);
    const inSlot = (sl: Slot) => this.state.field.filter((c) => { const h = slotAt(c); return !!h && h.slot === sl && h.side === at.side; });
    const start = row.indexOf(at.slot);
    if (inSlot(row[start]).length) return; // something is still there: nothing to close
    let free = start; // the next slot to fill
    for (let i = start + 1; i < row.length; i++) {
      const movers = inSlot(row[i]);
      if (!movers.length) continue;
      const dx = (row[free].x - row[i].x) * at.side, dz = (row[free].z - row[i].z) * at.side;
      const riders = new Set(movers.flatMap((c) => this.tokensOn(c)));
      for (const c of movers) { c.x += dx; c.z += dz; }
      for (const t of riders) { t.x += dx; t.z += dz; }
      free++;
    }
  }
  private tokensOn(card: FieldCard): Token[] {
    // the tokens on a summoned unit attached to its bottom edge are that unit's, not this card's
    const kids = this.state.field.filter((c) => c.attachedTo === card.id);
    return this.state.tokens.filter((t) => this.isOn(t, card) && !kids.some((k) => this.isOn(t, k)));
  }
  /**
   * The tokens that belong to a card in the middle of a move, while cards may still lie on top of each other: a token
   * that was on a card before the move stays that card's while it is still on it; a new one is the top-most card's.
   * (The same rule `layout` uses.)
   */
  private tokensOwned(card: FieldCard, before: Map<string, string>): Token[] {
    return this.tokensOn(card).filter((t) => {
      const was = before.get(t.id);
      const wasCard = was ? this.findField(was) : undefined;
      const owner = wasCard && this.isOn(t, wasCard) ? wasCard : this.cardUnder(t);
      return !owner || owner.id === card.id;
    });
  }
  /** The top-most card a token is sitting on, if any. */
  private cardUnder(pt: { x: number; z: number }): FieldCard | undefined {
    // (an untouchable card, a Calling Pillar, carries nothing: a token over it and another card belongs to the other card)
    let best: FieldCard | undefined;
    let pillar: FieldCard | undefined;
    for (const c of this.state.field) {
      if (!this.isOn(pt, c)) continue;
      if (this.untouchable(c)) { if (!pillar || c.layer > pillar.layer) pillar = c; }
      else if (!best || c.layer > best.layer) best = c;
    }
    return best ?? pillar;
  }
  private removeTokens(tokens: Token[]) {
    if (!tokens.length) return;
    const gone = new Set(tokens.map((t) => t.id));
    this.state.tokens = this.state.tokens.filter((t) => !gone.has(t.id));
  }
  /**
   * Ambush lasts for the turn it arrives on. When a turn ends, every Ambush token still on the table
   * goes: on a card that was summoned or had one put on it this turn and never used it, or lying loose.
   */
  private endAmbush() {
    if (!CONFIG.tokenRules.ambushEndsWithTurn) return;
    const left = this.state.tokens.filter((t) => this.effectOf(t) === CONFIG.tokenRules.ambushToken);
    if (!left.length) return;
    const on = this.state.field.filter((c) => this.tokensOn(c).some((t) => left.includes(t)));
    this.removeTokens(left);
    const names = on.map((c) => (c.faceUp ? this.cardName(c.num) : 'a face-down card'));
    if (names.length) this.log(`Ambush ended with the turn on ${names.join(', ')}.`, null);
  }
  /** When a card leaves the table, the counters and status tokens on it go with it (resources stay). */
  private clearTokensOn(card: FieldCard) {
    this.removeTierToken(card);
    // resources and dice aren't attached to the card, so they stay on the table
    this.removeTokens(this.tokensOn(card).filter((t) => !['resource', 'dice'].includes(this.tokenDb.byId.get(t.tokenId)?.category ?? '')));
  }
  /** Why a card can't be tapped or flipped right now (Frozen or Locked), or null. */
  private lockedBy(card: FieldCard): string | null {
    // a Lock on an Emperor locks one of its abilities, not the card itself
    const abilityLocks = CONFIG.emperorAbilities.enabled && card.faceUp && !!this.cards.byNumber.get(card.num)?.emperorAbilities.length;
    const lock = this.tokensOn(card).find((t) => this.effectOf(t) === 'frozen' || (this.effectOf(t) === 'locked' && !abilityLocks));
    if (!lock) return null;
    const def = this.tokenDb.byId.get(lock.tokenId)!;
    const what = card.faceUp ? this.cardName(card.num) : 'That card';
    if (def.effect === 'frozen') return `${what} is frozen — take the ${def.name} token off it first.`;
    // (in a game a Lock is paid off, from the card's own menu or the Lock's; with no cost, only a card takes it off)
    if (CONFIG.lockHandEdits && this.state.mode !== 'solo') return lock.price ? `${what} is locked — pay ${lock.price} ${lock.priceDark ? 'Dark Gerzite' : 'Gerzite'} to unlock it first (click it).` : `${what} is locked — it stays locked until a card effect takes the Lock off.`;
    return `${what} is locked — take the ${def.name} token off it first.`;
  }
  /** Puts a resource token in the next free slot of a player's bar. A full bar takes no more: returns false. */
  private placeBarToken(seat: Seat, def: { id: string; start: number; bar: '' | 'gerzite' | 'dark' }): boolean {
    const s = this.state;
    let pos = this.spawnPoint(seat);
    if (def.bar) {
      const { slots, spacing } = barSlots(seat, def.bar);
      const free = slots.find((sl) => !s.tokens.some((t) => Math.hypot(t.x - sl.x, t.z - sl.z) < spacing * 0.6));
      if (!free) return false;
      pos = free;
    }
    s.tokens.push({ id: this.newId('t'), tokenId: def.id, ...pos, value: def.start, layer: s.nextLayer++, by: seat, since: s.players[seat]?.turns ?? 0 });
    return true;
  }
  /**
   * A Gerzite or Dark Gerzite token was dropped. On a bar of its kind it snaps into the free slot nearest where
   * it was dropped and is a resource again (a counter of 3 puts 3 there, as many as fit). Anywhere else on the
   * table it is a counter. Returns a refusal, null when it is settled, or undefined to carry on as any token.
   */
  private landResource(seat: Seat, t: Token, def: { id: string; name: string; start: number; bar: 'gerzite' | 'dark' }): string | null | undefined {
    const s = this.state;
    const seats = [...new Set(s.players.map((_, i) => teamOf(i as Seat)))];
    for (const owner of seats) {
      const { slots, spacing } = barSlots(owner, def.bar);
      const near = Math.min(...slots.map((sl) => Math.hypot(sl.x - t.x, sl.z - t.z)));
      if (near > spacing * 0.9) continue;
      const taken = (sl: { x: number; z: number }) => s.tokens.some((o) => o.id !== t.id && Math.hypot(o.x - sl.x, o.z - sl.z) < spacing * 0.6);
      const free = slots.filter((sl) => !taken(sl)).sort((a, b) => Math.hypot(a.x - t.x, a.z - t.z) - Math.hypot(b.x - t.x, b.z - t.z));
      if (!free.length) return `That ${def.name} bar is full.`;
      const want = t.asCounter ? Math.max(1, t.value) : 1;
      Object.assign(t, free[0], { value: def.start, layer: s.nextLayer++, by: owner });
      delete t.asCounter;
      delete t.flow;
      delete t.turns;
      delete t.turnsSince;
      delete t.drain;
      delete t.drainSince;
      let got = 1;
      for (let k = 1; k < want; k++) if (this.placeBarToken(owner, def)) got++;
      if (got < want) this.barFull(owner, def, want, got);
      if (def.bar === 'dark') this.darkGained(owner);
      if (want > 1) this.log(`${s.players[seat].name} put ${this.gainText(def.id, want, got)} on ${owner === teamOf(seat) ? 'their' : `${s.players[owner].name}’s`} bar.`, seat);
      return null;
    }
    // off the bars: a counter (it keeps its number if it already was one)
    if (!t.asCounter) {
      t.asCounter = true;
      t.value = 1;
    }
    return undefined;
  }
  /**
   * Gerzite counters on the table set to add to or take from their owner's bar as a turn begins or ends: they
   * do it now. A counter on a Frozen card waits, as other counters' clocks do.
   * A turn clock on such a counter counts how many more times it does it: the clock goes down right after
   * each time (not at the usual moment for clocks), so a clock of 3 always means 3 times, and the counter
   * is taken off after the last one.
   */
  private gerziteFlows(seat: Seat, when: 'start' | 'end') {
    const s = this.state;
    for (const t of [...s.tokens]) {
      if (!t.asCounter || !t.flow || t.flow.when !== when || !s.tokens.includes(t)) continue;
      const card = this.cardUnder(t);
      const owner = card ? card.owner : t.by;
      if (this.turnOf(owner) !== seat) continue;
      if (card && this.paused(t, card)) continue;
      const def = this.tokenDb.byId.get(t.tokenId);
      if (!def?.bar) continue;
      const n = Math.max(1, t.value);
      const from = card?.faceUp ? ` (${this.cardName(card.num)})` : '';
      const moment = when === 'start' ? 'as their turn begins' : 'as their turn ends';
      if (t.flow.mode === 'add') {
        const got = this.gain(seat, def.id, n);
        this.log(`${s.players[seat].name} gained ${this.gainText(def.id, n, got)} from a ${def.name} counter${from} ${moment}.`, seat);
      } else {
        const mine = this.barTokens(seat, def.bar);
        const lose = mine.slice(-n);
        this.removeTokens(lose);
        this.log(`${s.players[seat].name} lost ${lose.length} ${def.name} to a ${def.name} counter${from} ${moment}${lose.length < n ? ` (they had only ${lose.length})` : ''}.`, seat);
      }
      if (t.turns) {
        t.turns -= 1;
        if (t.turns <= 0) {
          this.removeTokens([t]);
          if (card) this.fx('status', card.id, undefined, { effect: 'notice', text: `The ${def.name} counter has ended.` });
          this.log(`${s.players[seat].name}’s turn clocks: the ${def.name} counter${from} ended.`, seat);
        }
      }
    }
  }
  /** Magical Coin's flip, made with a die: even = heads (the caster gains), odd = tails (the opponent gains). Then it is discarded. */
  private coinFlip(seat: Seat, card: FieldCard, rolled: number): null {
    const s = this.state; const p = s.players[seat];
    const name = this.cardName(card.num);
    delete card.roll;
    delete card.rollFor;
    this.throwDie(seat, this.besideCard(card), rolled);
    const gerzite = CONFIG.tokenRules.fractalGives;
    const heads = rolled % 2 === 0;
    let text: string;
    if (heads) {
      const want = CONFIG.coinGerzite.heads;
      text = `${p.name} rolled a ${rolled}: heads! ${name} gives ${p.name} ${this.gainText(gerzite, want, this.gain(seat, gerzite, want))}.`;
    } else {
      const other = s.players.findIndex((pl, i) => !pl.out && this.opposed(seat, i as Seat));
      const want = CONFIG.coinGerzite.tails;
      text = other < 0 ? `${p.name} rolled a ${rolled}: tails.` : `${p.name} rolled a ${rolled}: tails. ${s.players[other].name} gains ${this.gainText(gerzite, want, this.gain(other as Seat, gerzite, want))}.`;
    }
    this.fx('cure', card.id, undefined, { value: rolled, effect: heads ? 'heads' : 'tails', seat, text, at: { x: card.x, z: card.z } });
    // (once the die lies still: a purple pulse on a win; on a loss it rises and shatters as it goes)
    if (CONFIG.looks.enabled && heads) this.look('coin-win-magical', card);
    else if (CONFIG.looks.enabled) this.fx('sacrifice', card.id, undefined, { seat, effect: 'coin-shatter' });
    this.log(text, seat);
    // its effect is over: the Spell goes to its owner's discard pile
    this.discardFromPlay(card);
    this.log(`${name} was discarded.`, seat);
    return null;
  }
  /** The resource tokens sitting on one of a player's bars, in slot order. */
  private barTokens(seat: Seat, bar: 'gerzite' | 'dark'): Token[] {
    const { slots, spacing } = barSlots(seat, bar);
    const out: Token[] = [];
    for (const sl of slots) {
      const t = this.state.tokens.find((tk) => !tk.asCounter && this.tokenDb.byId.get(tk.tokenId)?.bar === bar && Math.hypot(tk.x - sl.x, tk.z - sl.z) < spacing * 0.6 && !out.includes(tk));
      if (t) out.push(t);
    }
    return out;
  }
  /** Gives a player up to `n` of a bar resource (Gerzite, Dark Gerzite). Returns how many fitted on the bar. */
  private gain(seat: Seat, tokenId: string, n: number): number {
    const def = this.tokenDb.byId.get(tokenId);
    let got = 0;
    for (let k = 0; def && k < n; k++) if (this.placeBarToken(seat, def)) got++;
    if (def?.bar && got < n) this.barFull(seat, def, n, got);
    if (def?.bar === 'dark' && got > 0) this.darkGained(seat);
    return got;
  }
  /** Some of a gain was lost because the bar is full: the bar's outline flashes, and its owner is told. */
  private barFull(seat: Seat, def: { name: string; bar: '' | 'gerzite' | 'dark' }, wanted: number, got: number) {
    const text = got === 0 ? `Your ${def.name} bar is full: no ${def.name} gained.` : `Your ${def.name} bar is full: only ${got} of ${wanted} ${def.name} gained.`;
    this.fx('barFull', '', undefined, { seat, effect: def.bar, count: wanted - got, text });
  }
  /** "gained 2 Gerzite" / "gained 1 Gerzite (1 lost: the bar is full)" */
  private gainText(tokenId: string, wanted: number, got: number) {
    const name = this.tokenDb.byId.get(tokenId)?.name ?? tokenId;
    if (got === 0 && wanted > 0) return `no ${name} (the bar is full)`;
    return `${got} ${name}` + (got < wanted ? ` (${wanted - got} lost: the bar is full)` : '');
  }
  /** Reaching 5 Fractals turns them into 1 Gerzite. There is no carry-over: any extra Fractals are lost. */
  private convertFractals(seat: Seat, stack: Token) {
    const per = CONFIG.tokenRules.fractalsPerGerzite;
    const gives = this.tokenDb.byId.get(CONFIG.tokenRules.fractalGives);
    const name = this.tokenDb.byId.get(stack.tokenId)?.name ?? 'Fractal';
    if (gives && per > 0 && stack.value >= per) {
      const got = this.placeBarToken(seat, gives);
      if (!got) this.barFull(seat, gives, 1, 0);
      this.log(`${this.state.players[seat].name} turned ${stack.value} ${name}s into 1 ${gives.name}${got ? '' : ' (lost: the bar is full)'}. Their ${name}s reset to 0.`, seat);
      stack.value = 0;
    }
    if (stack.value <= 0) this.removeTokens([stack]);
  }
  /**
   * A token was just dropped somewhere. Handles Fractals merging and Divinity.
   * Returns a message if the token isn't allowed to land there.
   */
  private landToken(seat: Seat, t: Token): string | null {
    const def = this.tokenDb.byId.get(t.tokenId);
    const effect = def?.effect ?? '';
    // a cooldown timer or ability Lock taken off its Emperor no longer belongs to that ability
    if (t.ability && this.cardUnder(t)?.id !== t.ability.card) delete t.ability;
    // a status waiting for its cure roll that is taken off its card: the roll is no longer owed
    if (t.cure !== undefined && !this.cardUnder(t)) delete t.cure;
    if (def?.bar) {
      const refused = this.landResource(seat, t, def as { id: string; name: string; start: number; bar: 'gerzite' | 'dark' });
      if (refused !== undefined) return refused;
    }
    if (effect === 'fractal') {
      const other = this.state.tokens.find(
        (o) => o.id !== t.id && this.effectOf(o) === 'fractal' && Math.hypot(o.x - t.x, o.z - t.z) < 2.2,
      );
      if (other) {
        other.value += t.value;
        this.removeTokens([t]);
        this.convertFractals(seat, other);
      }
      return null;
    }
    const card = this.cardUnder(t);
    if (!card) return null;
    // some tokens only belong on one kind of card (the blue SP Decay counter: Buildings)
    const only = CONFIG.tokenRules.onlyOn[effect];
    if (only && !(card.faceUp && this.cards.byNumber.get(card.num)?.types.some((ty) => only.includes(ty)))) {
      return `${def?.name ?? effect} counters only go on ${only.map((ty) => `${typeName(ty)}s`).join(' and ')}.`;
    }
    const vac = this.vacuumFor(card, effect || (def?.id ?? ''));
    if (vac) return `${this.cardName(vac.num)}: ${this.cardName(card.num)} can’t gain ${def?.name ?? effect} while it is in that lane.`;
    if (card.faceUp) {
      const types = this.cards.byNumber.get(card.num)?.types ?? [];
      const rules = CONFIG.tokenRules;
      // some card types shrug certain effects off…
      if (this.untouchable(card) && ['status', 'damage'].includes(def?.category ?? '')) return `${this.cardName(card.num)} can’t be damaged or carry status effects.`;
      const immune = types.find((ty) => rules.immune[ty]?.includes(effect));
      if (immune) return `${typeName(immune)}s can’t be affected by ${def?.name ?? effect}.`;
      // …and on some, one effect turns into another (Comatose on a Building becomes Crumble)
      const swap = types.map((ty) => rules.becomes[ty]?.[effect]).find(Boolean);
      const into = swap ? this.tokenDb.list.find((d) => d.effect === swap) : undefined;
      if (into) {
        this.log(`${def?.name ?? effect} on a ${typeName(types[0])} becomes ${into.name}.`, seat);
        t.tokenId = into.id;
        t.value = into.start;
        return null;
      }
      const cap = this.capFor(effect, card);
      if (cap !== null && t.value > cap) t.value = cap;
    }
    const arrivalMarker = (e: string) => e === 'entrenched' || e === 'ambush';
    const isStatus = (o: Token) => this.tokenDb.byId.get(o.tokenId)?.category === 'status' && !arrivalMarker(this.effectOf(o));
    if (effect === 'divinity') {
      // Divinity wipes every status effect off the card it lands on
      const also = CONFIG.tokenRules.divinityAlsoRemoves;
      this.removeTokens(this.tokensOn(card).filter((o) => o.id !== t.id && ((isStatus(o) && this.effectOf(o) !== 'divinity') || also.includes(this.effectOf(o)))));
    } else if (isStatus(t) && this.tokensOn(card).some((o) => o.id !== t.id && this.effectOf(o) === 'divinity')) {
      return 'Divinity protects that card from status effects.';
    }
    if (effect === 'possession') {
      // Minions can't be Possessed; a Unit that becomes Possessed is cured of Entrenched at once
      if (slotAt(card)?.slot.name === 'Minion') return 'Minions can’t be Possessed.';
      const dug = this.tokensOn(card).filter((o) => this.effectOf(o) === CONFIG.tokenRules.entrenchedToken);
      if (dug.length) {
        this.removeTokens(dug);
        if (card.rot === CONFIG.arrival.entrenchedRotation) card.rot = 0;
        this.log(`${card.faceUp ? this.cardName(card.num) : 'A face-down card'} is Possessed, which cures its Entrenched.`, seat);
        this.freed(card);
      }
    }
    // a Lock or a turn timer put on an Emperor is for one of its abilities: whoever put it there is asked which
    const kind = this.abilityTokenKind(t);
    if (kind && !t.ability && card.faceUp && CONFIG.emperorAbilities.enabled && this.cards.byNumber.get(card.num)?.emperorAbilities.length) {
      this.fx('abilityAsk', card.id, undefined, { token: t.id, seat, effect: kind });
    }
    return null;
  }
  /** The highest number a counter of this effect may show on this card (Frozen: 3, or 2 on an Emperor), or null. */
  private capFor(effect: string, card: FieldCard): number | null {
    const caps = CONFIG.tokenRules.caps[effect];
    if (!caps) return null;
    const types = this.cards.byNumber.get(card.num)?.types ?? [];
    const own = types.map((ty) => caps[ty]).filter((n) => n !== undefined);
    return own.length ? Math.min(...own) : (caps.other ?? null);
  }
  private findField(id: string) {
    return this.state.field.find((c) => c.id === id);
  }
  /** A Spell has left play: what it lent with "with_spell" (Buffer's Buffer) comes off the cards it was lent to. */
  private endLent(spell: FieldCard) {
    const gone: Token[] = [];
    const where = new Set<string>();
    for (const t of this.state.tokens) {
      const mine = t.lent?.filter((l) => l.spell === spell.id) ?? [];
      if (!mine.length) continue;
      t.lent = t.lent!.filter((l) => l.spell !== spell.id);
      if (!t.lent.length) delete t.lent;
      t.value -= mine.reduce((a, l) => a + l.n, 0);
      if (t.value <= 0) gone.push(t);
      const under = this.cardUnder(t);
      if (under) { where.add(under.id); this.fx('status', under.id, undefined, { token: t.id, effect: 'notice' }); }
    }
    if (gone.length) this.removeTokens(gone);
    if (where.size) this.log(`${this.cardName(spell.num)} has left play: what it gave ${where.size === 1 ? 'one card' : `${where.size} cards`} wears off.`, spell.owner);
  }
  private removeField(id: string) {
    const i = this.state.field.findIndex((c) => c.id === id);
    const out = i >= 0 ? this.state.field.splice(i, 1)[0] : undefined;
    if (out) this.endLent(out);
    if (out?.trueNum !== undefined) { out.num = out.trueNum; delete out.trueNum; } // (a Sheep leaves play as the card it really is)
    if (out && out.faceUp && !out.spawned && !out.heldBy && this.isUnit(this.cards.byNumber.get(out.num))) this.goneToDiscard.push({ num: out.num, owner: out.owner }); // (checked against the discard piles after the action: Into the Soul Realm)
    return out;
  }
  private spawnPoint(seat: Seat) {
    const base = seatPoint(seat, CONFIG.zones.spawn);
    return { x: base.x + (this.rng() - 0.5) * 10, z: base.z + (this.rng() - 0.5) * 4 };
  }
  /**
   * Where something brought in by hand appears: near `at` (the spot its player is looking at) if they
   * sent one, else in the spawn zone. Either way it is then moved to the nearest free spot (see freeSpot),
   * so a new counter never lands on a card and attaches itself.
   */
  private spawnSpot(seat: Seat, at: { x: number; z: number } | undefined, size: { halfW: number; halfD: number; card?: boolean }, taken: { x: number; z: number }[] = []) {
    const zone = this.spawnPoint(seat);
    const near = at && Number.isFinite(at.x) && Number.isFinite(at.z) && CONFIG.spawning.followCamera ? this.clampToTable(at.x, at.z) : zone;
    return this.freeSpot(seat, near, size, taken);
  }
  /**
   * The nearest spot to `pt` where a new token (or spawned card) is clear of every card on the table, by
   * CONFIG.spawning.gap, and not on top of another token. A spawned card also keeps its middle out of
   * every slot, so it is never "in" a slot before its player puts it there. If a card is in the way, the
   * spots just off that card are tried first: in front of it (the player's side), to its right, to its left,
   * behind it. Failing those, the search goes outward from `pt` in rings.
   */
  private freeSpot(seat: Seat, pt: { x: number; z: number }, size: { halfW: number; halfD: number; card?: boolean }, taken: { x: number; z: number }[] = []) {
    const rules = CONFIG.spawning;
    if (!rules.avoidCards) return pt;
    const s = this.state;
    const hx = CONFIG.table.width / 2 - 2;
    const hz = CONFIG.table.depth / 2 - 2;
    const apart = Math.min(size.halfW, size.halfD) * 2 * 0.85; // new tokens may sit close to each other, not on each other
    const slots = size.card ? [1, -1].flatMap((side) => CONFIG.slots.map((sl) => ({ x: sl.x * side, z: sl.z * side, halfW: sl.width / 2, halfD: sl.depth / 2 }))) : [];
    const clear = (q: { x: number; z: number }) => {
      if (Math.abs(q.x) > hx || Math.abs(q.z) > hz) return false;
      for (const c of s.field) {
        const turned = c.rot % 2 === 1;
        const halfW = (turned ? CONFIG.card.height : CONFIG.card.width) / 2;
        const halfD = (turned ? CONFIG.card.width : CONFIG.card.height) / 2;
        if (Math.abs(q.x - c.x) < halfW + size.halfW + rules.gap && Math.abs(q.z - c.z) < halfD + size.halfD + rules.gap) return false;
      }
      for (const sl of slots) if (Math.abs(q.x - sl.x) <= sl.halfW + 0.3 && Math.abs(q.z - sl.z) <= sl.halfD + 0.3) return false;
      if (!size.card && s.tokens.some((t) => Math.hypot(t.x - q.x, t.z - q.z) < apart)) return false;
      return !taken.some((t) => Math.hypot(t.x - q.x, t.z - q.z) < apart);
    };
    if (clear(pt)) return pt;
    const side = sideOf(seat); // z grows toward seat 0's edge of the table
    const inWay = s.field
      .map((c) => {
        const turned = c.rot % 2 === 1;
        return { c, halfW: (turned ? CONFIG.card.height : CONFIG.card.width) / 2 + size.halfW + rules.gap, halfD: (turned ? CONFIG.card.width : CONFIG.card.height) / 2 + size.halfD + rules.gap };
      })
      .filter((b) => Math.abs(pt.x - b.c.x) < b.halfW && Math.abs(pt.z - b.c.z) < b.halfD)
      .sort((a, b) => Math.hypot(pt.x - a.c.x, pt.z - a.c.z) - Math.hypot(pt.x - b.c.x, pt.z - b.c.z))[0];
    if (inWay) {
      const { c, halfW, halfD } = inWay;
      const off = 0.01;
      const beside = [
        { x: pt.x, z: c.z + side * (halfD + off) },
        { x: c.x + side * (halfW + off), z: pt.z },
        { x: c.x - side * (halfW + off), z: pt.z },
        { x: pt.x, z: c.z - side * (halfD + off) },
      ].find(clear);
      if (beside) return beside;
    }
    const toward = side * (Math.PI / 2); // the direction of the player's own edge
    for (let r = rules.step; r <= rules.reach; r += rules.step) {
      const n = Math.max(8, Math.ceil((2 * Math.PI * r) / rules.step));
      for (let k = 0; k < n; k++) {
        // 0, +1, −1, +2, −2 … steps round the ring, starting on the player's side
        const turn = (k % 2 === 1 ? 1 : -1) * Math.ceil(k / 2) * ((2 * Math.PI) / n);
        const q = { x: pt.x + Math.cos(toward + turn) * r, z: pt.z + Math.sin(toward + turn) * r };
        if (clear(q)) return q;
      }
    }
    return pt;
  }
  private insertAt(list: HeldCard[], card: HeldCard, where: Where) {
    if (typeof where === 'number') list.splice(Number.isFinite(where) ? Math.max(0, Math.min(list.length, Math.floor(where))) : list.length, 0, card);
    else if (where === 'top') list.unshift(card);
    else if (where === 'bottom') list.push(card);
    else {
      const middle = Math.floor(list.length / 2) + Math.floor(this.rng() * 5) - 2;
      list.splice(Math.max(0, Math.min(list.length, middle)), 0, card);
    }
  }
  private fx(kind: FxEvent['kind'], card: string, tier?: number, extra: Partial<FxEvent> = {}) {
    const s = this.state;
    // (a card drawn into a hand by a card with a glow of its own: Cosmic Beyonder, Occult Sorcerer)
    if (kind === 'toHand' && this.handTint && !extra.tint) extra = { ...extra, tint: this.handTint };
    s.fx.push({ id: (s.fx.at(-1)?.id ?? 0) + 1, kind, card, tier, ...extra });
    if (s.fx.length > 40) s.fx.splice(0, s.fx.length - 40);
    if (kind === 'play') this.arriveLook(card);
  }
  /** How an attack looks as it flies: the attacker's own style (The Remaining), else its damage's affiliation. */
  private attackTint(from: FieldCard): string {
    return (from.faceUp ? CONFIG.looks.attackStyle[this.cardName(from.num)] : undefined) ?? this.damageAffOf(from)[0] ?? 'neutral';
  }
  private discountFrom: string | null = null;
  private burstOverride: string | null = null; // a destroyed card bursts this way, whatever it is (Forbidden Resurgence) // the card whose discount the card just played used
  /** The glow cards drawn into a hand get while a card with one of its own is at work (CONFIG.looks.handTint). */
  private handTint: string | null = null;
  /** A card's own effect, for both players to see (CONFIG.looks; the recipes are the client's). */
  private look(effect: string, card: FieldCard | null, extra: Partial<FxEvent> = {}) {
    if (!CONFIG.looks.enabled) return;
    this.fx('look', card?.id ?? '', undefined, { effect, ...(card ? { from: { x: card.x, z: card.z } } : {}), ...extra });
  }
  /** A unit has just arrived on the table: one that has an arrival look of its own plays it (CONFIG.looks.arrive). */
  private arriveLook(id: string) {
    const c = this.findField(id);
    if (!c || !c.faceUp) return;
    const name = this.cardName(c.num);
    // Clay Soldiers: one big Troop lands with a large cloud of sand; the four small ones (or a Lanespanner's units) each with a small one
    if (name === 'Clay Soldiers') { this.look(!c.form && !c.spanOf && !c.swarm && this.valueOn(c, CONFIG.tokenRules.countToken) <= 1 ? 'clay-big' : 'clay-small', c); return; }
    const own = CONFIG.looks.arrive[name];
    if (own) this.look(own, c);
  }

  // ---------------- tiers ----------------

  private tierOf(card: FieldCard) {
    // (a tier lent by an effect: for a turn, Temporal Powers; or while it lasts, Castle on the Cliff)
    const lent = card.tierBoost && (card.tierBoostAt === undefined || card.tierBoostAt === this.turnStamp()) ? card.tierBoost : 0;
    return Math.min(MAX_TIERS, 1 + Math.max(card.merged.length, card.swarmMerged ?? 0) + lent);
  }
  /** Every copy in a tiered card: the one on top plus the ones merged underneath. */
  private copiesOf(card: FieldCard): HeldCard[] {
    return [card, ...card.merged];
  }
  private removeTierToken(card: FieldCard) {
    if (!card.tierToken) return;
    const i = this.state.tokens.findIndex((t) => t.id === card.tierToken);
    if (i >= 0) this.state.tokens.splice(i, 1);
    card.tierToken = null;
  }
  /** Puts the right Tier II / Tier III token on a card (or removes it at Tier I). */
  private refreshTierToken(card: FieldCard) {
    this.removeTierToken(card);
    const def = this.tokenDb.byId.get(CONFIG.tierTokens[this.tierOf(card)] ?? '');
    if (!def) return;
    const side = sideOf(card.owner);
    const id = this.newId('t');
    this.state.tokens.push({
      id,
      tokenId: def.id,
      x: card.x + 1.7 * side,
      z: card.z - 2.6 * side,
      value: def.start,
      layer: this.state.nextLayer++,
      by: card.owner,
    });
    card.tierToken = id;
  }

  // ---------------- what the table does for you each turn ----------------

  /** Overtime's damage to each Emperor at the end of a turn: doubled from global turn CONFIG.overtime.doubleFrom. */
  private overtimeToll() {
    return this.doubleOvertime() ? CONFIG.overtime.doubleDamage : CONFIG.overtime.emperorDamage;
  }
  /** Double Overtime: from global turn CONFIG.overtime.doubleFrom, more damage and more Gerzite. */
  private doubleOvertime() {
    const o = CONFIG.overtime;
    return this.overtime() && o.doubleFrom > 0 && this.globalTurn() >= o.doubleFrom;
  }
  /** Overtime: both players have finished 20 turns. */
  private overtime() {
    const after = this.state.teams ? CONFIG.teams.overtimeAfter : CONFIG.overtime.afterTurns;
    return after > 0 && this.globalTurn() >= after;
  }
  /** The start of a turn (Overdraw Phase): Gerzite, untap, and the counters that tick now. */
  private startTurn(seat: Seat) {
    const s = this.state; const p = s.players[seat]; const T = CONFIG.turn;
    // Time Bender: its tokens as an opponent's turn begins, and again as that turn is over (its own side's next turn
    // begins): its Tap undoes only what changed in between, the opponent's last turn, never more than that one turn
    for (const c of s.field) {
      if (!c.faceUp || !this.cards.byNumber.get(c.num)?.effects.some((e) => e.words[0] === 'rewind')) continue;
      const now = () => this.tokensOn(c).filter((t) => t.id !== c.tierToken).map((t) => JSON.parse(JSON.stringify(t)) as Token);
      if (this.opposed(c.owner, seat)) c.rewind = { x: c.x, z: c.z, from: now() };
      else if (c.rewind && !c.rewind.to) { c.rewind.to = now(); c.rewind.ready = s.players[c.owner].turns; }
    }
    s.fractalsTaken = false;
    s.missRolls = {};
    s.lastMiss = null;
    this.jump(seat, 'deck');
    this.jump(seat, 'discard');
    this.statusActs(seat, 'start');
    this.shedOverheal(seat);
    this.gerziteFlows(seat, 'start');
    this.tickClocks(seat, 'start');
    this.tick(seat, 'start');
    this.tickUp(seat);
    this.askRolls(seat);
    this.soulsPay(seat);
    this.transmitTick(seat);
    this.celestialAsk(seat);
    // what cards do by themselves as a turn begins (Headstone's Skell-Mini, Campfire's healing…)
    this.fireAll('start', (c) => this.turnOf(c.owner) === seat);
    this.fireAll('any_start', () => true);
    this.settleFallen();
    const overtime = this.overtime();
    // 2v2: the team's Gerzite comes once per team turn, onto its shared bar
    const wanted = s.teams ? (!this.teamTurnStarts(seat) ? 0 : overtime ? CONFIG.teams.overtimeAtStart : CONFIG.teams.gerziteAtStart) : overtime ? (this.doubleOvertime() ? CONFIG.overtime.doubleGerzite : CONFIG.overtime.gerzite) : T.gerziteAtStart;
    // (Valuable Taxation: an opponent's Spell takes some of it)
    const tax = CONFIG.combat.enabled ? s.field.filter((c) => c.faceUp && this.opposed(c.owner, seat)).flatMap((c) => (this.cards.byNumber.get(c.num)?.effects.length ? this.effectsOf(c, 'always') : [])).filter((e) => e.words[0] === 'tax').reduce((n, e) => n + (Number(e.words[1]) || 1), 0) : 0;
    const gerzite = CONFIG.tokenRules.fractalGives;
    // the player who goes first starts on their opening Gerzite alone: no start-of-turn gain on the game's very first turn
    const opening = seat === s.first && p.turns === 0;
    if (wanted > 0 && (!opening || T.gerziteOnOpeningTurn)) {
      const who = s.teams ? this.teamName(seat) : p.name;
      const owed = Math.max(0, wanted - tax);
      // (Valuable Taxation: its light runs to the bar it is taxing, so it is clear why)
      if (tax) for (const c of s.field.filter((c2) => c2.faceUp && this.opposed(c2.owner, seat) && !!this.cards.byNumber.get(c2.num)?.effects.some((e) => e.when === 'always' && e.words[0] === 'tax'))) {
        this.fx('spell', c.id, undefined, { at: seatPoint(s.teams ? teamOf(seat) as Seat : seat, CONFIG.zones.gerziteBar), from: { x: c.x, z: c.z }, tint: this.affOf(c)[0] ?? 'magical', seat: c.owner });
      }
      this.log(`${who} gained ${this.gainText(gerzite, owed, this.gain(seat, gerzite, owed))} as ${s.teams ? 'their team turn' : 'their turn'} begins${tax ? ` (${Math.min(tax, wanted)} less: taxed)` : ''}.`, seat);
    }
    // 2v2: the turn passed from one teammate to the other: the team gains a little more (CONFIG.teams.handoffGerzite)
    if (s.teams && !this.teamTurnStarts(seat) && CONFIG.teams.handoffGerzite > 0) {
      const mate = this.mateOf(seat);
      const n = CONFIG.teams.handoffGerzite;
      this.log(`${mate !== null ? s.players[mate].name : 'Their teammate'} passes the turn to ${p.name}: ${this.teamName(seat)} gained ${this.gainText(gerzite, n, this.gain(seat, gerzite, n))}.`, seat);
    }
    if (T.untapAtStart) {
      // (a card told to stay tapped an extra turn, Ballista Artillery after it attacks, sits this untap out)
      const held = s.field.filter((c) => this.turnOf(c.owner) === seat && (c.holdTapped ?? 0) > 0);
      const tapped = s.field.filter((c) => this.turnOf(c.owner) === seat && c.rot % 2 === 1 && !this.sitsSideways(c) && !held.includes(c));
      for (const c of held) { c.holdTapped! -= 1; if (!c.holdTapped) delete c.holdTapped; }
      for (const c of tapped) c.rot = 0;
      if (tapped.length) this.log(`${p.name} untapped ${tapped.length} card${tapped.length === 1 ? '' : 's'}.`, seat);
      for (const c of tapped) this.untapped(c);
    }
  }
  /** The end of a turn: the passive draw (with Deck-Out damage), Gerzite, and Overtime's toll on both Emperors. */
  private endTurn(seat: Seat) {
    const s = this.state; const p = s.players[seat]; const T = CONFIG.turn;
    // (Tome of Greed: one more card in the passive draw)
    const more = CONFIG.combat.enabled ? s.field.filter((c) => c.owner === seat && this.effectsOf(c, 'always').some((e) => e.words[0] === 'extra_draw')).length : 0;
    const want = Math.max(0, Math.min(T.passiveDrawMax + more, T.passiveDrawTo + more - p.hand.length));
    let drew = 0;
    for (let k = 0; k < want; k++) {
      const card = this.drawTop(p);
      if (!card) break;
      p.hand.push(this.rehide(card)); drew++;
    }
    if (drew) this.log(`${p.name} drew ${drew} card${drew === 1 ? '' : 's'} as their turn ends.`, seat);
    if (drew) this.fx('toHand', '', undefined, { effect: 'deck', seat, count: drew });
    const missed = want - drew;
    if (missed > 0 && T.deckOutDamage > 0 && this.damageEmperor(seat, missed * T.deckOutDamage)) {
      this.deckOutLook(seat);
      this.log(`Deck-Out: ${p.name} could not draw ${missed} card${missed === 1 ? '' : 's'}, so their Emperor takes ${missed * T.deckOutDamage} damage.`, seat);
    }
    const overtime = this.overtime();
    const teamEnds = this.teamTurnEnds(seat);
    const wanted = s.teams ? (!teamEnds ? 0 : overtime ? CONFIG.teams.overtimeAtEnd : CONFIG.teams.gerziteAtEnd) : overtime ? (this.doubleOvertime() ? CONFIG.overtime.doubleGerzite : CONFIG.overtime.gerzite) : T.gerziteAtEnd;
    const gerzite = CONFIG.tokenRules.fractalGives;
    // a card played this turn can take the end-of-turn Gerzite away (on_play = no_end_gerzite): the log says which.
    // 2v2: a card either teammate played this team turn does
    const mate = this.mateOf(seat);
    const noGain = p.noEndGerzite ?? (s.teams && mate !== null ? s.players[mate].noEndGerzite : null);
    const who = s.teams ? this.teamName(seat) : p.name;
    if (wanted > 0 && noGain) this.log(`${who} gain${s.teams ? '' : 's'} no Gerzite as ${s.teams ? 'their team turn' : 'their turn'} ends: ${noGain} was cast this turn.`, seat);
    else if (wanted > 0) this.log(`${who} gained ${this.gainText(gerzite, wanted, this.gain(seat, gerzite, wanted))} as ${s.teams ? 'their team turn' : 'their turn'} ends.`, seat);
    if (!s.teams || teamEnds) {
      p.noEndGerzite = null;
      if (mate !== null) s.players[mate].noEndGerzite = null;
    }
    const toll = this.overtimeToll();
    if (overtime && toll > 0 && (!s.teams || teamEnds)) {
      let any = false;
      s.players.forEach((pl, i) => {
        if (pl.out) return;
        const hit = this.damageEmperor(i as Seat, toll);
        any = hit || any;
        // (a burst of embers on each Emperor as Overtime takes its toll: fiercer in Double Overtime)
        const e = this.emperorOf(i as Seat);
        if (hit && e) this.look(this.doubleOvertime() ? 'overtime-hit-2' : 'overtime-hit', e);
      });
      if (any) this.log(`Overtime: ${s.players.length > 2 ? 'every Emperor takes' : s.players.length > 1 ? 'both Emperors take' : 'the Emperor takes'} ${toll} damage.`, null);
    }
  }
  /**
   * Turn counters on the player's own cards go down by 1; at 0 they are removed.
   * Turn timers (CONFIG.turn.timers) also count when they aren't on a card, for whoever put them down.
   */
  private tick(seat: Seat, when: keyof typeof CONFIG.turn.tickDown) {
    const s = this.state; const p = s.players[seat];
    const list: string[] = CONFIG.turn.tickDown[when] ?? [];
    if (!list.length) return;
    const notes: string[] = [];
    const freedNow: FieldCard[] = []; // cards whose Entrenched ran out just now
    for (const t of [...s.tokens]) {
      const def = this.tokenDb.byId.get(t.tokenId);
      if (!def?.counter || !list.includes(def.effect)) continue;
      const timer = CONFIG.turn.timers.includes(def.effect);
      const card = this.cardUnder(t);
      // Possession counts down in the End Phase of the player controlling the card, not its owner's
      const owner = card ? (def.effect === 'possession' ? (this.possessor(card) ?? card.owner) : card.owner) : timer ? t.by : null;
      if (this.turnOf(owner) !== seat) continue;
      if (t.unset) continue; // a timer still waiting for its number doesn't count
      if (card && !timer && this.paused(t, card)) continue;
      const entrenched = def.effect === CONFIG.tokenRules.entrenchedToken;
      if (entrenched && t.since === p.turns) continue; // put down this very turn: it starts counting next turn
      const where = !card ? '' : card.faceUp ? this.cardName(card.num) : 'a face-down card';
      const on = where ? ` on ${where}` : '';
      t.value -= 1;
      if (t.value > 0) notes.push(`${def.name} ${t.value + 1} → ${t.value}${on}`);
      else {
        this.removeTokens([t]);
        if (entrenched && card && card.rot === CONFIG.arrival.entrenchedRotation) card.rot = 0; // cured: it turns to face its owner
        if (entrenched && card) freedNow.push(card);
        const ability = timer && t.ability ? this.cards.byNumber.get(this.findField(t.ability.card)?.num ?? -1)?.emperorAbilities.find((x) => x.slot === t.ability!.slot) : undefined;
        if (timer && t.ability) {
          // the cooldown of an Emperor ability is over: its owner is told it is ready again
          const emperor = this.findField(t.ability.card);
          const whose = emperor ? this.cardName(emperor.num) : 'The Emperor';
          const label = ability ? `Ability ${ability.slot}, ${ability.name},` : `Ability ${t.ability.slot}`;
          if (emperor) this.fx('status', emperor.id, undefined, { effect: 'notice', seat, text: `${whose}’s ${label} is ready again.` });
          notes.push(`${whose}’s ${label} is off cooldown`);
        } else if (timer) {
          // a timer running out is news for both players: the card flashes (or the spot where the timer lay)
          // …and on a Biome or Tome it means the card's turns are over: it is marked as no longer active
          const over = !!card && card.faceUp && this.expire(card);
          const text = over ? `${where} has run its turns: it is no longer active.` : `${def.name}${on} has run out.`;
          if (card) this.fx('status', card.id, undefined, { effect: 'notice', text });
          else this.fx('ping', '', undefined, { at: { x: t.x, z: t.z }, seat, text });
          notes.push(over ? `${where} ran its turns and is no longer active` : `${def.name}${on} ran out`);
        } else notes.push(entrenched ? `${where} is no longer Entrenched` : `${def.name} ended${on}`);
      }
    }
    if (notes.length) this.log(`${p.name}’s counters: ${notes.join('; ')}.`, seat);
    for (const card of freedNow) this.freed(card);
  }
  /**
   * Adds Fractals to a player's stack, beside their Gerzite bar: one stack each, or in 2v2 one stack per team
   * (by the team's shared bar, on its left player's side) that both teammates' Fractals go on.
   */
  private addFractals(seat: Seat, n: number) {
    const s = this.state;
    const def = this.tokenDb.list.find((d) => d.effect === 'fractal');
    if (!def || n <= 0) return;
    const ours = (t: Token) => (s.teams ? t.by !== null && teamOf(t.by) === teamOf(seat) : t.by === seat);
    let stack = s.tokens.find((t) => this.effectOf(t) === 'fractal' && ours(t));
    if (stack) stack.value += n;
    else {
      const at = s.teams ? (teamOf(seat) as Seat) : seat; // seats 0 and 1 are the left players of teams 1 and 2
      stack = { id: this.newId('t'), tokenId: def.id, ...seatPoint(at, CONFIG.zones.fractal), value: n, layer: s.nextLayer++, by: at };
      s.tokens.push(stack);
    }
    this.convertFractals(seat, stack);
  }
  /** Hands the turn player the Fractals their idle units earned. Returns how many that was. */
  private collectFractals(seat: Seat) {
    const n = this.fractalsEarned(seat);
    if (n <= 0) return 0;
    this.state.fractalsTaken = true;
    this.log(`${this.state.players[seat].name} collected ${n} Fractal${n === 1 ? '' : 's'} from idle units.`, seat);
    const earners = this.fractalEarners(seat);
    this.addFractals(seat, n);
    // each unit that earned one: its own "fractal" effects (Fractal Hoarder), and its side's "ally_fractal" (Star Observatory)
    for (const c of earners) {
      if (!this.state.field.includes(c)) continue;
      this.fire(c, 'fractal');
      const side = this.sideOfCard(c);
      this.fireAll('ally_fractal', (o) => this.sideOfCard(o) === side && this.allies(o.owner, c.owner), { it: c });
    }
    this.settleFallen();
    return n;
  }
  /** How many Fractals a player's idle units have earned this turn. */
  private fractalsEarned(seat: Seat) {
    const blockers = CONFIG.turn.fractalBlockers;
    // Vialmaster, never tapped this turn: 1 more for each Spell its owner cast this turn
    const p0 = this.state.players[seat];
    const spells = p0.playedNow?.stamp === this.turnStamp() ? p0.playedNow.types.filter((t) => t === 'spell').length : 0;
    const vials = spells ? this.state.field.filter((c) => this.turnOf(c.owner) === seat && c.faceUp && c.rot % 2 === 0 && c.acted !== this.turnKey() && this.effectsOf(c, 'always').some((e) => e.words[0] === 'vial_attacks')).length : 0;
    return vials * spells + this.fractalEarners(seat).length;
  }
  /** The idle units that earn their player a Fractal this turn (one for each: a swarm's or a Legion's counts once). */
  private fractalEarners(seat: Seat): FieldCard[] {
    const blockers = CONFIG.turn.fractalBlockers;
    const swarms = new Set<string>(); // (an idle swarm earns one Fractal, not one for each of its units)
    return this.state.field.filter((c) => {
      const types = this.cards.byNumber.get(c.num)?.types ?? [];
      if (this.turnOf(c.owner) !== seat || !c.faceUp || c.rot % 2 === 1) return false;
      // a summoned unit (a Necromancer's Skell-Minis, a Scarecrow's Icewings…) earns none: spawners would pile up
      // Fractals otherwise. (A swarm's or a Lanespanner's own units and clones are not summoned units.)
      if (this.isSummonedUnit(c)) return false;
      // (units that attack together earn one Fractal between them, and only while every one of them is untapped: a swarm,
      // an Undead Legion — General, Undead and Spirit Soldiers — and a Lanespanner's units)
      // (an Undead Legion: only its General earns one, while the General itself is untapped; its Soldiers never do)
      if (this.isGeneral(c)) { /* counted below like any unit */ } else if (this.legionHead(c)) return false;
      else if (CONFIG.swarms.fractalsPerSwarm) {
        const group = this.attackGroup(c);
        const key = c.swarm ? `swarm:${c.swarm}` : group.length > 1 ? `span:${c.spanOf ?? c.id}` : null;
        if (key) { if (swarms.has(key) || group.some((m) => m.rot % 2 === 1)) return false; swarms.add(key); }
      }
      if (!types.includes('troop') && !types.includes('building')) return false;
      if (slotAt(c)?.slot.name === 'Minion') return false;
      if (this.untouchable(c)) return false; // a Calling Pillar earns no Fractals
      if (c.soul) return false; // nor does a Soul (Valley of Waiting)
      // nor a Unit that can never attack (0 damage: Headstone, Gerzite Mine, an Undead Guard), nor a card that says so (always: no_fractal):
      // a Fractal is for holding back an attack
      if (this.neverAttacks(c) || this.effectsOf(c, 'always').some((e) => e.words[0] === 'bulwark' || e.words[0] === 'no_fractal')) return false;
      return !this.tokensOn(c).some((t) => blockers.includes(this.effectOf(t)));
    });
  }

  /** How many global turns have been played (everyone still in has finished this many turns). */
  get globalTurns() {
    return this.globalTurn();
  }
  private globalTurn() {
    // a player who is out (2v2) takes no more turns: the count goes by the players still in
    const turns = this.state.players.filter((p) => !p.out).map((p) => p.turns);
    return turns.length ? Math.min(...turns) : 0;
  }

  // ---------------- actions ----------------

  /** Applies one action for a seat. Returns an error message, or null if it worked. */
  apply(seat: Seat, action: Action): string | null {
    const s = this.state;
    this.aimed.clear();
    this.hopsNow.clear();
    if (s.result) return 'This game is over.';
    const p = s.players[seat];
    if (!p) return 'You aren’t seated at this table.';
    const before = this.owners();
    const onField = new Set(s.field.map((c) => c.id)); // (what was already on the table: a Thorny Snare springs on what arrives)
    const piles = s.players.map((q) => q.discard.length);
    this.goneToDiscard = [];
    // (what was already lying destroyed before this move stays for its owner to clear away: only what falls now goes by itself)
    this.pickedNow = [];
    this.guardHits.clear(); this.reached.clear();
    this.fallenBefore = new Set(s.field.filter((c) => this.hasOn(c, CONFIG.turn.lifetime.expiredToken)).map((c) => c.id));
    // 2v2: a player whose Emperor has fallen watches their teammate. They can look, but not change anything
    // (and can't search their own piles: nothing to pass on to their teammate)
    if (s.teams && p.out && !['closeReveal', 'peekClose', 'answerEnd', 'requestEnd'].includes(action.type)) return 'Your Emperor has fallen: you are watching your teammate play on.';
    // a call that is still waiting is answered before any real move is made (pointing, dragging and the like don't count)
    if (s.calling?.length && ['nextPhase', 'play', 'draw', 'toPile', 'toHand', 'searchStart', 'takeDiscard', 'pileMove', 'shuffle', 'requestEnd', 'answerEnd', 'sacrifice', 'merge'].includes(action.type)) this.resolveCalling();
    const err = this.run(seat, p, action);
    if (!err) this.legionSweep();
    if (!err) s.version++;
    // the Units that went from play to their owner's discard pile in this action (Into the Soul Realm)
    if (this.goneToDiscard.length) {
      const gone = this.goneToDiscard.filter((g) => { const q = s.players[g.owner]; const fresh = q.discard.slice(0, Math.max(0, q.discard.length - piles[g.owner])); return fresh.some((c) => c.num === g.num); });
      this.goneToDiscard = [];
      this.soulRealm(gone);
    }
    this.splitSwarms(before);
    this.layout(before);
    if (!err) this.springSnares(onField);
    this.settleAlsoAffs();
    this.syncTallies();
    this.markDestroyed();
    if (CONFIG.combat.enabled && CONFIG.combat.emperorFallEnds) this.checkOut(); // an Emperor that has fallen ends the game (2v2: when a team has none left)
    this.checkCalling();
    this.settleBrought();
    this.soulsWithoutValley();
    // a Unit waiting to be played for free is forgotten once it has left its owner's hand, whichever way it went
    for (const q of s.players) if (q.freeUnit && !q.hand.some((c) => c.id === q.freeUnit!.id)) q.freeUnit = null;
    this.syncOpenHands();
    this.settleLimbo();
    return err;
  }
  /**
   * Cagurn's Thorny Snare: a Unit that has just arrived in a snared lane (played or summoned there; every unit of a
   * swarm) takes the snare's hits one after another, each a hit of its own (a Shield takes the first and breaks, and
   * so on). Then the snare is gone.
   */
  private springSnares(was: Set<string>) {
    const s = this.state;
    if (!s.snares?.length || !CONFIG.combat.enabled) return;
    const damageId = this.tokenDb.list.find((d) => CONFIG.tokenRules.damageTokens.includes(d.effect))?.id;
    if (!damageId) return;
    for (const snare of [...s.snares]) {
      const lane = laneOrder().find((l) => l.name === snare.lane);
      if (!lane) { s.snares = s.snares.filter((n) => n !== snare); continue; }
      const fresh = this.unitsAt(lane, snare.side).filter((u) => !was.has(u.id) && u.faceUp && this.isUnit(this.cards.byNumber.get(u.num)) && (s.mode === 'solo' || this.opposed(u.owner, snare.by)));
      if (!fresh.length) continue;
      s.snares = s.snares.filter((n) => n !== snare);
      const from = this.findField(snare.from);
      const by = from ? this.cardName(from.num) : 'The Thorny Snare';
      for (const u of fresh) {
        const notes: string[] = [];
        let took = 0;
        for (let k = 0; k < snare.hits && s.field.includes(u) && !this.hasOn(u, CONFIG.turn.lifetime.expiredToken); k++) {
          took += this.landHit(snare.by, u, snare.amount, { status: true }, notes);
          this.fx('look', u.id, undefined, { effect: 'snare-bite' });
        }
        this.log(`${by}’s Thorny Snare springs on ${this.cardName(u.num)} in ${snare.lane}: ${snare.hits} hits of ${snare.amount}, ${took} damage${notes.length ? ` (${[...new Set(notes)].join('; ')})` : ''}.`, snare.by);
      }
      this.settleFallen();
      this.layout(this.owners());
    }
  }
  /**
   * Heart of Limbo: as it becomes active, every player's discard pile is set aside in a Limbo pile that nothing
   * can reach; cards discarded meanwhile go to the normal discard pile. When it ends (its turns run out, or it
   * leaves the table), each player shuffles their Limbo pile into their discard pile and draws its top card if
   * they have fewer than CONFIG.limbo.drawUnder cards in hand.
   */
  private settleLimbo() {
    const s = this.state;
    const active = s.field.filter((f) => f.faceUp && this.cards.byNumber.get(f.num)?.whileInPlay.includes('limbo_discards') && !this.isExpired(f));
    const was = s.limboCards ?? [];
    s.limboCards = active.length ? active.map((f) => f.id) : undefined;
    if (active.length && !was.length) {
      const name = this.cardName(active[0].num);
      const notes: string[] = [];
      for (const q of s.players) {
        if (!q.discard.length) continue;
        notes.push(`${q.name} ${q.discard.length}`);
        q.limbo = [...q.discard, ...(q.limbo ?? [])];
        q.discard = [];
        if (q.searching === 'discard') q.searching = null;
      }
      this.log(`${name} is active: ${notes.length ? `the discard piles (${notes.join(', ')}) are set aside in Limbo, out of reach` : 'the discard piles are empty'}. Cards discarded from now on go to the discard pile as usual.`, null);
    } else if (!active.length && was.length) this.releaseLimbo();
  }
  private releaseLimbo() {
    const s = this.state;
    const notes: string[] = [];
    s.players.forEach((q, i) => {
      if (q.out) return;
      const back = q.limbo?.length ?? 0;
      q.discard = [...(q.limbo ?? []), ...q.discard];
      delete q.limbo;
      this.shuffle(q.discard);
      q.shuffles.discard++;
      let drew = '';
      if (q.hand.length < CONFIG.limbo.drawUnder && q.discard.length) {
        const card = q.discard.shift()!;
        q.hand.push(card);
        this.fx('toHand', '', undefined, { effect: 'discard', seat: i as Seat, count: 1 });
        drew = ` and draws ${this.cardName(card.num)}`;
        void i;
      }
      notes.push(`${q.name} shuffles ${back} card${back === 1 ? '' : 's'} from Limbo into their discard pile${drew}`);
    });
    this.log(`Heart of Limbo has ended: ${notes.join('; ')}.`, null);
  }

  // ---------------- shared lanes and attached units ----------------

  /** Which card each token is resting on (the top one), before an action moves things about. */
  private owners() {
    const m = new Map<string, string>();
    for (const t of this.state.tokens) {
      const c = this.cardUnder(t);
      if (c) m.set(t.id, c.id);
    }
    return m;
  }
  /**
   * Run after every action: cards sharing a lane are laid out side by side (or in a grid) at a smaller size, a card
   * alone in a lane goes back to full size, and attached summoned units follow the card they sit on. The tokens
   * on each card go with it. A token keeps the card it was on before the action while it is still on it (a card
   * dropped into a lane covers the one already there until they are laid out); otherwise it is on whatever is under it.
   */
  private layout(before: Map<string, string>) {
    const s = this.state;
    const L = CONFIG.sharedLanes;
    const owner = new Map<Token, FieldCard>();
    for (const t of s.tokens) {
      const was = before.get(t.id);
      const wasCard = was ? this.findField(was) : undefined;
      const on = wasCard && this.isOn(t, wasCard) ? wasCard : this.cardUnder(t);
      if (on) owner.set(t, on);
    }
    const place = (c: FieldCard, x: number, z: number, scale: number) => {
      const old = c.scale ?? 1;
      if (Math.abs(c.x - x) < 1e-6 && Math.abs(c.z - z) < 1e-6 && Math.abs(old - scale) < 1e-6) return;
      for (const [t, o] of owner) {
        if (o !== c) continue;
        t.x = x + ((t.x - c.x) * scale) / old;
        t.z = z + ((t.z - c.z) * scale) / old;
      }
      c.x = x;
      c.z = z;
      if (Math.abs(scale - 1) < 1e-6) delete c.scale;
      else c.scale = scale;
    };
    // a summoned unit whose card has gone is attached to nothing
    // (a Troop Soul Collector held, whose Collector has left play some other way: it goes back to the discard pile)
    for (const c of [...s.field]) if (c.heldBy && !this.findField(c.heldBy)) { this.removeField(c.id); this.insertAt(s.players[c.owner].discard, this.rehide(c), 'top'); }
    for (const c of s.field) if (c.attachedTo && !this.findField(c.attachedTo)) { delete c.attachedTo; delete c.attachOrder; }
    // the lanes
    const groups = new Map<string, { slot: Slot; side: 1 | -1; cards: FieldCard[] }>();
    const loose: FieldCard[] = [];
    for (const c of s.field) {
      if (c.attachedTo) continue;
      const at = slotAt(c);
      // (a swarm's units are laid out side by side wherever they stand, the Minion slot too: Skell-Mateys there are 2 cards)
      // (the Minion slot is shared like a lane: a Necromancer there lays its Skell-Minis out beside it, at the smaller size)
      const shared = !!at && (!!at.slot.counts || at.slot.effect === 'minion');
      if (!at || (!shared && !c.swarm)) { loose.push(c); continue; }
      const key = `${CONFIG.slots.indexOf(at.slot)}:${at.side}${shared ? '' : `:${c.swarm}`}`;
      if (!groups.has(key)) groups.set(key, { slot: at.slot, side: at.side, cards: [] });
      groups.get(key)!.cards.push(c);
    }
    for (const c of loose) if (c.scale) place(c, c.x, c.z, 1);
    for (const { slot, side, cards } of groups.values()) {
      const cx = slot.x * side, cz = slot.z * side;
      if (cards.length === 1) {
        const c = cards[0];
        if (c.scale) place(c, cx, cz, 1); // on its own again: full size, in the middle
        continue;
      }
      const n = cards.length;
      const cols = n <= 2 ? 2 : n <= 4 ? 2 : n <= 9 ? 3 : 4;
      const rows = Math.ceil(n / cols);
      const size = n <= 4 ? L.two : Math.min((slot.width - L.gap * (cols - 1)) / cols / CONFIG.card.width, (slot.depth - L.gap * (rows - 1)) / rows / CONFIG.card.height);
      const cellW = CONFIG.card.width * size + L.gap, cellD = CONFIG.card.height * size + L.gap;
      // the ones already laid out keep their places (row by row, left to right as their owner sees it); newcomers go last
      const rank = (c: FieldCard) => {
        // (an Undead Legion's General stands behind its Soldiers: two in front, the General behind, a triangle point-down)
        if (this.isGeneral(c) && cards.some((m) => m.legion === c.id)) return 2e9;
        const fx = (c.x - cx) * side, fz = -(c.z - cz) * side * (cards.some((g) => this.isGeneral(g) && cards.some((m) => m.legion === g.id)) ? -1 : 1); // left → right, row by row
        return c.scale ? Math.round(fz / (cellD / 2)) * 1000 + fx : 1e9 + c.layer;
      };
      cards.sort((a, b) => rank(a) - rank(b));
      // (rows fill from the back, nearest the owner; a Legion's fill from the front, so its General, last, stands behind)
      const legionLane = cards.some((c) => this.isGeneral(c) && cards.some((m) => m.legion === c.id));
      cards.forEach((c, i) => {
        const col = i % cols, row = Math.floor(i / cols);
        const inRow = Math.min(cols, n - row * cols);
        const fx = (col - (inRow - 1) / 2) * cellW;
        const fz = (row - (rows - 1) / 2) * cellD * (legionLane ? -1 : 1);
        // a tapped card lies sideways: it is shrunk a little more to fit its place
        const sc = c.rot % 2 === 1 ? size * (CONFIG.card.width / CONFIG.card.height) : size;
        place(c, cx + fx * side, cz - fz * side, sc);
      });
    }
    // attached summoned units: in a row along the bottom edge of their card (the edge nearest its owner)
    const parents = new Map<string, FieldCard[]>();
    for (const c of s.field) if (c.attachedTo) (parents.get(c.attachedTo) ?? parents.set(c.attachedTo, []).get(c.attachedTo)!).push(c);
    for (const [pid, kids] of parents) {
      const parent = this.findField(pid)!;
      kids.sort((a, b) => (a.attachOrder ?? 0) - (b.attachOrder ?? 0));
      const side = sideOf(parent.owner);
      const { halfW, halfD } = cardHalf(parent);
      const sc = L.attachScale * (parent.scale ?? 1);
      const kidW = cardHalf({ rot: kids[0].rot, scale: sc }).halfW * 2;
      const step = kids.length > 1 ? Math.min(kidW + 0.15, (halfW * 2 - kidW) / (kids.length - 1)) : 0;
      kids.forEach((k, i) => {
        place(k, parent.x + (i - (kids.length - 1) / 2) * step * side, parent.z + halfD * side, sc);
        if (k.layer <= parent.layer) {
          k.layer = s.nextLayer++; // always on top of its card
        }
      });
    }
    // a token is the size of the card it sits on
    for (const t of s.tokens) {
      const sc = owner.get(t)?.scale;
      if (sc && sc !== 1) t.scale = sc;
      else delete t.scale;
    }
  }

  /**
   * The table plays the rules itself, so in a game (anything but the solo table) a player can't do by hand what only
   * the rules or a card effect may do: change, copy, move or delete counters and tokens; move, turn or flip cards
   * once they are played (a clone may still be moved to another lane); draw, search, shuffle or take from their
   * piles; or put a card from their hand into their discard pile outside their own Overdraw Phase. Bots, which
   * tidy up after themselves this way, are let through.
   */
  /** A card effect is doing this through an ordinary action (Spectral Clone making its clone): not a player's hand. */
  private byEffect = false;
  private byHandRefusal(seat: Seat, a: Action): string | null {
    const s = this.state;
    if (!CONFIG.lockHandEdits || s.mode === 'solo' || s.bots?.includes(seat) || this.byEffect) return null;
    const TOKENS = 'Counters and tokens can’t be changed by hand in a game: the table keeps them itself.';
    switch (a.type) {
      case 'tokenValue': case 'tokenTurns': case 'tokenActAt': case 'tokenDrain': case 'cloneToken': case 'tokenFlow': case 'linkHalf': case 'lockPrice':
        return TOKENS;
      case 'move': {
        const card = this.findField(a.id);
        if (!card) return s.tokens.some((t) => t.id === a.id) ? 'Counters and tokens can’t be moved by hand in a game: the table puts them where they go.' : null;
        if (card.clone) return null; // (a clone may be moved to another lane)
        return 'Cards can’t be moved by hand once they are played: only a card effect can move them.';
      }
      case 'moveMany':
        return a.items.some((it) => !this.findField(it.id)?.clone) ? 'Cards and counters can’t be moved by hand in a game: only a card effect can move them.' : null;
      case 'tap':
        return a.as ? null : 'Cards can’t be turned by hand in a game: they turn when they attack or use their abilities.';
      case 'flip':
        return 'Cards can’t be flipped by hand in a game: only a card effect can flip them.';
      case 'draw':
        return a.pay ? null : 'You can’t draw by hand in a game: cards that draw do it for you (an Overdraw is the only draw you make yourself).';
      case 'clone':
        return 'Clones are made by the cards that make them, when they are played.';
      case 'searchStart': case 'takeDiscard': case 'pileMove': case 'shuffle': case 'peekAsk': case 'deckOut':
        return 'Your piles can’t be searched, shuffled or drawn from by hand in a game: a card that lets you does it for you.';
      case 'toHand':
        return 'Cards can’t be taken back into your hand by hand in a game: only a card effect can return them.';
      case 'toPile': {
        if (this.findField(a.id)) return a.pile === 'deck' ? 'Cards can’t be put into your deck by hand in a game.' : null;
        if (!s.players[seat].hand.some((c) => c.id === a.id)) return null;
        if (a.pile !== 'discard') return 'Cards from your hand can’t be put back into your deck by hand in a game.';
        return s.turn === seat && s.phase === 0 ? null : 'You can only discard cards from your hand during your own Overdraw Phase.';
      }
    }
    return null;
  }

  private run(seat: Seat, p: PlayerState, a: Action): string | null {
    const s = this.state;
    const byHand = this.byHandRefusal(seat, a);
    if (byHand) return byHand;
    switch (a.type) {
      case 'draw': {
        if (p.overflow) return 'First decide what to do with the card you just drew.';
        if (p.hand.length >= CONFIG.handLimit) {
          // an Effect Draw with a full hand: the card is drawn, but it can't stay in the hand
          if (!a.overflow || a.pay || !CONFIG.turn.effectDrawOverflow.enabled) return `Your hand is full (${CONFIG.handLimit} cards), so you can’t draw.`;
          if (!this.canDraw(p)) return 'Your deck is empty.';
          p.overflow = this.rehide(this.drawTop(p)!);
          this.log(`${p.name} drew a card from an effect with a full hand: it can’t stay in their hand.`, seat);
          return null;
        }
        if (!this.canDraw(p)) return 'Your deck is empty.';
        const cost = CONFIG.turn.overdrawCost;
        if (a.pay && cost > 0) {
          // an Overdraw: only in your own Overdraw Phase, and it costs Gerzite off your bar
          if (s.turn !== seat || s.phase !== 0) return 'You can only Overdraw in your own Overdraw Phase.';
          const mine = this.barTokens(seat, 'gerzite');
          if (mine.length < cost) return `An Overdraw costs ${cost} Gerzite, and you don’t have that on your bar.`;
          this.removeTokens(mine.slice(-cost));
        }
        const drawn = this.drawTop(p)!;
        p.hand.push(this.rehide(drawn));
        this.fx('toHand', '', undefined, { effect: 'deck', seat, count: 1 }); // both players see the card fly to the hand
        if (s.turn === seat) this.drew(seat, drawn.num);
        this.log(a.pay && cost > 0 ? `${p.name} paid ${cost} Gerzite to Overdraw a card.` : `${p.name} drew a card.`, seat);
        if (a.pay && cost > 0) this.springTriggers(seat, 'overdraw');
        return null;
      }

      case 'overflowChoice': {
        const card = p.overflow;
        if (!card) return 'There is no drawn card waiting for a choice.';
        const O = CONFIG.turn.effectDrawOverflow;
        p.overflow = null;
        if (a.to === 'discard') {
          const gerzite = CONFIG.tokenRules.fractalGives;
          this.insertAt(p.discard, this.rehide(card), 'top');
          const got = this.gain(seat, gerzite, O.discardGerzite);
          this.log(`${p.name} discarded the card they drew, ${this.cardName(card.num)}${O.discardGerzite > 0 ? `, and gained ${this.gainText(gerzite, O.discardGerzite, got)}` : ''}.`, seat);
        } else {
          this.insertAt(p.deck, this.rehide(card), 'bottom');
          const n = O.bottomFractals;
          this.log(`${p.name} put the card they drew on the bottom of their deck${n > 0 ? ` and gained ${n} Fractal${n === 1 ? '' : 's'}` : ''}.`, seat);
          this.addFractals(seat, n);
        }
        return null;
      }

      case 'play': {
        const i = p.hand.findIndex((c) => c.id === a.id);
        if (i < 0) return 'That card isn’t in your hand anymore.';
        const def = this.cards.byNumber.get(p.hand[i].num);
        if (p.span) return this.spanWaiting(p.span);
        const barred = a.faceDown ? null : this.playBarred(seat, def, !!a.free);
        if (barred) return barred;
        const shut = this.shutBy(def, a.x, a.z);
        if (shut) return shut;
        // (Mystwing: Support cards may go in your Spell slots)
        const slotDef = def && this.supportInSpell(seat) && def.types.some((t) => ['biome', 'tome', 'trigger'].includes(t)) ? { ...def, extraSlots: [...def.extraSlots, 'spell'] } : def;
        const land = landing(slotDef, this.clampToTable(a.x, a.z), this.placed(), this.wideLanes());
        if (land.refusal) return land.refusal;
        const pos = land.at;
        // a Lanespanner played face-up: this one is the middle unit, in an empty lane, with room either side
        const chosenForm = def?.forms ? Math.max(0, Math.min(def.forms.length - 1, Math.round(a.form ?? 0))) : 0;
        const spreads = !a.faceDown && isLanespanner(def, chosenForm, !!a.spread);
        const units = def ? (formOf(def, chosenForm).count ?? 1) : 1;
        let spanFrom: number[] = [];
        if (spreads) {
          const into = slotAt(pos);
          if (!into || slotKind(into.slot.name) !== 'Lane') return `${def!.name} is a Lanespanner: play it into a lane (it can’t go in the Minion slot).`;
          spanFrom = spanStarts(units, laneOrder().indexOf(into.slot));
          // more units than the table has lanes on a side (Flesh-Eating Locusts on the 2v2 table): it goes in a middle
          // lane and fills every lane, and a unit that would go off the end joins the one in the end lane (like
          // Undead Legion's soldiers)
          const wide = units > laneOrder().length;
          if (wide) {
            const mids = middleLanes();
            if (!mids.includes(into.slot)) return `${def!.name} spans ${units} lanes, more than the ${laneOrder().length} on this side: play it in ${mids.map((l) => l.name).join(' or ')}, and the unit that would go off the end joins the one in the end lane.`;
            spanFrom = [laneOrder().indexOf(into.slot) - Math.floor(units / 2)];
          }
          if (!spanFrom.length) {
            const ok = laneOrder().filter((_, li) => spanStarts(units, li).length).map((l) => l.name);
            return `${def!.name} spans ${units} lanes with this one in the middle: play it in ${ok.length > 1 ? `${ok.slice(0, -1).join(', ')} or ${ok.at(-1)}` : ok[0]}.`;
          }
        }
        // Undead Legion: its General is only played in the middle lane (its soldiers spread out from there); on the
        // 2v2 table, with an even number of lanes, either of the middle two
        if (!a.faceDown && def?.effects.some((e) => e.words[0] === 'legion')) {
          const mids = middleLanes();
          const at0 = slotAt(pos);
          if (!at0 || !mids.includes(at0.slot)) return `${def.name} can only be played in the middle lane${mids.length > 1 ? 's' : ''} (${mids.map((l) => l.name).join(' or ')}).`;
        }
        // in a two-player game your cards go into your own slots, not your opponent's (CONFIG.playOwnSide)
        const into = slotAt(pos);
        if (CONFIG.playOwnSide && s.mode !== 'solo' && into && !CONFIG.sharedSlots.includes(into.slot.name) && into.side !== sideOf(seat)) {
          return 'You can only play cards into the slots on your own side of the mat.';
        }
        // 2v2: your own half of your side (your two lanes, your Emperor, Minion, Support and Spell slots). Once your
        // teammate is out, their two lanes are yours too (their Support and Spell slots are not)
        const halfNo = this.halfRefusal(seat, pos, def);
        if (halfNo) return halfNo;
        const outOfTurn = this.playWindow(seat, def, !!a.free || p.freeUnit?.id === a.id);
        if (outOfTurn) return outOfTurn;
        if (CONFIG.combat.enabled && def?.types.includes('spell') && !a.free && !a.faceDown) {
          // no Spells on your first turn; and Arcane Dome: no more than so many while an opponent's is in play
          if (CONFIG.combat.firstTurnSpells === false && p.turns === 0) return 'No Spells may be cast on your first turn.';
          const cap = s.field.filter((c) => c.faceUp && this.opposed(c.owner, seat)).flatMap((c) => (this.cards.byNumber.get(c.num)?.effects.length ? this.effectsOf(c, 'always') : [])).filter((e) => e.words[0] === 'spell_limit').map((e) => Number(e.words[1]) || 1);
          const cast = p.playedNow?.stamp === this.turnStamp() ? p.playedNow.types.filter((t) => t === 'spell').length : 0;
          if (cap.length && cast >= Math.min(...cap)) return `You can’t cast more than ${Math.min(...cap)} Spell${Math.min(...cap) === 1 ? '' : 's'} this turn (your opponent’s Spell forbids it).`;
        }
        // The Remaining: only while Realm Beyond Comprehension is the active Biome (a free play doesn't get round it)
        if (def?.whileInPlay.includes('needs_gate')) {
          const gate = s.field.some((c) => c.faceUp && !!this.cards.byNumber.get(c.num)?.whileInPlay.includes('remaining_gate') && !this.isExpired(c));
          const gateName = this.cards.list.find((c) => c.whileInPlay.includes('remaining_gate'))?.name ?? 'its Biome';
          if (!gate) return `${def.name} can only be played while ${gateName} is the active Biome.`;
        }
        // A Biome goes where a Biome already is: the old one has to be removed first, at its removal cost
        // (nothing, if its turns are over). The player agrees to that by naming it in `replace`.
        const landsIn = slotAt(pos);
        const inTheWay = def?.types.some((t) => CONFIG.turn.removable.includes(t))
          ? s.field.find((c) => c.faceUp && this.removalOf(c) && slotAt(c)?.slot === landsIn?.slot && !!landsIn)
          : undefined;
        const removal = inTheWay ? this.removalOf(inTheWay)! : null;
        if (inTheWay && removal && a.replace !== inTheWay.id) {
          return removal.expired
            ? `${this.cardName(inTheWay.num)} is still lying there, though its turns are over: clear it away first.`
            : `${this.cardName(inTheWay.num)} is in play: its removal cost (${removal.cost} ${removal.dark ? 'Dark Gerzite' : 'Gerzite'}) has to be paid before another can take its place.`;
        }
        // playing a card costs its Gerzite, taken off your bar (unless it is played free).
        // A Darkgale summon is paid in Dark Gerzite instead, and some card types pay more that way (Spells: double).
        const darkToken = a.dark ? this.tokenDb.list.find((t) => t.effect === CONFIG.turn.darkgale.token) : undefined;
        if (a.dark && !darkToken) return 'Darkgale summons are switched off.';
        // Some cards are summoned with Dark Gerzite by nature (cost_type = dark in cards.csv). That is their
        // ordinary cost, not a Darkgale summon: no Darkgale token, no doubling, and they can't be Darkgaled on top.
        // A card with two costs (3/7) is summoned at the one the player picked; a cost formula is worked out now.
        const form = def?.forms ? Math.max(0, Math.min(def.forms.length - 1, Math.round(a.form ?? 0))) : 0;
        let base = def ? Math.max(0, formOf(def, form).cost ?? 0) : 0;
        let copiesDark = false; // "last_card + 1": paid in whatever that card was paid in
        // The Unit a "unit + 1" card already paid for (Tribunal Trigger): it goes down face-down, for nothing.
        const afterLog: string[] = []; // what the card set off, said after the line about the card itself
        const pledged = p.freeUnit?.id === a.id ? p.freeUnit : null;
        const free = !!a.free || !!pledged;
        const laidFaceDown = a.faceDown || !!pledged;
        if (pledged) {
          if (a.dark) return `${def?.name ?? 'This card'} is being played for free by ${pledged.by}, so it can’t be a Darkgale summon.`;
        }
        const facts = this.costFacts(seat);
        const lastBefore = this.opponentsLast(seat); // read now: on the solo table this play replaces it
        if (def?.costFormula && CONFIG.turn.payToPlay && !free) {
          const picked = a.unit ? p.hand.find((c) => c.id === a.unit && c.id !== a.id) : undefined;
          const pickedDef = picked ? this.cards.byNumber.get(picked.num) : undefined;
          if (a.unit && !(pickedDef && (pickedDef.types.includes('troop') || pickedDef.types.includes('building')))) return 'Pick a Troop or Building from your hand: its cost sets this card’s cost.';
          const price = priceOf(def.costFormula, facts, a.choice, pickedDef ? Math.max(0, pickedDef.cost ?? 0) : null);
          if (typeof price === 'string') return price;
          base = price;
          copiesDark = def.costFormula.uses.includes('last_card') && facts.lastDark;
        }
        const natural = !!def?.costDark || copiesDark;
        if (a.dark && natural) return `${def!.name} is already summoned with Dark Gerzite, so it can’t be a Darkgale summon.`;
        // what the cards in play, and any discount held, change it by (Arcane Library, Oasis Nymph's Tap…)
        if (CONFIG.turn.payToPlay && !free && !laidFaceDown) base = Math.max(0, base + this.costChange(seat, def));
        const times = darkToken ? darkgaleMultiplier(def) : 1;
        const cost = CONFIG.turn.payToPlay && !free ? base * times : 0;
        const coin = darkToken || natural ? 'Dark Gerzite' : 'Gerzite';
        // A cost the bar could never hold can never be paid. This is what keeps the dearest cards from being
        // Darkgale summons (the Dark Gerzite bar holds 10: no Spell of 6 or more, no Troop of 11 or more), and
        // a copying card from copying something that was paid for with a full bar.
        const holds = darkToken || natural ? CONFIG.zones.darkGerziteBar.slots : CONFIG.zones.gerziteBar.slots;
        if (cost > holds) {
          if (darkToken) return `${def!.name} would cost ${cost} Dark Gerzite as a Darkgale summon${times > 1 ? ` (${times} times its cost)` : ''}, more than the Dark Gerzite bar holds (${holds}): it can’t be a Darkgale summon.`;
          if (def?.costFormula?.uses.includes('last_card')) return `${def.name} would cost ${cost} ${coin} to copy ${facts.lastName}, more than your ${coin} bar holds (${holds}): that card can’t be copied.`;
          return `${def!.name} would cost ${cost} ${coin}, more than your ${coin} bar holds (${holds}).`;
        }
        if (inTheWay && removal) {
          // both have to be affordable before anything is taken: the old Biome's removal, then this card's own cost
          const sameBar = removal.dark === !!(darkToken || natural);
          const have = this.barTokens(seat, removal.dark ? 'dark' : 'gerzite').length;
          const rCoin = removal.dark ? 'Dark Gerzite' : 'Gerzite';
          if (have < removal.cost + (sameBar ? cost : 0)) {
            return sameBar && cost > 0
              ? `Removing ${this.cardName(inTheWay.num)} costs ${removal.cost} ${rCoin} and ${def!.name} costs ${cost}: that is ${removal.cost + cost}, and you have ${have}.`
              : `Removing ${this.cardName(inTheWay.num)} costs ${removal.cost} ${rCoin} and you have ${have}.`;
          }
          if (!sameBar && this.barTokens(seat, darkToken || natural ? 'dark' : 'gerzite').length < cost) return `${def!.name} costs ${cost} ${coin} and you have ${this.barTokens(seat, darkToken || natural ? 'dark' : 'gerzite').length}.`;
          this.payBar(seat, removal.cost, removal.dark);
          const gone = this.cardName(inTheWay.num);
          const whose = inTheWay.owner === seat ? 'their own' : `${s.players[inTheWay.owner].name}’s`;
          this.discardFromPlay(inTheWay);
          this.log(removal.expired ? `${p.name} cleared away ${gone}, whose turns were over.` : `${p.name} paid ${removal.cost} ${rCoin} to remove ${whose} ${gone} from play.`, seat);
        }
        if (cost > 0) {
          if ((darkToken || natural) && this.darkHeld(seat, cost)) return this.darkHeld(seat, cost);
          const mine = this.barTokens(seat, darkToken || natural ? 'dark' : 'gerzite');
          if (mine.length < cost) {
            return darkToken
              ? `${def!.name} costs ${cost} Dark Gerzite as a Darkgale summon${times > 1 ? ` (${times} times its cost)` : ''} and you have ${mine.length}.`
              : `${def!.name} costs ${cost} ${coin} and you have ${mine.length}. (Hold Ctrl, or ⌘ on a Mac, as you drop it to play it for free.)`;
          }
          this.removeTokens(mine.slice(-cost));
          if (darkToken || natural) this.spentDark(seat, cost);
        }
        const paid = cost > 0 ? ` for ${cost} ${coin}` : pledged ? `, the Unit chosen for ${pledged.by}, for free` : CONFIG.turn.payToPlay && free && (base > 0 || !!def?.costFormula) ? ' for free' : '';
        const [card] = p.hand.splice(i, 1);
        // Triggers (CONFIG.arrival.faceDownTypes) always go down face-down, Shift or not.
        const faceDown = laidFaceDown || arrivesFaceDown(def);
        // Troops and buildings arrive Entrenched (turned to face the opponent), or facing their
        // owner and ready to go if they have Ambush. Every other card type arrives facing its
        // owner. So do face-down cards, whatever they are, so their position gives nothing away.
        const types = CONFIG.arrival.types;
        const applies = !faceDown && !!def && (types === 'all' || def.types.some((t) => types.includes(t))) && !def.whileInPlay.includes('untouchable'); // (an untouchable card carries no statuses: it never arrives Entrenched)
        const ambush = applies && def.arrival === 'ambush';
        const rot = sitsSideways(def) ? 1 : !applies ? 0 : ambush ? CONFIG.arrival.ambushRotation : CONFIG.arrival.entrenchedRotation;
        s.field.push({ ...card, owner: seat, ...pos, rot, faceUp: !faceDown, layer: s.nextLayer++, merged: [], tierToken: null, ...(form ? { form } : {}) });
        if (!p.played.includes(card.num)) p.played.push(card.num);
        // what this card cost, for a card that copies it later: the amount that came off the bar (a Darkgale
        // Spell: double), and which bar. A card played face-down tells nobody what it cost, so it doesn't count.
        this.gifts = [];
        this.lastPaid = cost;
        this.lastChoice = def?.costFormula?.choice && !free && a.choice !== undefined ? Math.max(0, Math.round(a.choice)) : null;
        this.discountFrom = null;
        if (!faceDown && CONFIG.turn.payToPlay && !free) this.costChange(seat, def, true); // a discount that was used is spent
        const placed = this.findField(card.id);
        // a discount from a card with a beam of its own (Occult Sorcerer): the beam runs from it to the card played for less
        const giver = this.discountFrom ? this.findField(this.discountFrom) : undefined;
        if (placed && giver && giver.faceUp && CONFIG.looks.discountBeam[this.cardName(giver.num)]) this.look(CONFIG.looks.discountBeam[this.cardName(giver.num)], giver, { target: placed.id, at: { x: placed.x, z: placed.z } }); // (at: where it landed, for a Spell that is already gone by the time the table shows it)
        if (placed) placed.playedTurn = p.turns;
        if (placed) placed.paidFor = cost;
        // (Gerzite Dagger, Crystalline Discharge: the Gerzite paid flies off the bar into the card, which glows brighter the more it took)
        if (placed && cost > 0 && CONFIG.looks.enabled && CONFIG.looks.gerziteCharge.includes(def?.name ?? '')) this.fx('look', placed.id, undefined, { effect: 'gz-charge', seat, value: cost });
        // the Unit Tribunal Trigger paid for, laid face-down: it waits for a card of its cost
        if (placed && pledged) { const trib = [...s.field].reverse().find((c) => c.faceUp && c.owner === seat && this.cardName(c.num) === pledged.by && !this.isExpired(c)); if (trib) { placed.tribunalOf = trib.id; trib.host = placed.id; } }
        // Tribunal Trigger: a card of the waiting Unit's cost played by an opponent turns it face-up; its caster gets half of what they paid back
        if (placed && !laidFaceDown) for (const u of [...s.field]) {
          if (!u.tribunalOf || u.faceUp || !this.opposed(u.owner, seat)) continue;
          const uDef = this.cards.byNumber.get(u.num);
          if (!uDef || (uDef.cost ?? -1) !== (def?.cost ?? -2)) continue;
          const trib = this.findField(u.tribunalOf);
          u.faceUp = true; delete u.tribunalOf; u.rot = 0;
          this.fx('reveal', u.id);
          this.countOnReveal(u);
          this.look('trigger', u); // (it springs like a Trigger)
          const back = Math.ceil((trib?.paidFor ?? 0) / 2);
          if (back > 0) this.gain(u.owner, CONFIG.tokenRules.fractalGives, back);
          afterLog.push(`${def!.name} costs ${def!.cost}: ${s.players[u.owner].name}’s ${uDef.name} is turned face-up by ${trib ? this.cardName(trib.num) : 'Tribunal Trigger'}${back ? `, and they get ${back} Gerzite back` : ''}.`);
          if (trib) this.discardFromPlay(trib);
        }
        if (placed && this.gifts.length) {
          for (let k = 0; k < this.gifts.length; k++) {
            const g = this.gifts[k];
            if (g === 'ambush') this.giveAmbush(placed);
            else if (!/^\d+$/.test(g)) this.giveStatus(seat, placed, g, /^\d+$/.test(this.gifts[k + 1] ?? '') ? Number(this.gifts[k + 1]) : null, null);
          }
          this.gifts = [];
        }
        if (!faceDown && def) {
          const stamp = this.turnStamp();
          if (p.playedNow?.stamp !== stamp) p.playedNow = { stamp, types: [] };
          p.playedNow.types.push(...def.types);
        }
        if (!faceDown) s.lastPlayed[seat] = { num: card.num, cost: base * times, dark: natural || !!darkToken, at: s.version };
        if (!faceDown) this.fx('play', card.id);
        // a card that would have cost something, played for free: a small beacon rises from it for both players
        if (free && CONFIG.turn.payToPlay && (base > 0 || !!def?.costFormula || !!pledged)) this.fx('freePlay', card.id);
        if (ambush) this.fx('ambush', card.id);
        this.enterSlot(card.id, null, pos);
        if (applies) {
          // mark it with its Entrenched or Ambush token (top-left corner; the tier token uses the top-right)
          const marker = this.tokenDb.list.find(
            (t) => t.effect === (ambush ? CONFIG.tokenRules.ambushToken : CONFIG.tokenRules.entrenchedToken),
          );
          const side = sideOf(seat);
          if (marker) {
            s.tokens.push({
              id: this.newId('t'),
              tokenId: marker.id,
              x: pos.x - 1.7 * side,
              z: pos.z - 2.6 * side,
              value: !ambush && def.entrenchedTurns > 1 ? def.entrenchedTurns : marker.start, // some cards dig in for longer
              layer: s.nextLayer++,
              by: seat,
              since: p.turns,
            });
          }
        }
        this.addCount(s.field.at(-1)!, spreads);
        this.addStartCounters(s.field.at(-1)!);
        this.addTimer(s.field.at(-1)!);
        this.bring(s.field.at(-1)!);
        if (!faceDown) this.summonUnits(s.field.at(-1)!, 'arrive'); // summoned units it arrives with, attached to it (Zombie Knight's Horse)
        if (spreads) {
          // its Lanespanner token, then (once its marks are on it) the rest of its units: placed at once if there
          // is only one way to spread, otherwise its owner is asked where
          this.spanToken(s.field.at(-1)!, card.id);
          p.span = { card: card.id, starts: spanFrom };
        }
        if (darkToken) {
          // the Darkgale token goes on its bottom-left corner (Entrenched and Ambush use the top-left, tiers the top-right)
          const side = sideOf(seat);
          s.tokens.push({ id: this.newId('t'), tokenId: darkToken.id, x: pos.x - 1.7 * side, z: pos.z + 2.6 * side, value: darkToken.start, layer: s.nextLayer++, by: seat, since: p.turns });
          this.fx('slot', card.id, undefined, { effect: 'darkgale' });
        }
        // what the card's on_play cell asks for (face-up only: a card laid face-down hasn't been cast)
        if (!faceDown && def?.onPlay.includes('no_end_gerzite')) p.noEndGerzite = def.name;
        if (!faceDown) {
          const placed = s.field.find((c) => c.id === card.id);
          if (placed) this.laneMark(placed, 'arrive'); // Drifts Tarragon
        }
        if (!faceDown && def?.onPlay.includes('coin_gerzite')) {
          // a coin flip, made with a die: its caster rolls before they can move on
          const placed = s.field.find((c) => c.id === card.id);
          if (placed) {
            placed.roll = seat;
            placed.rollFor = 'coin';
            afterLog.push(`${def.name}: ${p.name} rolls a die for the coin flip. Even is heads (${p.name} gains ${CONFIG.coinGerzite.heads} Gerzite), odd is tails (their opponent gains ${CONFIG.coinGerzite.tails}).`);
          }
        }
        if (!faceDown && def?.onPlay.includes('discard_recycle')) {
          const placed = s.field.find((c) => c.id === card.id);
          if (placed) afterLog.push(this.discardRecycle(seat, placed));
        }
        // (The Remaining and Calling Pillars can't be copied by any card effect)
        if (!faceDown && def?.onPlay.includes('copy_last_card') && lastBefore && this.uncloneable(lastBefore.num)) afterLog.push(`${def.name} can’t copy ${this.cardName(lastBefore.num)}: it can’t be cloned or copied by card effects.`);
        if (!faceDown && def?.onPlay.includes('copy_last_card') && lastBefore && this.cards.byNumber.has(lastBefore.num) && !this.uncloneable(lastBefore.num)) {
          // the copy comes in like a card from the Spawn menu: on your side, for you to put in its slot
          const copyDef = this.cards.byNumber.get(lastBefore.num)!;
          s.field.push({ ...this.held(lastBefore.num, true), owner: seat, ...this.spawnSpot(seat, undefined, { halfW: (sitsSideways(copyDef) ? CONFIG.card.height : CONFIG.card.width) / 2, halfD: (sitsSideways(copyDef) ? CONFIG.card.width : CONFIG.card.height) / 2, card: true }), rot: sitsSideways(copyDef) ? 1 : 0, faceUp: true, layer: s.nextLayer++, merged: [], tierToken: null, clone: { by: def.name } });
          this.addCount(s.field.at(-1)!);
        this.addStartCounters(s.field.at(-1)!);
          this.addTimer(s.field.at(-1)!);
          this.bring(s.field.at(-1)!);
          this.fx('play', s.field.at(-1)!.id);
          afterLog.push(`${def.name} copies ${copyDef.name}: a copy is spawned on ${p.name}’s side of the table.`);
        }
        // the secret rares' Entrances: Calling Pillar hurts its lane, The Remaining everything else on the table
        if (!faceDown && def?.onPlay.includes('lane_damage')) afterLog.push(this.laneDamage(this.findField(card.id)!));
        if (!faceDown && def?.onPlay.includes('field_wipe')) afterLog.push(this.fieldWipe(this.findField(card.id)!));
        if (!faceDown && def && this.findField(card.id)) {
          const here = this.findField(card.id)!;
          if (this.isUnit(def)) {
            this.fireAll('any_summon', (c) => c !== here, { it: here, actor: seat });
            this.fireAll('ally_summon', (c) => c !== here && this.allies(c.owner, seat), { it: here, actor: seat });
            this.fireAll('foe_summon', (c) => c !== here && this.opposed(c.owner, seat), { it: here, actor: seat });
          }
          // the opponent's Triggers that wait for this: a card of this kind, of this affiliation
          if (def.types.includes('spell')) this.fireAll('ally_spell', (c) => c !== here && this.allies(c.owner, seat), { it: here, actor: seat });
          for (const ty of ['biome', 'spell', 'tome']) if (def.types.includes(ty)) this.springTriggers(seat, ty);
          if (this.isUnit(def)) this.springTriggers(seat, 'summon');
          for (const a of def.affiliations) this.springTriggers(seat, 'aff', a);
        }
        // Ashy Wasteland: a Spell has a 1 in 6 chance to be discarded as it is cast (its cost is still paid)
        if (!faceDown && def?.types.includes('spell') && this.findField(card.id) && this.ashActive()) {
          const rolled = this.rollDie();
          this.throwDie(seat, this.besideCard(this.findField(card.id)!), rolled);
          if (rolled === 1) {
            this.discardFromPlay(this.findField(card.id)!);
            afterLog.push(`Ashy Wasteland: ${p.name} rolled a 1, and ${def.name} crumbles to ash: it is discarded with no effect.`);
          } else afterLog.push(`Ashy Wasteland: ${p.name} rolled a ${rolled}; ${def.name} holds.`);
        }
        // a Biome put into the Biome slot sends an Eldritch Realm Crosser that is the Biome back (it is removed as its text says)
        if (def?.types.includes('biome') && !faceDown) for (const c of [...s.field]) if (c.asBiome && c.id !== card.id) this.discardFromPlay(c);
        // Reflection: an opponent's Spell cast during its turn is turned back: its holder makes its choices, and it works against its caster
        if (!faceDown && def?.types.includes('spell') && this.findField(card.id) && CONFIG.combat.enabled) {
          const mirror = s.field.find((c) => c.faceUp && this.opposed(c.owner, seat) && !this.isExpired(c) && !!this.cards.byNumber.get(c.num)?.effects.length && this.effectsOf(c, 'always').some((e) => e.words[0] === 'reflect'));
          if (mirror) {
            const here = this.findField(card.id)!;
            here.reflectedFrom = seat;
            here.owner = mirror.owner;
            here.sideAs = sideOf(mirror.owner);
            this.fx('status', mirror.id, undefined, { effect: 'notice' });
            // it bounces off the Reflection and back at its caster, like a Spell off the Great Hay Golem
            this.look('golem-reflect', mirror);
            const back = this.emperorOf(seat);
            if (back) this.fx('spell', mirror.id, undefined, { target: back.id, from: { x: mirror.x, z: mirror.z }, tint: this.damageAffOf(here)[0] ?? this.affOf(here)[0] ?? 'magical', seat: mirror.owner, effect: 'bounce' });
            afterLog.push(`${this.cardName(mirror.num)} turns ${def.name} back: ${s.players[mirror.owner].name} makes its choices, and it works against ${p.name}.`);
          }
        }
        // its Entrance, where the table can carry it out (the `effects` cell: "enter: …")
        if (!faceDown && def?.effects.some((e) => e.when === 'enter') && this.findField(card.id)) {
          this.fire(this.findField(card.id)!, 'enter');
          this.settleFallen();
        }
        // a "unit + 1" card marks the Unit it paid for: that card will go down face-down, for nothing
        if (!faceDown && a.unit && def?.costFormula?.uses.includes('unit') && p.hand.some((c) => c.id === a.unit)) {
          p.freeUnit = { id: a.unit, by: def.name };
          afterLog.push(`${p.name} chose a Unit in their hand to play face-down for free with ${def.name}.`);
        }
        const how = darkToken ? ' as a Darkgale summon' : '';
        this.log(
          faceDown
            ? `${p.name} played a face-down card${how}${paid}.`
            : `${p.name} played ${def ? typeWithArticle(def) : 'a card'}${ambush ? ' with Ambush' : applies && def.entrenchedTurns > 1 ? `, Entrenched for ${def.entrenchedTurns} turns,` : ''}${how}${paid}.`,
          seat,
        );
        for (const line of afterLog) this.log(line, seat);
        if (p.span?.card === card.id && p.span.starts.length === 1) this.placeSpan(seat, p.span.starts[0]);
        else if (p.span?.card === card.id) this.log(`${p.name} chooses where the rest of ${def!.name} goes.`, seat);
        return null;
      }

      case 'move': {
        const card = this.findField(a.id);
        const token = card ? undefined : s.tokens.find((t) => t.id === a.id);
        let snap = { x: 0, z: 0 }; // how far the card slid to settle into a slot (its tokens slide too)
        const cardBefore = card ? { x: card.x, z: card.z } : null;
        if (card) {
          if (!this.canControl(seat, card)) return 'That’s your opponent’s card.';
          const mateMove = this.mateHeld(seat, card);
          if (mateMove) return mateMove;
          if (card.heldBy) return `${this.cardName(card.num)} is held by ${this.cardName(this.findField(card.heldBy)?.num ?? card.num)}: it goes where that card goes.`;
          const before = { x: card.x, z: card.z };
          const dropped = this.clampToTable(a.x, a.z);
          const land = landing(this.slotDef(card), dropped, this.placed([card.id]), this.wideLanes());
          if (land.refusal) return land.refusal;
          // 2v2: a card can't be moved over into your teammate's half (their lanes once they are out)
          const fromSlot = slotAt(before);
          const toSlot = slotAt(land.at);
          if (s.mode !== 'solo' && toSlot && (toSlot.slot !== fromSlot?.slot || toSlot.side !== fromSlot?.side)) { const halfMove = this.halfRefusal(seat, land.at); if (halfMove) return halfMove; }
          // a unit that stretches across lanes (a Lanespanner's units, Undead Legion and its soldiers) never goes in the Minion slot
          if (toSlot && slotKind(toSlot.slot.name) === 'Minion' && fromSlot?.slot !== toSlot.slot && this.spansLanes(card)) return `${this.cardName(card.num)} stretches across lanes: it can’t go in the Minion slot.`;
          // (no Biome can be moved into the Biome slot while something forbids Biomes: Tectonic Shift)
          if (card.faceUp && slotAt(land.at)?.slot.name === 'Biome' && slotAt(cardBefore!)?.slot.name !== 'Biome') { const shut = this.shutBy(this.cards.byNumber.get(card.num), land.at.x, land.at.z); if (shut) return shut; }
          if (card.attachedTo) { delete card.attachedTo; delete card.attachOrder; } // dragged off the card it was attached to
          const rest = land.at;
          snap = { x: rest.x - dropped.x, z: rest.z - dropped.z };
          Object.assign(card, rest, { layer: s.nextLayer++ });
          this.enterSlot(card.id, before, card);
          this.movedLane(card, before);
          const tierToken = s.tokens.find((t) => t.id === card.tierToken);
          if (tierToken) {
            tierToken.x += card.x - before.x;
            tierToken.z += card.z - before.z;
            tierToken.layer = s.nextLayer++;
          }
        } else if (token) {
          const before = { x: token.x, z: token.z };
          const wasOn = this.cardUnder(token);
          Object.assign(token, this.clampToTable(a.x, a.z), { layer: s.nextLayer++ });
          // dropped on top of a Splash token: it lands on every unit in that lane instead
          const splash = this.splashUnder(token);
          if (splash) {
            const refusedSplash = this.splashOnto(seat, token, splash);
            if (refusedSplash) Object.assign(token, before);
            return refusedSplash;
          }
          const refused = this.landToken(seat, token);
          if (refused) {
            Object.assign(token, before);
            return refused;
          }
          if (s.tokens.includes(token)) {
            this.hit(token, wasOn, token.value);
            this.mirrorStatus(seat, token, wasOn);
          }
          if (wasOn && this.effectOf(token) === CONFIG.tokenRules.entrenchedToken && this.cardUnder(token)?.id !== wasOn.id) this.freed(wasOn); // lifted off its card
        } else return 'That piece isn’t on the table anymore.';
        for (const c of a.carry ?? []) {
          const t = s.tokens.find((tk) => tk.id === c.id);
          if (t) Object.assign(t, this.clampToTable(c.x + snap.x, c.z + snap.z), { layer: s.nextLayer++ });
        }
        if (card) this.closeUp(cardBefore!); // a Spell moved out of its row: the rest close up
        return null;
      }

      case 'moveMany': {
        // if any card of the group would land in a slot it isn't allowed in, the whole group stays put
        const staying = this.placed(a.items.map((it) => it.id));
        for (const it of a.items) {
          const card = this.findField(it.id);
          if (!card || !this.canControl(seat, card)) continue;
          const noEntry = slotRefusal(this.slotDef(card), this.clampToTable(it.x, it.z), staying, this.wideLanes());
          if (noEntry) return noEntry;
        }
        // cards first (their tier tokens follow), then tokens at the exact spots they were dropped
        for (const it of a.items) {
          const card = this.findField(it.id);
          if (!card || !this.canControl(seat, card)) continue;
          const before = { x: card.x, z: card.z };
          Object.assign(card, this.clampToTable(it.x, it.z), { layer: s.nextLayer++ });
          if (card.attachedTo && !a.items.some((o) => o.id === card.attachedTo)) { delete card.attachedTo; delete card.attachOrder; }
          this.enterSlot(card.id, before, card);
          this.movedLane(card, before);
          const tierToken = s.tokens.find((t) => t.id === card.tierToken);
          if (tierToken) {
            tierToken.x += card.x - before.x;
            tierToken.z += card.z - before.z;
            tierToken.layer = s.nextLayer++;
          }
        }
        // tokens that ride on one of the moved cards just go along; loose ones "land" where dropped
        const riders = new Set(a.items.filter((it) => this.findField(it.id)).flatMap((it) => this.tokensOn(this.findField(it.id)!).map((t) => t.id)));
        const landed = new Map<Token, { x: number; z: number; wasOn?: FieldCard }>();
        for (const it of a.items) {
          const t = s.tokens.find((tk) => tk.id === it.id);
          if (!t) continue;
          if (!riders.has(t.id)) landed.set(t, { x: t.x, z: t.z, wasOn: this.cardUnder(t) });
          Object.assign(t, this.clampToTable(it.x, it.z), { layer: s.nextLayer++ });
        }
        for (const [t, before] of landed) {
          if (!s.tokens.includes(t)) continue;
          if (this.landToken(seat, t)) Object.assign(t, { x: before.x, z: before.z }); // not allowed there: put it back
          else if (s.tokens.includes(t)) {
            this.hit(t, before.wasOn, t.value);
            this.mirrorStatus(seat, t, before.wasOn);
          }
          if (before.wasOn && this.effectOf(t) === CONFIG.tokenRules.entrenchedToken && this.cardUnder(t)?.id !== before.wasOn.id) this.freed(before.wasOn);
        }
        return null;
      }

      case 'deleteMany': {
        for (const id of a.ids) { const why = this.noDeleting(seat, id); if (why) return why; }
        for (const id of a.ids) {
          const ti = s.tokens.findIndex((t) => t.id === id);
          if (ti >= 0) {
            const on = this.effectOf(s.tokens[ti]) === CONFIG.tokenRules.entrenchedToken ? this.cardUnder(s.tokens[ti]) : undefined;
            s.tokens.splice(ti, 1);
            this.freed(on);
            continue;
          }
          const card = this.findField(id);
          if (card?.spawned) {
            const due = this.dueOnLeaving(card); // a summoned unit with destroyed_gains (Gerzite Crystallite)
            this.clearTokensOn(card);
            this.removeField(card.id);
            this.closeUp(card);
            this.destroyedGain(card, due);
          }
        }
        return null;
      }

      case 'tap': {
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, card)) return 'That’s your opponent’s card.';
        const mateTap = this.mateHeld(seat, card);
        if (mateTap) return mateTap;
        const awayTap = this.possessedAway(seat, card);
        if (awayTap) return awayTap;
        const tapLock = this.lockedBy(card);
        if (tapLock) return tapLock;
        if (this.sitsSideways(card)) return `${typeName(this.cards.byNumber.get(card.num)!.types.find((t) => CONFIG.arrival.sidewaysTypes.includes(t))!)} cards stay sideways and are never tapped. Flip one with F if something happens to it.`;
        // a Tap ability used while its card is already tapped, for a price (Wandering Spirit): it stays as it is
        if (CONFIG.combat.enabled && a.as === 'ability' && card.faceUp && card.rot % 2 === 1 && this.retapCost(card) !== null) {
          const whyNot = this.tapWhy(seat, card);
          if (whyNot) return whyNot;
          const cost = this.retapCost(card)!;
          if (!this.payBar(seat, cost, false)) return `Using it again costs ${cost} Gerzite.`;
          card.retapAt = this.turnKey();
          card.acted = this.turnKey();
          const d = this.cards.byNumber.get(card.num)!;
          this.log(`${p.name} pays ${cost} Gerzite to use ${d.name}’s Tap ability while it is tapped.`, seat);
          this.fire(card, 'tap');
          this.settleFallen();
          return null;
        }
        // Frenzy: its second action is its Tap again (or its Tap after an attack): it stays tapped and is used again
        if (CONFIG.combat.enabled && a.as === 'ability' && card.faceUp && card.acted === this.turnKey() && this.frenzyLeft(card) !== null) {
          const whyNot = this.tapWhy(seat, card);
          if (whyNot) return whyNot;
          this.frenzyTapSpend(card);
          const d = this.cards.byNumber.get(card.num)!;
          const text = `${p.name} uses ${d.name}’s Tap ability again (Frenzy). ${d.text}`;
          this.fx('status', card.id, undefined, { effect: 'notice', text });
          this.log(text, seat);
          this.fire(card, 'tap');
          this.settleFallen();
          return null;
        }
        // a card that has attacked or used its Tap this turn stays tapped until the turn is over
        if (CONFIG.combat.enabled && card.acted === this.turnKey() && card.rot % 2 === 1) return `${card.faceUp ? this.cardName(card.num) : 'That card'} has been used this turn: it stays tapped until the turn is over.`;
        if (a.as === 'ability') {
          const whyNot = CONFIG.combat.enabled ? this.tapWhy(seat, card) : null;
          if (whyNot) return whyNot;
        }
        const frenzyTap = CONFIG.combat.enabled && a.as === 'ability' && card.faceUp && this.frenzyLeft(card) !== null;
        const dug = this.turnCard(card, a.dir, a.as) ? [1] : [];
        if (frenzyTap) this.frenzyTapSpend(card); // (one of its two Frenzy actions)
        if (a.as === 'ability' && card.faceUp) {
          const d = this.cards.byNumber.get(card.num)!;
          const text = `${p.name} uses ${d.name}’s Tap ability. ${d.text}`;
          this.fx('status', card.id, undefined, { effect: 'notice', text });
          this.log(text, seat);
          this.fire(card, 'tap');
          this.settleFallen();
          if (dug.length) this.freed(card);
          return null;
        }
        const what = card.faceUp ? this.cardName(card.num) : 'a face-down card';
        this.log(`${p.name} tapped ${what} — now ${ROTATION_NAMES[card.rot]}.`, seat);
        if (dug.length) this.freed(card);
        return null;
      }

      case 'flip': {
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, card)) return 'That’s your opponent’s card.';
        const flipLock = this.lockedBy(card);
        if (flipLock) return flipLock;
        card.faceUp = !card.faceUp;
        if (card.faceUp) this.fx('reveal', card.id);
        if (card.faceUp) this.countOnReveal(card); // (a face-down Skell-Mateys turned up is 2 units)
        this.log(`${p.name} flipped ${this.cardName(card.num)} face-${card.faceUp ? 'up' : 'down'}.`, seat);
        return null;
      }

      case 'toHand': {
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, card)) return 'That’s your opponent’s card.';
        if (card.spanOf) return this.clearSpanUnit(card);
        const copies = this.copiesOf(card);
        if (p.hand.length + copies.length > CONFIG.handLimit) {
          return copies.length === 1
            ? `Your hand is full (${CONFIG.handLimit} cards).`
            : `Your hand doesn’t have room for all ${copies.length} merged copies (limit ${CONFIG.handLimit}).`;
        }
        this.clearTokensOn(card);
        this.removeField(card.id);
        this.closeUp(card);
        for (const c of copies) p.hand.push(this.rehide(c));
        const tierNote = copies.length > 1 ? ` (Tier ${roman(copies.length)}, ${copies.length} cards)` : '';
        this.log(
          card.faceUp
            ? `${p.name} returned ${this.cardName(card.num)}${tierNote} to their hand.`
            : `${p.name} returned ${copies.length > 1 ? `${copies.length} cards` : 'a card'} to their hand.`,
          seat,
        );
        return null;
      }

      case 'merge': {
        const target = this.findField(a.into);
        if (!target) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, target)) return 'That’s your opponent’s card.';
        if (!target.faceUp) return 'Flip that card face-up before merging into it.';
        const source = this.findField(a.id);
        const handIndex = source ? -1 : p.hand.findIndex((c) => c.id === a.id);
        if (!source && handIndex < 0) return 'That card isn’t available anymore.';
        if (source) {
          if (source.id === target.id) return 'A card can’t merge into itself.';
          if (!this.canControl(seat, source)) return 'That’s your opponent’s card.';
          if (!source.faceUp) return 'Flip that card face-up before merging it.';
        }
        const copies = source ? this.copiesOf(source) : [p.hand[handIndex]];
        const name = this.cardName(target.num);
        if (copies[0].num !== target.num) return 'Only copies of the same card can merge.';
        // a clone (Illusionist's copy, one from From the Mirror Realm…) isn't a real copy of the card: it can't tier up, or be tiered into
        if (target.clone || source?.clone) return 'A clone can’t be tiered up, or tier anything up.';
        if (source && (source.illusion === target.id || target.illusion === source.id)) return 'An Illusionist and its illusion can’t be merged.';
        if (source?.swarm) return 'A swarm can’t be merged into another card: play a copy from your hand onto it instead.';
        // a card can only be tiered up while it is unhurt and free of statuses (Entrenched and Ambush aside); a swarm,
        // only while every one of its units is still on the table, all of them unhurt and free of statuses
        const tDef0 = this.cards.byNumber.get(target.num);
        if (target.swarm && tDef0?.count && this.unitsLeft(target) < tDef0.count) return `${name} has lost ${tDef0.count - this.unitsLeft(target) === 1 ? 'a unit' : 'units'}: a swarm can only be tiered up while all of it is on the table.`;
        for (const c of [...this.swarmOf(target), ...(source ? [source] : [])]) {
          const why = this.tierBlock(c);
          if (why) return `${c === source ? 'That card' : target.swarm ? `One of ${name}` : name} ${why}: it can’t be tiered up.`;
        }
        // a copy from your hand is a card being played: it is held to the same phases
        const tooSoon = source ? null : this.playWindow(seat, this.cards.byNumber.get(target.num), !!a.free);
        if (tooSoon) return tooSoon;
        const maxTier = this.cards.byNumber.get(target.num)?.tiers ?? 1;
        const oldTier = this.tierOf(target);
        const newTier = oldTier + copies.length;
        if (newTier > maxTier) {
          return maxTier === 1 ? `${name} has no higher tiers.` : `${name} only goes up to Tier ${roman(maxTier)}.`;
        }
        if (source) {
          this.clearTokensOn(source);
          this.removeField(source.id);
        } else {
          p.hand.splice(handIndex, 1);
        }
        target.merged.push(...copies.map((c) => ({ id: c.id, num: c.num, spawned: c.spawned })));
        // a swarm is tiered up whole: every unit of it takes the new tier
        if (target.swarm) {
          const total = this.swarmOf(target).reduce((n, m) => n + m.merged.length, 0);
          for (const m of this.swarmOf(target)) { m.swarmMerged = total; if (m !== target) this.refreshTierToken(m); }
        }
        // a stack holding a real (non-spawned) card is a real card: it can't be deleted
        if (target.spawned && copies.some((c) => !c.spawned)) {
          target.spawned = false;
          target.owner = seat;
        }
        target.layer = s.nextLayer++;
        this.refreshTierToken(target);
        this.fx('tier', target.id, newTier);
        // a Spell at Tier II costs 1 less: its owner gets that Gerzite back as it merges
        const tDef = this.cards.byNumber.get(target.num);
        const refund = tDef ? tierRefund(tDef, oldTier, newTier) : 0;
        const gerzite = refund ? this.tokenDb.list.find((t) => t.bar === 'gerzite') : undefined;
        const back = gerzite ? this.gain(seat, gerzite.id, refund) : 0;
        this.log(`${p.name} merged ${name} up to Tier ${roman(newTier)}${back ? ` and got ${back} Gerzite back` : ''}.`, seat);
        this.springTriggers(seat, 'tierup');
        return null;
      }

      case 'split': {
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, card)) return 'That’s your opponent’s card.';
        if (card.swarm) return 'A swarm’s tier can’t be separated from it.';
        const copy = card.merged.pop();
        if (!copy) return 'That card has no merged copies.';
        const side = sideOf(card.owner);
        const pos = this.clampToTable(card.x + (CONFIG.card.width + 0.8) * side, card.z);
        s.field.push({ ...copy, owner: card.owner, ...pos, rot: card.rot, faceUp: card.faceUp, layer: s.nextLayer++, merged: [], tierToken: null });
        this.refreshTierToken(card);
        const tier = this.tierOf(card);
        this.log(`${p.name} separated a copy from ${this.cardName(card.num)} — now ${tier > 1 ? `Tier ${roman(tier)}` : 'a single card'}.`, seat);
        return null;
      }

      case 'toPile': {
        let card: HeldCard | undefined;
        let copies: HeldCard[] = [];
        let fromField = false;
        let wasFaceUp = false;
        let gains = 0; // units of a destroyed_gains card to pay for (0 = nothing)
        const fieldCard = this.findField(a.id);
        // Emperors never go into a deck or a discard pile
        const moving = fieldCard ?? p.hand.find((c) => c.id === a.id);
        const movingDef = moving ? this.cards.byNumber.get(moving.num) : undefined;
        if (movingDef && isEmperor(movingDef)) return 'Emperors can’t go into the deck or the discard pile.';
        if (fieldCard) {
          if (!this.canControl(seat, fieldCard)) return 'That’s your opponent’s card.';
          if (fieldCard.spanOf) return this.clearSpanUnit(fieldCard);
          // a card with destroyed_gains (Gerzite Farmer), discarded face-up with a Destroyed token on it: its owner gains that
          if (a.pile === 'discard') gains = this.dueOnLeaving(fieldCard);
          this.clearTokensOn(fieldCard);
          this.removeField(fieldCard.id);
          this.closeUp(fieldCard);
          card = fieldCard;
          copies = this.copiesOf(fieldCard); // a tiered card takes every merged copy with it
          fromField = true;
          wasFaceUp = fieldCard.faceUp;
        } else {
          const i = p.hand.findIndex((c) => c.id === a.id);
          if (i >= 0) card = p.hand.splice(i, 1)[0];
          if (card) copies = [card];
        }
        if (!card) return 'That card isn’t available anymore.';
        const spot = whereWords(a.where);
        const tierNote = copies.length > 1 ? ` (Tier ${roman(copies.length)}, all ${copies.length} cards)` : '';
        const pile = a.pile === 'discard' ? p.discard : p.deck;
        for (const c of copies) this.insertAt(pile, this.rehide(c), a.where);
        if (a.pile === 'discard') {
          this.log(
            `${p.name} discarded ${this.cardName(card.num)}${tierNote}${a.where === 'top' ? '' : ` (${spot} the pile)`}.`,
            seat,
          );
        } else {
          const what = fromField && wasFaceUp ? `${this.cardName(card.num)}${tierNote}` : copies.length > 1 ? `${copies.length} cards` : 'a card';
          this.log(`${p.name} put ${what} ${spot} their deck.`, seat);
        }
        if (fieldCard) this.destroyedGain(fieldCard, gains);
        if (a.pile === 'discard') this.springTriggers(seat, 'discard');
        return null;
      }

      case 'shuffle': {
        if (p.searching === a.pile) return 'Finish searching that pile first.';
        this.shuffle(p[a.pile]);
        p.shuffles[a.pile]++;
        this.log(`${p.name} shuffled their ${a.pile === 'deck' ? 'deck' : 'discard pile'}.`, seat);
        return null;
      }

      case 'searchStart': {
        if (p.searching) return 'You’re already searching a pile.';
        p.searching = a.pile;
        this.log(`${p.name} is searching their ${a.pile === 'deck' ? 'deck' : 'discard pile'}.`, seat);
        return null;
      }

      case 'searchTake': {
        if (!p.searching) return 'You aren’t searching a pile.';
        const pile = p[p.searching];
        const i = pile.findIndex((c) => c.id === a.id);
        if (i < 0) return 'That card isn’t in the pile anymore.';
        if (this.jumper(pile[i])) return `${this.cardName(pile[i].num)} can’t be taken from a pile. It only comes to your hand when a Calling Pillar stands in each of your lanes.`;
        if (p.hand.length >= CONFIG.handLimit) return `Your hand is full (${CONFIG.handLimit} cards).`;
        const [card] = pile.splice(i, 1);
        p.hand.push(this.rehide(card));
        this.fx('toHand', '', undefined, { effect: p.searching, seat, count: 1 });
        this.log(
          p.searching === 'deck'
            ? `${p.name} took a card from their deck into their hand.`
            : `${p.name} took ${this.cardName(card.num)} from their discard pile into their hand.`,
          seat,
        );
        return null;
      }

      case 'takeDiscard': {
        if (p.overflow) return 'First decide what to do with the card you just drew.';
        if (p.searching === 'discard') return 'Finish searching that pile first.';
        if (!p.discard.length) return 'Your discard pile is empty.';
        // The Remaining on top can't be taken: it slips away to somewhere else in the pile
        if (this.jumper(p.discard[0])) {
          this.jump(seat, 'discard');
          return null;
        }
        if (p.hand.length >= CONFIG.handLimit) return `Your hand is full (${CONFIG.handLimit} cards).`;
        const card = p.discard.shift()!; // the top card
        p.hand.push(this.rehide(card));
        this.fx('toHand', '', undefined, { effect: 'discard', seat, count: 1 });
        this.log(`${p.name} took ${this.cardName(card.num)} back from the top of their discard pile.`, seat);
        return null;
      }

      case 'removeCard': {
        // either player may take a Biome out of play by paying its removal cost; it goes to its owner's discard pile
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        const removal = this.removalOf(card);
        if (!removal) return 'Only a face-up Biome can be removed from play by paying for it.';
        const name = this.cardName(card.num);
        const coin = removal.dark ? 'Dark Gerzite' : 'Gerzite';
        if (removal.dark && removal.cost > 0 && this.darkHeld(seat, removal.cost)) return this.darkHeld(seat, removal.cost);
        if (!this.payBar(seat, removal.cost, removal.dark)) return `Removing ${name} costs ${removal.cost} ${coin} and you have ${this.barTokens(seat, removal.dark ? 'dark' : 'gerzite').length}.`;
        const whose = card.owner === seat ? 'their own' : `${s.players[card.owner].name}’s`;
        this.discardFromPlay(card);
        this.log(removal.expired ? `${p.name} cleared away ${name}, whose turns were over.` : `${p.name} paid ${removal.cost} ${coin} to remove ${whose} ${name} from play.`, seat);
        return null;
      }

      case 'restart': {
        // a Tome whose turns are over: its owner pays its replay cost and it runs again, one turn shorter each time
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        const def = this.cards.byNumber.get(card.num);
        if (!def || !card.faceUp || !def.types.some((t) => CONFIG.turn.restartable.includes(t))) return 'Only a face-up Tome can be restarted.';
        if (card.owner !== seat) return 'Only its owner can restart a Tome.';
        const name = def.name;
        if (!def.turns || def.replayCost === null) return `${name} has no turns or no replay cost set in cards.csv, so it can’t be restarted.`;
        if (!this.isExpired(card)) return `${name} is still active. It can be restarted once its turns are over.`;
        const n = (card.restarts ?? 0) + 1;
        const turns = def.turns - n;
        if (turns <= 0) return `${name} can’t be restarted again: another restart would last 0 turns.`;
        const coin = def.replayDark ? 'Dark Gerzite' : 'Gerzite';
        const replay = Math.max(0, def.replayCost - (this.libraryActive() ? 1 : 0)); // (Library of Infinite Knowledge: 1 less)
        if (def.replayDark && this.darkHeld(seat, replay)) return this.darkHeld(seat, replay);
        if (!this.payBar(seat, replay, def.replayDark)) return `Restarting ${name} costs ${replay} ${coin} and you have ${this.barTokens(seat, def.replayDark ? 'dark' : 'gerzite').length}.`;
        this.removeTokens(this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.turn.lifetime.expiredToken));
        card.restarts = n;
        this.addTimer(card, turns);
        this.fx('play', card.id);
        this.log(`${p.name} paid ${replay} ${coin} to restart ${name} for ${turns + (this.libraryActive() ? 1 : 0)} turn${turns === 1 ? '' : 's'}.`, seat);
        return null;
      }

      case 'pileMove': {
        // an effect that sends a card straight from one pile to the other: it never passes through the hand
        if (p.overflow) return 'First decide what to do with the card you just drew.';
        const to: PileName = a.from === 'deck' ? 'discard' : 'deck';
        if (p.searching) return 'Finish searching first.';
        const source = p[a.from];
        if (!source.length) return a.from === 'deck' ? 'Your deck is empty.' : 'Your discard pile is empty.';
        const card = source.shift()!; // the top card
        this.insertAt(p[to], this.rehide(card), a.where);
        const spot = whereWords(a.where);
        // either way the card is seen: it lands in the discard pile, or it was lying on top of it
        this.log(
          a.from === 'deck'
            ? `${p.name} moved the top card of their deck, ${this.cardName(card.num)}, straight to their discard pile${a.where === 'top' ? '' : ` (${spot} it)`}.`
            : `${p.name} moved ${this.cardName(card.num)} from the top of their discard pile straight ${spot} their deck.`,
          seat,
        );
        this.fx('pileMove', '', undefined, { effect: a.from, seat });
        return null;
      }

      case 'searchEnd': {
        const pile = p.searching;
        if (!pile) return null;
        p.searching = null;
        if (pile === 'deck') {
          this.shuffle(p.deck);
          p.shuffles.deck++;
          this.log(`${p.name} finished searching and shuffled their deck.`, seat);
        } else {
          this.log(`${p.name} finished searching their discard pile.`, seat);
        }
        return null;
      }

      case 'spawnCard': {
        if (!this.cards.byNumber.has(a.num)) return 'That card doesn’t exist.';
        const sideways = sitsSideways(this.cards.byNumber.get(a.num));
        s.field.push({
          ...this.held(a.num, true),
          owner: seat,
          ...this.spawnSpot(seat, a.at, { halfW: (sideways ? CONFIG.card.height : CONFIG.card.width) / 2, halfD: (sideways ? CONFIG.card.width : CONFIG.card.height) / 2, card: true }),
          rot: sideways ? 1 : 0,
          faceUp: true,
          layer: s.nextLayer++,
          merged: [],
          tierToken: null,
        });
        this.addCount(s.field.at(-1)!);
        this.addStartCounters(s.field.at(-1)!);
        this.addTimer(s.field.at(-1)!);
        this.bring(s.field.at(-1)!); // a summoned unit brings its tokens (a Leviathan Body Segment: its Link, Health and Range)
        this.summonUnits(s.field.at(-1)!, 'arrive');
        return null; // spawns are not logged
      }

      case 'attach': {
        const card = this.findField(a.id);
        const to = this.findField(a.to);
        if (!card || !to) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, card)) return 'That’s your opponent’s card.';
        if (!this.cards.byNumber.get(card.num)?.spawnOnly) return 'Only a summoned unit (from the Spawn menu) can be attached to a card.';
        if (card.id === to.id || to.attachedTo) return 'A summoned unit can’t be attached there.';
        if (s.field.some((c) => c.attachedTo === card.id)) return 'Something is attached to this card: take that off first.';
        card.attachedTo = to.id;
        card.attachOrder = s.nextLayer;
        card.layer = s.nextLayer++;
        this.attachGains(card, to, seat);
        return null; // the layout puts it in its place
      }

      case 'clone': {
        const card = this.findField(a.id);
        if (!card || !card.faceUp) return 'Only a face-up card can be cloned.';
        if (this.uncloneable(card.num)) return `${this.cardName(card.num)} can’t be cloned or copied by card effects.`;
        const src = CONFIG.cloneSources.find((c) => c.name.toLowerCase() === a.by.toLowerCase());
        if (!src) return `${a.by} doesn’t make clones.`;
        const def = this.cards.byNumber.get(card.num);
        const clone: FieldCard = {
          ...this.held(card.num, true),
          owner: seat,
          x: card.x,
          z: card.z,
          rot: 0,
          faceUp: true,
          layer: s.nextLayer++,
          merged: [],
          tierToken: null,
          ...(card.form !== undefined ? { form: card.form } : {}),
          clone: { by: src.name, ...(src.health !== undefined ? { health: src.health } : {}) },
        };
        // outside a lane, it goes just beside the card; in a lane it shares the lane (the layout makes room)
        if (!slotAt(card)?.slot.counts) Object.assign(clone, this.besideCard(card));
        s.field.push(clone);
        this.addCount(clone);
        this.addStartCounters(clone);
        const side = sideOf(seat);
        if (src.statuses) {
          for (const t of this.tokensOn(card)) {
            if (this.tokenDb.byId.get(t.tokenId)?.category !== 'status') continue;
            s.tokens.push({ id: this.newId('t'), tokenId: t.tokenId, x: clone.x + (t.x - card.x) / (card.scale ?? 1), z: clone.z + (t.z - card.z) / (card.scale ?? 1), value: t.value, layer: s.nextLayer++, by: t.by, since: t.since, ...(t.turns ? { turns: t.turns } : {}) });
          }
        }
        const marker = this.tokenDb.list.find((d) => d.effect === CONFIG.tokenRules.entrenchedToken);
        if (src.entrenched && marker && !this.tokensOn(clone).some((t) => t.tokenId === marker.id)) {
          s.tokens.push({ id: this.newId('t'), tokenId: marker.id, x: clone.x - 1.7 * side, z: clone.z - 2.6 * side, value: marker.start, layer: s.nextLayer++, by: seat, since: p.turns });
        }
        this.fx('play', clone.id);
        this.log(`${src.name} makes a clone of ${def?.name ?? 'a card'}${src.health !== undefined ? ` with ${src.health} health` : ''}.`, seat);
        return null;
      }

      case 'spawnToken': {
        const def = this.tokenDb.byId.get(a.tokenId);
        if (!def) return 'That token doesn’t exist.';
        // several at once (the Spawn menu's right-click): they are laid out in tidy rows, not on top of each other
        const many = Math.max(1, Math.min(CONFIG.tokenRules.spawnManyMax, Math.round(a.count ?? 1) || 1));
        // a counter can be given the number it starts at
        const start = def.counter && a.value !== undefined && Number.isFinite(a.value) ? Math.max(0, Math.min(999, Math.round(a.value))) : def.start;
        if (def.effect === 'fractal') {
          this.addFractals(seat, Math.max(1, start) * many);
          return null;
        }
        // Gerzite or Dark Gerzite brought in while looking at your resource bars goes straight onto its bar; it
        // lies on the table as a counter only when you are looking at the table itself
        const nearBars = !!def.bar && !!a.at && Number.isFinite(a.at.x) && Number.isFinite(a.at.z) && (['gerzite', 'dark'] as const).some((b) => barSlots(seat, b).slots.some((sl) => Math.hypot(sl.x - a.at!.x, sl.z - a.at!.z) <= CONFIG.spawning.barSnap));
        if (def.bar && (a.toBar || nearBars)) {
          let placed = 0;
          while (placed < many && this.placeBarToken(seat, def)) placed++;
          if (!placed) return `Your ${def.name} bar is full.`;
          if (def.bar === 'dark') this.darkGained(seat);
        } else {
          // it appears near where its player is looking (or in the spawn zone), never on a card
          const width = def.size ?? (def.category === 'dice' ? CONFIG.dice.size : CONFIG.token.size);
          const room = { halfW: width / 2, halfD: width / 2 };
          // Link counters come in pairs, each pair with the next number no Link on the table has yet
          const link = def.effect === CONFIG.tokenRules.linkToken;
          const total = link ? many * 2 : many;
          const firstLink = 1 + Math.max(0, ...s.tokens.filter((o) => this.effectOf(o) === CONFIG.tokenRules.linkToken).map((o) => o.value));
          const looking = a.at && Number.isFinite(a.at.x) && Number.isFinite(a.at.z) && CONFIG.spawning.followCamera ? this.clampToTable(a.at.x, a.at.z) : null;
          const base = total > 1 ? (looking ?? seatPoint(seat, CONFIG.zones.spawn)) : this.spawnSpot(seat, a.at, room);
          const side = sideOf(seat);
          const perRow = Math.min(total, link ? 2 : 5);
          const gap = width + 0.5;
          const placed: { x: number; z: number }[] = [];
          for (let i = 0; i < total; i++) {
            const value = def.category === 'dice' ? this.rollDie() : link ? firstLink + Math.floor(i / 2) : start; // a new die shows a random face
            const col = (i % perRow) - (perRow - 1) / 2;
            const row = Math.floor(i / perRow);
            const at = total > 1 ? this.freeSpot(seat, this.clampToTable(base.x + col * gap * side, base.z + row * gap * side), room, placed) : base;
            placed.push(at);
            const t: Token = { id: this.newId('t'), tokenId: def.id, ...at, value, layer: s.nextLayer++, by: seat, since: p.turns };
            if (def.bar) {
              // Gerzite brought onto the table is a counter: its number is how much (1 unless one was given)
              t.asCounter = true;
              t.value = a.value !== undefined && Number.isFinite(a.value) ? Math.max(1, Math.min(999, Math.round(a.value))) : 1;
            }
            s.tokens.push(t);
            if (a.turns && this.canClock(t)) this.setTurns(t, a.turns);
            this.hit(t, undefined, t.value);
          }
        }
        return null; // spawns are not logged
      }

      case 'handMove': {
        const from = p.hand.findIndex((c) => c.id === a.id);
        if (from < 0) return 'That card isn’t in your hand.';
        const to = Math.max(0, Math.min(p.hand.length - 1, Math.round(a.to) || 0));
        if (to === from) return null;
        const [card] = p.hand.splice(from, 1);
        p.hand.splice(to, 0, card);
        return null; // only you see your hand: nothing to log
      }

      case 'cloneToken': {
        const src = s.tokens.find((tk) => tk.id === a.id);
        if (!src) return 'That token isn’t on the table anymore.';
        const def = this.tokenDb.byId.get(src.tokenId);
        if (!def) return 'That token doesn’t exist.';
        if (def.effect === 'fractal') return 'Fractals can’t be copied.';
        // a Gerzite on a bar: another one on that player's bar
        if (def.bar && !src.asCounter) return this.placeBarToken(seat, def) ? null : `Your ${def.name} bar is full.`;
        const width = (def.size ?? (def.category === 'dice' ? CONFIG.dice.size : CONFIG.token.size)) * (src.scale ?? 1);
        const room = { halfW: width / 2, halfD: width / 2 };
        // its number, clocks and settings come with it; what ties it to one card, ability or player's roll doesn't
        const { ability: _ab, twin: _tw, span: _sp, from: _fr, cure: _cu, cureDie: _cd, linkSum: _ls, ...rest } = src;
        const at = this.freeSpot(seat, this.clampToTable(src.x + width * sideOf(seat), src.z), room);
        const t: Token = { ...rest, id: this.newId('t'), ...at, layer: s.nextLayer++, by: seat, since: p.turns };
        if (t.flow) t.flow = { ...t.flow };
        s.tokens.push(t);
        this.hit(t, undefined, t.value);
        return null; // like a spawn, not logged
      }

      case 'tokenValue': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (this.isDie(t)) return 'Roll the die to change it.';
        const under = this.cardUnder(t);
        const cap = under?.faceUp ? this.capFor(this.effectOf(t), under) : null;
        if (cap !== null && a.delta > 0 && t.value + a.delta > cap) {
          return `${this.tokenDb.byId.get(t.tokenId)?.name ?? 'That counter'} can’t go above ${cap} on ${this.cardName(under!.num)}.`;
        }
        const was = t.value;
        t.value = Math.max(t.asCounter ? 1 : 0, Math.min(999, t.value + a.delta));
        if (t.unset && t.value > 0) {
          // a timer that arrived blank now has its number: from here on it counts down like any other
          delete t.unset;
        }
        if (this.effectOf(t) === 'fractal') this.convertFractals(seat, t);
        // a Count turned down by hand: units of a "destroyed_gains … each" card have fallen (Gerzite Crystallites)
        if (t.value < was && under?.faceUp && this.effectOf(t) === CONFIG.tokenRules.countToken) {
          if (this.cards.byNumber.get(under.num)?.destroyedGains?.each) this.destroyedGain(under, was - t.value);
          for (let k = was; k > Math.max(1, t.value); k--) this.summonUnits(under, 'destroyed'); // each unit taken off leaves what it leaves (its last one does when the card is destroyed)
        }
        if (t.value > was && s.tokens.includes(t)) this.hit(t, undefined, t.value - was); // going up on a card shows the hit again
        // a status copied across the Emperor–Minion link keeps the same number on both cards
        const twin = t.twin ? s.tokens.find((o) => o.id === t.twin) : undefined;
        if (twin && s.tokens.includes(t)) {
          const twinCard = this.cardUnder(twin);
          const twinCap = twinCard?.faceUp ? this.capFor(this.effectOf(twin), twinCard) : null;
          twin.value = twinCap !== null ? Math.min(twinCap, t.value) : t.value;
        }
        return null;
      }

      case 'tokenFlow': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (!t.asCounter) return 'Only Gerzite lying on the table (not on a bar) can do that.';
        if (!a.mode) delete t.flow;
        else t.flow = { mode: a.mode, when: a.when ?? t.flow?.when ?? 'start' };
        return null;
      }

      case 'callingResolve': {
        this.resolveCalling();
        return null;
      }

      case 'harvestSpend': {
        const H = CONFIG.harvest;
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t || !H.token || t.tokenId !== H.token) return 'That counter isn’t on the table anymore.';
        const under = this.cardUnder(t);
        if (under ? !this.canControl(seat, under) : t.by !== null && !this.allies(t.by, seat)) return 'That’s your opponent’s counter.';
        const deal = H.spend.find((x) => x.gives === a.gives);
        const name = this.tokenDb.byId.get(t.tokenId)?.name ?? 'Harvest';
        if (!deal) return `${name} can’t be spent for that.`;
        if (t.value < deal.cost) return `That takes ${deal.cost} ${name}: this counter has ${t.value}.`;
        t.value -= deal.cost;
        if (t.value <= 0) this.removeTokens([t]);
        const got = this.gain(seat, deal.gives, deal.amount);
        if (under) this.fx('status', under.id, undefined, { effect: 'notice' });
        this.log(`${p.name} spent ${deal.cost} ${name}${under?.faceUp ? ` from ${this.cardName(under.num)}` : ''} and gained ${this.gainText(deal.gives, deal.amount, got)}.`, seat);
        return null;
      }

      case 'tokenToBar': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        const def = this.tokenDb.byId.get(t.tokenId);
        if (!def?.bar || !t.asCounter) return 'Only Gerzite lying on the table can go onto a bar.';
        const want = Math.max(1, t.value);
        this.removeTokens([t]);
        const got = this.gain(seat, def.id, want);
        this.log(`${p.name} put ${this.gainText(def.id, want, got)} on their bar.`, seat);
        return null;
      }

      case 'tokenTurns': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (!this.canClock(t)) return `${this.tokenDb.byId.get(t.tokenId)?.name ?? 'That token'} can’t carry a turn clock.`;
        this.setTurns(t, a.turns);
        return null;
      }

      case 'spanPlace': {
        if (!p.span) return 'There is no Lanespanner waiting to be spread.';
        if (!p.span.starts.includes(a.start)) return 'Its units can’t go there.';
        this.placeSpan(seat, a.start);
        return null;
      }

      case 'linkHalf': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (this.effectOf(t) !== CONFIG.tokenRules.linkToken) return 'Only a Link counter can be set to half.';
        if (a.half) t.half = true;
        else delete t.half;
        delete t.linkSum;
        return null;
      }

      case 'tokenActAt': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        const def = this.tokenDb.byId.get(t.tokenId);
        const act = CONFIG.turn.statusActs.find((x) => x.effect === def?.effect && x.choose);
        if (!act || !act.choose!.includes(a.at)) return `${def?.name ?? 'That token'} always acts at the same moment.`;
        for (const k of [t, t.twin ? s.tokens.find((o) => o.id === t.twin) : undefined]) {
          if (!k) continue;
          if (a.at === act.when) delete k.actAt;
          else k.actAt = a.at;
        }
        return null;
      }

      case 'tokenDrain': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (!this.canClock(t)) return `${this.tokenDb.byId.get(t.tokenId)?.name ?? 'That token'} can’t wear off a turn at a time.`;
        this.setDrain(t, a.drain);
        return null;
      }

      case 'cureRoll': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t || t.cure === undefined) return 'There is no roll waiting for that token.';
        if (t.cure !== seat && s.mode !== 'solo') return `That roll is ${s.players[t.cure].name}’s to make.`;
        const def = this.tokenDb.byId.get(t.tokenId);
        const faces = CONFIG.turn.statusActs.find((x) => x.effect === def?.effect)?.cure ?? [];
        const card = this.cardUnder(t);
        const where = !card ? '' : card.faceUp ? this.cardName(card.num) : 'a face-down card';
        const on = where ? ` on ${where}` : '';
        const name = def?.name ?? 'The status';
        const rolled = this.rollDie();
        const cured = faces.includes(rolled);
        const twin = t.twin ? s.tokens.find((o) => o.id === t.twin) : undefined;
        delete t.cure;
        if (twin) delete twin.cure;
        // the roll is made with a real die in the roller's color, thrown down just in front of the card
        this.throwDie(seat, card ? this.besideCard(card) : this.clampToTable(t.x + 3, t.z), rolled);
        const text = cured ? `${p.name} rolled a ${rolled}: ${name}${on} is cured.` : `${p.name} rolled a ${rolled}: ${name} stays${on}.`;
        this.fx('cure', card?.id ?? '', undefined, { token: t.id, value: rolled, effect: cured ? 'cured' : 'stays', seat, text, at: { x: t.x, z: t.z }, ...(twin ? { target: twin.id } : {}) });
        if (cured) this.removeTokens(twin ? [t, twin] : [t]);
        this.log(text, seat);
        return null;
      }

      case 'lockPrice': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (this.effectOf(t) !== CONFIG.tokenRules.lockToken) return 'Only a Locked token can carry an unlock cost.';
        const max = a.dark ? CONFIG.zones.darkGerziteBar.slots : CONFIG.tokenRules.lockMaxCost;
        t.price = Math.max(0, Math.min(max, Math.round(a.cost) || 0));
        t.priceDark = !!a.dark;
        // the copy across the Emperor–Minion link carries the same cost
        const twin = t.twin ? s.tokens.find((o) => o.id === t.twin) : undefined;
        if (twin) Object.assign(twin, { price: t.price, priceDark: t.priceDark });
        return null;
      }

      case 'payLock': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        if (this.effectOf(t) !== CONFIG.tokenRules.lockToken) return 'Only a Locked token can be paid off.';
        const cost = t.price ?? 0;
        if (cost <= 0) return 'That Lock has no cost set. Right-click it to set one.';
        const card = this.cardUnder(t);
        if (t.payBy !== undefined && s.mode !== 'solo') {
          if (!this.allies(t.payBy, seat)) return `Only ${s.teams ? this.teamName(t.payBy) : s.players[t.payBy].name} can pay this Lock off.`;
        } else if (card && s.mode !== 'solo' && !this.canControl(seat, card)) return 'Only the owner of the locked card can pay to unlock it.';
        const coin = t.priceDark ? 'Dark Gerzite' : 'Gerzite';
        if (t.priceDark && this.darkHeld(seat, cost)) return this.darkHeld(seat, cost);
        const mine = this.barTokens(seat, t.priceDark ? 'dark' : 'gerzite');
        if (mine.length < cost) return `Unlocking costs ${cost} ${coin} and you have ${mine.length}.`;
        this.removeTokens(mine.slice(-cost));
        if (t.priceDark) this.spentDark(seat, cost);
        const twin = t.twin ? s.tokens.find((o) => o.id === t.twin) : undefined;
        this.removeTokens(twin ? [t, twin] : [t]);
        const where = !card ? '' : card.faceUp ? this.cardName(card.num) : 'a face-down card';
        if (card) this.fx('status', card.id, undefined, { effect: 'heal' });
        this.log(`${p.name} paid ${cost} ${coin} to unlock ${where || 'a Lock'}.`, seat);
        if (t.onPaid) {
          const w = t.onPaid.w;
          if (w[0] === 'gain') {
            const n = Number(w[1]) || 1;
            const id = w.includes('dark') ? (this.tokenDb.list.find((d) => d.bar === 'dark')?.id ?? CONFIG.tokenRules.fractalGives) : CONFIG.tokenRules.fractalGives;
            this.log(`${s.players[t.onPaid.by].name} gains ${this.gainText(id, n, this.gain(t.onPaid.by, id, n))}: the Lock was paid off.`, t.onPaid.by);
          }
          else if (w[0] === 'status' && card) for (let k = 1; k + 1 < w.length; k += 2) this.giveStatus(t.onPaid.by, card, w[k], Number(w[k + 1]) || 1, 1);
        }
        return null;
      }

      case 'cardRoll': {
        // the die a card asks for as a turn begins (Pestilence Approaching): an even roll ends the card
        const card = this.findField(a.id);
        if (!card || card.roll === undefined) return 'There is no roll waiting for that card.';
        if (card.roll !== seat && s.mode !== 'solo') return `That roll is ${s.players[card.roll].name}’s to make.`;
        const name = this.cardName(card.num);
        const rolled = this.rollDie();
        if (card.rollFor === 'coin') return this.coinFlip(seat, card, rolled);
        const over = CONFIG.turn.endings.rollEnds.includes(rolled);
        delete card.roll;
        this.throwDie(seat, this.besideCard(card), rolled);
        const text = over ? `${p.name} rolled a ${rolled}: ${name} ends. It is no longer active.` : `${p.name} rolled a ${rolled}: ${name} stays in play.`;
        this.fx('cure', card.id, undefined, { value: rolled, effect: over ? 'ended' : 'stays', seat, text, at: { x: card.x, z: card.z } });
        if (over) this.endCard(card, `${p.name} rolled a ${rolled}`, false);
        else {
          this.log(text, seat);
          this.fire(card, 'rolled', { actor: seat }); // (Pestilence Approaching spreads again)
          this.settleFallen();
        }
        return null;
      }

      case 'recast': {
        // a card that has ended and may be cast again (ends = recast: Cursed Chains): its owner pays its cost
        // once more, the skull comes off and it brings a new token
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        const def = this.cards.byNumber.get(card.num);
        if (!def || !card.faceUp || !def.ends.includes('recast')) return 'That card can’t be recast.';
        if (card.owner !== seat) return 'Only its owner can recast it.';
        if (!this.isExpired(card)) return `${def.name} is still active. It can be recast once it has ended.`;
        const cost = Math.max(0, def.cost ?? 0);
        const coin = def.costDark ? 'Dark Gerzite' : 'Gerzite';
        if (def.costDark && cost > 0 && this.darkHeld(seat, cost)) return this.darkHeld(seat, cost);
        if (!this.payBar(seat, cost, def.costDark)) return `Recasting ${def.name} costs ${cost} ${coin} and you have ${this.barTokens(seat, def.costDark ? 'dark' : 'gerzite').length}.`;
        this.removeTokens(this.tokensOn(card).filter((t) => this.effectOf(t) === CONFIG.turn.lifetime.expiredToken));
        this.fx('play', card.id);
        this.log(`${p.name} paid ${cost} ${coin} to recast ${def.name}.`, seat);
        this.bring(card);
        return null;
      }

      case 'roll': {
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That die isn’t on the table anymore.';
        if (!this.isDie(t)) return 'Only dice can be rolled.';
        const from = { x: t.x, z: t.z };
        if (a.x !== undefined && a.z !== undefined) Object.assign(t, this.clampToTable(a.x, a.z)); // thrown
        t.layer = s.nextLayer++;
        t.value = this.rollDie();
        this.fx('roll', '', undefined, { token: t.id, from });
        this.log(`${p.name} rolled a ${t.value} on the ${this.tokenDb.byId.get(t.tokenId)?.name ?? 'die'}.`, seat);
        return null;
      }

      case 'collectFractals': {
        if (s.turn !== seat) return 'It’s not your turn.';
        if (s.phase !== PHASES.length - 1) return 'Fractals are collected in your End Phase.';
        if (s.fractalsTaken) return 'You already collected this turn’s Fractals.';
        if (!this.collectFractals(seat)) return 'None of your units earned a Fractal this turn.';
        return null;
      }

      case 'sacrifice': {
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        if (!this.allies(card.owner, seat) || (card.spawned && !card.swarm)) return 'You can only sacrifice your own units.';
        const mateSac = this.mateHeld(seat, card);
        if (mateSac) return mateSac;
        const def = this.cards.byNumber.get(card.num);
        if (!card.faceUp || !def || !(def.types.includes('troop') || def.types.includes('building'))) return 'Only face-up Troops and Buildings can be sacrificed.';
        const on = this.tokensOn(card);
        const tooSoon = sacrificeWindowRefusal(s.turn, s.phase, seat);
        if (tooSoon) return tooSoon;
        if (on.some((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken)) return 'A unit that is still Entrenched can’t be sacrificed.';
        if (CONFIG.turn.darkgale.token && on.some((t) => this.effectOf(t) === CONFIG.turn.darkgale.token)) return 'A Darkgale unit can’t be sacrificed for Dark Gerzite.';
        // Undead Legion: sacrificing the General takes its whole Legion (see legionWorth)
        if (this.isGeneral(card)) {
          const legion = [card, ...s.field.filter((c) => c.legion === card.id)];
          if (legion.some((m) => this.hasOn(m, CONFIG.tokenRules.entrenchedToken))) return 'A unit that is still Entrenched can’t be sacrificed.';
          const worth = this.legionWorth(card);
          const dark = this.tokenDb.list.find((t) => t.bar === 'dark');
          const got = dark ? this.gain(seat, dark.id, worth) : 0;
          const from = { x: card.x, z: card.z };
          const copies = this.copiesOf(card);
          for (const m of legion) {
            this.fx('sacrifice', m.id, undefined, { seat });
            this.clearTokensOn(m);
            this.removeField(m.id);
          }
          for (const c of copies) this.insertAt(p.discard, this.rehide(c), 'top');
          this.closeUp(from);
          this.log(`${p.name} sacrificed ${def.name} and its Legion for ${dark ? this.gainText(dark.id, worth, got) : '0 Dark Gerzite'}.`, seat);
          return null;
        }
        // a swarm is sacrificed whole: every one of its units goes, for what the swarm is worth as one card
        const units = card.swarm ? this.swarmOf(card).filter((m) => !this.hasOn(m, CONFIG.turn.lifetime.expiredToken)) : [card];
        if (card.swarm && units.some((m) => this.tokensOn(m).some((t) => this.effectOf(t) === CONFIG.tokenRules.entrenchedToken))) return 'A unit that is still Entrenched can’t be sacrificed.';
        const allOn = units.flatMap((m) => this.tokensOn(m));
        const overheal = allOn.filter((t) => this.effectOf(t) === 'overheal').reduce((n, t) => n + t.value, 0);
        // health it has gained counts like health it never lost (it can give more than the card's cost); a card that
        // is several units (a Count) is worth the share of its units still alive
        const alive = card.swarm ? units.length : on.find((t) => this.effectOf(t) === CONFIG.tokenRules.countToken)?.value ?? null;
        const worth = countedSacrificeWorth(this.fullForm(card, def), formOf(def, card.form).count, alive, units.reduce((n, m) => n + this.damageOn(m), 0), overheal, units.reduce((n, m) => n + this.gainedHealth(this.tokensOn(m)), 0));
        if (worth === null) return `${def.name} has no health set in cards.csv, so its worth can’t be worked out.`;
        const dark = this.tokenDb.list.find((t) => t.bar === 'dark');
        const got = dark ? this.gain(seat, dark.id, worth) : 0;
        const from = { x: card.x, z: card.z };
        const copies = card.swarm ? [{ id: card.swarm, num: card.num, spawned: false } as HeldCard, ...units.flatMap((m) => m.merged)] : this.copiesOf(card);
        for (const m of units) {
          this.fx('sacrifice', m.id, undefined, { seat });
          this.clearTokensOn(m);
          this.removeField(m.id);
        }
        for (const c of copies) this.insertAt(p.discard, this.rehide(c), 'top');
        // a Lanespanner's card (the one played from the hand): the extra units it spread go with it (they add nothing)
        const spread = card.swarm ? [] : s.field.filter((c) => c.spanOf === card.id);
        for (const m of spread) {
          this.fx('sacrifice', m.id, undefined, { seat, effect: 'destroyed' });
          this.clearTokensOn(m);
          this.removeField(m.id);
          this.closeUp(m);
        }
        this.closeUp(from);
        this.log(`${p.name} sacrificed ${def.name} for ${dark ? this.gainText(dark.id, worth, got) : '0 Dark Gerzite'}${spread.length ? `; the ${spread.length === 1 ? 'unit' : `${spread.length} units`} it spread ${spread.length === 1 ? 'is' : 'are'} destroyed with it` : ''}.`, seat);
        return null;
      }

      case 'payout': {
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        if (!this.allies(card.owner, seat)) return 'That’s your opponent’s card.';
        const def = this.cards.byNumber.get(card.num);
        if (!card.faceUp || !def?.types.includes('trigger')) return 'Only a Trigger that has been flipped face-up pays out.';
        const amount = triggerPayout(def);
        const gerzite = CONFIG.tokenRules.fractalGives;
        const got = this.gain(seat, gerzite, amount);
        const from = { x: card.x, z: card.z };
        this.clearTokensOn(card);
        this.removeField(card.id);
        this.insertAt(p.discard, this.rehide(card), 'top');
        this.closeUp(from);
        this.log(`${p.name}’s Trigger, ${def.name}, paid out ${this.gainText(gerzite, amount, got)} and was discarded.`, seat);
        return null;
      }

      case 'deckOut': {
        if (this.canDraw(p)) return 'Your deck still has cards in it.';
        if (!this.damageEmperor(seat, CONFIG.turn.deckOutDamage || 1)) return 'Your Emperor isn’t on the table.';
        this.deckOutLook(seat);
        this.log(`Deck-Out: ${p.name} could not draw, so their Emperor takes ${CONFIG.turn.deckOutDamage || 1} damage.`, seat);
        return null;
      }

      case 'attack': {
        const from = this.findField(a.from);
        if (!from) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, from)) return 'That’s your opponent’s card.';
        const mateAtk = this.mateHeld(seat, from);
        if (mateAtk) return mateAtk;
        const away = this.possessedAway(seat, from);
        if (away) return away;
        const to = a.to ? this.findField(a.to) : undefined;
        if (to?.id === from.id) return null;
        const name = (c: FieldCard) => (c.faceUp ? this.cardName(c.num) : 'a face-down card');
        if (to) this.fx('attack', from.id, undefined, { target: to.id, seat });
        else if (a.x !== undefined && a.z !== undefined) this.fx('attack', from.id, undefined, { at: this.clampToTable(a.x, a.z), seat });
        else return null;
        this.log(to ? `${p.name}: ${name(from)} attacks ${name(to)}.` : `${p.name}: ${name(from)} declares an attack.`, seat);
        if (to && !a.effect) this.rollMiss(seat, from, to);
        return null;
      }

      case 'activate': {
        const card = this.findField(a.id);
        const def = card?.faceUp ? this.cards.byNumber.get(card.num) : undefined;
        if (!card || !def) return 'That card isn’t on the table anymore.';
        const list = this.effectsOf(card, 'click');
        const anyone = list.length > 0 && list.every((e) => e.conds.some((c) => c[0] === 'anyone'));
        if (!this.allies(card.owner, seat) && !anyone) return 'That’s your opponent’s card.';
        const mateUse = anyone ? null : this.mateHeld(seat, card);
        if (mateUse) return mateUse;
        if (!list.length) return `${def.name} has nothing to use right now.`;
        if (s.turn !== seat) return `${def.name} is used on your own turn.`;
        if (this.isExpired(card)) return `${def.name} is no longer active.`;
        if (anyone) {
          // either player, once in each of their turns (Coral Reef)
          if (card.usedBy?.[seat] === this.turnStamp()) return `You have already used ${def.name} this turn.`;
          if (list.some((e) => e.words[0] === 'coral')) { this.coralAsk(seat, card); return null; }
        }
        if (!anyone && card.usedAt === this.turnStamp()) return `${def.name} has already been used this turn.`;
        // (The River Keeper: nothing is paid when there is no opposing Unit to put the Bell on)
        // (the opposing Units are those on the other side of the table: on the solo table, or a Unit spawned over there, too)
        if (list.some((e) => e.words[0] === 'bell_on') && !this.whoOf(card, 'foes', {}).some((u) => this.hittable(u))) return `${def.name}: there is no opposing Unit to put a Bell on.`;
        card.usedAt = this.turnStamp();
        this.runEffects(card, list, {});
        this.settleFallen();
        return null;
      }

      case 'pick': {
        return this.answerPick(seat, a);
      }

      case 'bargain': {
        // Dark Bargain: either player changes the amount (and both have to agree again), or agrees to it
        const b = s.bargain;
        if (!b) return 'There is no bargain waiting.';
        if (a.delta) { b.value = Math.max(1, Math.min(b.max, b.value + Math.sign(a.delta))); b.agreed = s.bots?.length ? b.agreed.filter((x) => s.bots!.includes(x)) : []; return null; }
        if (a.deal && !b.agreed.includes(seat)) b.agreed.push(seat);
        if (b.agreed.length >= s.players.filter((q) => !q.out).length) {
          for (let i = 0; i < s.players.length; i++) {
            const n = Math.min(b.value, Math.max(0, this.barTokens(i as Seat, 'gerzite').length - 1));
            // the Gerzite each loses rises off the bar and shatters
            if (CONFIG.looks.enabled) for (const t of this.barTokens(i as Seat, 'gerzite').slice(-n)) this.fx('look', '', undefined, { effect: 'gz-shatter', at: { x: t.x, z: t.z } });
            this.payBar(i as Seat, n, false);
          }
          this.log(`${b.by}: both players lose ${b.value} Gerzite.`, b.seat);
          delete s.bargain;
        }
        return null;
      }

      case 'strike': {
        const from = this.findField(a.from);
        const to = this.findField(a.to);
        if (!from || !to) return 'That card isn’t on the table anymore.';
        if (!this.canControl(seat, from)) return 'That’s your opponent’s card.';
        const mateStrike = this.mateHeld(seat, from);
        if (mateStrike) return mateStrike;
        const away = this.possessedAway(seat, from);
        if (away) return away;
        const tapLock = this.lockedBy(from);
        if (tapLock) return tapLock;
        if (s.picks?.some((k) => k.strike)) return 'An attack is waiting for an answer first.';
        let why = this.attackWhy(seat, from);
        if (why) return why;
        if (!this.attackTargets(from).includes(to)) return `${this.cardName(from.num)} can’t reach that.`;
        // attacking ends the Summon Phase: the Attack Phase begins (and what happens as it begins happens first)
        if (s.phase === 1) {
          const held = this.run(seat, p, { type: 'nextPhase' });
          if (held) return held;
          this.markDestroyed();
          if (!s.field.includes(from) || !s.field.includes(to)) return null;
          why = this.attackWhy(seat, from);
          if (why || !this.attackTargets(from).includes(to)) return null;
        }
        return this.strike(seat, from, to);
      }

      case 'endAttack': {
        // only the player who drew an arrow can take it away; an arrow that is already gone is no error
        const drawn = s.fx.find((f) => f.id === a.arrow && f.kind === 'attack');
        if (!drawn) return null;
        if (drawn.seat !== seat) return 'That arrow isn’t yours.';
        if (s.fx.some((f) => f.kind === 'attackEnd' && f.ref === a.arrow)) return null;
        this.fx('attackEnd', '', undefined, { ref: a.arrow, seat });
        return null;
      }

      case 'ping': {
        this.fx('ping', '', undefined, { at: this.clampToTable(a.x, a.z), seat });
        return null;
      }

      case 'delete': {
        { const why = this.noDeleting(seat, a.id); if (why) return why; }
        const ti = s.tokens.findIndex((t) => t.id === a.id);
        if (ti >= 0) {
          const on = this.effectOf(s.tokens[ti]) === CONFIG.tokenRules.entrenchedToken ? this.cardUnder(s.tokens[ti]) : undefined;
          s.tokens.splice(ti, 1);
          this.freed(on);
          return null;
        }
        const card = this.findField(a.id);
        if (!card) return 'That piece isn’t on the table anymore.';
        if (!card.spawned) return 'Only spawned cards can be deleted.';
        const due = this.dueOnLeaving(card); // a summoned unit with destroyed_gains (Gerzite Crystallite)
        this.clearTokensOn(card);
        this.removeField(card.id);
        this.closeUp(card);
        this.destroyedGain(card, due);
        return null;
      }

      case 'nextPhase': {
        if (s.turn !== seat) return 'It’s not your turn.';
        if (p.overflow) return 'First decide what to do with the card you just drew.';
        const owedPick = s.picks?.find((k) => k.seat === seat || k.strike);
        if (owedPick) return owedPick.seat === seat ? `First make your choice. ${owedPick.text}.` : 'An attack is waiting for your opponent’s answer first.';
        if (p.span) return this.spanWaiting(p.span);
        const owed = this.curesOwed(seat)[0];
        if (owed) {
          const on = this.cardUnder(owed)!;
          return `First roll to cure ${this.tokenDb.byId.get(owed.tokenId)?.name ?? 'the status'} on ${on.faceUp ? this.cardName(on.num) : 'a face-down card'}.`;
        }
        const rollFor = s.field.find((c) => c.roll === seat);
        if (rollFor) return `First roll the die for ${this.cardName(rollFor.num)}.`;
        if (s.bargain) return `First settle ${s.bargain.by}: both players press Deal.`;
        if (s.phase < PHASES.length - 1) {
          // a swarm that attacked this turn: its units that haven't are tapped as the Attack Phase ends (no Fractals for them)
          // (a Lanespanner's units the same way; only units that were alive and able to attack lose it)
          if (s.phase === 2) for (const c of [...s.field]) if (c.swarmHit === this.turnKey() && c.rot % 2 === 0 && this.allies(c.owner, seat) && !c.atZero && !this.hasOn(c, CONFIG.turn.lifetime.expiredToken) && !(this.lockedBy(c) ?? this.attackWhy(seat, c))) { this.turnCard(c, 1); this.log(`${this.cardName(c.num)} didn’t attack with the rest of its ${c.swarm ? 'swarm' : 'Lanespanner'}: it is tapped.`, seat); }
          s.phase++;
          const moment = s.phase === 1 ? 'summon' : s.phase === 2 ? 'attack' : 'end';
          if (moment === 'end' && CONFIG.turn.lifetime.unsetReminder) {
            // a timer that arrived blank and still has no number: its owner is reminded before the turn is over
            for (const u of this.unsetTimers(seat)) {
              this.fx('status', u.card.id, undefined, { token: u.token.id, effect: 'notice', seat, text: `${this.cardName(u.card.num)}’s turn timer still isn’t set. Click the “?” timer to give it a number.` });
            }
          }
          this.statusActs(seat, moment);
          // what cards do by themselves at this moment (spawners as the Attack Phase begins, healing as the turn ends)
          if (moment === 'attack') this.fireAll('attack_phase', (c) => this.turnOf(c.owner) === seat);
          if (moment === 'end') this.fireAll('end', (c) => this.turnOf(c.owner) === seat);
          if (moment === 'end') this.fireAll('any_end', () => true, { actor: seat }); // (Biomes: each player's own end of turn)
          if (moment !== 'summon') this.settleFallen();
          if (moment === 'end') this.gerziteFlows(seat, 'end'); // before the clocks: a counter on its last turn still pays out
          this.tickClocks(seat, moment); // before the counters: a card that is Frozen right now keeps its clocks on hold
          this.tick(seat, moment);
          return null;
        }
        const before = this.globalTurn();
        const wasOvertime = this.overtime();
        if (CONFIG.turn.fractalsAtEndTurn && !s.fractalsTaken) this.collectFractals(seat); // not collected by hand: the table does it
        // a Tome whose turns were already over, and that its owner didn't pay to restart this turn, is discarded now
        // (one that runs out just below, as this turn ends, still gets its owner's next turn to be restarted)
        if (CONFIG.turn.discardSpentTomes) this.discardSpentTomes(seat);
        this.tick(seat, 'turnEnd');
        // statuses that last "until the end of your (next) turn"
        const over = s.tokens.filter((t) => t.until && t.until.seat === seat && t.until.turn <= p.turns);
        // (a Unit Possessed for the turn goes back to its owner untapped: Forgotten Monarch)
        for (const t of over) if (this.effectOf(t) === 'possession') { const c = this.cardUnder(t); if (c && !this.sitsSideways(c)) { c.rot = 0; delete c.tierBoost; delete c.tierBoostAt; this.refreshTierToken(c); } }
        if (over.length) this.removeTokens(over);
        this.returnWalkers(seat);
        this.echoesLand(seat);
        this.soulsGo(seat);
        // a tier lent for this turn only is over: its token goes back to what the card really is
        for (const c of s.field) if (c.tierBoostAt !== undefined && c.tierBoostAt <= this.turnStamp()) { delete c.tierBoost; delete c.tierBoostAt; this.refreshTierToken(c); }
        for (const c of s.field) if (this.turnOf(c.owner) === seat && c.rot % 2 === 0) c.restedAt = p.turns; // (it stayed untapped all turn)
        for (const c of s.field) if (this.turnOf(c.owner) === seat && c.restHits && c.swings?.key === this.turnKey()) delete c.restHits; // (Marlin Knight attacked this turn, even with hits left over: back to its 2)
        this.spDecay(seat);
        this.endAmbush();
        this.endTurn(seat);
        p.turns++;
        this.log(`${p.name} ended turn ${p.turns}.`, seat);
        const after = this.globalTurn();
        if (after > before) this.log(`Global turn count is now ${after}.`, null);
        if (!wasOvertime && this.overtime()) {
          const o = CONFIG.overtime;
          const toll = this.overtimeToll();
          if (s.teams) this.log(`OVERTIME begins! Each team now gains ${CONFIG.teams.overtimeAtStart} Gerzite at the start and ${CONFIG.teams.overtimeAtEnd} at the end of its turn, and every Emperor takes ${toll} damage at the end of each team's turn.`, null);
          else this.log(`OVERTIME begins! Gerzite is now ${o.gerzite} at the start and ${o.gerzite} at the end of each turn, and both Emperors take ${toll} damage at the end of every turn.`, null);
        }
        if (this.overtime() && after > before && after === CONFIG.overtime.doubleFrom && wasOvertime) {
          const o = CONFIG.overtime;
          this.log(`DOUBLE OVERTIME begins! ${s.teams ? '' : `Gerzite is now ${o.doubleGerzite} at the start and ${o.doubleGerzite} at the end of each turn, and `}${s.players.length > 2 ? 'every Emperor now takes' : 'both Emperors now take'} ${o.doubleDamage} damage at the end of every turn.`, null);
        }
        if (this.checkOut(true)) return null; // a side has no Emperor left (Overtime can take both at once): the game is over
        s.turn = this.nextSeat(seat);
        if (s.showing && !s.showing.waiting) s.showing = null; // (cards shown last turn are put away)
        s.phase = 0;
        if (s.mode === 'versus') this.log(`It’s ${s.players[s.turn].name}’s turn.`, s.turn);
        this.startTurn(s.turn);
        return null;
      }

      case 'revealCard': {
        const places: [HeldCard[], string][] = [[p.hand, 'hand'], [p.deck, 'deck'], [p.discard, 'discard pile']];
        const found = places.map(([list, where]) => ({ card: list.find((c) => c.id === a.id), where })).find((f) => f.card);
        if (!found?.card) return 'That card isn’t in your hand, deck or discard pile.';
        const text = `${p.name} revealed ${this.cardName(found.card.num)} from their ${found.where}.`;
        this.fx('revealCard', '', undefined, { seat, num: found.card.num, text });
        this.log(text, seat);
        return null;
      }

      case 'revealHand': {
        if (s.mode === 'solo') return 'There’s no opponent to reveal your hand to.';
        for (let v = 0; v < s.players.length; v++) {
          if (this.opposed(seat, v as Seat)) s.reveals[v] = { from: seat, cards: p.hand.map((c) => ({ ...c })) };
        }
        this.log(`${p.name} revealed their hand.`, seat);
        return null;
      }

      case 'closeReveal': {
        s.reveals[seat] = null;
        return null;
      }

      case 'peekAsk': {
        const owner = s.players[a.seat];
        if (!owner) return 'That pile isn’t on this table.';
        const pile = owner[a.pile];
        const what = a.pile === 'deck' ? 'deck' : 'discard pile';
        if (!pile.length) return a.seat === seat ? `Your ${what} is empty.` : `${owner.name}’s ${what} is empty.`;
        const count = Math.max(1, Math.min(pile.length, Math.round(a.count) || 1));
        const cards = `the top ${count === 1 ? 'card' : `${count} cards`}`;
        if (a.seat === seat) {
          // your own pile: no one to ask, but your opponent is told
          s.peeks[seat] = { owner: seat, pile: a.pile, cards: pile.slice(0, count).map((c) => ({ ...c })) };
          this.log(`${p.name} looked at ${cards} of their ${what}.`, seat);
          this.fx('peek', '', undefined, { effect: a.pile, seat, count }); // both players see it happen on the pile
          return null;
        }
        if (s.peekRequest) return 'A request to peek is already waiting for an answer.';
        s.peekRequest = { from: seat, pile: a.pile, count, ...(s.teams ? { owner: a.seat } : {}) };
        this.log(`${p.name} wants to see ${cards} of ${owner.name}’s ${what}.`, seat);
        return null;
      }

      case 'peekAnswer': {
        const req = s.peekRequest;
        if (!req) return null;
        s.peekRequest = null;
        if (req.from === seat) {
          this.log(`${p.name} took back their request to peek.`, seat);
          return null;
        }
        if (req.owner !== undefined && req.owner !== seat) {
          s.peekRequest = req;
          return `That request is for ${s.players[req.owner].name} to answer.`;
        }
        const what = req.pile === 'deck' ? 'deck' : 'discard pile';
        if (!a.accept) {
          this.log(`${p.name} declined to show their ${what}.`, seat);
          return null;
        }
        const shown = p[req.pile].slice(0, req.count).map((c) => ({ ...c })); // as the pile stands now
        s.peeks[req.from] = { owner: seat, pile: req.pile, cards: shown };
        if (shown.length) this.fx('peek', '', undefined, { effect: req.pile, seat, count: shown.length });
        this.log(`${p.name} showed ${s.players[req.from].name} the top ${shown.length === 1 ? 'card' : `${shown.length} cards`} of their ${what}.`, seat);
        return null;
      }

      case 'emperorAbility': {
        // The skeleton of Emperor abilities: the table takes the cost and starts the cooldown. What the
        // ability does is its text, which the players carry out themselves.
        const E = CONFIG.emperorAbilities;
        if (!E.enabled) return 'Emperor abilities are switched off.';
        const card = this.findField(a.id);
        if (!card) return 'That card isn’t on the table anymore.';
        const def = this.cards.byNumber.get(card.num);
        if (!def || !card.faceUp || !isEmperor(def)) return 'Only a face-up Emperor has abilities.';
        if (card.owner !== seat) return 'That is your opponent’s Emperor.';
        const ab = def.emperorAbilities.find((x) => x.slot === a.slot);
        if (!ab) return `${def.name} has no Ability ${a.slot} yet. (Abilities are written in data/emperor_abilities.csv.)`;
        const tooSoon = abilityWindowRefusal(ab, s.turn, s.phase, seat, s.mode === 'solo');
        if (tooSoon) return tooSoon;
        // Frozen, Stunned or Entrenched: no abilities at all (their cooldowns keep counting down all the same)
        const on = this.tokensOn(card);
        const held = on.find((t) => E.blockedBy.includes(this.effectOf(t)));
        if (held) return `${def.name} is ${this.tokenDb.byId.get(held.tokenId)?.name ?? 'held'}: its abilities can’t be used until that token is gone.`;
        // a Lock on this ability, or one on the Emperor that hasn't been given an ability yet
        if (this.abilityToken(card, ab.slot, 'lock')) return `${ab.name} is Locked. The Lock has to come off first${this.abilityToken(card, ab.slot, 'lock')!.price ? ' (its cost can be paid from its right-click menu)' : ''}.`;
        if (on.some((t) => this.abilityTokenKind(t) === 'lock' && !t.ability)) return `There is a Lock on ${def.name} that hasn’t been put on an ability yet: right-click it and choose which one.`;
        const waiting = this.abilityToken(card, ab.slot, 'cooldown');
        if (waiting) return `${ab.name} is on cooldown for ${waiting.value} more of your turn${waiting.value === 1 ? '' : 's'}.`;
        const coin = ab.dark ? 'Dark Gerzite' : 'Gerzite';
        if (ab.dark && ab.cost > 0 && this.darkHeld(seat, ab.cost)) return this.darkHeld(seat, ab.cost);
        if (!this.payBar(seat, ab.cost, ab.dark)) return `${ab.name} costs ${ab.cost} ${coin} and you have ${this.barTokens(seat, ab.dark ? 'dark' : 'gerzite').length}.`;
        // the cooldown: a turn timer on the Emperor, each ability's in its own corner
        const timer = ab.cooldown > 0 ? this.tokenDb.list.find((d) => d.effect === E.timer) : undefined;
        // (Emperor's Tavern: a turn less of cooldown, never less than 1)
        const sooner = CONFIG.combat.enabled ? s.field.filter((c) => this.effectsOf(c, 'always').some((e) => e.words[0] === 'cooldown_less')).length : 0;
        const wait1 = Math.max(1, ab.cooldown - sooner);
        if (timer) {
          const side = sideOf(card.owner);
          const spot = E.spots[ab.slot - 1] ?? E.spots[0] ?? { x: 0, z: 0 };
          s.tokens.push({ id: this.newId('t'), tokenId: timer.id, x: card.x + spot.x * side, z: card.z + spot.z * side, value: wait1, layer: s.nextLayer++, by: seat, since: p.turns, ability: { card: card.id, slot: ab.slot } });
        }
        this.fx('ability', card.id, undefined, { seat, ref: ab.slot });
        this.springTriggers(seat, 'emperor');
        const paid = ab.costVaries ? ' (its cost is worked out as it is used: pay it by hand)' : ab.cost > 0 ? ` for ${ab.cost} ${coin}` : '';
        const wait = ab.cooldown > 0 ? ` It is on cooldown for ${ab.cooldown} turn${ab.cooldown === 1 ? '' : 's'}.` : '';
        this.log(`${p.name} used ${def.name}’s Ability ${ab.slot}, ${ab.name}${paid}.${wait}`, seat);
        // what the table can carry out of it (the `effects` cell of emperor_abilities.csv); the rest is its text
        if (CONFIG.combat.enabled && ab.effects.length) {
          this.runEffects(card, ab.effects.filter((e) => e.when === 'use'), {});
          this.settleFallen();
        }
        return null;
      }

      case 'linkAbility': {
        // A timer or a Lock lying on an Emperor is given to one of its abilities (or taken back from it).
        // A timer becomes that ability's cooldown; a Lock locks it. Either player may do this, as card effects do.
        const E = CONFIG.emperorAbilities;
        const t = s.tokens.find((tk) => tk.id === a.id);
        if (!t) return 'That token isn’t on the table anymore.';
        const kind = this.abilityTokenKind(t);
        if (!kind) return 'Only a turn timer or a Locked token can be put on an Emperor ability.';
        if (a.slot === null) {
          delete t.ability;
          return null;
        }
        const card = this.cardUnder(t);
        const def = card ? this.cards.byNumber.get(card.num) : undefined;
        if (!card || !def || !card.faceUp || !isEmperor(def)) return 'Put it on a face-up Emperor first.';
        const ab = def.emperorAbilities.find((x) => x.slot === a.slot);
        if (!ab) return `${def.name} has no Ability ${a.slot} yet.`;
        const there = this.abilityToken(card, ab.slot, kind);
        if (there && there.id !== t.id) return kind === 'lock' ? `${ab.name} is already Locked.` : `${ab.name} is already on cooldown: change the number on its timer instead.`;
        t.ability = { card: card.id, slot: ab.slot };
        const side = sideOf(card.owner);
        const spot = (kind === 'lock' ? E.lockSpots : E.spots)[ab.slot - 1] ?? { x: 0, z: 0 };
        Object.assign(t, { x: card.x + spot.x * side, z: card.z + spot.z * side, layer: s.nextLayer++ });
        const whose = card.owner === seat ? 'their own' : `${s.players[card.owner].name}’s`;
        this.fx('status', card.id, undefined, { effect: 'notice' });
        this.log(
          kind === 'lock'
            ? `${p.name} put a Lock on ${whose} ${def.name}’s Ability ${ab.slot}, ${ab.name}.`
            : `${p.name} put ${whose} ${def.name}’s Ability ${ab.slot}, ${ab.name}, on cooldown for ${t.value} turn${t.value === 1 ? '' : 's'}.`,
          seat,
        );
        return null;
      }

      case 'peekClose': {
        s.peeks[seat] = null;
        return null;
      }

      case 'requestEnd': {
        if (s.mode === 'solo') {
          // nobody to ask: the game just ends
          this.finish(a.kind === 'call' ? 'call' : 'end', null);
          const hurt = s.result!.damage[0];
          this.log(a.kind === 'call' ? `${p.name} called the game here, with ${hurt} damage on their Emperor.` : `${p.name} ended the game.`, seat);
          return null;
        }
        if (a.kind === 'end') return 'With two players, a game ends by a forfeit, a win, or calling it together.';
        if (s.endRequest) return 'A request is already waiting for an answer.';
        s.endRequest = { kind: a.kind, from: seat };
        if (s.teams) this.log(a.kind === 'forfeit' ? `${p.name} wants their team to forfeit.` : a.kind === 'win' ? `${p.name} says their team has won!` : `${p.name} proposes to call the game here: the team whose Emperors have the larger share of their health left wins.`, seat);
        else this.log(a.kind === 'forfeit' ? `${p.name} wants to forfeit.` : a.kind === 'win' ? `${p.name} says they have won!` : `${p.name} proposes to call the game here: the Emperor with the larger share of its health left wins.`, seat);
        return null;
      }

      case 'timerAsk': {
        if (s.mode !== 'versus' || s.tutorial) return 'The Battle Timer is for 1v1 and 2v2 games.';
        if (s.bots?.includes(seat)) return 'Bots don’t ask for the Battle Timer.';
        const bt = (s.battleTimer ??= { on: false, ask: null });
        const on = !!a.on;
        if (bt.on === on) return on ? 'The Battle Timer is already on.' : 'The Battle Timer is already off.';
        if (bt.ask) return 'A Battle Timer request is already waiting for an answer.';
        bt.ask = { from: seat, on, yes: [] };
        this.log(on ? `${p.name} asks to turn on the Battle Timer: ${this.timerWords()} per turn, and running out loses the game.` : `${p.name} asks to turn off the Battle Timer.`, seat);
        this.settleTimerAsk();
        return null;
      }

      case 'timerAnswer': {
        const bt = s.battleTimer;
        const ask = bt?.ask;
        if (!bt || !ask) return null;
        if (ask.from === seat || (this.allies(ask.from, seat) && !a.accept)) {
          bt.ask = null;
          this.log(`${p.name} took back the Battle Timer request.`, seat);
          return null;
        }
        if (this.allies(ask.from, seat)) return 'Your teammate asked: the other team answers that.';
        if (!a.accept) {
          bt.ask = null;
          this.log(`${p.name} declined to turn the Battle Timer ${ask.on ? 'on' : 'off'}.`, seat);
          return null;
        }
        if (!ask.yes.includes(seat)) ask.yes.push(seat);
        this.log(`${p.name} agreed to turn the Battle Timer ${ask.on ? 'on' : 'off'}.`, seat);
        this.settleTimerAsk();
        return null;
      }

      case 'timeUp': {
        if (s.mode !== 'versus') return 'Only in a game against someone.';
        if (a.why === 'clock' && (!s.battleTimer?.on || s.turn !== seat)) return 'The Battle Timer isn’t running for that player.';
        if (a.why === 'everyone') {
          // every player left and none came back in time: nobody wins
          this.finish('end', null);
          s.result!.timeUp = { seat, why: 'everyone' };
          this.log('Everyone left the game and nobody came back in time, so it ends in a draw.', null);
          return null;
        }
        // 2v2, a player who left while their teammate plays on: they drop out as if their Emperor had fallen, but
        // their Emperor leaves the table. Their teammate takes over their lanes and everything they left in play.
        const mate = this.mateOf(seat);
        if (a.why === 'gone' && s.teams && mate !== null && !s.players[mate].out) {
          this.dropOut(seat);
          return null;
        }
        const winner = this.sideAfter(seat);
        this.finish('forfeit', winner);
        s.result!.timeUp = { seat, why: a.why === 'gone' ? 'gone' : 'clock' };
        const side = s.teams ? this.teamName(winner) : s.players[winner].name;
        this.log(a.why === 'clock' ? `${p.name} ran out of time on the Battle Timer and loses by forfeit. ${side} win${s.teams ? '' : 's'}!` : `${p.name} left the game and didn’t come back in time, so they lose by forfeit. ${side} win${s.teams ? '' : 's'}!`, seat);
        return null;
      }

      case 'announce': {
        if (!s.bots?.includes(seat)) return 'Only a bot announces like that.';
        const text = String(a.text ?? '').slice(0, 600);
        if (!text) return null;
        const card = a.card ? this.findField(a.card) : undefined;
        this.log(`${p.name}: ${text}`, seat);
        this.fx('status', card?.id ?? '', undefined, { effect: 'notice' }); // the card pulses; the words go in the log (bots carry out their cards themselves: there is nothing to settle)
        return null;
      }

      case 'botAsk': {
        if (!s.bots?.includes(seat)) return 'Only a bot asks like that.';
        s.botAsk = { from: seat, text: String(a.text ?? '').slice(0, 200), ...(a.end ? { end: true } : {}) };
        return null;
      }

      case 'botSettle': {
        // the player has settled one thing a bot's card asked for: it comes off the bot's list
        if (s.bots?.includes(seat)) return 'The other player settles that.';
        const notes = s.botNotes ?? [];
        if (a.all) {
          s.botNotes = [];
          return null;
        }
        const i = notes.indexOf(String(a.text ?? ''));
        if (i < 0) return null;
        s.botNotes = [...notes.slice(0, i), ...notes.slice(i + 1)];
        return null;
      }

      case 'botAnswer': {
        if (!s.botAsk) return null;
        if (s.bots?.includes(seat)) return 'The other player answers that.';
        // before a bot ends its turn, everything it asked to be settled this turn has to be
        if (s.botAsk.end && s.botNotes?.length) return `First settle everything on ${s.players[s.botAsk.from].name}’s list (or press Settled on each).`;
        s.botAsk = null;
        return null;
      }

      case 'answerEnd': {
        const req = s.endRequest;
        if (!req) return null;
        s.endRequest = null;
        if (req.from === seat) {
          this.log(`${p.name} took back their request.`, seat);
          return null;
        }
        if (this.allies(req.from, seat)) {
          s.endRequest = req; // a teammate's request is for the other team to answer
          return 'Your teammate asked: the other team answers that.';
        }
        if (!a.accept) {
          this.log(`${p.name} declined.`, seat);
          return null;
        }
        if (req.kind === 'call') {
          // called where it stands: the side whose Emperor has the larger share of its health left wins (a share, so a
          // bigger Emperor doesn't win just by being bigger); even, the one that dealt more damage to Emperors with
          // attacks and Spells; even again, a draw. (2v2: each team's Emperors together)
          const { winner, why } = this.decideEven(false);
          this.finish('call', winner);
          const score = s.players.map((pl, i) => { const l = this.emperorLeft(i as Seat); return `${pl.name}’s Emperor ${l.left}/${l.full}`; }).join(', ');
          const won = winner === null ? 'It’s a draw!' : s.teams ? `${this.teamName(winner)} win!` : `${s.players[winner].name} wins!`;
          this.log(`${p.name} accepted. The game is called here on health left: ${score}${why ? ` (${why})` : ''}. ${won}`, seat);
          return null;
        }
        const winner = req.kind === 'forfeit' ? seat : req.from;
        this.finish(req.kind, winner);
        this.log(`${p.name} accepted. ${s.teams ? `${this.teamName(winner)} win!` : `${s.players[winner].name} wins!`}`, seat);
        return null;
      }
    }
  }

  /** 2v2: has this player's Emperor fallen (its damage has reached its health, or it has left the table)? */
  private fallen(seat: Seat) {
    const emperor = this.emperorOf(seat);
    const health = emperor ? this.cards.byNumber.get(emperor.num)?.health : null;
    return !emperor || (!!health && this.damageOn(emperor) >= health);
  }
  /**
   * 2v2, as a turn ends: a player whose Emperor has fallen is out (their turns are skipped, their teammate takes
   * over both their lanes, and they watch their teammate). A team with no one left loses. Returns true if the
   * game has ended.
   */
  private checkOut(turnEnds = false): boolean {
    const s = this.state;
    if (s.result || s.mode !== 'versus') return false;
    // a team whose Emperors have all fallen has lost at once, as in 1v1
    const wiped = new Set(([0, 1] as const).filter((team) => s.players.every((pl, i) => teamOf(i) !== team || pl.out || this.fallen(i as Seat))));
    s.players.forEach((pl, i) => {
      if (pl.out || !this.fallen(i as Seat)) return;
      // 2v2: otherwise a fallen Emperor puts its player out only as the turn ends (it can still be healed until
      // then). After any other move a player whose Emperor fell in their own turn would be out with the turn still
      // theirs, and nobody could end it.
      if (s.teams && !turnEnds && !wiped.has(teamOf(i))) return;
      pl.out = true;
      pl.searching = null;
      this.passOwed(i as Seat);
      const mi = this.mateOf(i as Seat);
      const mate = mi !== null && mi !== undefined ? s.players[mi] : undefined;
      this.log(`${pl.name}’s Emperor has fallen${s.teams ? `: ${pl.name} is out.${!mate || mate.out ? '' : ` ${mate.name} plays on alone and takes over all four lanes.`}` : '.'}`, i as Seat);
    });
    const down = ([0, 1] as const).filter((team) => s.players.every((pl, i) => teamOf(i) !== team || pl.out));
    if (!down.length) return false;
    const name = (t: Seat) => (s.teams ? this.teamName(t) : s.players[t].name);
    if (down.length === 2) {
      // both fell together (Overtime): the one that dealt more damage to Emperors with attacks and Spells wins
      const { winner, why } = this.decideEven(true);
      this.finish('win', winner);
      this.log(`Every Emperor has fallen at once${why ? `: ${why}` : ''}. ${winner === null ? 'It’s a draw!' : `${name(winner)} win${s.teams ? '' : 's'}!`}`, null);
      return true;
    }
    const winner = (1 - down[0]) as Seat; // the other side (2v2: that team's left seat)
    this.finish('win', winner);
    // (1v1: the line just above already says whose Emperor has fallen)
    this.log(s.teams ? `${this.teamName(down[0] as Seat)} have no Emperor left. ${this.teamName(winner)} win!` : `${s.players[winner].name} wins!`, null);
    return true;
  }
  /** An Emperor's health left and its full health (0/… once it has left the table). */
  private emperorLeft(seat: Seat): { left: number; full: number } {
    const e = this.emperorOf(seat);
    const def = e ? this.cards.byNumber.get(e.num) : undefined;
    const full = e && def ? (this.fullForm(e, def).health ?? 0) + this.gainedHealth(this.tokensOn(e)) : 1;
    return { left: e ? Math.max(0, full - this.damageOn(e)) : 0, full: Math.max(1, full) };
  }
  /**
   * The winner of a game decided where it stands (a call), or by every Emperor falling at once: the side with the
   * larger share of its Emperors' health left; even (or `fallen`: all at 0), the side that dealt more damage to
   * Emperors with attacks and Spells; even again, nobody (a draw).
   */
  private decideEven(fallen: boolean): { winner: Seat | null; why: string } {
    const s = this.state;
    const side = (t: number) => s.players.map((_, i) => i).filter((i) => teamOf(i) === t);
    const share = [0, 1].map((t) => { const l = side(t).map((i) => this.emperorLeft(i as Seat)); return l.reduce((n, x) => n + x.left, 0) / l.reduce((n, x) => n + x.full, 0); });
    if (!fallen && Math.abs(share[0] - share[1]) > 1e-9) return { winner: (share[0] > share[1] ? 0 : 1) as Seat, why: `${Math.round(share[0] * 100)}% against ${Math.round(share[1] * 100)}% of their health left` };
    const hits = [0, 1].map((t) => side(t).reduce((n, i) => n + (s.emperorHits?.[i] ?? 0), 0));
    if (hits[0] !== hits[1]) return { winner: (hits[0] > hits[1] ? 0 : 1) as Seat, why: `${fallen ? '' : 'even on health left; '}${s.teams ? this.teamName((hits[0] > hits[1] ? 0 : 1) as Seat) : s.players[hits[0] > hits[1] ? 0 : 1].name} dealt more damage to Emperors with attacks and Spells (${Math.max(...hits)} to ${Math.min(...hits)})` };
    return { winner: null, why: fallen ? '' : 'even on health left and on damage dealt' };
  }
  /** All the damage on a player's Emperor (0 if it has left the table). */
  private emperorDamage(seat: Seat) {
    const emperor = this.emperorOf(seat);
    return emperor ? this.damageOn(emperor) : 0;
  }
  /**
   * 2v2: a player who left and didn't come back drops out while their teammate plays on. Their Emperor leaves the
   * table, what they were asked or asking is put away, and their teammate takes over their two lanes and every
   * card they left in play (as when an Emperor falls). If it was their turn, the turn goes on to the next player.
   */
  private dropOut(seat: Seat) {
    const s = this.state;
    const p = s.players[seat];
    const mate = this.mateOf(seat)!;
    p.out = true;
    p.searching = null;
    p.overflow = null;
    p.span = null;
    this.passOwed(seat);
    for (const e of s.field.filter((c) => c.owner === seat && !c.spawned && this.cards.byNumber.get(c.num)?.types.includes('emperor'))) {
      this.clearTokensOn(e);
      this.removeField(e.id);
      this.closeUp(e);
    }
    if (s.picks?.length) s.picks = s.picks.filter((k) => k.seat !== seat);
    if (s.endRequest?.from === seat) s.endRequest = null;
    if (s.peekRequest?.from === seat) s.peekRequest = null;
    if (s.battleTimer?.ask?.from === seat) s.battleTimer.ask = null;
    this.log(`${p.name} left the game and didn’t come back in time, so they are out: their Emperor leaves the table. ${s.players[mate].name} plays on alone and takes over all four lanes and everything ${p.name} left in play.`, seat);
    if (s.turn === seat) {
      s.turn = this.nextSeat(seat);
      s.phase = 0;
      this.log(`It’s ${s.players[s.turn].name}’s turn.`, s.turn);
      this.startTurn(s.turn);
    }
    this.settleTimerAsk();
  }

  /**
   * Whose turns a card's own goings-on happen in (statuses, counters and timers ticking, untapping, its start- and
   * end-of-turn effects): its owner's. 2v2: a player who is out has no more turns, so what they left in play goes
   * by their teammate's turns instead (it is the teammate's to use now).
   */
  private turnOf(owner: Seat | null | undefined): Seat | null {
    if (owner === null || owner === undefined) return null;
    const s = this.state;
    if (!s.teams || !s.players[owner]?.out) return owner;
    const mate = this.mateOf(owner);
    return mate !== null && !s.players[mate].out ? mate : owner;
  }

  /** 2v2: a player who is out can't roll any more: the cure rolls and die rolls they still owed go to their teammate. */
  private passOwed(seat: Seat) {
    const s = this.state;
    const mate = this.mateOf(seat);
    if (mate === null || s.players[mate].out) return;
    for (const t of s.tokens) if (t.cure === seat) t.cure = mate;
    for (const c of s.field) if (c.roll === seat) c.roll = mate;
  }

  /** "1:30": how long each turn is on the Battle Timer. */
  private timerWords() {
    const t = CONFIG.battleTimer.seconds;
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
  }
  /** A seat on the other side from this one (2v2: the next seat round the table, on the other team). */
  private sideAfter(seat: Seat): Seat {
    return (this.state.teams ? (seat + 1) % 4 : seat === 0 ? 1 : 0) as Seat;
  }
  /**
   * A Battle Timer request goes through once everyone still playing on the other side has agreed. Bots agree at
   * once (they are quick anyway); a 2v2 player who is out has no say.
   */
  private settleTimerAsk() {
    const s = this.state;
    const bt = s.battleTimer;
    const ask = bt?.ask;
    if (!bt || !ask) return;
    const answer = s.players.map((_, i) => i as Seat).filter((i) => !this.allies(ask.from, i) && !s.players[i].out);
    const waiting = answer.filter((i) => !s.bots?.includes(i) && !ask.yes.includes(i));
    if (waiting.length) return;
    bt.on = ask.on;
    bt.ask = null;
    const bots = answer.filter((i) => s.bots?.includes(i));
    this.log(`${bots.length === answer.length ? `${bots.map((i) => s.players[i].name).join(' & ')} agreed. ` : ''}The Battle Timer is ${bt.on ? `on: each player has ${this.timerWords()} for each turn. Run out before you pass the turn on and you lose by forfeit.` : 'off.'}`, null);
  }

  private finish(kind: GameResult['kind'], winner: Seat | null) {
    const s = this.state;
    s.result = { winner, kind, damage: s.players.map((_, i) => this.emperorDamage(i as Seat)), ...(s.teams && winner !== null ? { team: teamOf(winner) } : {}) };
  }

  // ---------------- views ----------------

  /** Everything `seat` may see — and nothing more. */
  view(seat: Seat): GameView {
    const s = this.state;
    const me = s.players[seat];
    const reveal = s.reveals[seat];
    return {
      mode: s.mode,
      ...(s.teams ? { teams: true } : {}),
      you: seat,
      players: s.players.map((p, i) => ({
        name: p.name,
        ...(p.out ? { out: true } : {}),
        deckCount: p.deck.length,
        handCount: p.hand.length,
        // a hand in plain view: everyone's while a card keeps them open (Lexicon of Truth); 2v2: your teammate's
        ...(s.handsOpen || (s.teams && i !== seat && this.allies(i as Seat, seat)) ? { openHand: p.hand.map((c) => ({ ...c })) } : {}),
        discard: p.discard.map((c) => ({ ...c })),
        ...(p.limbo?.length ? { limboCount: p.limbo.length } : {}),
        // (a card's search, waiting for its player to choose — Land Looker — shows on the pile like a search by hand)
        searching: p.searching ?? ((): PileName | null => { const k = s.picks?.find((q) => q.seat === i && q.search); return k ? (k.search!.w[0] === 'discard' ? 'discard' : 'deck') : null; })(),
        shuffles: { ...p.shuffles },
        jumps: { deck: p.jumps?.deck ?? 0, discard: p.jumps?.discard ?? 0 },
        turns: p.turns,
        gerzite: this.barTokens(i as Seat, 'gerzite').length,
        darkGerzite: this.barTokens(i as Seat, 'dark').length,
      })),
      field: ((wyrm) => s.field.map((c) => c.secret && !this.allies(c.owner, seat) ? { id: c.id, num: null, owner: c.owner, x: c.x, z: c.z, rot: c.rot, faceUp: false, spawned: c.spawned, layer: c.layer, tier: 1 } : ({
        id: c.id,
        num: c.faceUp || this.allies(c.owner, seat) || this.revealedTo(c, seat) ? c.num : null,
        owner: c.owner,
        x: c.x,
        z: c.z,
        rot: c.rot,
        faceUp: c.faceUp,
        spawned: c.spawned,
        layer: c.layer,
        tier: this.tierOf(c),
        ...(c.spanOf ? { spanOf: c.spanOf } : {}),
        ...(c.asBiome && c.faceUp ? { asBiome: true } : {}),
        ...(c.faceUp && this.frenzied(c) ? { frenzy: true } : {}),
        ...(c.restHits && c.faceUp ? { restHits: c.restHits } : {}),
        ...(c.swarm ? { swarm: c.swarm } : {}),
        ...(this.isGeneral(c) ? { legionWorth: this.legionWorth(c) } : {}),
        ...((): { rangeAura?: number } => { const def = c.faceUp ? this.cards.byNumber.get(c.num) : undefined; const n = def && this.isUnit(def) ? this.auraFor(c, 'range') : 0; return n > 0 ? { rangeAura: n } : {}; })(),
        ...(c.faceUp && c.spellWardAt !== undefined && this.turnStamp() <= c.spellWardAt ? { warded: true as const } : {}),
        ...(c.faceUp && c.tauntAt !== undefined && this.turnStamp() <= c.tauntAt ? { taunting: true as const } : {}),
        ...(c.faceUp && this.tokensOn(c).some((t) => !!t.alsoWhile && this.tokenDb.byId.get(t.tokenId)?.category === 'affiliation') ? { ascended: true as const } : {}),
        ...(c.faceUp && c.mustAttack && !!this.findField(c.mustAttack) ? { jousted: true as const } : {}),
        ...(c.faceUp && (s.missMarks ?? []).some((m) => m.card === c.id && s.players[m.seat].turns <= m.turns && CONFIG.misses.laneMarks.some((r) => r.card === m.by && r.lingers)) ? { blinded: true as const } : {}),
        ...(c.faceUp && c.veilAt !== undefined && this.turnStamp() <= c.veilAt ? { veiled: true as const } : {}),
        ...(c.faceUp && c.negating && !!this.abilityToken(c, c.negating, 'cooldown') ? { exiting: true as const } : {}),
        ...(c.faceUp && s.biomeOff && s.biomeOff.at === this.turnStamp() && !!this.cards.byNumber.get(c.num)?.types.includes('biome') && !this.isExpired(c) ? { mapped: true as const } : {}),
        ...(c.faceUp && c.marks?.coat !== undefined && this.hasOn(c, 'shield') ? { coated: true as const } : {}),
        ...(c.faceUp && c.marks?.necrosis !== undefined && this.necrosisHolds(c) ? { necrotic: true as const } : {}),
        // Wingwrap: every opposing Troop it holds to the ground is wrapped in vines while it lasts (a Flying one also comes down to the table)
        ...((): { grounded?: true; vined?: 'wing' } => { const d = c.faceUp ? this.cards.byNumber.get(c.num) : undefined; if (d && !this.wingwrapped(c) && this.tokensOn(c).some((t) => t.grounds) && (d.movement === 'flying' || this.hasOn(c, 'flying'))) return { grounded: true }; if (!d || !this.wingwrapped(c)) return {}; return d.movement === 'flying' || this.hasOn(c, 'flying') ? { grounded: true, vined: 'wing' } : { vined: 'wing' }; })(),
        // Tangleweed's Lock: dark seaweed vines (over Wingwrap's, if both)
        ...(CONFIG.looks.lockVines.length && this.tokensOn(c).some((t) => this.effectOf(t) === CONFIG.tokenRules.lockToken && !!t.from && CONFIG.looks.lockVines.includes(this.cardName(this.findField(t.from)?.num ?? -1))) ? { vined: 'tangle' as const } : {}),
        ...(c.actions && c.actions > 1 && c.faceUp ? { actions: { total: c.actions, left: c.swings?.key === this.turnKey() ? Math.ceil(c.swings.left / Math.max(1, this.attackOf(c, this.cards.byNumber.get(c.num)!).hits)) : c.actions } } : {}),
        ...(c.lanes && c.faceUp ? { laneSpots: laneOrder().filter((l) => c.lanes!.names.includes(l.name)).map((l) => ({ x: l.x * c.lanes!.side, z: l.z * c.lanes!.side })) } : {}),
        ...(c.faceUp && s.field.some((g) => g.intercede === c.id && g.faceUp && !g.atZero && !this.hasOn(g, CONFIG.turn.lifetime.expiredToken)) ? { guarded: true } : {}),
        ...(c.scale && c.scale !== 1 ? { scale: c.scale } : {}),
        ...(c.attachedTo ? { attachedTo: c.attachedTo } : {}),
        ...(c.clone ? { clone: { ...c.clone } } : {}),
        ...(!c.faceUp && this.cards.byNumber.get(c.num)?.types.some((t) => CONFIG.arrival.faceDownTypes.includes(t)) ? { armed: true } : {}),
        ...(c.form && (c.faceUp || this.allies(c.owner, seat)) ? { form: c.form } : {}),
        ...(c.restarts ? { restarts: c.restarts } : {}),
        ...(c.roll !== undefined ? { roll: c.roll } : {}),
        ...(c.rollFor ? { rollFor: c.rollFor } : {}),
        ...(wyrm.has(c.id) ? { wyrm: true } : {}),
        ...((): { bufferAura?: number } => { const def = c.faceUp ? this.cards.byNumber.get(c.num) : undefined; const n = def && this.isUnit(def) ? this.auraFor(c, 'buffer') : 0; return n > 0 ? { bufferAura: n } : {}; })(),
      })))(this.wyrmLending()),
      // (the counters on a card its opponents mustn't see yet — Timed Blast's turn timer — are hidden from them with it)
      tokens: ((hid) => s.tokens.filter((t) => !hid.length || !hid.some((c) => tokensOnCard(c, [t]).length)).map((t) => ({ ...t })))(s.field.filter((c) => c.secret && !this.allies(c.owner, seat))),
      turn: s.turn,
      first: s.first,
      overtime: this.overtime(),
      ...(this.doubleOvertime() ? { doubleOvertime: true } : {}),
      fractalOffer: s.turn === seat && s.phase === PHASES.length - 1 && !s.fractalsTaken ? this.fractalsEarned(seat) : null,
      wideLanes: this.wideLanes(),
      costChange: Object.fromEntries(me.hand.map((c) => [c.id, this.costChange(seat, this.cards.byNumber.get(c.num))] as const).filter(([, d]) => d !== 0)),
      costFacts: this.costFacts(seat),
      handsOpen: s.handsOpen,
      freeUnit: s.players[seat]?.freeUnit?.id ?? null,
      bargain: s.bargain ? { by: s.bargain.by, value: s.bargain.value, max: s.bargain.max, agreed: [...s.bargain.agreed] } : null,
      ...(s.players[seat] && this.supportInSpell(seat) ? { supportInSpell: true } : {}),
      ...(() => {
        const p = s.players[seat];
        if (!p || s.turn !== seat) return {};
        const out: Record<string, string> = {};
        for (const c of p.hand) { const why = this.playBarred(seat, this.cards.byNumber.get(c.num), false); if (why) out[c.id] = why; }
        return Object.keys(out).length ? { handBlocked: out } : {};
      })(),
      fallen: s.players.map((_, i) => i as Seat).filter((i) => {
        const emperor = this.emperorOf(i);
        const health = emperor ? this.cards.byNumber.get(emperor.num)?.health : null;
        return !!emperor && !!health && this.damageOn(emperor) >= health;
      }),
      phase: s.phase,
      globalTurn: this.globalTurn(),
      log: s.log.map((l) => ({ ...l })),
      combat: this.combatView(seat),
      pick: (() => { const k = s.picks?.find((q) => q.seat === seat); return k ? { id: k.id, name: k.name, text: k.text, options: [...k.options], ...(k.yesNo ? { yesNo: true } : {}), ...(k.optional ? { optional: true } : {}), ...(k.skip ? { skip: k.skip } : {}), ...(k.numbers ? { numbers: k.numbers.map((n) => ({ ...n })), confirm: k.confirm ?? 'OK' } : {}), ...(k.split ? { done: 'Done', left: k.split.left } : {}), ...(k.arrange ? { arrange: true, undead: [...k.arrange.undead] } : {}), ...(k.cards ? { cards: k.cards.map((c) => ({ ...c })) } : {}), ...(k.choices ? { choices: [...k.choices] } : {}), ...(k.laneHints ? { laneHints: k.laneHints.map((l) => l.map((p) => ({ ...p }))) } : k.laneChoices ? { laneHints: k.laneChoices.map((o) => { const l = laneOrder().find((x) => x.name === o.name); return l ? [{ x: l.x * o.side, z: l.z * o.side }] : []; }) } : {}), ...(k.showing !== undefined ? { showing: k.showing } : {}) } : null; })(),
      fx: s.fx.map((f) => ({ ...f })),
      hand: me.hand.map((c) => ({ ...c })),
      ...((): { hornCards?: string[] } => {
        // a free summon held from an ability (a discount with a "gift": Bellowing Horn) — the cards it fits glow in the hand
        const stamp = this.turnStamp();
        const held = (me.discounts ?? []).filter((d) => d.w.includes('gift') && (d.stamp === undefined || d.stamp === stamp));
        if (!held.length) return {};
        const ids = me.hand.filter((c) => { const d = this.cards.byNumber.get(c.num); return !!d && held.some((h) => this.passes(d, h.w)); }).map((c) => c.id);
        return ids.length ? { hornCards: ids } : {};
      })(),
      search: me.searching ? { pile: me.searching, cards: me[me.searching].map((c) => ({ ...c })) } : null,
      reveal: reveal ? { fromName: s.players[reveal.from].name, cards: reveal.cards.map((c) => ({ ...c })) } : null,
      peekRequest: s.peekRequest ? { ...s.peekRequest } : null,
      showing: s.showing ? { ...s.showing, cards: s.showing.cards.map((c) => ({ ...c })) } : null,
      peek: s.peeks[seat] ? { owner: s.peeks[seat]!.owner, pile: s.peeks[seat]!.pile, cards: s.peeks[seat]!.cards.map((c) => ({ ...c })) } : null,
      overflow: me.overflow ? { ...me.overflow } : null,
      span: me.span ? { card: me.span.card, num: s.field.find((c) => c.id === me.span!.card)?.num ?? 0, choices: me.span.starts.map((st) => laneOrder().slice(st, st + this.spanCount(me.span!.card)).map((l) => l.name)) } : null,
      endRequest: s.endRequest ? { ...s.endRequest } : null,
      ...(s.battleTimer ? { battleTimer: { on: s.battleTimer.on, ask: s.battleTimer.ask ? { ...s.battleTimer.ask, yes: [...s.battleTimer.ask.yes] } : null } } : {}),
      ...(s.calling?.length ? { calling: [...s.calling] } : {}),
      ...(s.snares?.length ? { snares: s.snares.map((n) => { const l = laneOrder().find((x) => x.name === n.lane); return { x: (l?.x ?? 0) * n.side, z: (l?.z ?? 0) * n.side, by: n.by }; }) } : {}),
      ...(s.bots ? { bots: [...s.bots], botAsk: s.botAsk ? { ...s.botAsk } : null, botNotes: [...(s.botNotes ?? [])] } : {}),
      ...(s.tutorial ? { tutorial: true } : {}),
      ...(s.echoes?.length ? { echoes: s.echoes.map((e) => ({ lane: e.lane, side: e.side, by: e.by, amount: e.amount, foe: e.foe, dueNow: s.turn === e.foe && s.players[e.foe].turns >= e.turn })) } : {}),
      result: s.result ? { ...s.result, damage: [...s.result.damage], ...(s.result.timeUp ? { timeUp: { ...s.result.timeUp } } : {}) } : null,
      version: s.version,
    };
  }
  /**
   * What someone watching the game sees, looking from `side`'s end of the table: everything both players can
   * see, and nothing only one of them can (no hand, no face-down card, no search, peek or private notice).
   */
  spectatorView(side: Seat): GameView {
    const v = this.view(side);
    return {
      ...v,
      spectator: true,
      players: this.state.handsOpen ? v.players : v.players.map(({ openHand: _hidden, ...pl }) => pl),
      field: v.field.map((c) => (c.faceUp ? c : { ...c, num: null, form: undefined })),
      combat: {},
      pick: null,
      hand: [],
      search: null,
      reveal: null,
      peek: null,
      overflow: null,
      span: null,
      freeUnit: null,
      fractalOffer: null,
      // a line meant for one player only (a notice, a question) stays with that player
      fx: v.fx.map((f) => (f.seat !== undefined && f.text && f.kind !== 'ping' ? { ...f, text: undefined } : f)),
    };
  }
}
